import { useState, useEffect, useRef, useCallback, useMemo, useTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  ArrowLeft, Printer, ScrollText, Tag, Download, Upload, FileSpreadsheet, Search,
  Settings, AlertTriangle, Pencil, BarChart3, Trash2, Coins, Plus, ClipboardList,
  CircleDot, Lightbulb, BookOpen, Package, RefreshCw, Flame, Gem, Circle, Menu, X,
  Send, Inbox, BarChart2, Archive, ChevronDown, ChevronRight, ChevronLeft, Clock, User, Bell,
  ArrowLeftRight, Calculator, SlidersHorizontal, Calendar, Image, ShoppingCart, RotateCcw, BellOff, Check, ExternalLink, Star, Wifi, Layers, Camera, Smartphone
} from 'lucide-react';
import LogModal from '../components/LogModal';
import logService from '../services/logService';
import { API_URL, API_HOST } from '../config/api';
import useEscapeClose from '../hooks/useEscapeClose';
import { handlePrint } from '../utils/printHelper';
import { LABEL_EL_CLASS, getLabelPrintOffset, labelShiftScript, getBarcodeModuleMm, labelBarcodeSizeScript, getLabelPageHeightIn, labelElementCss, getLabelAreaPrintOffset, getLabelAlign, labelAlignJustify, labelBarcodeOptions } from '../utils/labelPrintOffset';
import { checkZebraAvailable, ensureZebraAvailable, printLabelsZebra } from '../utils/zebraLabel';
import { buildCustomerReceiptHtml, buildRepairReceiptHtml } from '../utils/repairReceiptHtml';
import { printSystemReceipt } from '../utils/systemPrintRelay';
import PriceInput from '../components/PriceInput';
import './Products.css';

const IMAGE_HOST = API_HOST;

// Varyant grubu ekseni olarak seçilebilecek ürün alanları - Shopify en fazla 3 option'a izin
// verdiği için en fazla 3'ü aynı anda seçilebilir. 'size' alanının gerçek Shopify option adı
// (Yüzük/Bileklik/Kolye Ölçüsü) ürünün kategorisine göre backend'de belirlenir.
const VARIANT_AXIS_OPTIONS = [
  { key: 'size', label: 'Ölçü (Yüzük/Bileklik/Kolye)' },
  { key: 'color', label: 'Renk' },
  { key: 'stone_color', label: 'Taş Rengi' },
  { key: 'stone_mm', label: 'Taş Milimi' },
];
const VARIANT_AXIS_PRIORITY = VARIANT_AXIS_OPTIONS.map(o => o.key);
const variantAxisLabel = (key) => VARIANT_AXIS_OPTIONS.find(o => o.key === key)?.label || key;
const suggestVariantAxes = (members) => {
  if (members.length < 1) return [];
  return VARIANT_AXIS_PRIORITY.filter(f => members.every(m => m[f] && String(m[f]).trim() !== '')).slice(0, 3);
};
const Products = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState(() => {
    // Admin olmayan kullanıcılar mağazalarının stok tipine göre başlasın.
    // Not: 'depo' rolü tek başına 'silver' anlamına gelmez - Akik Gold (store_id=1)
    // deposundaki depo kullanıcıları sadece altın görebilir, onlar 'gold' ile başlamalı.
    const sid = parseInt(user?.store_id);
    if (user?.role !== 'admin' && (sid === 2 || sid === 3 || sid === 4)) {
      return 'silver';
    }
    return 'gold';
  }); // 'gold', 'silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf', 'ecommerce', 'online'
  const [selectedCategory, setSelectedCategory] = useState('all');
  // "Markalar" sekmesi için marka filtresi dropdown'u
  const [brandMenuBrands, setBrandMenuBrands] = useState([]); // ['Marka A', 'Marka B', ...]
  const [brandMenuOpen, setBrandMenuOpen] = useState(false);
  const brandMenuCloseTimerRef = useRef(null);
  // Marka dropdown'undan seçim yapılınca ürün listesini yeniden çekmeyi tetikler.
  // (advancedFilters gelişmiş panelde her tuşta değiştiği için ana fetch effect'ine
  // dep olarak eklenemiyor; bu nonce sadece dropdown seçiminde artıyor.)
  const [brandFilterNonce, setBrandFilterNonce] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Döviz/Altın fiyatları (formüllü ürünler için)
  const [prices, setPrices] = useState(null);
  const [customRates, setCustomRates] = useState({});
  const [priceUpdateTrigger, setPriceUpdateTrigger] = useState(0); // Fiyat güncellendiğinde re-render için
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null); // Son güncelleme zamanı
  const [priceUpdateText, setPriceUpdateText] = useState(''); // Göreceli zaman metni
  const [isRefreshingPrices, setIsRefreshingPrices] = useState(false); // Güncelleme butonu için

  // Infinite scroll için
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const searchTimeoutRef = useRef(null);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const loadingRef = useRef(false); // pageNum=1 (baştan yükleme) devam ederken infinite-scroll'un erken sayfa-2 istemesini engellemek için
  const fetchGenRef = useRef(0); // Filtre/sıralama değişince eski (gecikmiş) fetch'lerin sonucu göz ardı edilsin diye
  const mainContentRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Düzenleme için
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [originalProduct, setOriginalProduct] = useState(null);
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const [lightboxImageUrl, setLightboxImageUrl] = useState(null);

  // Set/Paket bileşenleri için
  const [setComponents, setSetComponents] = useState([]);
  const [setComponentSearch, setSetComponentSearch] = useState('');
  const [setComponentResults, setSetComponentResults] = useState([]);
  const [loadingSetComponents, setLoadingSetComponents] = useState(false);
  const setComponentSearchTimeout = useRef(null);

  // Sanal (stoksuz) varyant seçenekleri için - tek ürünün stoğunu paylaşan ek Shopify varyantları
  const [virtualVariants, setVirtualVariants] = useState([]);
  const [loadingVirtualVariants, setLoadingVirtualVariants] = useState(false);
  const [newVirtualVariantAxis, setNewVirtualVariantAxis] = useState('color');
  const [newVirtualVariantValue, setNewVirtualVariantValue] = useState('');
  const [savingVirtualVariant, setSavingVirtualVariant] = useState(false);

  // Stok hareket geçmişi için
  const [showStockHistoryModal, setShowStockHistoryModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  const [loadingStockHistory, setLoadingStockHistory] = useState(false);
  const [stockHistoryStoreId, setStockHistoryStoreId] = useState(null);

  // Resim zoom için
  const [zoomedImage, setZoomedImage] = useState(null);
  const [zoomedProductIndex, setZoomedProductIndex] = useState(null);
  // Barkod bandı bu snapshot'tan okunur (sortedProducts'tan canlı index ile değil) —
  // liste arka planda yeniden yüklenir/sıralanırsa (örn. fiyat güncellemesi) index kayıp
  // barkod, ekrandaki fotoğrafla eşleşmeyen bir ürününkini göstermesin ya da boş kalmasın diye.
  const [zoomedProduct, setZoomedProduct] = useState(null);
  // Galeride ileri gidilirken yüklenmiş ürün listesinin sonuna gelinirse, kullanıcı ayrıca
  // sayfayı kaydırmadan da arkada bir sonraki sayfa otomatik yüklensin diye bekleyen yön burada tutulur.
  const pendingZoomNavRef = useRef(null);

  const getProductImageUrl = (product) => product?.image ? (product.image.startsWith('http') ? product.image : `${IMAGE_HOST}${product.image}`) : null;

  const closeZoomedImage = () => {
    setZoomedImage(null);
    setZoomedProductIndex(null);
    setZoomedProduct(null);
    pendingZoomNavRef.current = null;
  };

  const openZoomedProductImage = (index) => {
    const product = sortedProducts[index];
    const url = getProductImageUrl(product);
    if (!url) return;
    setZoomedImage(url);
    setZoomedProductIndex(index);
    setZoomedProduct(product);
  };

  // Grup üyesi satırları sortedProducts'ta olmadığından ok tuşuyla gezinme yapılmaz
  const openZoomedImageForProduct = (product) => {
    const url = getProductImageUrl(product);
    if (!url) return;
    setZoomedImage(url);
    setZoomedProductIndex(null);
    setZoomedProduct(product);
  };

  const navigateZoomedProduct = (direction) => {
    if (zoomedProductIndex === null) return;
    let idx = zoomedProductIndex;
    while (true) {
      idx += direction;
      if (idx < 0) return;
      if (idx >= sortedProducts.length) {
        // Yüklenmiş ürünlerin sonuna gelindi. Daha fazla ürün varsa (hasMore), kullanıcı
        // sayfayı kaydırmasa bile arkada otomatik olarak bir sonraki sayfayı yükle;
        // yeni ürünler gelince aşağıdaki useEffect navigasyona kaldığı yerden devam eder.
        if (direction > 0 && hasMoreRef.current) {
          pendingZoomNavRef.current = direction;
          if (!loadingMoreRef.current && !loadingRef.current) {
            loadingMoreRef.current = true;
            setLoadingMore(true);
            fetchProductsRef.current(pageRef.current + 1, true);
          }
        }
        return;
      }
      const product = sortedProducts[idx];
      const url = getProductImageUrl(product);
      if (url) {
        setZoomedImage(url);
        setZoomedProductIndex(idx);
        setZoomedProduct(product);
        return;
      }
    }
  };

  // Galeride "sonraki" için ürün listesinin sonuna gelinip arka planda yeni sayfa
  // yüklendiğinde, bekleyen navigasyonu otomatik tamamlar.
  useEffect(() => {
    if (pendingZoomNavRef.current !== null && zoomedImage) {
      const direction = pendingZoomNavRef.current;
      pendingZoomNavRef.current = null;
      navigateZoomedProduct(direction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length]);

  useEffect(() => {
    if (!zoomedImage) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeZoomedImage();
      else if (e.key === 'ArrowLeft') navigateZoomedProduct(-1);
      else if (e.key === 'ArrowRight') navigateZoomedProduct(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomedImage, zoomedProductIndex]);

  // Log modal için
  const [showLogModal, setShowLogModal] = useState(false);

  // Yardım modal için
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Modal kapanış animasyonu için state
  const [closingModal, setClosingModal] = useState(null);

  // Modal kapanış handler'ı (callback opsiyonel)
  const closeModalTimerRef = useRef(null);
  const handleCloseModal = (modalName, setShowModal, callback) => {
    setClosingModal(modalName);
    if (closeModalTimerRef.current) clearTimeout(closeModalTimerRef.current);
    closeModalTimerRef.current = setTimeout(() => {
      closeModalTimerRef.current = null;
      setShowModal(false);
      setClosingModal(null);
      if (callback) callback();
    }, 250);
  };

  // Mağazalar
  const [stores, setStores] = useState([]);

  // Sütun görünürlüğü ayarları
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // Detaylı filtre
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const emptyAdvancedFilters = {
    name: '',
    barcode: '',
    brand: '',
    supplier: '',
    description: '',
    notes: '',
    color: '',
    size: '',
    gender: '',
    createdBy: '',
    stockEntryBefore: '',
    stockEntryAfter: '',
    noStockChangeSince: '',
    createdAfter: '',
    createdBefore: '',
    updatedAfter: '',
    updatedBefore: '',
    minPrice: '',
    maxPrice: '',
    minStock: '',
    maxStock: '',
    stockScope: '',
    minWeight: '',
    maxWeight: '',
    minLaborCost: '',
    maxLaborCost: '',
    minProfitMargin: '',
    maxProfitMargin: '',
    minMinStockThreshold: '',
    maxMinStockThreshold: '',
    metalType: '',
    category: '',
    goldKarat: '',
    silverPurity: '',
    currency: '',
    pricingType: '',
    shopifyStatus: '',
    hasImage: '',
    ecommerceActive: '',
    shopifyActive: '',
    trendyolActive: '',
    hepsiburadaActive: '',
    amazonActive: '',
    n11Active: '',
    isSet: '',
    isFavoriteFlag: '',
    eskiEtiket: '',
    noDiscount: '',
    isSpecialDefined: ''
  };
  const [advancedFilters, setAdvancedFilters] = useState(emptyAdvancedFilters);
  // Detaylı Filtre dropdown seçenekleri (marka, tedarikçi, renk, beden, oluşturan, ayarlar)
  const [filterOptions, setFilterOptions] = useState(null);
  const defaultColumns = {
    image: true,
    name: true,
    barcode: true,
    category: true,
    brand: true,
    price: true,
    storeStock: true,
    osmangaziStock: false,
    yesilsuStock: false,
    depoStock: false,
    totalStock: true,
    description: false,
    supplier: false,
    color: false,
    size: false,
    weight: false,
    purity: true,
    profitMargin: false,
    laboredGram: false,
    currency: false,
    minStock: false,
    ecommerce: false,
    stockDates: false
  };
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('productsVisibleColumns');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Eski 'stock' ve 'status' ayarlarını yeni sütunlara dönüştür
      if ('stock' in parsed && !('storeStock' in parsed)) {
        parsed.storeStock = parsed.stock;
        parsed.totalStock = parsed.stock;
        delete parsed.stock;
      }
      if ('status' in parsed) {
        delete parsed.status;
      }
      // Eksik ayarları default ile doldur
      return { ...defaultColumns, ...parsed };
    }
    return defaultColumns;
  });

  // Tüm sütun tanımları
  const allColumns = [
    { key: 'image', label: 'Resim', default: true },
    { key: 'name', label: 'Ürün Adı', default: true },
    { key: 'barcode', label: 'Barkod', default: true },
    { key: 'category', label: 'Kategori', default: true },
    { key: 'brand', label: 'Marka', default: true },
    { key: 'price', label: 'Fiyat', default: true },
    { key: 'currency', label: 'Para Birimi', default: false },
    { key: 'storeStock', label: 'Mağaza Stoğu', default: true },
    { key: 'osmangaziStock', label: 'Osmangazi Stok', default: false },
    { key: 'yesilsuStock', label: 'Yeşilsu Stok', default: false },
    { key: 'depoStock', label: 'Depo Stok', default: false },
    { key: 'totalStock', label: 'Toplam Stok', default: true },
    { key: 'minStock', label: 'Min. Stok', default: false },
    { key: 'description', label: 'Açıklama', default: false },
    { key: 'supplier', label: 'Tedarikçi', default: false },
    { key: 'color', label: 'Renk', default: false },
    { key: 'size', label: 'Ölçü', default: false },
    { key: 'weight', label: 'Gramaj', default: false },
    { key: 'purity', label: 'Ayar', default: true },
    { key: 'profitMargin', label: 'Kat Sayı', default: false },
    { key: 'laboredGram', label: 'İşçilikli Gram', default: false },
    { key: 'ecommerce', label: 'E-Ticaret', default: false },
    { key: 'eskiEtiket', label: 'Eski Etiket', default: false },
    { key: 'noDiscount', label: 'İndirim Yapılamaz', default: false },
    { key: 'stockEntryDate', label: 'Giriş Tarihi', default: false },
    { key: 'stockChangeDate', label: 'Değişiklik Tarihi', default: false },
    { key: 'createdBy', label: 'Oluşturan', default: false }
  ];

  // Sütun görünürlüğü değiştir
  const toggleColumn = (columnKey) => {
    const newColumns = { ...visibleColumns, [columnKey]: !visibleColumns[columnKey] };
    setVisibleColumns(newColumns);
    localStorage.setItem('productsVisibleColumns', JSON.stringify(newColumns));
  };

  // Tüm sütunları sıfırla
  const resetColumns = () => {
    setVisibleColumns(defaultColumns);
    localStorage.setItem('productsVisibleColumns', JSON.stringify(defaultColumns));
  };

  // Excel işlemleri için
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPriceUpdateModal, setShowPriceUpdateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStep, setExportStep] = useState(1); // 1: metal türü, 2: kategori
  const [exportMetalType, setExportMetalType] = useState(null);
  const [exportCategories, setExportCategories] = useState([]); // Çoklu kategori seçimi
  const [exporting, setExporting] = useState(false); // Dışa aktarma işlemi durumu
  const [exportCounts, setExportCounts] = useState({}); // Metal türü bazlı ürün sayıları
  const [exportCategoryCounts, setExportCategoryCounts] = useState({}); // Kategori bazlı ürün sayıları
  const [importData, setImportData] = useState([]);
  const [priceUpdateData, setPriceUpdateData] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const priceFileInputRef = useRef(null);

  // Dinamik kategoriler için state
  const [dynamicCategories, setDynamicCategories] = useState({});
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Kategori bazlı online görünürlük ayarları: { 'Kategori Adı': true/false }
  const [categoryOnlineMap, setCategoryOnlineMap] = useState({});
  const fetchCategoryOnlineMap = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/category-settings`);
      if (!resp.ok) return;
      const data = await resp.json();
      const flat = {};
      Object.entries(data || {}).forEach(([k, v]) => { flat[k] = !!v.is_online_visible; });
      setCategoryOnlineMap(flat);
    } catch (e) { /* sessizce yok say */ }
  }, []);

  const setCategoryOnlineFlag = useCallback(async (categoryName, value) => {
    if (!categoryName) return;
    try {
      const resp = await fetch(`${API_URL}/category-settings/${encodeURIComponent(categoryName)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_online_visible: !!value })
      });
      if (!resp.ok) throw new Error('Güncellenemedi');
      setCategoryOnlineMap(prev => ({ ...prev, [categoryName]: !!value }));
      toast.success(`'${categoryName}' kategorisi ${value ? 'Online\'da görünür olarak' : 'Online\'dan gizli olarak'} işaretlendi`);
    } catch (e) {
      toast.error('Kategori ayarı kaydedilemedi');
    }
  }, []);

  // Sıralama için state
  const [sortField, setSortField] = useState(() => {
    const saved = localStorage.getItem('productsSortField');
    return saved || null;
  });
  const [sortDirection, setSortDirection] = useState(() => {
    const saved = localStorage.getItem('productsSortDirection');
    return saved || 'asc';
  });

  // Yeni kategori ekleme modal için
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Fiyat değiştirme yetkisi kontrolü - Kasiyerler fiyat değiştiremez
  const canEditPrice = () => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'depo') return user?.permissions?.canEditPrice === true;
    return false;
  };

  // İşçilik bilgisi girebilme yetkisi - ayrı bir yetki olarak gözükür
  const canEditLabor = () => {
    if (user?.role === 'admin') return true;
    if (user?.permissions?.canEditLabor === true) return true;
    // Geriye dönük uyumluluk: fiyat değiştirme yetkisi olan depo kullanıcıları işçilik girmeye devam eder
    if (user?.role === 'depo' && user?.permissions?.canEditPrice === true) return true;
    return false;
  };

  // Shopify/E-Ticaret erişim kontrolü
  const canAccessShopify = user?.role === 'admin' || user?.permissions?.canAccessShopify === true;

  // Transfer ve tamirata gönderme yetki kontrolleri
  const canTransferProducts = user?.role === 'admin' || user?.role === 'depo' || user?.permissions?.canTransfer === true;
  // Tamirata ürün gönderme: transfer yetkisi olanlar + ayrı "Tamirata Ürün Gönderme" yetkisi olanlar
  const canSendToRepair = canTransferProducts || user?.permissions?.canSendToRepair === true;

  // Sıralama fonksiyonu
  const handleSort = (field) => {
    let newDirection = 'asc';
    if (sortField === field) {
      // Aynı alana tıklandıysa yönü değiştir veya sıralamayı kaldır
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else {
        // Sıralamayı kaldır
        setSortField(null);
        setSortDirection('asc');
        localStorage.removeItem('productsSortField');
        localStorage.removeItem('productsSortDirection');
        return;
      }
    }
    setSortField(field);
    setSortDirection(newDirection);
    localStorage.setItem('productsSortField', field);
    localStorage.setItem('productsSortDirection', newDirection);
  };


  // Barkod oluşturma fonksiyonu - backend'den benzersiz sıralı barkod alır
  const generateBarcode = async (metalType) => {
    try {
      const url = metalType
        ? `${API_URL}/products/generate-barcode?metal_type=${encodeURIComponent(metalType)}`
        : `${API_URL}/products/generate-barcode`;
      const response = await fetch(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Sunucu hatası: ${response.status}`);
      }
      const data = await response.json();
      if (data.barcode) {
        return data.barcode;
      }
      throw new Error('Barkod alınamadı');
    } catch (error) {
      console.error('Barkod oluşturma hatası:', error);
      toast.error(error.message || 'Barkod oluşturulamadı!');
      return null;
    }
  };

  // Barkod bastır - ürün seçimi toggle
  const toggleProductForPrint = (productId) => {
    setSelectedProductsForPrint(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Tümünü seç/kaldır
  const toggleAllProductsForPrint = () => {
    if (selectedProductsForPrint.length === products.length) {
      setSelectedProductsForPrint([]);
    } else {
      setSelectedProductsForPrint(products.map(p => p.id));
    }
  };

  // Varsayılan içerikler - kullanıcı bunları değiştirmediyse ürün verisinden hesapla
  const LABEL_DEFAULTS = {
    brand: 'Akik Gold Jewellery', barcodeNumber: '01010163',
    weightPurity: '4.01 - 14K', weightPurity2: '4.01 - 14', has: '12.03 - HAS', has2: 'Has: 1.585',
    iscilik: 'Isc: 750.00', laboredGram: '3.618H', laboredGram2: 'Isc.Gr: 3.618',
    laborCost: 'Isc: 3.00$',
    usd: '2550 - USD',
    gram: '4.01 GR', gram2: '2.710 GR', category: 'Kupe',
    weight: '4.01', weight2: '2.710', purity: '14k',
    laborMultiplied: '25.62i',
    silverHas: '28.47',
    profitMultiplier: 'x3.00',
    price: '1.250,00 ₺', price2: '1.250,00 €', price3: '1.250,00 $', price4: '1.250,00 £',
    stockCode: 'STK001', productName: 'Gümüş Yüzük', supplier: 'Tedarikçi', customText: 'Metin',
    'weightPurity_silver': '12.50 - 925', 'brand_silver': 'Akik Silver',
  };

  // Ürün verilerini alan tipine göre çözümle (HTML ve ZPL ortak kullanır)
  const resolveFieldContent = (product, el, designSettings) => {
    const content = el.content || '';
    const def = LABEL_DEFAULTS[el.field] || '';
    const def2 = LABEL_DEFAULTS[el.field + '2'] || '';
    const defSilver = LABEL_DEFAULTS[el.field + '_silver'] || '';
    const isDefault = !content || content === def || content === def2 || content === defSilver;

    const alwaysDynamic = ['laboredGram', 'has', 'silverHas', 'iscilik', 'laborCost', 'laborMultiplied', 'weightPurity', 'gram', 'weight', 'purity', 'barcodeNumber', 'productName', 'category', 'stockCode', 'profitMultiplier', 'price', 'productColor', 'productSize', 'supplier'];
    if (!isDefault && !alwaysDynamic.includes(el.field)) return content;

    // Kullanıcı formatı özelleştirdiyse prefix/suffix'i çıkar
    // Örn: "3.618HAS" → prefix:"", suffix:"HAS" | "Has: 1.585" → prefix:"Has: ", suffix:""
    const extractFormat = (c) => {
      if (!c || isDefault) return null;
      const m = c.match(/^(.*?)[\d.,]+(.*)$/);
      if (m) return { prefix: m[1], suffix: m[2] };
      return { prefix: '', suffix: c }; // tamamen metin ise suffix olarak kullan
    };
    const fmt = extractFormat(content);

    const gram = parseFloat(product.weight) || 0;
    const profitMargin = parseFloat(product.profit_margin) || designSettings.defaultProfitMargin || 3;
    const isSilver = product.metal_type === 'silver' || (product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar');
    const purity = isSilver
      ? (product.silver_purity || '925') + ' Ayar'
      : (product.gold_karat ? product.gold_karat + 'k' : (designSettings.defaultPurity || '14k'));
    const karatMultipliers = { '8': 333, '9': 375, '10': 417, '14': 585, '18': 750, '21': 875, '22': 916, '24': 995 };
    const milyem = (product.metal_type === 'silver' || (product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar'))
      ? (parseInt(product.silver_purity) || 925)
      : (karatMultipliers[product.gold_karat] || 585);
    const labor = parseFloat(product.labor_cost) || 0;

    switch (el.field) {
      case 'brand': {
        const brandVal = product.brand || designSettings.defaultBrand || 'Akik Gold Jewellery';
        if (fmt) return `${fmt.prefix}${brandVal}${fmt.suffix}`;
        return brandVal;
      }
      case 'barcodeNumber': {
        const barcodeVal = product.barcode || '';
        if (fmt) return `${fmt.prefix}${barcodeVal}${fmt.suffix}`;
        return barcodeVal;
      }
      case 'weightPurity': {
        let ayar;
        if (isSilver) {
          ayar = product.silver_purity || '925';
        } else {
          ayar = product.gold_karat || designSettings.defaultPurity || '14';
        }
        if (fmt) return `${fmt.prefix}${gram} - ${ayar}${fmt.suffix}`;
        const suffix = product.metal_type === 'gold' ? 'K' : '';
        return `${gram} - ${ayar}${suffix}`;
      }
      case 'has': {
        const hasValue = (gram * milyem / 1000).toFixed(2);
        if (fmt) return `${fmt.prefix}${hasValue}${fmt.suffix}`;
        return `Has: ${hasValue}`;
      }
      case 'silverHas': {
        const silverHasVal = (gram * milyem / 1000 * profitMargin).toFixed(2);
        if (fmt) return `${fmt.prefix}${silverHasVal}${fmt.suffix}`;
        return `${silverHasVal}`;
      }
      case 'iscilik': {
        if (fmt) return `${fmt.prefix}${labor.toFixed(2)}${fmt.suffix}`;
        return `Isc: ${labor.toFixed(2)}`;
      }
      case 'laboredGram': {
        let val;
        if (product.metal_type === 'gold') {
          val = ((milyem + labor) * gram / 1000).toFixed(2);
        } else if (product.metal_type === 'silver' || product.metal_type === 'aksesuar') {
          const silverMargin = parseFloat(product.profit_margin) || 1;
          val = (gram * labor * silverMargin).toFixed(2);
        } else {
          return `-`;
        }
        if (fmt) return `${fmt.prefix}${val}${fmt.suffix}`;
        return `${val}H`;
      }
      case 'laborCost': {
        const lcVal = (labor * gram).toFixed(2);
        if (fmt) return `${fmt.prefix}${lcVal}${fmt.suffix}`;
        return `Isc: ${lcVal}$`;
      }
      case 'usd': {
        const laborCost = parseFloat(product.labor_cost) || 0;
        const usdVal = (laborCost * profitMargin).toFixed(2);
        if (fmt) return `${fmt.prefix}${usdVal}${fmt.suffix}`;
        return `${usdVal} - USD`;
      }
      case 'gram': {
        if (fmt) return `${fmt.prefix}${gram}${fmt.suffix}`;
        return `${gram} GR`;
      }
      case 'category': {
        const catVal = product.category || product.name || '';
        if (fmt) return `${fmt.prefix}${catVal}${fmt.suffix}`;
        return catVal;
      }
      case 'weight': {
        if (fmt) return `${fmt.prefix}${gram}${fmt.suffix}`;
        return `${gram}`;
      }
      case 'purity': {
        if (fmt) return `${fmt.prefix}${purity}${fmt.suffix}`;
        return purity;
      }
      case 'productName': {
        const nameVal = product.name || '';
        if (fmt) return `${fmt.prefix}${nameVal}${fmt.suffix}`;
        return nameVal;
      }
      case 'laborMultiplied': {
        const lm = (labor * gram * profitMargin).toFixed(2);
        if (fmt) return `${fmt.prefix}${lm}${fmt.suffix}`;
        return `${lm}i`;
      }
      case 'profitMultiplier': {
        const pm = parseFloat(product.profit_margin) || designSettings.defaultProfitMargin || 3;
        if (fmt) return `${fmt.prefix}${pm.toFixed(2)}${fmt.suffix}`;
        return `x${pm.toFixed(2)}`;
      }
      case 'price': {
        const productCurrency = product.currency || 'TRY';
        const hasForex = productCurrency && productCurrency !== 'TRY';

        // Override: etikette para birimi seçildiyse onu kullan, yoksa üründen otomatik
        const overrideSym = el.currencySymbol || null;
        let priceVal;
        let sym;

        if (hasForex) {
          priceVal = Number(product.sell_price) || 0;
          sym = getCurrencySymbol(productCurrency);
        } else {
          priceVal = getProductPrice(product);
          if (priceVal == null || priceVal <= 0) priceVal = Number(product.sell_price) || 0;
          sym = '₺';
        }

        // Override varsa ve ürünün para biriminden farklıysa çevir
        if (overrideSym && overrideSym !== sym) {
          const symToCurrency = { '₺': 'TRY', '€': 'EUR', '$': 'USD', '£': 'GBP' };
          const targetCurr = symToCurrency[overrideSym] || 'TRY';
          // Önce TL'ye çevir
          let priceTL = priceVal;
          if (hasForex) {
            const srcRate = getSatisRate(productCurrency) || 1;
            priceTL = priceVal * srcRate;
          }
          // TL'den hedef birime çevir
          if (targetCurr === 'TRY') {
            priceVal = priceTL;
          } else {
            const targetRate = getSatisRate(targetCurr) || 1;
            priceVal = priceTL / targetRate;
          }
          sym = overrideSym;
        }

        const formatted = Math.round(Number(priceVal)).toLocaleString('tr-TR');
        const priceWithSym = el.showCurrency === false ? formatted : `${formatted} ${sym}`;
        if (fmt) return `${fmt.prefix}${priceWithSym}${fmt.suffix}`;
        return priceWithSym;
      }
      case 'stockCode': {
        const codeVal = product.barcode ? product.barcode.slice(-5) : '';
        if (fmt) return `${fmt.prefix}${codeVal}${fmt.suffix}`;
        return codeVal;
      }
      case 'productColor': {
        const colorVal = product.color || '';
        if (fmt) return `${fmt.prefix}${colorVal}${fmt.suffix}`;
        return colorVal;
      }
      case 'productSize': {
        const sizeVal = product.size || '';
        if (fmt) return `${fmt.prefix}${sizeVal}${fmt.suffix}`;
        return sizeVal;
      }
      case 'supplier': {
        const supVal = product.supplier || '';
        if (fmt) return `${fmt.prefix}${supVal}${fmt.suffix}`;
        return supVal;
      }
      case 'customText': return content;
      default: return content;
    }
  };

  // Font listesi (LabelDesign ile aynı)
  const LABEL_FONTS_MAP = {
    'Arial': "Arial, Helvetica, sans-serif",
    'Roboto Condensed': "'Roboto Condensed', Arial, sans-serif",
    'Inter': "'Inter', Arial, sans-serif",
    'Oswald': "'Oswald', Arial, sans-serif",
    'Noto Sans': "'Noto Sans', Arial, sans-serif",
    'Montserrat': "'Montserrat', Arial, sans-serif",
  };
  const GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Montserrat:wght@400;600;700&family=Noto+Sans:wght@400;600;700&family=Oswald:wght@400;600;700&family=Roboto+Condensed:wght@400;700&display=swap';

  // Dizayn şablonundan etiket HTML'i oluştur (tek ürün için)
  const generateDesignLabelHTML = (product, design, productIndex) => {
    const dim = design.dimensions;
    const settings = design.settings;
    const elements = design.elements || [];

    const elementsHTML = elements.map((el) => {
      const areaOffset = getLabelAreaPrintOffset(el.area, design);
      // Barkod kutusundan asla taşmaz: doğal genişliği kutudan büyükse kutuya sığacak kadar daraltılır
      if (el.field === 'barcode' || el.isBarcode) {
        return `<div class="${LABEL_EL_CLASS}" style="position:absolute; left:${areaOffset + el.x}mm; top:${el.y}mm; width:${el.width}mm; height:${el.height}mm; display:flex; align-items:center; justify-content:${labelAlignJustify(el)}; overflow:hidden;">
          <svg class="barcode-svg" id="barcode-${productIndex}" style="height:${el.height}mm; width:auto; max-width:100%;"></svg>
        </div>`;
      }

      const content = resolveFieldContent(product, el, settings);
      const fontFamily = (el.fontFamily && LABEL_FONTS_MAP[el.fontFamily]) || ((el.field === 'barcodeNumber' || el.field === 'stockCode') ? "'Courier New', monospace" : "Arial, Helvetica, sans-serif");

      return `<div class="${LABEL_EL_CLASS}" style="position:absolute; left:${areaOffset + el.x}mm; top:${el.y}mm; width:${el.width}mm; height:${el.height}mm; display:flex; align-items:center; justify-content:${labelAlignJustify(el)}; overflow:hidden;">
        <span style="font-size:${el.fontSize}pt; font-weight:${el.fontWeight || 'bold'}; font-family:${fontFamily}; color:#000; white-space:nowrap; line-height:1; text-align:${getLabelAlign(el)}; width:100%;">${content}</span>
      </div>`;
    }).join('');

    return `<div class="label-container" style="position:relative;">${elementsHTML}</div>`;
  };

  // Tasarım seçim modalını aç (API'den tasarımları çek)
  const fetchDesignsAndShowModal = async (productsToprint) => {
    setLoadingDesigns(true);
    setPendingPrintProducts(productsToprint);
    setShowDesignSelectModal(true);
    // Qubite Print kontrolü modal açıkken arka planda yapılır; yazdır'a basınca hazır olur
    setZebraStatus(null);
    zebraCheckRef.current = checkZebraAvailable().then(r => { setZebraStatus(r); return r; });

    try {
      const token = localStorage.getItem('token');
      // Ürün tipine göre filtrele: altın ürünler → gold tasarımlar, diğerleri → silver tasarımlar
      const hasGold = productsToprint.some(p => p.metal_type === 'gold');
      const hasSilver = productsToprint.some(p => p.metal_type !== 'gold');
      const typeParam = (hasGold && hasSilver) ? '' : (hasGold ? '?type=gold' : '?type=silver');

      const res = await fetch(`${API_URL}/label-designs${typeParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.designs) {
        // Aktif olanları öne al
        const sorted = data.designs.sort((a, b) => {
          if (a.is_active && !b.is_active) return -1;
          if (!a.is_active && b.is_active) return 1;
          return 0;
        });
        setAvailableDesigns(sorted);
      } else {
        setAvailableDesigns([]);
      }
    } catch (e) {
      console.error('Tasarım yükleme hatası:', e);
      toast.error('Tasarımlar yüklenemedi!');
      setAvailableDesigns([]);
    } finally {
      setLoadingDesigns(false);
    }
  };

  // Seçilen tasarımla yazdır
  const printWithSelectedDesign = async (design) => {
    setShowDesignSelectModal(false);
    const selectedProducts = pendingPrintProducts;

    if (!design || !design.elements || design.elements.length === 0) {
      toast.warning('Seçilen tasarımda eleman bulunamadı!');
      return;
    }

    const dim = design.dimensions;
    const barcodeHeight = design.settings?.barcodeHeight || 3.5;
    const barcodeWidth = design.settings?.barcodeWidth || 1.0;
    const hasBarcodeElement = design.elements.some(el => el.field === 'barcode' || el.isBarcode);

    // Her ürünü, seçilen etiket adedi moduna göre kere tekrarla:
    // 'manual' → sabit sayı (printQuantity), 'stock' → seçili kaynaktaki (mağaza/depo/toplam)
    // stok kadar — aynı ürünün etiketleri arka arkaya (bloklar halinde) sıralanır.
    const expandedProducts = [];
    selectedProducts.forEach(p => {
      const qty = getLabelQtyForProduct(p);
      for (let i = 0; i < qty; i++) {
        expandedProducts.push(p);
      }
    });

    if (expandedProducts.length === 0) {
      toast.warning('Yazdırılacak etiket yok (seçili kaynakta stok bulunamadı)!');
      return;
    }

    // Qubite Print varsa ham ZPL ile doğrudan yazıcıya: Chrome yazdırma penceresi ve
    // Windows sürücüsü devre dışı → her bilgisayarda aynı konum, toplu baskıda kayma yok.
    // Program kapalıysa (bu bilgisayarda daha önce çalıştıysa) önce başlatılır, açılınca baskı ona gider.
    let zebra = zebraStatus || (zebraCheckRef.current ? await zebraCheckRef.current : null);
    if (!zebra?.ok) {
      let launchToast = null;
      zebra = await ensureZebraAvailable(zebra, { onLaunching: () => { launchToast = toast.info('Qubite Print başlatılıyor...', { autoClose: false }); } });
      if (launchToast) toast.dismiss(launchToast);
      setZebraStatus(zebra);
    }
    if (zebra?.ok) {
      const labels = [];
      expandedProducts.forEach(p => {
        const last = labels[labels.length - 1];
        if (last && last.product === p) { last.qty += 1; return; }
        labels.push({ product: p, qty: 1, barcodeValue: p.barcode, getContent: (el) => resolveFieldContent(p, el, design.settings) });
      });
      const toastId = toast.info(`Zebra yazıcıya gönderiliyor (${expandedProducts.length} etiket)...`, { autoClose: false });
      try {
        const r = await printLabelsZebra(design, labels, { printer: zebra.printer });
        toast.update(toastId, { render: `${r.count} etiket ${r.printer.name || 'Zebra'} yazıcısına gönderildi`, type: 'success', autoClose: 3000 });
        setPrintQuantity(1);
        return;
      } catch (e) {
        console.error('Zebra baskı hatası:', e);
        toast.update(toastId, { render: `Zebra doğrudan baskı başarısız (${e.message}) — normal yazdırma açılıyor`, type: 'warning', autoClose: 5000 });
      }
    }

    const printHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Kuyumcu Etiket Yazdır</title>
        <link rel="icon" type="image/png" href="${window.location.origin}/favicon.png" />
        <link rel="shortcut icon" href="${window.location.origin}/favicon.ico" />
        <link rel="stylesheet" href="${GOOGLE_FONTS_URL}" />
        ${hasBarcodeElement ? '<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>' : ''}
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: ${dim.labelWidth}mm ${getLabelPageHeightIn(design)}in; margin: 0; }
          ${labelElementCss(design)}
          body { font-family: Arial, Helvetica, sans-serif; background: #f0f0f0; padding: 5mm; }
          .label-grid { display: flex; flex-direction: column; gap: 2mm; }
          .label-container {
            width: ${dim.labelWidth}mm;
            height: ${dim.labelHeight}mm;
            background: white;
            border: 1px solid #ccc;
            overflow: hidden;
          }
          @media print {
            body { background: white; padding: 0; margin: 0; }
            .label-grid { display: block; }
            .label-container { border: none; page-break-after: always; page-break-inside: avoid; margin: 0; height: ${getLabelPageHeightIn(design)}in; }
            .label-container:last-child { page-break-after: auto; }
          }
        </style>
      </head>
      <body>
        <div class="label-grid">
          ${expandedProducts.map((p, index) => generateDesignLabelHTML(p, design, index)).join('')}
        </div>
        <script>
          ${labelShiftScript(getLabelPrintOffset(design))}
          ${labelBarcodeSizeScript(getBarcodeModuleMm(design), barcodeWidth, design.settings?.printerDpi)}
          window.onload = function() {
            ${hasBarcodeElement ? `
              var barcodes = ${JSON.stringify(expandedProducts.map(p => [p.barcode, labelBarcodeOptions(p.barcode, design, barcodeWidth, barcodeHeight * 3.78)]))};
              barcodes.forEach(function(b, index) {
                try {
                  JsBarcode("#barcode-" + index, b[0], b[1]);
                } catch (e) {
                  console.error('Barkod hatasi:', e);
                }
              });
            ` : ''}
            applyBarcodeSize();
            applyLabelShift();
            if (document.fonts && document.fonts.ready) document.fonts.ready.then(applyLabelShift);
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            setTimeout(function() {
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 500); }
            }, 500);
          }
        <\/script>
      </body>
      </html>
    `;
    handlePrint(printHtml, 'label');
    setPrintQuantity(1);
  };

  // Barkod yazdır - Kuyumcu plastik saplı etiket (dizayn şablonu veya ayarlardan)
  const printBarcodes = () => {
    // labelPrintFilteredProducts (Detaylı Filtre ile eşleşen TAM liste — mağaza bazlı stok
    // bilgisi dahil) öncelikli kaynak; henüz yüklenmediyse veya id orada yoksa mevcut
    // sayfadaki products'a düşülür.
    const productPool = new Map();
    products.forEach(p => productPool.set(p.id, p));
    labelPrintFilteredProducts.forEach(p => productPool.set(p.id, p));

    const selectedProducts = selectedProductsForPrint
      .map(id => productPool.get(id))
      .filter(p => p && p.barcode);

    if (selectedProducts.length === 0) {
      toast.warning('Barkodu olan en az bir ürün seçin!');
      return;
    }

    if (labelQtyMode === 'stock') {
      const withStock = selectedProducts.filter(p => getLabelQtyForProduct(p) > 0);
      if (withStock.length === 0) {
        toast.warning('Seçili kaynakta stok bulunan ürün yok!');
        return;
      }
      if (withStock.length < selectedProducts.length) {
        toast.info(`${selectedProducts.length - withStock.length} ürün seçili kaynakta stoksuz olduğu için atlandı`);
      }
    }

    // Tasarım seçim modalını aç
    fetchDesignsAndShowModal(selectedProducts);
  };

  // Barkod Bastır modunda "Detaylı Filtre" ile eşleşen TÜM ürünleri seç/kaldır
  // (sadece o an yüklenmiş sayfadakileri değil, filtreye uyan hepsini)
  const toggleSelectAllFilteredForPrint = () => {
    if (selectAllFilteredForPrint) {
      setSelectAllFilteredForPrint(false);
      setSelectedProductsForPrint([]);
    } else {
      setSelectAllFilteredForPrint(true);
      setSelectedProductsForPrint(labelPrintFilteredProducts.filter(p => p.barcode).map(p => p.id));
    }
  };

  // Tek ürün barkod yazdır (aksiyon butonundan)
  const printSingleBarcode = (product) => {
    if (!product.barcode) {
      toast.warning('Bu ürünün barkodu yok!');
      return;
    }

    // Tasarım seçim modalını aç
    fetchDesignsAndShowModal([product]);
  };

  // Barkod modunu kapat
  const exitBarcodePrintMode = () => {
    setBarcodePrintMode(false);
    setSelectedProductsForPrint([]);
    setSelectAllFilteredForPrint(false);
    setLabelPrintFilteredProducts([]);
  };

  // Stok Alım Modu
  const [showStockIntakeMode, setShowStockIntakeMode] = useState(false);
  const [stockIntakeBarcode, setStockIntakeBarcode] = useState('');
  const [stockIntakeData, setStockIntakeData] = useState({}); // { productId: { product, quantity } }
  const [lastScannedProduct, setLastScannedProduct] = useState(null);
  const [showUnknownBarcodeModal, setShowUnknownBarcodeModal] = useState(false);
  const [unknownBarcode, setUnknownBarcode] = useState('');
  const [savingStockIntake, setSavingStockIntake] = useState(false);
  const stockIntakeBarcodeRef = useRef(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // Stok Geçmişi (Artırma/Düşüm modallarında)
  const [showStockHistory, setShowStockHistory] = useState(false);
  const [stockHistoryData, setStockHistoryData] = useState([]);
  const [stockHistoryLoading, setStockHistoryLoading] = useState(false);
  const [stockHistoryType, setStockHistoryType] = useState('all'); // 'all', 'add', 'subtract'
  const [stockHistorySearch, setStockHistorySearch] = useState('');

  // Stok Düşüm Modu
  const [showStockDeductionMode, setShowStockDeductionMode] = useState(false);
  const [stockDeductionBarcode, setStockDeductionBarcode] = useState('');
  const [stockDeductionData, setStockDeductionData] = useState({}); // { productId: { product, quantity, reason } }
  const [lastDeductedProduct, setLastDeductedProduct] = useState(null);
  const [savingStockDeduction, setSavingStockDeduction] = useState(false);
  const [globalDeductionReason, setGlobalDeductionReason] = useState('');
  const stockDeductionBarcodeRef = useRef(null);

  // Barkod Bastır Modu
  const [barcodePrintMode, setBarcodePrintMode] = useState(false);
  const [selectedProductsForPrint, setSelectedProductsForPrint] = useState([]);
  // Detaylı Filtre ile eşleşen TÜM ürünler (sayfalama olmadan) — alt toplam özeti,
  // "filtrelenenlerin tümünü seç" ve stok bazlı etiket adedi hesabı için kullanılır.
  const [labelPrintFilteredProducts, setLabelPrintFilteredProducts] = useState([]);
  const [labelPrintFilteredLoading, setLabelPrintFilteredLoading] = useState(false);
  const [selectAllFilteredForPrint, setSelectAllFilteredForPrint] = useState(false);
  // Etiket adedi modu: 'manual' = sabit sayı (printQuantity), 'stock' = seçili kaynaktaki stok kadar
  const [labelQtyMode, setLabelQtyMode] = useState('manual');
  // 'total' = toplam stok, yoksa mağaza id'si (2=Osmangazi, 3=Yeşilsu, 4=Depo)
  const [labelStockScope, setLabelStockScope] = useState('total');

  // Tasarım Seçim Modalı
  const [showDesignSelectModal, setShowDesignSelectModal] = useState(false);
  const [zebraStatus, setZebraStatus] = useState(null);
  const zebraCheckRef = useRef(null);
  const [availableDesigns, setAvailableDesigns] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [pendingPrintProducts, setPendingPrintProducts] = useState([]);
  const [printQuantity, setPrintQuantity] = useState(1);

  // Varyant Grupları (Shopify çoklu varyant)
  const [showVariantGroupsModal, setShowVariantGroupsModal] = useState(false);
  const [variantGroups, setVariantGroups] = useState([]);
  const [loadingVariantGroups, setLoadingVariantGroups] = useState(false);
  const [showVariantGroupEditorModal, setShowVariantGroupEditorModal] = useState(false);
  const [editingVariantGroupId, setEditingVariantGroupId] = useState(null); // null = yeni grup
  const [variantGroupName, setVariantGroupName] = useState('');
  const [variantGroupMembers, setVariantGroupMembers] = useState([]); // [{id, name, barcode, image, category, size, color, stone_color, stone_mm, sell_price, currency}]
  const [variantGroupAxes, setVariantGroupAxes] = useState([]); // ['size','color','stone_color','stone_mm'] alt kümesi, en fazla 3
  const [variantGroupPrimaryId, setVariantGroupPrimaryId] = useState(null);
  const [variantGroupBarcodeInput, setVariantGroupBarcodeInput] = useState('');
  const [variantGroupSearchTerm, setVariantGroupSearchTerm] = useState('');
  const [variantGroupSearchResults, setVariantGroupSearchResults] = useState([]);
  const [variantGroupSearching, setVariantGroupSearching] = useState(false);
  const variantGroupSearchTimerRef = useRef(null);
  const [savingVariantGroup, setSavingVariantGroup] = useState(false);
  const [syncingVariantGroupId, setSyncingVariantGroupId] = useState(null);
  const variantGroupBarcodeRef = useRef(null);
  const [editProductVariantGroupInfo, setEditProductVariantGroupInfo] = useState(null); // {groupId, groupName} | null
  // Grup ekranında satır bazında sanal (stoksuz) varyant ekleme - hangi üyenin ekleme formu açık
  const [openVirtualVariantMemberId, setOpenVirtualVariantMemberId] = useState(null);
  const [memberVirtualVariantAxis, setMemberVirtualVariantAxis] = useState('color');
  const [memberVirtualVariantValue, setMemberVirtualVariantValue] = useState('');
  const [savingMemberVirtualVariant, setSavingMemberVirtualVariant] = useState(false);

  // ===== Ürün Grupları (birden çok ürünü tek satır gibi gösterme) =====
  const [showProductGroupsModal, setShowProductGroupsModal] = useState(false);
  const [productGroups, setProductGroups] = useState([]);
  const [loadingProductGroups, setLoadingProductGroups] = useState(false);
  const [showProductGroupEditorModal, setShowProductGroupEditorModal] = useState(false);
  const [editingProductGroupId, setEditingProductGroupId] = useState(null);
  const [productGroupName, setProductGroupName] = useState('');
  const [productGroupMetalType, setProductGroupMetalType] = useState('');
  const [productGroupNote, setProductGroupNote] = useState('');
  const [productGroupMembers, setProductGroupMembers] = useState([]); // [{id, name, barcode, image, category, brand, metal_type}]
  const [productGroupBarcodeInput, setProductGroupBarcodeInput] = useState('');
  const [productGroupSearchTerm, setProductGroupSearchTerm] = useState('');
  const [productGroupSearchResults, setProductGroupSearchResults] = useState([]);
  const [productGroupSearching, setProductGroupSearching] = useState(false);
  const [savingProductGroup, setSavingProductGroup] = useState(false);
  const productGroupBarcodeRef = useRef(null);
  const productGroupSearchTimerRef = useRef(null);
  // Listede açık olan grup id'leri ve her grubun yüklenmiş üye ürün detayları
  const [expandedProductGroupIds, setExpandedProductGroupIds] = useState(() => new Set());
  const [productGroupMemberData, setProductGroupMemberData] = useState({}); // { [groupId]: [product, ...] }
  const [loadingProductGroupMemberIds, setLoadingProductGroupMemberIds] = useState(() => new Set());
  // "Grup Oluştur" → varyant grubu / ürün grubu seçim kartları
  const [showGroupTypeChooserModal, setShowGroupTypeChooserModal] = useState(false);
  // "Tüm Ürünlerden Seç" - barkodsuz toplu ürün seçme modalı (hem varyant hem ürün grubu için)
  const [showGroupProductPicker, setShowGroupProductPicker] = useState(false);
  const [groupPickerTarget, setGroupPickerTarget] = useState('product'); // 'product' | 'variant'
  const [groupPickerType, setGroupPickerType] = useState('gold');
  const [groupPickerCategory, setGroupPickerCategory] = useState('all');
  const [groupPickerSearch, setGroupPickerSearch] = useState('');
  const [groupPickerBrand, setGroupPickerBrand] = useState('');
  const [groupPickerInStockOnly, setGroupPickerInStockOnly] = useState(false);
  const [groupPickerResults, setGroupPickerResults] = useState([]);
  const [groupPickerLoading, setGroupPickerLoading] = useState(false);
  const [groupPickerSelected, setGroupPickerSelected] = useState(() => new Map());
  const groupPickerSearchTimerRef = useRef(null);

  // Transfer Modal
  const [showTransferOptionsModal, setShowTransferOptionsModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferType, setTransferType] = useState('store'); // 'store' veya 'warehouse'
  const [transferTargetStore, setTransferTargetStore] = useState('');
  const [transferItems, setTransferItems] = useState([]);
  const [transferBarcode, setTransferBarcode] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [savingTransfer, setSavingTransfer] = useState(false);
  const transferBarcodeRef = useRef(null);
  const [transferFromRequest, setTransferFromRequest] = useState(null); // Talepten gelen transfer için
  const [lastScannedTransferProduct, setLastScannedTransferProduct] = useState(null);

  // Stok Alım Seçenekleri Modal
  const [showStockIntakeOptionsModal, setShowStockIntakeOptionsModal] = useState(false);
  const [stockIntakeType, setStockIntakeType] = useState('new'); // 'new' veya 'transfer'

  // Transfer Teslim Alma
  const [showReceiveTransferModal, setShowReceiveTransferModal] = useState(false);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [pendingTransferCount, setPendingTransferCount] = useState(0);
  const [selectedTransferForReceive, setSelectedTransferForReceive] = useState(null);
  const [transferDetailItems, setTransferDetailItems] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [transferBarcodeInput, setTransferBarcodeInput] = useState('');
  const transferBarcodeInputRef = useRef(null);

  // Hızlı Barkod Teslim (fiş seçmeden)
  const [quickTransferBarcode, setQuickTransferBarcode] = useState('');
  const [quickTransferItems, setQuickTransferItems] = useState([]); // Okutulup henüz onaylanmamış ürünler
  const [showQuickTransferSummary, setShowQuickTransferSummary] = useState(false);
  const [quickTransferSummaryData, setQuickTransferSummaryData] = useState(null);
  const [savingQuickTransfer, setSavingQuickTransfer] = useState(false);
  const quickTransferBarcodeRef = useRef(null);

  // Duplicate barkod uyarısı (transfer teslim al için)
  const [showDuplicateBarcodeModal, setShowDuplicateBarcodeModal] = useState(false);
  const [duplicateBarcodeData, setDuplicateBarcodeData] = useState(null);
  const quickTransferItemsRef = useRef(quickTransferItems);

  // Duplicate barkod uyarısı (stok alımı için)
  const [showStockIntakeDuplicateModal, setShowStockIntakeDuplicateModal] = useState(false);
  const [stockIntakeDuplicateData, setStockIntakeDuplicateData] = useState(null);
  const stockIntakeDataRef = useRef(stockIntakeData);

  // Transfer Talebi
  const [showTransferRequestModal, setShowTransferRequestModal] = useState(false);
  const [transferRequestFromStore, setTransferRequestFromStore] = useState('');
  const [transferRequestItems, setTransferRequestItems] = useState([]);
  const [transferRequestBarcode, setTransferRequestBarcode] = useState('');
  const [transferRequestNotes, setTransferRequestNotes] = useState('');
  const [savingTransferRequest, setSavingTransferRequest] = useState(false);
  const transferRequestBarcodeRef = useRef(null);

  // Gelen Transfer Talepleri
  const [showIncomingRequestsModal, setShowIncomingRequestsModal] = useState(false);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [selectedRequestForProcess, setSelectedRequestForProcess] = useState(null);
  const [requestDetailItems, setRequestDetailItems] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [incomingRequestCount, setIncomingRequestCount] = useState(0);
  const [requestApprovalBarcode, setRequestApprovalBarcode] = useState('');
  const requestApprovalBarcodeRef = useRef(null);

  // Gönderilen Transferler
  const [showOutgoingTransfersModal, setShowOutgoingTransfersModal] = useState(false);
  const [outgoingTransfers, setOutgoingTransfers] = useState([]);
  const [loadingOutgoingTransfers, setLoadingOutgoingTransfers] = useState(false);
  const [selectedOutgoingTransfer, setSelectedOutgoingTransfer] = useState(null);
  const [editingTransfer, setEditingTransfer] = useState(false);
  const [editingTransferItems, setEditingTransferItems] = useState([]);
  const [savingTransferEdit, setSavingTransferEdit] = useState(false);

  // Gönderilen Talepler
  const [showOutgoingRequestsModal, setShowOutgoingRequestsModal] = useState(false);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [loadingOutgoingRequests, setLoadingOutgoingRequests] = useState(false);
  const [selectedOutgoingRequest, setSelectedOutgoingRequest] = useState(null);

  // Stok Hareketleri
  const [showStockMovementsModal, setShowStockMovementsModal] = useState(false);
  const [stockMovements, setStockMovements] = useState([]);
  const [stockMovementsLoading, setStockMovementsLoading] = useState(false);
  const [stockMovementsPage, setStockMovementsPage] = useState(1);
  const [stockMovementsTotalPages, setStockMovementsTotalPages] = useState(1);
  const [stockMovementsTotalCount, setStockMovementsTotalCount] = useState(0);
  const [stockMovementsShowSales, setStockMovementsShowSales] = useState(false);
  const [stockMovementsSearch, setStockMovementsSearch] = useState('');
  const [stockMovementsDateFrom, setStockMovementsDateFrom] = useState('');
  const [stockMovementsDateTo, setStockMovementsDateTo] = useState('');
  const [stockMovementsStoreId, setStockMovementsStoreId] = useState(null);
  const stockMovementsSearchTimeout = useRef(null);
  const [editingMovement, setEditingMovement] = useState(null);
  const [editMovementQuantity, setEditMovementQuantity] = useState('');

  // Transfer Geçmişi
  const [showTransferHistoryModal, setShowTransferHistoryModal] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [loadingTransferHistory, setLoadingTransferHistory] = useState(false);
  const [selectedHistoryTransfer, setSelectedHistoryTransfer] = useState(null);

  // Transfer iptal
  const [cancellingTransfer, setCancellingTransfer] = useState(false);
  const [cancellingRequest, setCancellingRequest] = useState(false);
  const [editingRequest, setEditingRequest] = useState(false);
  const [editingRequestItems, setEditingRequestItems] = useState([]);
  const [savingRequest, setSavingRequest] = useState(false);

  // Transfer kasiyer seçimi
  const [transferStaffList, setTransferStaffList] = useState([]);
  const [transferStaffId, setTransferStaffId] = useState('');
  const [transferStaffName, setTransferStaffName] = useState('');
  const [receiveStaffId, setReceiveStaffId] = useState('');
  const [receiveStaffName, setReceiveStaffName] = useState('');
  const [showReceiveStaffModal, setShowReceiveStaffModal] = useState(false);
  const [pendingReceiveAction, setPendingReceiveAction] = useState(null);
  const [approveStaffId, setApproveStaffId] = useState('');
  const [approveStaffName, setApproveStaffName] = useState('');
  const [showApproveStaffModal, setShowApproveStaffModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectStaffId, setRejectStaffId] = useState(null);
  const [rejectStaffName, setRejectStaffName] = useState('');

  // Tamirata Gönder
  const [showSendToRepairModal, setShowSendToRepairModal] = useState(false);
  const [repairItems, setRepairItems] = useState([]);
  const [repairBarcode, setRepairBarcode] = useState('');
  const [repairCustomerName, setRepairCustomerName] = useState('');
  const [repairCustomerPhone, setRepairCustomerPhone] = useState('');
  const [repairNotes, setRepairNotes] = useState('');
  const [repairEstimatedPrice, setRepairEstimatedPrice] = useState('');
  const [repairStaffName, setRepairStaffName] = useState('');
  const [repairPointId, setRepairPointId] = useState('');
  const [repairPointsList, setRepairPointsList] = useState([]);
  const [savingRepair, setSavingRepair] = useState(false);
  const repairBarcodeRef = useRef(null);
  // Terminalden fotoğraf çekme (Repairs.js'teki terminal akışı ile aynı)
  const [terminals, setTerminals] = useState([]);
  const [loadingTerminals, setLoadingTerminals] = useState(false);
  const [showRepairTerminalModal, setShowRepairTerminalModal] = useState(false);
  const [selectedRepairTerminal, setSelectedRepairTerminal] = useState(null);
  const [waitingForRepairPhoto, setWaitingForRepairPhoto] = useState(false);
  const [repairPhotoRequestId, setRepairPhotoRequestId] = useState(null);
  const [repairTerminalItemIndex, setRepairTerminalItemIndex] = useState(0);
  const repairPhotoCheckIntervalRef = useRef(null);

  // Stok Uyarı Bildirimleri (0 stoklu ürünler, başka mağazada 3+ var)
  const [showStockAlertModal, setShowStockAlertModal] = useState(false);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [stockAlertCount, setStockAlertCount] = useState(0);
  const [loadingStockAlerts, setLoadingStockAlerts] = useState(false);
  const [selectedAlertProduct, setSelectedAlertProduct] = useState(null);

  // Depo Stok Al ve Transfer Yap
  const [showDepoStockTransferModal, setShowDepoStockTransferModal] = useState(false);
  const [depoMode, setDepoMode] = useState('stock_entry'); // 'stock_entry' | 'transfer_osmangazi' | 'transfer_yesilsu'
  const [depoCenterItems, setDepoCenterItems] = useState({}); // {productId: {product, quantity}}
  const [depoOsmangaziItems, setDepoOsmangaziItems] = useState({});
  const [depoYesilsuItems, setDepoYesilsuItems] = useState({});
  const [depoBarcode, setDepoBarcode] = useState('');
  const [depoSoundEnabled, setDepoSoundEnabled] = useState(true);
  const [showDepoInventory, setShowDepoInventory] = useState(false);
  const [depoInventoryProducts, setDepoInventoryProducts] = useState([]);
  const [loadingDepoInventory, setLoadingDepoInventory] = useState(false);
  const [savingDepoOperation, setSavingDepoOperation] = useState(false);
  const [showDepoCloseConfirm, setShowDepoCloseConfirm] = useState(false);
  const depoBarcodeRef = useRef(null);

  // Toplu Silme
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  // Sol Menü
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [menuAccordion, setMenuAccordion] = useState({
    transfers: true,
    stock: true,
    excel: true,
    bulk: true
  });

  // Menü toggle - hızlı tepki için
  const toggleSideMenu = () => {
    startTransition(() => {
      setSideMenuOpen(prev => !prev);
    });
  };

  // Yeni ürün form state
  const [newProduct, setNewProduct] = useState({
    name: '',
    barcode: '',
    metal_type: 'gold',
    category: 'Altın Kolye',
    description: '',
    notes: '',
    supplier: '',
    sell_price: '',
    currency: 'TRY',
    color: '',
    size: '',
    stone_color: '',
    stone_mm: '',
    brand: '',
    weight: '',
    gold_karat: '14', // Varsayılan 14 ayar
    silver_purity: '1000', // Varsayılan 1000 ayar (milyem)
    labor_cost: '', // İşçilik
    profit_margin: '', // Kar çarpanı (varsayılan 3)
    pricing_type: 'formula', // 'formula' veya 'fixed' - altın/gümüş için
    ecommerce_active: false,
    shopify_active: false,
    trendyol_active: false,
    hepsiburada_active: false,
    amazon_active: false,
    n11_active: false,
    eski_etiket: false,
    no_discount: false,
    min_stock: '',
    active: true,
    store_stocks: {},
    enabled_stores: {}, // Hangi mağazalarda satılacağı
    gender: '', // Cinsiyet: erkek, kadın, unisex
    is_special_defined: false // Özel tanımlı ürün (altın için)
  });

  // Resim yükleme state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newProductImage, setNewProductImage] = useState(null);
  const [newProductImagePreview, setNewProductImagePreview] = useState(null);

  // Son eklenen ürün bilgisi (hızlı doldurma için)
  const [lastAddedProduct, setLastAddedProduct] = useState(null);
  const [showFillSettings, setShowFillSettings] = useState(false);
  const [showProductSearchModal, setShowProductSearchModal] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [fillOptions, setFillOptions] = useState({
    metal_type: true,
    category: true,
    name: true,
    brand: true,
    description: true,
    notes: true,
    supplier: false,
    sell_price: false,
    currency: false,
    color: false,
    size: false,
    weight: false,
    gold_karat: true,
    silver_purity: true,
    labor_cost: false,
    profit_margin: true,
    min_stock: false,
    gender: true
  });

  // Ayar katsayıları (sepete eklerken hesaplama için kullanılacak)
  const karatMultipliers = {
    '8': 333,
    '9': 375,
    '10': 417,
    '14': 585,
    '18': 750,
    '21': 875,
    '22': 916,
    '24-995': 995,
    '24-999': 999
  };

  // ESC tuşu ile modalları kapatma
  useEscapeClose([
    [showVariantGroupEditorModal, () => setShowVariantGroupEditorModal(false)],
    [showVariantGroupsModal, () => setShowVariantGroupsModal(false)],
    [showProductGroupEditorModal, () => setShowProductGroupEditorModal(false)],
    [showProductGroupsModal, () => setShowProductGroupsModal(false)],
    [showGroupProductPicker, () => setShowGroupProductPicker(false)],
    [showGroupTypeChooserModal, () => setShowGroupTypeChooserModal(false)],
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showDepoCloseConfirm, () => setShowDepoCloseConfirm(false)],
    [showHelpModal, () => setShowHelpModal(false)],
    [showLogModal, () => {}], // LogModal handles its own ESC
    [showDuplicateBarcodeModal, () => setShowDuplicateBarcodeModal(false)],
    [showStockIntakeDuplicateModal, () => setShowStockIntakeDuplicateModal(false)],
    [showUnknownBarcodeModal, () => setShowUnknownBarcodeModal(false)],
    [showImageLightbox, () => setShowImageLightbox(false)],
    [showNewCategoryModal, () => setShowNewCategoryModal(false)],
    [showFillSettings, () => setShowFillSettings(false)],
    [showProductSearchModal, () => setShowProductSearchModal(false)],
    [showQuickTransferSummary, () => setShowQuickTransferSummary(false)],
    [showStockMovementsModal, () => setShowStockMovementsModal(false)],
    [showStockHistoryModal, () => setShowStockHistoryModal(false)],
    [showStockAlertModal, () => setShowStockAlertModal(false)],
    [showApproveStaffModal, () => setShowApproveStaffModal(false)],
    [showReceiveStaffModal, () => setShowReceiveStaffModal(false)],
    [showSendToRepairModal, () => setShowSendToRepairModal(false)],
    [showRepairTerminalModal, () => setShowRepairTerminalModal(false)],
    [showTransferHistoryModal, () => closeTransferHistoryModal()],
    [showIncomingRequestsModal, () => setShowIncomingRequestsModal(false)],
    [showOutgoingTransfersModal, () => closeOutgoingTransfersModal()],
    [showOutgoingRequestsModal, () => setShowOutgoingRequestsModal(false)],
    [showTransferRequestModal, () => closeTransferRequestModal()],
    [showReceiveTransferModal, () => closeReceiveTransferModal()],
    [showTransferModal, () => closeTransferModal()],
    [showDesignSelectModal, () => setShowDesignSelectModal(false)],
    [showTransferOptionsModal, () => setShowTransferOptionsModal(false)],
    [showStockIntakeOptionsModal, () => setShowStockIntakeOptionsModal(false)],
    [showDepoStockTransferModal, () => handleDepoClose()],
    [showDepoInventory, () => setShowDepoInventory(false)],
    [showEditModal, () => setShowEditModal(false)],
    [showExcelModal, () => setShowExcelModal(false)],
    [showImportModal, () => setShowImportModal(false)],
    [showPriceUpdateModal, () => setShowPriceUpdateModal(false)],
    [showExportModal, () => setShowExportModal(false)],
    [showColumnSettings, () => setShowColumnSettings(false)],
    [showAdvancedFilter, () => setShowAdvancedFilter(false)],
    [showStockIntakeMode, () => setShowStockIntakeMode(false)],
    [showStockDeductionMode, () => setShowStockDeductionMode(false)],
    [showStockHistory, () => setShowStockHistory(false)],
    [showModal, () => setShowModal(false)],
  ]);

  // Aktif kategoriler - Dinamik API'den gelen kategoriler kullanılıyor
  const getCategoriesForType = (type) => {
    const categoryList = dynamicCategories[type] || [];
    // API'den gelen kategorileri id ve name formatına dönüştür
    const formattedCategories = categoryList.map(cat => ({
      id: cat,
      name: cat
    }));
    // Başa "Tümü" seçeneğini ekle
    return [{ id: 'all', name: 'Tümü' }, ...formattedCategories];
  };
  const categories = getCategoriesForType(selectedType);

  // Tüm kategorileri al (dinamik)
  const allCategories = Object.values(dynamicCategories)
    .flat()
    .map(cat => ({ id: cat, name: cat }));

  // Detaylı Filtre parametrelerini URLSearchParams'a ekler — ana ürün listesi ve toplu
  // etiket bastırmadaki "filtrelenen tüm ürünleri" çekme aynı fonksiyonu kullanır, ikisi
  // arasında filtre tutarsızlığı olmasın diye.
  const appendAdvancedFilterParams = useCallback((params) => {
    if (advancedFilters.name) params.append('filter_name', advancedFilters.name);
    if (advancedFilters.barcode) params.append('filter_barcode', advancedFilters.barcode);
    if (advancedFilters.brand) params.append('filter_brand', advancedFilters.brand);
    if (advancedFilters.supplier) params.append('filter_supplier', advancedFilters.supplier);
    if (advancedFilters.description) params.append('filter_description', advancedFilters.description);
    if (advancedFilters.notes) params.append('filter_notes', advancedFilters.notes);
    if (advancedFilters.color) params.append('filter_color', advancedFilters.color);
    if (advancedFilters.size) params.append('filter_size', advancedFilters.size);
    if (advancedFilters.gender) params.append('filter_gender', advancedFilters.gender);
    if (advancedFilters.createdBy) params.append('filter_created_by', advancedFilters.createdBy);
    if (advancedFilters.stockEntryBefore) params.append('stock_entry_before', advancedFilters.stockEntryBefore);
    if (advancedFilters.stockEntryAfter) params.append('stock_entry_after', advancedFilters.stockEntryAfter);
    if (advancedFilters.noStockChangeSince) params.append('no_stock_change_since', advancedFilters.noStockChangeSince);
    if (advancedFilters.createdAfter) params.append('created_after', advancedFilters.createdAfter);
    if (advancedFilters.createdBefore) params.append('created_before', advancedFilters.createdBefore);
    if (advancedFilters.updatedAfter) params.append('updated_after', advancedFilters.updatedAfter);
    if (advancedFilters.updatedBefore) params.append('updated_before', advancedFilters.updatedBefore);
    if (advancedFilters.minStock) params.append('min_stock', advancedFilters.minStock);
    if (advancedFilters.maxStock) params.append('max_stock', advancedFilters.maxStock);
    if (advancedFilters.minWeight) params.append('min_weight', advancedFilters.minWeight);
    if (advancedFilters.maxWeight) params.append('max_weight', advancedFilters.maxWeight);
    if (advancedFilters.minLaborCost) params.append('min_labor_cost', advancedFilters.minLaborCost);
    if (advancedFilters.maxLaborCost) params.append('max_labor_cost', advancedFilters.maxLaborCost);
    if (advancedFilters.minProfitMargin) params.append('min_profit_margin', advancedFilters.minProfitMargin);
    if (advancedFilters.maxProfitMargin) params.append('max_profit_margin', advancedFilters.maxProfitMargin);
    if (advancedFilters.minMinStockThreshold) params.append('min_min_stock_threshold', advancedFilters.minMinStockThreshold);
    if (advancedFilters.maxMinStockThreshold) params.append('max_min_stock_threshold', advancedFilters.maxMinStockThreshold);
    if (advancedFilters.stockScope) params.append('stock_scope_store_id', advancedFilters.stockScope);
    if (advancedFilters.goldKarat) params.append('filter_gold_karat', advancedFilters.goldKarat);
    if (advancedFilters.silverPurity) params.append('filter_silver_purity', advancedFilters.silverPurity);
    if (advancedFilters.currency) params.append('filter_currency', advancedFilters.currency);
    if (advancedFilters.pricingType) params.append('filter_pricing_type', advancedFilters.pricingType);
    if (advancedFilters.shopifyStatus) params.append('filter_shopify_status', advancedFilters.shopifyStatus);
    if (advancedFilters.hasImage) params.append('has_image', advancedFilters.hasImage);
    if (advancedFilters.ecommerceActive) params.append('ecommerce_active', advancedFilters.ecommerceActive);
    if (advancedFilters.shopifyActive) params.append('shopify_active', advancedFilters.shopifyActive);
    if (advancedFilters.trendyolActive) params.append('trendyol_active', advancedFilters.trendyolActive);
    if (advancedFilters.hepsiburadaActive) params.append('hepsiburada_active', advancedFilters.hepsiburadaActive);
    if (advancedFilters.amazonActive) params.append('amazon_active', advancedFilters.amazonActive);
    if (advancedFilters.n11Active) params.append('n11_active', advancedFilters.n11Active);
    if (advancedFilters.isSet) params.append('is_set', advancedFilters.isSet);
    if (advancedFilters.isFavoriteFlag) params.append('is_favorite_flag', advancedFilters.isFavoriteFlag);
    if (advancedFilters.eskiEtiket) params.append('eski_etiket', advancedFilters.eskiEtiket);
    if (advancedFilters.noDiscount) params.append('no_discount', advancedFilters.noDiscount);
    if (advancedFilters.isSpecialDefined) params.append('is_special_defined', advancedFilters.isSpecialDefined);
    // Marka/tedarikçi/renk/beden/oluşturan dropdown'dan seçildiği için tam eşleşme
    if (advancedFilters.brand || advancedFilters.supplier || advancedFilters.color || advancedFilters.size || advancedFilters.createdBy) {
      params.append('attr_exact', '1');
    }
  }, [advancedFilters]);

  // Detaylı Filtre açıkken, seçili metal türüne (yoksa aktif sekmeye) göre dropdown seçeneklerini yükle
  const filterOptionsMetal = advancedFilters.metalType || (['gold', 'silver', 'steel', 'aksesuar', 'brands', 'zippo', 'sarf'].includes(selectedType) ? selectedType : '');
  useEffect(() => {
    if (!showAdvancedFilter) return;
    let cancelled = false;
    fetch(`${API_URL}/products/filter-options${filterOptionsMetal ? `?metal_type=${filterOptionsMetal}` : ''}`)
      .then(r => r.json())
      .then(data => { if (!cancelled && data && !data.error) setFilterOptions(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showAdvancedFilter, filterOptionsMetal]);

  // Mağaza kapsamı (rol bazlı) parametresini ekler — fetchProducts ile aynı kural
  const appendStoreScopeParams = useCallback((params) => {
    if (user?.role === 'admin' || user?.role === 'depo' || user?.permissions?.canEditAllStores) {
      // tüm ürünleri görsün - store_id filtresi yok
    } else if ((user?.role === 'cashier' || user?.role === 'user') && (parseInt(user?.store_id) === 2 || parseInt(user?.store_id) === 3)) {
      // Gümüş mağaza kasiyerleri tüm ürünleri görsün
    } else if ((user?.role === 'cashier' || user?.role === 'user') && user?.store_id) {
      params.append('store_id', user.store_id);
    } else if (selectedStore?.id) {
      params.append('store_id', selectedStore.id);
    } else if (user?.store_id) {
      params.append('store_id', user.store_id);
    }
  }, [user, selectedStore?.id]);

  // Ürünleri ve mağazaları API'den yükle
  // Ürünleri getir (sayfalama ile)
  const fetchProducts = useCallback(async (pageNum = 1, append = false, preserveScroll = false) => {
    const myFetchGen = ++fetchGenRef.current;
    const preservedScrollTop = (pageNum === 1 && !append && preserveScroll) ? tableContainerRef.current?.scrollTop : null;
    const preservedMainScrollTop = (pageNum === 1 && !append && preserveScroll) ? mainContentRef.current?.scrollTop : null;
    try {
      if (pageNum === 1) {
        setLoading(true);
        loadingRef.current = true;
      } else {
        setLoadingMore(true);
        loadingMoreRef.current = true;
      }

      // Ürün Grupları sekmesi kendi listesini (productGroups state'i) kullanır;
      // burada normal ürün arama endpoint'ine hiç gidilmez. Aksi halde arama
      // metal_type filtresini boşalttığından tüm türlerdeki ürünler karışıp
      // listeye düşüyor ve sonuç türüne göre sekme otomatik değişiyordu.
      if (selectedType === 'productGroups') {
        setProducts([]);
        setHasMore(false);
        hasMoreRef.current = false;
        setTotalProducts(0);
        setPage(1);
        pageRef.current = 1;
        return;
      }

      // E-Ticaret tab için ayrı endpoint
      if (selectedType === 'ecommerce') {
        const params = new URLSearchParams({
          page: pageNum,
          limit: 50
        });

        const response = await fetch(`${API_URL}/shopify/products?${params}`);
        const data = await response.json();
        if (myFetchGen !== fetchGenRef.current) return; // Bu arada filtre/sıralama değişip yeni bir fetch başladıysa bu (gecikmiş) sonucu uygulama

        if (append) {
          setProducts(prev => [...prev, ...data.products]);
        } else {
          setProducts(data.products);
        }

        const moreAvailable = data.pagination.page * data.pagination.limit < data.pagination.total;
        setHasMore(moreAvailable);
        hasMoreRef.current = moreAvailable;
        setTotalProducts(data.pagination.total);
        setPage(pageNum);
        pageRef.current = pageNum;
      } else {
        // Normal ürünler için mevcut endpoint
        // Fiyat filtresi aktifse tüm ürünleri çek (frontend'de filtreleme yapılıyor)
        const hasPriceFilter = advancedFilters.minPrice || advancedFilters.maxPrice;
        const isOnlineFilter = selectedType === 'online';
        const physicalTypes = ['gold', 'silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf'];
        const isPhysicalTab = physicalTypes.includes(selectedType);
        const params = new URLSearchParams({
          page: hasPriceFilter ? 1 : pageNum,
          limit: hasPriceFilter ? 1000 : 50,
          metal_type: (searchTerm.trim() || isOnlineFilter) ? '' : (advancedFilters.metalType || selectedType),
          search: searchTerm,
          category: advancedFilters.category || (searchTerm.trim() ? '' : (selectedCategory !== 'all' ? selectedCategory : ''))
        });
        if (isOnlineFilter) params.append('marketplace_any', '1');
        // Fiziksel tür sekmesindeyken pazaryerine açık ürünleri gizle (sadece Online'da görünsün)
        if (isPhysicalTab && !searchTerm.trim()) params.append('exclude_marketplace', '1');

        // Detaylı filtre parametreleri
        appendAdvancedFilterParams(params);

        // Favori bilgisi için store_id her zaman gönder
        const favStoreId = selectedStore?.id || user?.store_id;
        if (favStoreId) params.append('favorite_store_id', favStoreId);

        // Sıralama parametreleri
        if (sortField) {
          // Mağazaya özel stok sütunları (Osmangazi/Yeşilsu/Depo) backend'in tanıdığı
          // 'storeStock' + sabit sort_store_id sıralamasına yönlendirilir. Aksi halde
          // backend bu alan adlarını tanımadığından varsayılan (isim) sırasına döner ve
          // "sıralama" sadece o ana kadar yüklenmiş sayfadaki ürünler arasında yapılmış
          // gibi görünür - scroll ile yeni sayfa geldikçe tutarsız/eksik sonuç verir.
          const namedStoreSortMap = { osmangaziStock: 2, yesilsuStock: 3, depoStock: 4 };
          if (namedStoreSortMap[sortField]) {
            params.append('sort_field', 'storeStock');
            params.append('sort_direction', sortDirection);
            params.append('sort_store_id', namedStoreSortMap[sortField]);
          } else {
            params.append('sort_field', sortField);
            params.append('sort_direction', sortDirection);
            if (sortField === 'storeStock' && selectedStore?.id) {
              params.append('sort_store_id', selectedStore.id);
            }
          }
        }

        // Admin tüm ürünleri görsün (mağaza filtresi yok)
        // Kasiyer ise ve tüm mağaza yetkisi yoksa sadece kendi mağazasındaki ürünleri görsün
        // Depo sorumlusu altın hariç tüm ürünleri görsün (store_id filtresi yok)
        // canEditAllStores yetkisi olanlar da tüm ürünleri görsün
        appendStoreScopeParams(params);

        const response = await fetch(`${API_URL}/products?${params}`);
        const data = await response.json();
        if (myFetchGen !== fetchGenRef.current) return; // Bu arada filtre/sıralama değişip yeni bir fetch başladıysa bu (gecikmiş) sonucu uygulama

        // Arama sırasında metal_type filtresi boşaltıldığı için (tüm türlerde arama
        // yapılabilsin diye), admin/tüm-mağaza yetkisi olmayanlarda sonuçları mağazaya
        // izinli türlerle sınırlıyoruz: gümüş mağaza kasiyeri altın, altın mağazası
        // kasiyeri gümüş ürün göremesin (depo için de aynı kural)
        const restrictToAllowedTypes = user?.role === 'depo' ||
          (user?.role !== 'admin' && !user?.permissions?.canEditAllStores);
        const scopedProducts = restrictToAllowedTypes
          ? data.products.filter(p => allowedProductTypes.includes(p.metal_type))
          : data.products;

        if (append) {
          setProducts(prev => [...prev, ...scopedProducts]);
        } else {
          setProducts(scopedProducts);

          // Arama yapıldıysa ve sonuçlar farklı türdeyse sekmeyi otomatik değiştir
          if (searchTerm.trim() && scopedProducts.length > 0 && !append) {
            const resultTypes = [...new Set(scopedProducts.map(p => p.metal_type))];
            if (resultTypes.length === 1 && resultTypes[0] !== selectedType) {
              setSelectedType(resultTypes[0]);
            }
          }
        }

        // Fiyat filtresi aktifse hasMore false (tüm ürünler tek seferde yüklendi)
        const moreAvailable = hasPriceFilter ? false : data.pagination.hasMore;
        setHasMore(moreAvailable);
        hasMoreRef.current = moreAvailable;
        setTotalProducts(data.pagination.total);
        setPage(pageNum);
        pageRef.current = pageNum;
      }
    } catch (error) {
      console.error('Ürün yükleme hatası:', error);
    } finally {
      // Bu artık en güncel istek değilse (araya yeni bir filtre/sıralama/scroll isteği girdiyse)
      // loading bayraklarını sıfırlama - o iş zaten en güncel isteğin kendi finally'sinde yapılacak.
      if (myFetchGen === fetchGenRef.current) {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
        loadingRef.current = false;
        if (preservedScrollTop != null || preservedMainScrollTop != null) {
          requestAnimationFrame(() => {
            if (preservedScrollTop != null && tableContainerRef.current) {
              tableContainerRef.current.scrollTop = preservedScrollTop;
            }
            if (preservedMainScrollTop != null && mainContentRef.current) {
              mainContentRef.current.scrollTop = preservedMainScrollTop;
            }
          });
        }
      }
    }
  }, [selectedType, searchTerm, selectedCategory, user, selectedStore?.id, advancedFilters, sortField, sortDirection, appendAdvancedFilterParams, appendStoreScopeParams]);

  // Barkod Bastır modunda Detaylı Filtre ile eşleşen TÜM ürünleri (sayfalama olmadan) çeker.
  // fetchProducts ile aynı filtre/kapsam mantığını (appendAdvancedFilterParams/appendStoreScopeParams)
  // kullanır ki alt toplam özeti ve yazdırılacak liste, ekrandaki filtreyle birebir tutarlı olsun.
  const fetchAllProductsForLabelPrint = useCallback(async () => {
    if (selectedType === 'productGroups' || selectedType === 'ecommerce') {
      setLabelPrintFilteredProducts([]);
      return;
    }
    setLabelPrintFilteredLoading(true);
    try {
      const isOnlineFilter = selectedType === 'online';
      const physicalTypes = ['gold', 'silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf'];
      const isPhysicalTab = physicalTypes.includes(selectedType);
      const params = new URLSearchParams({
        page: 1,
        limit: 20000,
        metal_type: (searchTerm.trim() || isOnlineFilter) ? '' : (advancedFilters.metalType || selectedType),
        search: searchTerm,
        category: advancedFilters.category || (searchTerm.trim() ? '' : (selectedCategory !== 'all' ? selectedCategory : ''))
      });
      if (isOnlineFilter) params.append('marketplace_any', '1');
      if (isPhysicalTab && !searchTerm.trim()) params.append('exclude_marketplace', '1');

      appendAdvancedFilterParams(params);
      appendStoreScopeParams(params);

      const response = await fetch(`${API_URL}/products?${params}`);
      const data = await response.json();
      const restrictToAllowedTypes = user?.role === 'depo' ||
        (user?.role !== 'admin' && !user?.permissions?.canEditAllStores);
      const scopedProducts = restrictToAllowedTypes
        ? (data.products || []).filter(p => getAllowedProductTypes().includes(p.metal_type))
        : (data.products || []);
      setLabelPrintFilteredProducts(scopedProducts);
    } catch (error) {
      console.error('Etiket bastırma için ürün listesi alınamadı:', error);
      setLabelPrintFilteredProducts([]);
    } finally {
      setLabelPrintFilteredLoading(false);
    }
  }, [selectedType, searchTerm, selectedCategory, advancedFilters, user, appendAdvancedFilterParams, appendStoreScopeParams]);

  // Barkod Bastır modu açıkken filtre/arama/tür değiştikçe (debounce'lu) listeyi tazele
  useEffect(() => {
    if (!barcodePrintMode) return;
    const t = setTimeout(() => {
      fetchAllProductsForLabelPrint();
    }, 300);
    return () => clearTimeout(t);
  }, [barcodePrintMode, selectedType, selectedCategory, searchTerm, advancedFilters, fetchAllProductsForLabelPrint]);

  // Detaylı Filtre ile eşleşen ürünlerin alt toplam özeti: kaç çeşit, kaç adet, mağaza bazlı kırılım
  const labelPrintSummary = useMemo(() => {
    const list = labelPrintFilteredProducts;
    let totalQty = 0;
    const perStoreMap = {};
    list.forEach(p => {
      totalQty += parseInt(p.stock) || 0;
      (p.store_stocks || []).forEach(ss => {
        perStoreMap[ss.store_id] = (perStoreMap[ss.store_id] || 0) + (parseInt(ss.stock) || 0);
      });
    });
    const perStoreList = Object.entries(perStoreMap)
      .map(([sid, qty]) => ({
        storeId: parseInt(sid),
        storeName: stores.find(s => s.id === parseInt(sid))?.name || `Mağaza ${sid}`,
        qty
      }))
      .sort((a, b) => a.storeId - b.storeId);
    return { totalTypes: list.length, totalQty, perStoreList };
  }, [labelPrintFilteredProducts, stores]);

  // Etiket bastırmada seçilen kaynağa göre bir ürün için basılacak etiket adedi
  const getLabelQtyForProduct = useCallback((product) => {
    if (labelQtyMode !== 'stock') {
      return Math.max(1, Math.floor(printQuantity) || 1);
    }
    if (labelStockScope === 'total') {
      return Math.max(0, parseInt(product.stock) || 0);
    }
    const ss = (product.store_stocks || []).find(s => s.store_id === labelStockScope);
    return Math.max(0, parseInt(ss?.stock) || 0);
  }, [labelQtyMode, labelStockScope, printQuantity]);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/stores`);
      const data = await response.json();
      setStores(data);
    } catch (error) {
      console.error('Mağaza yükleme hatası:', error);
    }
  };

  // Son eklenen ürünü veritabanından çek
  const fetchLastProduct = async () => {
    try {
      // Depo rolü sadece kendi mağazasına izinli türlerden son ürünü görsün
      // (örn. Akik Gold deposu için son gümüş ürün değil, son altın ürün gelmeli)
      let lastProductUrl = `${API_URL}/products/last`;
      if (user?.role === 'depo') {
        const physicalTypes = ['gold', 'silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf'];
        const allowedTypes = getAllowedProductTypes().filter(t => physicalTypes.includes(t));
        if (allowedTypes.length > 0) {
          lastProductUrl += `?metal_types=${encodeURIComponent(allowedTypes.join(','))}`;
        }
      }
      const response = await fetch(lastProductUrl);
      const data = await response.json();
      if (data.product) {
        const lastProduct = data.product;
        setLastAddedProduct({
          metal_type: lastProduct.metal_type,
          category: lastProduct.category,
          name: lastProduct.name,
          brand: lastProduct.brand,
          description: lastProduct.description,
          notes: lastProduct.notes,
          supplier: lastProduct.supplier,
          sell_price: lastProduct.sell_price,
          currency: lastProduct.currency,
          color: lastProduct.color,
          size: lastProduct.size,
          gender: lastProduct.gender,
          weight: lastProduct.weight,
          gold_karat: lastProduct.gold_karat,
          silver_purity: lastProduct.silver_purity,
          labor_cost: lastProduct.labor_cost,
          profit_margin: lastProduct.profit_margin,
          min_stock: lastProduct.min_stock
        });
      }
    } catch (error) {
      console.error('Son ürün yükleme hatası:', error);
    }
  };

  // Fill options ayarlarını veritabanından çek
  const fetchFillOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/settings/product-fill-options`);
      const data = await response.json();
      if (data.fillOptions) {
        setFillOptions(prev => ({ ...prev, ...data.fillOptions }));
      }
    } catch (error) {
      console.error('Fill options yükleme hatası:', error);
    }
  };

  // Fill options ayarlarını veritabanına kaydet
  const saveFillOptions = async (options) => {
    try {
      await fetch(`${API_URL}/settings/product-fill-options`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fillOptions: options })
      });
    } catch (error) {
      console.error('Fill options kaydetme hatası:', error);
    }
  };

  // Stok uyarılarını getir (localStorage'dan - sadece yeni bitenler)
  const fetchStockAlerts = async () => {
    const storeId = user?.role === 'cashier' ? user?.store_id : (selectedStore?.id || user?.store_id);
    if (!storeId) {
      setStockAlerts([]);
      setStockAlertCount(0);
      return;
    }

    try {
      setLoadingStockAlerts(true);
      const response = await fetch(`${API_URL}/stock-alerts?store_id=${storeId}`);
      if (!response.ok) throw new Error('Stok uyarıları alınamadı');
      const rows = await response.json();
      // Formatı UI beklentisine çevir
      const formatted = (Array.isArray(rows) ? rows : []).map(r => ({
        id: r.id,
        alertType: r.alert_type,
        product: {
          id: r.product_id,
          name: r.product_name,
          barcode: r.barcode,
          image: r.image
        },
        myStock: Number(r.current_stock) || 0,
        threshold: r.threshold !== null ? Number(r.threshold) : null,
        availableStore: r.available_store_name,
        availableStoreId: r.available_store_id,
        availableStock: r.available_stock !== null ? Number(r.available_stock) : null,
        createdAt: r.created_at
      }));
      setStockAlerts(formatted);
      setStockAlertCount(formatted.length);
    } catch (error) {
      console.error('Stok uyarıları yükleme hatası:', error);
      setStockAlerts([]);
      setStockAlertCount(0);
    } finally {
      setLoadingStockAlerts(false);
    }
  };

  // Stok uyarısını veritabanından sil
  const removeStockAlert = async (alertId) => {
    try {
      await fetch(`${API_URL}/stock-alerts/${alertId}`, { method: 'DELETE' });
      fetchStockAlerts();
    } catch (error) {
      console.error('Stok uyarı silme hatası:', error);
    }
  };

  // Stok uyarısından transfer talebi oluştur - Perakende Satış'taki transfer
  // talebi ekranına ürün ön dolu şekilde yönlendirir
  const handleStockAlertRequest = (alert) => {
    setShowStockAlertModal(false);
    removeStockAlert(alert.id);

    navigate('/retail-sales', {
      state: {
        openTransferMode: 'transfer-request',
        prefillRequestFromStoreId: String(alert.availableStoreId),
        prefillRequestProduct: {
          product_id: alert.product.id,
          product_name: alert.product.name,
          barcode: alert.product.barcode,
          quantity: 1, // Kullanıcı miktarı kendisi girecek
          stock_quantity: alert.availableStock,
          image: alert.product.image,
        },
      },
    });

    toast.info(`"${alert.product.name}" ürünü için talep formu açıldı. Miktar girin.`);
  };

  // Kategori online ayarlarını yükle (mount'ta bir kez)
  useEffect(() => { fetchCategoryOnlineMap(); }, [fetchCategoryOnlineMap]);

  // URL parametrelerinden modal açma (RetailSales'den yönlendirme için)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');

    if (action) {
      // URL'den action parametresini temizle
      window.history.replaceState({}, '', '/products');

      switch (action) {
        case 'transfer':
          openRetailSalesTransfer('transfer');
          break;
        case 'outgoing-transfers':
          openRetailSalesTransfer('sent-transfers');
          break;
        case 'transfer-request':
          openRetailSalesTransfer('transfer-request');
          break;
        case 'outgoing-requests':
          openRetailSalesTransfer('sent-requests');
          break;
        case 'incoming-requests':
          openRetailSalesTransfer('incoming-requests');
          break;
        case 'stock-intake':
          setShowStockIntakeOptionsModal(true);
          break;
        default:
          break;
      }
    }
  }, [location.search]);

  // İlk yükleme
  useEffect(() => {
    fetchStores();
    fetchLastProduct();
    fetchFillOptions();
    fetchTransferStaff();
    fetchRepairPointsList();
  }, []);

  // Mağaza değişince transfer personellerini yeniden yükle
  useEffect(() => {
    fetchTransferStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore?.id]);

  // Bekleyen transfer sayısını çek (sayfa yüklendiğinde + mağaza değişince)
  useEffect(() => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;
    fetch(`${API_URL}/transfers/pending/${storeId}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setPendingTransferCount(Array.isArray(data) ? data.length : 0))
      .catch(() => setPendingTransferCount(0));
  }, [selectedStore, user]);

  // Kategorileri API'den yükle
  const fetchCategoriesForType = async (metalType) => {
    if (dynamicCategories[metalType]) {
      return; // Zaten yüklenmişse tekrar çekme
    }
    try {
      setLoadingCategories(true);
      const response = await fetch(`${API_URL}/categories?metal_type=${metalType}`);
      if (response.ok) {
        const categories = await response.json();
        setDynamicCategories(prev => ({
          ...prev,
          [metalType]: categories
        }));
      }
    } catch (error) {
      console.error('Kategori yükleme hatası:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Metal türü değiştiğinde kategorileri yükle
  useEffect(() => {
    // 'online' bir filtre türü (gerçek metal_type değil) — kategori yüklemesi atlanır
    const metalType = selectedType === 'gold' ? 'gold' :
                      selectedType === 'silver' ? 'silver' :
                      selectedType === 'steel' ? 'steel' :
                      selectedType === 'zippo' ? 'zippo' :
                      selectedType === 'brands' ? 'brands' :
                      selectedType === 'aksesuar' ? 'aksesuar' :
                      selectedType === 'sarf' ? 'sarf' : '';
    if (metalType) {
      fetchCategoriesForType(metalType);
    }
  }, [selectedType]);

  // "Markalar" sekmesindeki marka dropdown'u için markaları çek
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/products/brands-by-type`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;
        const list = (data?.brands?.brands || []).filter(b => b && String(b).trim() !== '');
        setBrandMenuBrands([...new Set(list)]);
      } catch (e) { /* sessiz geç */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Stok uyarılarını yükle + her 60sn'de bir yenile (DB'de üretilen uyarılar için)
  useEffect(() => {
    if (user?.store_id || selectedStore?.id) {
      fetchStockAlerts();
      const iv = setInterval(fetchStockAlerts, 60000);
      return () => clearInterval(iv);
    }
  }, [user?.store_id, selectedStore?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fiyatları her 1 dakikada bir güncelle ve değişim varsa Shopify'a sync et
  useEffect(() => {
    const fetchPricesAndSync = async () => {
      try {
        const response = await fetch(`${API_URL}/prices`);
        if (response.ok) {
          const data = await response.json();
          setPrices(data);

          // Fiyat güncellemesi için trigger'ı artır (ürün listesini yeniden render etmek için)
          setPriceUpdateTrigger(prev => prev + 1);
          setLastPriceUpdate(new Date());
          console.log('Kurlar güncellendi, ürün fiyatları yeniden hesaplandı:', new Date().toLocaleTimeString('tr-TR'));

          // Fiyat değişim kontrolü yap, eşik aşılırsa Shopify'a sync et
          try {
            const syncResponse = await fetch(`${API_URL}/shopify/check-price-sync`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ prices: data })
            });
            const syncResult = await syncResponse.json();
            if (syncResult.synced) {
              console.log('Shopify fiyat senkronizasyonu:', syncResult.reason);
            }
          } catch (syncError) {
            console.error('Shopify sync hatası:', syncError);
          }

          // Fiyat değişim kontrolü - Trendyol'a da sync et
          try {
            const trendyolSyncResponse = await fetch(`${API_URL}/trendyol/check-price-sync`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ prices: data })
            });
            const trendyolSyncResult = await trendyolSyncResponse.json();
            if (trendyolSyncResult.synced) {
              console.log('Trendyol fiyat senkronizasyonu:', trendyolSyncResult.reason);
            }
          } catch (syncError) {
            console.error('Trendyol sync hatası:', syncError);
          }
        }
      } catch (error) {
        console.error('Fiyat çekme hatası:', error);
      }
    };

    fetchPricesAndSync();
    const priceInterval = setInterval(fetchPricesAndSync, 60000); // 1 dakikada bir kontrol et
    return () => clearInterval(priceInterval);
  }, []);

  // Göreceli zaman hesaplayan useEffect
  useEffect(() => {
    const updateRelativeTime = () => {
      if (!lastPriceUpdate) return;

      const now = new Date();
      const diff = Math.floor((now - lastPriceUpdate) / 1000); // saniye cinsinden fark

      if (diff < 60) {
        setPriceUpdateText('az önce');
      } else if (diff < 120) {
        setPriceUpdateText('1 dk önce');
      } else if (diff < 3600) {
        setPriceUpdateText(`${Math.floor(diff / 60)} dk önce`);
      } else {
        setPriceUpdateText(`${Math.floor(diff / 3600)} saat önce`);
      }
    };

    updateRelativeTime();
    const relativeTimeInterval = setInterval(updateRelativeTime, 30000); // 30 saniyede bir güncelle
    return () => clearInterval(relativeTimeInterval);
  }, [lastPriceUpdate]);

  // Manuel fiyat güncelleme fonksiyonu
  const handleManualPriceRefresh = async () => {
    setIsRefreshingPrices(true);
    try {
      const response = await fetch(`${API_URL}/prices`);
      if (response.ok) {
        const data = await response.json();
        setPrices(data);
        setPriceUpdateTrigger(prev => prev + 1);
        setLastPriceUpdate(new Date());
        toast.success('Kurlar ve fiyatlar güncellendi');
      }
    } catch (error) {
      console.error('Fiyat güncelleme hatası:', error);
      toast.error('Kurlar güncellenirken hata oluştu');
    } finally {
      setIsRefreshingPrices(false);
    }
  };

  // Özel kurları getir (manuel girilen kurlar)
  const fetchCustomRates = async () => {
    try {
      const response = await fetch(`${API_URL}/custom-rates`);
      if (response.ok) {
        const data = await response.json();
        const ratesObj = {};
        data.forEach(rate => {
          ratesObj[rate.currency] = rate;
        });
        // Önceki değerle karşılaştır, değiştiyse trigger'ı artır
        setCustomRates(prevRates => {
          const prevStr = JSON.stringify(prevRates);
          const newStr = JSON.stringify(ratesObj);
          if (prevStr !== newStr) {
            setPriceUpdateTrigger(prev => prev + 1);
            setLastPriceUpdate(new Date());
            console.log('Özel kurlar güncellendi, ürün fiyatları yeniden hesaplandı:', new Date().toLocaleTimeString('tr-TR'));
          }
          return ratesObj;
        });
      }
    } catch (error) {
      console.error('Özel kur getirme hatası:', error);
    }
  };

  // Özel kurları sayfa yüklendiğinde ve periyodik olarak getir
  useEffect(() => {
    fetchCustomRates();
    const customRateInterval = setInterval(fetchCustomRates, 60000); // Her dakika güncelle
    return () => clearInterval(customRateInterval);
  }, []);

  // Dinamik satış kuru hesaplama - makas farkı yüzdesinden veya sabit kurdan
  const getSatisRate = (currency) => {
    const customRate = customRates[currency];
    let liveSatis;
    if (currency === 'GOLD') {
      liveSatis = parseFloat(prices?.altin_satis) || parseFloat(prices?.altin) || 0;
    } else if (currency === 'SILVER') {
      liveSatis = parseFloat(prices?.gumus_satis) || parseFloat(prices?.gumus) || 0;
    } else {
      liveSatis = parseFloat(prices?.[`${currency}_satis`]) || parseFloat(prices?.[currency]) || 0;
    }

    // Önce makas farkı yüzdesini kontrol et (dinamik hesaplama)
    if (customRate?.satis_percentage != null && customRate.satis_percentage !== '' && !isNaN(customRate.satis_percentage)) {
      return liveSatis * (1 + parseFloat(customRate.satis_percentage) / 100);
    }
    // Sonra sabit kuru kontrol et
    if (customRate?.rate_satis != null) {
      return parseFloat(customRate.rate_satis);
    }
    // Geriye uyumluluk: eski rate alanı
    if (customRate?.rate != null) {
      return parseFloat(customRate.rate);
    }
    return liveSatis;
  };

  // Altın gram fiyatını al
  const getGoldGramPrice = () => {
    return getSatisRate('GOLD');
  };

  // Altın fiyat hesaplama
  const calculateGoldPrice = (product) => {
    if (product.pricing_type === 'fixed') {
      return Number(product.sell_price) || 0;
    }

    if (product.metal_type !== 'gold' || !product.weight || !product.gold_karat) {
      return Number(product.sell_price) || 0;
    }

    const goldGramPrice = getGoldGramPrice();
    if (goldGramPrice <= 0) {
      return null; // Fiyat alınamadı
    }

    const gram = parseFloat(product.weight) || 0;
    const labor = parseFloat(product.labor_cost) || 0;
    const multiplier = karatMultipliers[product.gold_karat] || 585;

    // Formül: ((ayar_katsayısı + işçilik) × gram / 1000) × altın_gram_fiyatı
    const laboredGram = (multiplier + labor) * gram / 1000;
    const calculatedPrice = laboredGram * goldGramPrice;

    return calculatedPrice;
  };

  // Gümüş fiyat hesaplama
  const calculateSilverPrice = (product) => {
    if (product.pricing_type === 'fixed') {
      return Number(product.sell_price) || 0;
    }

    if (product.metal_type !== 'silver' || !product.weight) {
      return Number(product.sell_price) || 0;
    }

    const silverGramPrice = getSatisRate('SILVER');
    const usdSellRate = getSatisRate('USD');

    if (silverGramPrice <= 0 || usdSellRate <= 0) {
      return null; // Fiyat alınamadı
    }

    const gram = parseFloat(product.weight) || 0;
    const purity = parseInt(product.silver_purity) || 925; // Varsayılan 925 ayar
    const profitMargin = parseFloat(product.profit_margin) || 3; // Kar oranı çarpanı (varsayılan 3)
    const laborPerGram = parseFloat(product.labor_cost) || 0; // Gram başı işçilik ($)

    // Formül:
    // 1. Has gümüş gramı: (ürün gramı × ayar) / 1000
    const pureGram = (gram * purity) / 1000;

    // 2. Gümüş maliyeti (TL): has gram × gümüş kuru
    const silverCost = pureGram * silverGramPrice;

    // 3. İşçilik maliyeti (TL): ürün gramı × gram başı işçilik × dolar kuru
    const laborCostTL = gram * laborPerGram * usdSellRate;

    // 4. Toplam maliyet (TL): gümüş maliyeti + işçilik maliyeti
    const totalCostTL = silverCost + laborCostTL;

    // 5. Satış fiyatı (TL): toplam maliyet × kar oranı
    const salePriceTL = totalCostTL * profitMargin;

    // Dolar bazlı yuvarlama: TL → USD → yukarı yuvarla → TL
    const salePriceUSD = salePriceTL / usdSellRate;
    const roundedUSD = Math.ceil(salePriceUSD);
    return roundedUSD * usdSellRate;
  };

  // Gümüş Aksesuar fiyat hesaplama (aksesuar türü, Gümüş Aksesuar kategorisi)
  const calculateSilverAccessoryPrice = (product) => {
    if (product.pricing_type === 'fixed') {
      return Number(product.sell_price) || 0;
    }

    if (!product.weight) {
      return Number(product.sell_price) || 0;
    }

    const silverGramPrice = getSatisRate('SILVER');

    if (silverGramPrice <= 0) {
      return null;
    }

    const gram = parseFloat(product.weight) || 0;
    const purity = parseInt(product.silver_purity) || 925;
    const profitMargin = parseFloat(product.profit_margin) || 3;
    const laborPerGram = parseFloat(product.labor_cost) || 0;

    // Gümüş Aksesuar Formülü:
    // Satış Fiyatı = (Has Gümüş Gramı × Gümüş Kuru) + (Ürün Gramı × İşçilik × Kar Oranı)
    // Dolar kuru YOK, kar oranı sadece işçiliğe uygulanıyor

    // 1. Has gümüş gramı: (ürün gramı × ayar) / 1000
    const pureGram = (gram * purity) / 1000;

    // 2. Gümüş maliyeti: has gram × gümüş kuru
    const silverCost = pureGram * silverGramPrice;

    // 3. İşçilik maliyeti: ürün gramı × işçilik × kar oranı
    const laborCost = gram * laborPerGram * profitMargin;

    // 4. Satış fiyatı (TL): gümüş maliyeti + işçilik maliyeti
    const salePrice = silverCost + laborCost;

    return salePrice;
  };

  // Ürün fiyatını hesapla (formüllü veya sabit)
  const getProductPrice = (product) => {
    if (product.metal_type === 'gold' && product.pricing_type !== 'fixed') {
      return calculateGoldPrice(product);
    } else if (product.metal_type === 'silver' && product.pricing_type !== 'fixed') {
      return calculateSilverPrice(product);
    } else if (product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar' && product.pricing_type !== 'fixed') {
      return calculateSilverAccessoryPrice(product);
    }
    return Number(product.sell_price) || 0;
  };

  // İşçilikli gram hesapla
  const calculateLaboredGram = (product) => {
    if (!product.weight) return null;
    const gram = parseFloat(product.weight) || 0;
    const labor = parseFloat(product.labor_cost) || 0;

    if (product.metal_type === 'gold') {
      // Altın için: (ayar_katsayısı + işçilik) × gram / 1000
      const karatMultipliers = { '8': 333, '9': 375, '10': 417, '14': 585, '18': 750, '21': 875, '22': 916, '24': 995 };
      const multiplier = karatMultipliers[product.gold_karat] || 585;
      return ((multiplier + labor) * gram / 1000).toFixed(2);
    } else if (product.metal_type === 'silver' || product.metal_type === 'aksesuar') {
      // Gümüş için: gram × işçilik × kar oranı
      const profitMargin = parseFloat(product.profit_margin) || 1;
      return (gram * labor * profitMargin).toFixed(2);
    }
    return null;
  };

  // Sıralanmış ve filtrelenmiş ürünler
  const getSortedProducts = () => {
    // Önce fiyat filtrelemesi yap (formüllü fiyatlar için frontend'de)
    let filteredProducts = products;

    const minPrice = advancedFilters.minPrice ? parseFloat(advancedFilters.minPrice) : null;
    const maxPrice = advancedFilters.maxPrice ? parseFloat(advancedFilters.maxPrice) : null;

    if (minPrice !== null || maxPrice !== null) {
      filteredProducts = products.filter(product => {
        const price = getProductPrice(product) || 0;
        if (minPrice !== null && price < minPrice) return false;
        if (maxPrice !== null && price > maxPrice) return false;
        return true;
      });
    }

    if (!sortField) return filteredProducts;

    return [...filteredProducts].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'barcode':
          aValue = a.barcode || '';
          bValue = b.barcode || '';
          break;
        case 'category':
          aValue = (a.category || '').toLowerCase();
          bValue = (b.category || '').toLowerCase();
          break;
        case 'brand':
          aValue = (a.brand || '').toLowerCase();
          bValue = (b.brand || '').toLowerCase();
          break;
        case 'price':
          // Formüllü fiyat hesaplaması dahil
          aValue = getProductPrice(a) || 0;
          bValue = getProductPrice(b) || 0;
          break;
        case 'storeStock':
          // store_stocks array'inden seçili mağazanın stoğunu bul
          aValue = a.store_stocks?.find(ss => ss.store_id === selectedStore?.id)?.stock || 0;
          bValue = b.store_stocks?.find(ss => ss.store_id === selectedStore?.id)?.stock || 0;
          break;
        case 'osmangaziStock':
          aValue = a.store_stocks?.find(ss => ss.store_id === 2)?.stock || 0;
          bValue = b.store_stocks?.find(ss => ss.store_id === 2)?.stock || 0;
          break;
        case 'yesilsuStock':
          aValue = a.store_stocks?.find(ss => ss.store_id === 3)?.stock || 0;
          bValue = b.store_stocks?.find(ss => ss.store_id === 3)?.stock || 0;
          break;
        case 'depoStock':
          aValue = a.store_stocks?.find(ss => ss.store_id === 4)?.stock || 0;
          bValue = b.store_stocks?.find(ss => ss.store_id === 4)?.stock || 0;
          break;
        case 'totalStock':
          aValue = parseInt(a.stock) || 0;
          bValue = parseInt(b.stock) || 0;
          break;
        case 'stockEntryDate':
          aValue = a.stock_entry_date ? new Date(a.stock_entry_date).getTime() : 0;
          bValue = b.stock_entry_date ? new Date(b.stock_entry_date).getTime() : 0;
          break;
        case 'stockChangeDate':
          aValue = a.last_stock_change ? new Date(a.last_stock_change).getTime() : 0;
          bValue = b.last_stock_change ? new Date(b.last_stock_change).getTime() : 0;
          break;
        case 'noDiscount':
          aValue = a.no_discount ? 1 : 0;
          bValue = b.no_discount ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue, 'tr');
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });
  };

  const sortedProducts = getSortedProducts();

  // Ürün Yönetimi listesinde tek satır olarak gösterilecek ürün grupları - artık kendi
  // "Ürün Grupları" sekmesinde toplanıyor, fiziksel türe (Altın/Gümüş/...) göre dağılmıyor.
  // Arama varsa grup adı aramaya uymalı; özel bir kategori seçiliyse gruplar gizlenir
  // (grupların kategorisi yoktur).
  const displayProductGroups = productGroups.filter(g => {
    if (selectedCategory !== 'all') return false;
    if (selectedType !== 'productGroups') return false;
    const term = searchTerm.trim().toLocaleLowerCase('tr');
    if (term) return (g.name || '').toLocaleLowerCase('tr').includes(term);
    return true;
  });

  // Filtre, mağaza veya sıralama değiştiğinde yeniden yükle
  useEffect(() => {
    setPage(1);
    pageRef.current = 1;
    hasMoreRef.current = true;
    loadingMoreRef.current = false;
    setProducts([]);
    fetchProducts(1, false);
  }, [selectedType, selectedCategory, selectedStore?.id, sortField, sortDirection, brandFilterNonce]);

  // Arama için debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      pageRef.current = 1;
      hasMoreRef.current = true;
      loadingMoreRef.current = false;
      setProducts([]);
      fetchProducts(1, false);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Fiyat filtresi aktif mi kontrol et
  const isPriceFilterActive = advancedFilters.minPrice || advancedFilters.maxPrice;

  // Infinite scroll - IntersectionObserver ile
  const loadMoreRef = useRef(null);
  const fetchProductsRef = useRef(fetchProducts);
  fetchProductsRef.current = fetchProducts;

  useEffect(() => {
    if (isPriceFilterActive) return;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current && !loadingRef.current && hasMoreRef.current) {
          loadingMoreRef.current = true;
          fetchProductsRef.current(pageRef.current + 1, true);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isPriceFilterActive, products.length]);

  // Gelen transfer talep sayısını kontrol et
  useEffect(() => {
    if (selectedStore?.id) {
      fetchIncomingRequestCount();
      // Her 30 saniyede bir kontrol et
      const interval = setInterval(fetchIncomingRequestCount, 30000);
      return () => clearInterval(interval);
    }
  }, [selectedStore?.id]);

  // Yeni ürün ekle
  // Son eklenen üründen bilgi doldur
  const fillFromLastProduct = async () => {
    if (!lastAddedProduct) {
      toast.info('Henüz ürün eklenmedi');
      return;
    }

    // Metal türü değişiyorsa, yeni türün kategorilerini önceden yükle
    if (fillOptions.metal_type && lastAddedProduct.metal_type && !dynamicCategories[lastAddedProduct.metal_type]) {
      try {
        const response = await fetch(`${API_URL}/categories?metal_type=${lastAddedProduct.metal_type}`);
        if (response.ok) {
          const categories = await response.json();
          setDynamicCategories(prev => ({
            ...prev,
            [lastAddedProduct.metal_type]: categories
          }));
        }
      } catch (error) {
        console.error('Kategori yükleme hatası:', error);
      }
    }

    const updates = {};
    if (fillOptions.metal_type) updates.metal_type = lastAddedProduct.metal_type;
    if (fillOptions.category) updates.category = lastAddedProduct.category;
    if (fillOptions.name) updates.name = lastAddedProduct.name;
    if (fillOptions.brand) updates.brand = lastAddedProduct.brand || '';
    if (fillOptions.description) updates.description = lastAddedProduct.description || '';
    if (fillOptions.notes) updates.notes = lastAddedProduct.notes || '';
    if (fillOptions.supplier) updates.supplier = lastAddedProduct.supplier || '';
    if (fillOptions.sell_price) {
      updates.sell_price = lastAddedProduct.sell_price || '';
      // Fiyat tipini de birlikte senkronize et, yoksa sabit fiyat dolduğu halde
      // sekme "Formül" seçiliymiş gibi görünmeye devam ediyordu.
      const lastHasForex = lastAddedProduct.currency && lastAddedProduct.currency !== 'TRY';
      updates.pricing_type = lastHasForex ? 'fixed' : (lastAddedProduct.pricing_type || 'formula');
    }
    if (fillOptions.currency) updates.currency = lastAddedProduct.currency || 'TRY';
    if (fillOptions.color) updates.color = lastAddedProduct.color || '';
    if (fillOptions.size) updates.size = lastAddedProduct.size || '';
    if (fillOptions.weight) updates.weight = lastAddedProduct.weight || '';
    if (fillOptions.gold_karat) updates.gold_karat = lastAddedProduct.gold_karat || '14';
    if (fillOptions.silver_purity) updates.silver_purity = lastAddedProduct.silver_purity || '1000';
    if (fillOptions.labor_cost) updates.labor_cost = lastAddedProduct.labor_cost || '';
    if (fillOptions.profit_margin) updates.profit_margin = lastAddedProduct.profit_margin || '';
    if (fillOptions.min_stock) updates.min_stock = lastAddedProduct.min_stock || '';
    if (fillOptions.gender) updates.gender = lastAddedProduct.gender || '';

    if (Object.keys(updates).length === 0) {
      toast.warning('Hiçbir alan seçilmedi');
      return;
    }

    // Metal türü değişiyorsa yeni türe göre barkodu yeniden üret
    if (updates.metal_type && updates.metal_type !== newProduct.metal_type) {
      try {
        const response = await fetch(`${API_URL}/products/generate-barcode?metal_type=${encodeURIComponent(updates.metal_type)}`);
        if (response.ok) {
          const data = await response.json();
          if (data.barcode) {
            updates.barcode = data.barcode;
          }
        }
      } catch (error) {
        console.error('Otomatik barkod oluşturma hatası:', error);
      }
    }

    setNewProduct(prev => ({ ...prev, ...updates }));
    toast.success('Son ürün bilgileri dolduruldu');
  };

  // Fill options toggle
  const toggleFillOption = (field) => {
    setFillOptions(prev => {
      const newOptions = { ...prev, [field]: !prev[field] };
      saveFillOptions(newOptions);
      return newOptions;
    });
  };

  // Seçilen üründen bilgileri doldur
  const fillFromSelectedProduct = async (product) => {
    // Metal türü değişiyorsa, yeni türün kategorilerini önceden yükle
    if (fillOptions.metal_type && product.metal_type && !dynamicCategories[product.metal_type]) {
      try {
        const response = await fetch(`${API_URL}/categories?metal_type=${product.metal_type}`);
        if (response.ok) {
          const categories = await response.json();
          setDynamicCategories(prev => ({
            ...prev,
            [product.metal_type]: categories
          }));
        }
      } catch (error) {
        console.error('Kategori yükleme hatası:', error);
      }
    }

    const updates = {};
    if (fillOptions.metal_type) updates.metal_type = product.metal_type;
    if (fillOptions.category) updates.category = product.category;
    if (fillOptions.name) updates.name = product.name;
    if (fillOptions.brand) updates.brand = product.brand || '';
    if (fillOptions.description) updates.description = product.description || '';
    if (fillOptions.notes) updates.notes = product.notes || '';
    if (fillOptions.supplier) updates.supplier = product.supplier || '';
    if (fillOptions.sell_price) {
      updates.sell_price = product.sell_price || '';
      // Fiyat tipini de birlikte senkronize et, yoksa sabit fiyat dolduğu halde
      // sekme "Formül" seçiliymiş gibi görünmeye devam ediyordu.
      const hasForex = product.currency && product.currency !== 'TRY';
      updates.pricing_type = hasForex ? 'fixed' : (product.pricing_type || 'formula');
    }
    if (fillOptions.currency) updates.currency = product.currency || 'TRY';
    if (fillOptions.color) updates.color = product.color || '';
    if (fillOptions.size) updates.size = product.size || '';
    if (fillOptions.weight) updates.weight = product.weight || '';
    if (fillOptions.gold_karat) updates.gold_karat = product.gold_karat || '14';
    if (fillOptions.silver_purity) updates.silver_purity = product.silver_purity || '1000';
    if (fillOptions.labor_cost) updates.labor_cost = product.labor_cost || '';
    if (fillOptions.profit_margin) updates.profit_margin = product.profit_margin || '';
    if (fillOptions.min_stock) updates.min_stock = product.min_stock || '';
    if (fillOptions.gender) updates.gender = product.gender || '';

    if (Object.keys(updates).length === 0) {
      toast.warning('Hiçbir alan seçilmedi');
      return;
    }

    // Metal türü değişiyorsa yeni türe göre barkodu yeniden üret
    if (updates.metal_type && updates.metal_type !== newProduct.metal_type) {
      try {
        const response = await fetch(`${API_URL}/products/generate-barcode?metal_type=${encodeURIComponent(updates.metal_type)}`);
        if (response.ok) {
          const data = await response.json();
          if (data.barcode) {
            updates.barcode = data.barcode;
          }
        }
      } catch (error) {
        console.error('Otomatik barkod oluşturma hatası:', error);
      }
    }

    setNewProduct(prev => ({ ...prev, ...updates }));
    setShowProductSearchModal(false);
    setProductSearchQuery('');
    toast.success(`"${product.name}" bilgileri dolduruldu`);
  };

  // Altın/Gümüş ürünlerde fiyatlama alanlarının zorunluluk kontrolü.
  // Sabit fiyat: sadece sabit fiyat zorunlu. Formül: formülde kullanılan inputlar zorunlu
  // (gram + işçilik; gümüşte ayrıca kat sayı). Ayar/Milyem her zaman varsayılan dolu gelir.
  const validatePricingFields = (p) => {
    const isGold = p.metal_type === 'gold';
    const isSilver = p.metal_type === 'silver' || (p.metal_type === 'aksesuar' && p.category === 'Gümüş Aksesuar');
    if (!isGold && !isSilver) return null;

    if (p.pricing_type === 'fixed') {
      if (!(parseFloat(p.sell_price) > 0)) return 'Sabit fiyat girilmeli!';
      return null;
    }

    // Formül modu
    if (!(parseFloat(p.weight) > 0)) return 'Formül için ürün gramı (ağırlık) zorunlu!';

    const laborEmpty = p.labor_cost === '' || p.labor_cost === null || p.labor_cost === undefined || isNaN(parseFloat(p.labor_cost));
    if (laborEmpty) return 'Formül için işçilik değeri zorunlu!';

    if (isSilver && !(parseFloat(p.profit_margin) > 0)) return 'Formül için kat sayı (kar oranı) zorunlu!';

    return null;
  };

  const handleAddProduct = async () => {
    if (user?.role !== 'admin' && !(user?.role === 'depo' && user?.permissions?.canCreateProduct === true) && user?.permissions?.canCreateProduct !== true) {
      toast.error('Ürün oluşturma yetkiniz yok!');
      return;
    }
    if (!newProductImage) {
      toast.warning('Ürün fotoğrafı gerekli!');
      return;
    }

    if (!newProduct.metal_type) {
      toast.warning('Ürün türü seçilmeli!');
      return;
    }

    if (!newProduct.category) {
      toast.warning('Kategori seçilmeli!');
      return;
    }

    if (!newProduct.name) {
      toast.warning('Ürün adı gerekli!');
      return;
    }

    if (!newProduct.barcode || !newProduct.barcode.trim()) {
      toast.warning('Barkod numarası gerekli!');
      return;
    }

    if (!newProduct.supplier || !newProduct.supplier.trim()) {
      toast.warning('Tedarikçi bilgisi gerekli!');
      return;
    }

    const pricingError = validatePricingFields(newProduct);
    if (pricingError) {
      toast.warning(pricingError);
      return;
    }

    const isGoldOrSilver = newProduct.metal_type === 'gold' || newProduct.metal_type === 'silver'
      || (newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar');
    if (!isGoldOrSilver && !(parseFloat(newProduct.sell_price) > 0)) {
      toast.warning('Fiyat girilmeli!');
      return;
    }

    if (!newProduct.currency) {
      toast.warning('Para birimi seçilmeli!');
      return;
    }

    // Store stocks array oluştur
    const storeStocksArray = user?.role === 'depo' && user?.store_id
      ? [{ store_id: user.store_id, stock: parseInt(newProduct.store_stocks[user.store_id]) || 0 }]
      : Object.entries(newProduct.enabled_stores)
          .filter(([storeId, enabled]) => enabled)
          .map(([storeId]) => ({
            store_id: parseInt(storeId),
            stock: parseInt(newProduct.store_stocks[storeId]) || 0
          }));

    try {
      // Gümüş ürünlerde kayıt anında güncel kur bilgisi + maliyet hesapları ile notes oluştur
      let finalNotes = newProduct.notes || '';
      let creationRates = null;

      if (newProduct.metal_type === 'silver' || (newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar')) {
        try {
          const gumusSatis = getSatisRate('SILVER');
          const usdSatis = getSatisRate('USD');
          const now = new Date();
          const tarih = now.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
          const gram = parseFloat(newProduct.weight) || 0;
          const purity = parseInt(newProduct.silver_purity) || 925;
          const laborPerGram = parseFloat(newProduct.labor_cost) || 0;

          // Kur kaynağını belirle (manuel kur ayarı var mı?)
          const silverCustom = customRates['SILVER'];
          const usdCustom = customRates['USD'];
          const silverSource = (silverCustom?.satis_percentage != null && silverCustom.satis_percentage !== '' && !isNaN(silverCustom.satis_percentage))
            || silverCustom?.rate_satis != null || silverCustom?.rate != null ? 'manuel' : 'canlı';
          const usdSource = (usdCustom?.satis_percentage != null && usdCustom.satis_percentage !== '' && !isNaN(usdCustom.satis_percentage))
            || usdCustom?.rate_satis != null || usdCustom?.rate != null ? 'manuel' : 'canlı';

          // Gümüş formülü ile hesapla
          const pureGram = (gram * purity) / 1000;
          const silverCost = pureGram * gumusSatis;
          const laborCostTL = gram * laborPerGram * usdSatis;

          // USD cinsinden hesaplamalar
          const silverUSD = usdSatis > 0 ? gumusSatis / usdSatis : 0;
          const silverCostUSD = usdSatis > 0 ? silverCost / usdSatis : 0;
          const laborCostUSD = gram * laborPerGram;

          creationRates = {
            silver: gumusSatis,
            silverUSD: silverUSD,
            usd: usdSatis,
            date: tarih,
            pureGram: pureGram,
            silverCost: silverCost,
            silverCostUSD: silverCostUSD,
            laborCostTL: laborCostTL,
            laborCostUSD: laborCostUSD,
            rateSource: {
              silver: silverSource,
              usd: usdSource
            }
          };
        } catch (err) {
          console.error('Kur bilgisi alınamadı:', err);
        }
      }

      const response = await fetch(`${API_URL}/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProduct.name,
          barcode: newProduct.barcode || null,
          metal_type: newProduct.metal_type,
          category: newProduct.category,
          description: newProduct.description || '',
          notes: finalNotes,
          supplier: newProduct.supplier || '',
          sell_price: parseFloat(newProduct.sell_price) || 0,
          currency: newProduct.currency || 'TRY',
          color: newProduct.color || '',
          size: newProduct.size || '',
          stone_color: newProduct.stone_color || '',
          stone_mm: newProduct.stone_mm || '',
          brand: newProduct.brand || '',
          gender: newProduct.gender || '',
          weight: parseFloat(newProduct.weight) || null,
          gold_karat: newProduct.metal_type === 'gold' ? newProduct.gold_karat : null,
          silver_purity: (newProduct.metal_type === 'silver' || (newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar')) ? newProduct.silver_purity : null,
          labor_cost: (newProduct.metal_type === 'gold' || newProduct.metal_type === 'silver' || (newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar')) ? parseFloat(newProduct.labor_cost) || 0 : null,
          profit_margin: (newProduct.metal_type === 'silver' || (newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar')) ? (newProduct.profit_margin !== '' ? parseFloat(newProduct.profit_margin) : null) : null,
          pricing_type: (newProduct.metal_type === 'gold' || newProduct.metal_type === 'silver' || (newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar')) ? newProduct.pricing_type : null,
          ecommerce_active: newProduct.ecommerce_active,
          shopify_active: newProduct.shopify_active,
          trendyol_active: newProduct.trendyol_active,
          hepsiburada_active: newProduct.hepsiburada_active,
          amazon_active: newProduct.amazon_active,
          n11_active: newProduct.n11_active,
          shopify_status: newProduct.shopify_status || 'active',
          eski_etiket: newProduct.eski_etiket,
          no_discount: newProduct.no_discount,
          min_stock: parseInt(newProduct.min_stock) || 0,
          active: newProduct.active,
          store_stocks: storeStocksArray,
          is_special_defined: newProduct.metal_type === 'gold' ? newProduct.is_special_defined : false,
          creation_rates: creationRates,
          created_by: user?.name || user?.username || null
        })
      });

      const data = await response.json();
      if (data.success) {
        // Fotoğraf seçildiyse yükle
        if (newProductImage && data.id) {
          await handleImageUpload(data.id, newProductImage);
        }
        setNewProductImage(null);
        setNewProductImagePreview(null);
        toast.success('Ürün başarıyla eklendi!');
        const typeNames = { gold: 'Altın', silver: 'Gümüş', steel: 'Çelik', zippo: 'Zippo', brands: 'Markalar', aksesuar: 'Aksesuar', sarf: 'Sarf Malzemesi' };
        const metalTypeTurkish = typeNames[newProduct.metal_type] || newProduct.metal_type;

        // Enabled mağaza isimlerini al
        const enabledStoreNames = Object.entries(newProduct.enabled_stores)
          .filter(([id, enabled]) => enabled)
          .map(([id]) => stores.find(s => s.id === parseInt(id))?.name)
          .filter(Boolean)
          .join(', ') || '-';

        logService.log('products', 'create',
          `Yeni ürün eklendi: ${newProduct.name} - ${newProduct.category} - ₺${parseFloat(newProduct.sell_price || 0).toLocaleString()}`, {
          entity_type: 'product',
          entity_id: data.product?.id,
          details: {
            urun_adi: newProduct.name,
            barkod: newProduct.barcode || '-',
            kategori: newProduct.category,
            tur: metalTypeTurkish,
            fiyat: `₺${parseFloat(newProduct.sell_price || 0).toLocaleString()}`,
            agirlik: newProduct.weight ? `${newProduct.weight} gr` : '-',
            tedarikci: newProduct.supplier || '-',
            satis_magazalari: enabledStoreNames
          },
          status: 'success'
        });

        // Son eklenen ürün bilgisini kaydet (hızlı doldurma için)
        setLastAddedProduct({
          metal_type: newProduct.metal_type,
          category: newProduct.category,
          name: newProduct.name,
          brand: newProduct.brand,
          description: newProduct.description,
          notes: newProduct.notes,
          supplier: newProduct.supplier,
          sell_price: newProduct.sell_price,
          currency: newProduct.currency,
          pricing_type: newProduct.pricing_type,
          color: newProduct.color,
          size: newProduct.size,
          gender: newProduct.gender,
          weight: newProduct.weight,
          gold_karat: newProduct.gold_karat,
          silver_purity: newProduct.silver_purity,
          labor_cost: newProduct.labor_cost,
          profit_margin: newProduct.profit_margin,
          min_stock: newProduct.min_stock
        });

        setShowModal(false);
        setNewProduct({
          name: '',
          barcode: '',
          metal_type: 'gold',
          category: 'Altın Kolye',
          description: '',
          notes: '',
          supplier: '',
          sell_price: '',
          currency: 'TRY',
          color: '',
          size: '',
          brand: '',
          gender: '',
          weight: '',
          ecommerce_active: false,
          shopify_active: false,
          trendyol_active: false,
          hepsiburada_active: false,
          amazon_active: false,
          n11_active: false,
          eski_etiket: false,
          no_discount: false,
          min_stock: '',
          active: true,
          store_stocks: {},
          enabled_stores: {}
        });
        fetchProducts(1, false, true);
      } else {
        toast.error('Ürün eklenemedi: ' + (data.error || 'Bilinmeyen hata'));
        logService.log('products', 'create', `Ürün eklenemedi: ${newProduct.name}`, { status: 'error', details: { error: data.error } });
      }
    } catch (error) {
      console.error('Ürün ekleme hatası:', error);
      toast.error('Ürün eklenemedi!');
      logService.log('products', 'create', `Ürün ekleme hatası: ${newProduct.name}`, { status: 'error', details: { error: error.message } });
    }
  };

  // Türe göre varsayılan kategoriyi getir
  const getDefaultCategoryForType = (type) => {
    switch (type) {
      case 'gold': return 'Altın Kolye';
      case 'silver': return 'Gümüş Kolye';
      case 'steel': return 'Çelik Yüzük';
      case 'zippo': return 'Zippo Çakmak';
      case 'brands': return 'Marka Kolye';
      case 'aksesuar': return 'Aksesuar Hediyelik';
      case 'sarf': return 'Sarf Kutu';
      default: return 'Altın Kolye';
    }
  };

  // Form tür değişince kategoriyi güncelle
  const handleMetalTypeChange = async (metalType) => {
    const defaultCategory = getDefaultCategoryForType(metalType);
    const updates = {
      ...newProduct,
      metal_type: metalType,
      category: defaultCategory,
      store_stocks: {},
      enabled_stores: {}
    };

    // Not: Kur bilgileri artık creation_rates alanına kaydediliyor, notes boş kalacak
    updates.notes = '';

    // Gümüş ürün: formül seçili ve 1000 ayar varsayılan
    if (metalType === 'silver') {
      updates.pricing_type = 'formula';
      updates.silver_purity = '1000';
    }

    // Seçilen türe göre otomatik barkod oluştur
    try {
      const response = await fetch(`${API_URL}/products/generate-barcode?metal_type=${encodeURIComponent(metalType)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.barcode) {
          updates.barcode = data.barcode;
        }
      }
    } catch (error) {
      console.error('Otomatik barkod oluşturma hatası:', error);
    }

    setNewProduct(updates);
    // Kategorileri API'den yükle
    fetchCategoriesForType(metalType);
  };

  // ===== Varyant Grupları =====
  const fetchVariantGroups = async () => {
    setLoadingVariantGroups(true);
    try {
      const response = await fetch(`${API_URL}/shopify/variant-groups`);
      const data = await response.json();
      if (response.ok && data.success) {
        setVariantGroups(data.groups || []);
      } else {
        toast.error(data.error || 'Varyant grupları alınamadı');
      }
    } catch (error) {
      console.error('Varyant grupları alma hatası:', error);
      toast.error('Varyant grupları alınamadı');
    } finally {
      setLoadingVariantGroups(false);
    }
  };

  const openVariantGroupsModal = () => {
    setShowVariantGroupsModal(true);
    fetchVariantGroups();
  };

  const resetVariantGroupEditor = () => {
    setEditingVariantGroupId(null);
    setVariantGroupName('');
    setVariantGroupMembers([]);
    setVariantGroupAxes([]);
    setVariantGroupPrimaryId(null);
    setVariantGroupBarcodeInput('');
    setVariantGroupSearchTerm('');
    setVariantGroupSearchResults([]);
  };

  const openNewVariantGroupEditor = () => {
    resetVariantGroupEditor();
    setShowVariantGroupEditorModal(true);
    setTimeout(() => variantGroupBarcodeRef.current?.focus(), 100);
  };

  const openEditVariantGroupEditor = async (group) => {
    try {
      const response = await fetch(`${API_URL}/shopify/variant-groups/${group.id}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.error || 'Grup detayı alınamadı');
        return;
      }
      setEditingVariantGroupId(group.id);
      setVariantGroupName(data.group.name);
      setVariantGroupPrimaryId(data.group.primary_product_id);
      const members = data.members.map(m => ({
        id: m.id, name: m.name, barcode: m.barcode, image: m.image, category: m.category,
        is_set: m.is_set, size: m.size, color: m.color, stone_color: m.stone_color, stone_mm: m.stone_mm,
        sell_price: m.sell_price, currency: m.currency, virtual_variants: m.virtual_variants || []
      }));
      setVariantGroupMembers(members);
      const storedAxes = Array.isArray(data.group.variant_axes) ? data.group.variant_axes : [];
      setVariantGroupAxes(storedAxes.length > 0 ? storedAxes : suggestVariantAxes(members));
      setVariantGroupBarcodeInput('');
      setShowVariantGroupEditorModal(true);
      setTimeout(() => variantGroupBarcodeRef.current?.focus(), 100);
    } catch (error) {
      console.error('Grup düzenleme açma hatası:', error);
      toast.error('Grup detayı alınamadı');
    }
  };

  const closeVariantGroupEditor = () => {
    setShowVariantGroupEditorModal(false);
    resetVariantGroupEditor();
  };

  // Varyant sistemi yokken "Set/Paket Bileşenleri" ile elle çözülmüş renk/beden farklarını
  // (örn. KY700264İ + KY700264H) doğrudan Varyant Grubu editörüne aktarır - bileşenler zaten
  // insan tarafından ilişkilendirildiği için barkod barkod yeniden aramaya gerek kalmaz.
  const openVariantGroupEditorFromSetComponents = (setProduct, components) => {
    if (!components || components.length < 2) {
      toast.warning('Varyant grubu oluşturmak için en az 2 bileşen gerekli');
      return;
    }
    resetVariantGroupEditor();
    const members = components.map(c => ({
      id: c.component_product_id, name: c.name, barcode: c.barcode, image: c.image, category: c.category,
      is_set: c.is_set, size: c.size, color: c.color, stone_color: c.stone_color, stone_mm: c.stone_mm,
      sell_price: c.sell_price, currency: c.currency, virtual_variants: []
    }));
    setVariantGroupName(setProduct.name || '');
    setVariantGroupMembers(members);
    setVariantGroupPrimaryId(members[0].id);
    setVariantGroupAxes(suggestVariantAxes(members));
    setShowEditModal(false);
    setShowVariantGroupEditorModal(true);
  };

  // Varyant grubu düzenleyicisine bir ürünü ekler (barkod okutma veya arama sonucu tıklamadan
  // gelebilir) - henüz kaydedilmemiş, sadece yerel liste.
  const addVariantGroupMember = (product) => {
    if (variantGroupMembers.some(m => m.id === product.id)) {
      toast.warning('Bu ürün zaten grupta');
      return;
    }
    const newMember = {
      id: product.id, name: product.name, barcode: product.barcode, image: product.image, category: product.category,
      is_set: product.is_set, size: product.size, color: product.color, stone_color: product.stone_color, stone_mm: product.stone_mm,
      sell_price: product.sell_price, currency: product.currency, virtual_variants: []
    };
    setVariantGroupMembers(prev => {
      const updated = [...prev, newMember];
      if (!variantGroupPrimaryId && updated.length === 1) {
        setVariantGroupPrimaryId(newMember.id);
      }
      if (updated.length >= 2) {
        setVariantGroupAxes(curAxes => curAxes.length === 0 ? suggestVariantAxes(updated) : curAxes);
      }
      return updated;
    });
    setVariantGroupSearchTerm('');
    setVariantGroupSearchResults([]);
  };

  const handleAddVariantGroupBarcode = async (e) => {
    if (e.key !== 'Enter' || !variantGroupBarcodeInput.trim()) return;
    const barcode = variantGroupBarcodeInput.trim();
    setVariantGroupBarcodeInput('');

    if (variantGroupMembers.some(m => m.barcode === barcode)) {
      toast.warning('Bu ürün zaten grupta');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/products/barcode/${barcode}`);
      if (!response.ok) {
        toast.warning('Ürün bulunamadı: ' + barcode);
        return;
      }
      const product = await response.json();
      addVariantGroupMember(product);
    } catch (error) {
      console.error('Barkod arama hatası:', error);
      toast.error('Ürün aranırken hata oluştu');
    }
  };

  // İsim / barkod / SKU ile canlı ürün arama (debounced) - barkod okutmadan, yazarak eklemek için.
  const handleVariantGroupSearchChange = (value) => {
    setVariantGroupSearchTerm(value);
    if (variantGroupSearchTimerRef.current) clearTimeout(variantGroupSearchTimerRef.current);
    const term = value.trim();
    if (term.length < 2) {
      setVariantGroupSearchResults([]);
      setVariantGroupSearching(false);
      return;
    }
    setVariantGroupSearching(true);
    variantGroupSearchTimerRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/products?search=${encodeURIComponent(term)}&limit=8`);
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data.products || []);
        const memberIds = new Set(variantGroupMembers.map(m => m.id));
        setVariantGroupSearchResults(list.filter(p => !memberIds.has(p.id)));
      } catch (error) {
        console.error('Ürün arama hatası:', error);
      } finally {
        setVariantGroupSearching(false);
      }
    }, 300);
  };

  const removeVariantGroupMember = (productId) => {
    setVariantGroupMembers(prev => prev.filter(m => m.id !== productId));
    if (variantGroupPrimaryId === productId) {
      setVariantGroupPrimaryId(null);
    }
  };

  // ===================== ÜRÜN GRUPLARI =====================
  const fetchProductGroups = useCallback(async () => {
    setLoadingProductGroups(true);
    try {
      const resp = await fetch(`${API_URL}/product-groups`);
      const data = await resp.json();
      if (resp.ok) setProductGroups(data.groups || []);
    } catch (e) {
      console.error('Ürün grupları alınamadı:', e);
    } finally {
      setLoadingProductGroups(false);
    }
  }, []);

  // Sayfa açılışında grupları yükle (liste satırlarını oluşturmak için)
  useEffect(() => { fetchProductGroups(); }, [fetchProductGroups]);

  const openProductGroupsModal = () => {
    setShowProductGroupsModal(true);
    fetchProductGroups();
  };

  const resetProductGroupEditor = () => {
    setEditingProductGroupId(null);
    setProductGroupName('');
    setProductGroupMetalType('');
    setProductGroupNote('');
    setProductGroupMembers([]);
    setProductGroupBarcodeInput('');
    setProductGroupSearchTerm('');
    setProductGroupSearchResults([]);
  };

  const openNewProductGroupEditor = () => {
    resetProductGroupEditor();
    setShowProductGroupEditorModal(true);
    setTimeout(() => productGroupBarcodeRef.current?.focus(), 100);
  };

  const openEditProductGroupEditor = async (group) => {
    try {
      const resp = await fetch(`${API_URL}/product-groups/${group.id}`);
      const data = await resp.json();
      if (!resp.ok) { toast.error(data.error || 'Grup açılamadı'); return; }
      setEditingProductGroupId(group.id);
      setProductGroupName(data.group.name || '');
      setProductGroupMetalType(data.group.metal_type || '');
      setProductGroupNote(data.group.note || '');
      setProductGroupMembers((data.members || []).map(m => ({
        id: m.id, name: m.name, barcode: m.barcode, image: m.image,
        category: m.category, brand: m.brand, metal_type: m.metal_type
      })));
      setProductGroupBarcodeInput('');
      setProductGroupSearchTerm('');
      setProductGroupSearchResults([]);
      setShowProductGroupEditorModal(true);
      setTimeout(() => productGroupBarcodeRef.current?.focus(), 100);
    } catch (e) {
      toast.error('Grup açılamadı');
    }
  };

  const closeProductGroupEditor = () => {
    setShowProductGroupEditorModal(false);
    resetProductGroupEditor();
  };

  const addProductGroupMember = (product) => {
    setProductGroupMembers(prev => {
      if (prev.some(m => m.id === product.id)) return prev;
      const next = [...prev, {
        id: product.id, name: product.name, barcode: product.barcode, image: product.image,
        category: product.category, brand: product.brand, metal_type: product.metal_type
      }];
      // Grup türü ilk üyeden otomatik doldurulsun (kullanıcı değiştirebilir)
      if (prev.length === 0 && product.metal_type) setProductGroupMetalType(product.metal_type);
      return next;
    });
    setProductGroupSearchTerm('');
    setProductGroupSearchResults([]);
  };

  const handleAddProductGroupBarcode = async (e) => {
    if (e.key !== 'Enter' || !productGroupBarcodeInput.trim()) return;
    const barcode = productGroupBarcodeInput.trim();
    setProductGroupBarcodeInput('');
    if (productGroupMembers.some(m => m.barcode === barcode)) {
      toast.info('Bu ürün zaten grupta');
      return;
    }
    try {
      const resp = await fetch(`${API_URL}/products?search=${encodeURIComponent(barcode)}&limit=5`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (data.products || []);
      const product = list.find(p => p.barcode === barcode) || list[0];
      if (!product) { toast.error(`Ürün bulunamadı: ${barcode}`); return; }
      addProductGroupMember(product);
    } catch (err) {
      toast.error('Ürün eklenemedi');
    }
  };

  const handleProductGroupSearchChange = (value) => {
    setProductGroupSearchTerm(value);
    if (productGroupSearchTimerRef.current) clearTimeout(productGroupSearchTimerRef.current);
    const term = value.trim();
    if (term.length < 2) {
      setProductGroupSearchResults([]);
      setProductGroupSearching(false);
      return;
    }
    setProductGroupSearching(true);
    productGroupSearchTimerRef.current = setTimeout(async () => {
      try {
        const resp = await fetch(`${API_URL}/products?search=${encodeURIComponent(term)}&limit=10`);
        const data = await resp.json();
        const list = Array.isArray(data) ? data : (data.products || []);
        const memberIds = new Set(productGroupMembers.map(m => m.id));
        setProductGroupSearchResults(list.filter(p => !memberIds.has(p.id)));
      } catch (error) {
        console.error('Ürün arama hatası:', error);
      } finally {
        setProductGroupSearching(false);
      }
    }, 300);
  };

  const removeProductGroupMember = (productId) => {
    setProductGroupMembers(prev => prev.filter(m => m.id !== productId));
  };

  const saveProductGroup = async () => {
    if (!productGroupName.trim()) { toast.warning('Grup adı girin'); return; }
    if (productGroupMembers.length < 1) { toast.warning('En az 1 ürün ekleyin'); return; }
    setSavingProductGroup(true);
    try {
      const isEdit = !!editingProductGroupId;
      const url = isEdit ? `${API_URL}/product-groups/${editingProductGroupId}` : `${API_URL}/product-groups`;
      const resp = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: productGroupName.trim(),
          metal_type: productGroupMetalType || null,
          note: productGroupNote || null,
          created_by: user?.name || user?.username || null,
          member_product_ids: productGroupMembers.map(m => m.id)
        })
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (data.conflicts?.length) {
          toast.error(`Zaten başka grupta: ${data.conflicts.map(c => `${c.name} (${c.group_name})`).join(', ')}`);
        } else {
          toast.error(data.error || 'Grup kaydedilemedi');
        }
        return;
      }
      toast.success(isEdit ? 'Grup güncellendi' : 'Grup oluşturuldu');
      closeProductGroupEditor();
      // Açık grup üye önbelleğini temizle ve listeyi yenile
      setProductGroupMemberData({});
      setExpandedProductGroupIds(new Set());
      await fetchProductGroups();
      fetchProducts(1, false);
    } catch (e) {
      console.error('Grup kaydetme hatası:', e);
      toast.error('Grup kaydedilemedi');
    } finally {
      setSavingProductGroup(false);
    }
  };

  const deleteProductGroup = (group) => {
    setConfirmModalConfig({
      title: 'Ürün Grubunu Sil',
      message: `"${group.name}" grubu silinecek. Grup içindeki ürünler silinmez, tekrar normal listede görünür.`,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      type: 'danger',
      onConfirm: async () => {
        try {
          const resp = await fetch(`${API_URL}/product-groups/${group.id}`, { method: 'DELETE' });
          const data = await resp.json();
          if (!resp.ok) { toast.error(data.error || 'Grup silinemedi'); return; }
          toast.success('Grup silindi');
          setProductGroupMemberData(prev => { const n = { ...prev }; delete n[group.id]; return n; });
          setExpandedProductGroupIds(prev => { const n = new Set(prev); n.delete(group.id); return n; });
          await fetchProductGroups();
          fetchProducts(1, false);
        } catch (e) {
          toast.error('Grup silinemedi');
        }
      }
    });
    setShowConfirmModal(true);
  };

  // Liste satırında grubu aç/kapa - ilk açılışta üye ürün detaylarını çeker
  const toggleProductGroupExpand = async (group) => {
    const isOpen = expandedProductGroupIds.has(group.id);
    setExpandedProductGroupIds(prev => {
      const n = new Set(prev);
      if (isOpen) n.delete(group.id); else n.add(group.id);
      return n;
    });
    if (isOpen || productGroupMemberData[group.id]) return;
    if (!group.member_ids?.length) { setProductGroupMemberData(prev => ({ ...prev, [group.id]: [] })); return; }
    setLoadingProductGroupMemberIds(prev => new Set(prev).add(group.id));
    try {
      const params = new URLSearchParams({ ids: group.member_ids.join(','), limit: 500 });
      const favStoreId = selectedStore?.id || user?.store_id;
      if (favStoreId) params.append('favorite_store_id', favStoreId);
      const resp = await fetch(`${API_URL}/products?${params}`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (data.products || []);
      // Grup üye sırasını koru
      const orderMap = new Map(group.member_ids.map((id, i) => [id, i]));
      list.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
      setProductGroupMemberData(prev => ({ ...prev, [group.id]: list }));
    } catch (e) {
      console.error('Grup üyeleri alınamadı:', e);
      toast.error('Grup üyeleri yüklenemedi');
    } finally {
      setLoadingProductGroupMemberIds(prev => { const n = new Set(prev); n.delete(group.id); return n; });
    }
  };

  // ===== "Tüm Ürünlerden Seç" ürün seçici (barkodsuz toplu ekleme) =====
  const openGroupProductPicker = (target) => {
    setGroupPickerTarget(target);
    const physicalTypes = ['gold', 'silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf'];
    const raw = target === 'variant'
      ? (variantGroupMembers[0]?.metal_type || 'gold')
      : (productGroupMetalType || productGroupMembers[0]?.metal_type || 'gold');
    let safeType = physicalTypes.includes(raw) ? raw : 'gold';
    if (!allowedProductTypes.includes(safeType)) {
      safeType = physicalTypes.find(t => allowedProductTypes.includes(t)) || 'gold';
    }
    setGroupPickerType(safeType);
    setGroupPickerCategory('all');
    setGroupPickerSearch('');
    setGroupPickerBrand('');
    setGroupPickerInStockOnly(false);
    setGroupPickerSelected(new Map());
    setGroupPickerResults([]);
    fetchCategoriesForType(safeType);
    setShowGroupProductPicker(true);
  };

  const changeGroupPickerType = (t) => {
    setGroupPickerType(t);
    setGroupPickerCategory('all');
    fetchCategoriesForType(t);
  };

  const fetchGroupPickerResults = useCallback(async () => {
    setGroupPickerLoading(true);
    try {
      const params = new URLSearchParams({ limit: 300, exclude_group_members: '1' });
      if (groupPickerType) params.append('metal_type', groupPickerType);
      if (groupPickerCategory && groupPickerCategory !== 'all') params.append('category', groupPickerCategory);
      if (groupPickerSearch.trim()) params.append('search', groupPickerSearch.trim());
      if (groupPickerBrand.trim()) params.append('filter_brand', groupPickerBrand.trim());
      if (groupPickerInStockOnly) params.append('min_stock', '1');
      const favStoreId = selectedStore?.id || user?.store_id;
      if (favStoreId) params.append('favorite_store_id', favStoreId);
      const resp = await fetch(`${API_URL}/products?${params}`);
      const data = await resp.json();
      setGroupPickerResults(Array.isArray(data) ? data : (data.products || []));
    } catch (e) {
      console.error('Ürün seçici arama hatası:', e);
      toast.error('Ürünler yüklenemedi');
    } finally {
      setGroupPickerLoading(false);
    }
  }, [groupPickerType, groupPickerCategory, groupPickerSearch, groupPickerBrand, groupPickerInStockOnly, selectedStore?.id, user?.store_id]);

  useEffect(() => {
    if (!showGroupProductPicker) return;
    if (groupPickerSearchTimerRef.current) clearTimeout(groupPickerSearchTimerRef.current);
    groupPickerSearchTimerRef.current = setTimeout(() => { fetchGroupPickerResults(); }, 300);
    return () => { if (groupPickerSearchTimerRef.current) clearTimeout(groupPickerSearchTimerRef.current); };
  }, [showGroupProductPicker, fetchGroupPickerResults]);

  // groupPickerSelected: Map(product.id -> product). Filtreler değişse bile seçim korunur.
  const groupPickerExistingIds = groupPickerTarget === 'variant'
    ? new Set(variantGroupMembers.map(m => m.id))
    : new Set(productGroupMembers.map(m => m.id));

  const toggleGroupPickerSelect = (product) => {
    if (groupPickerExistingIds.has(product.id)) return;
    setGroupPickerSelected(prev => {
      const n = new Map(prev);
      if (n.has(product.id)) n.delete(product.id); else n.set(product.id, product);
      return n;
    });
  };

  const toggleGroupPickerSelectAll = () => {
    const selectable = groupPickerResults.filter(p => !groupPickerExistingIds.has(p.id));
    setGroupPickerSelected(prev => {
      const allSelected = selectable.length > 0 && selectable.every(p => prev.has(p.id));
      const n = new Map(prev);
      if (allSelected) selectable.forEach(p => n.delete(p.id));
      else selectable.forEach(p => n.set(p.id, p));
      return n;
    });
  };

  const confirmGroupProductPicker = () => {
    const chosen = [...groupPickerSelected.values()].filter(p => !groupPickerExistingIds.has(p.id));
    if (chosen.length === 0) { toast.info('Ürün seçilmedi'); return; }
    if (groupPickerTarget === 'variant') {
      chosen.forEach(p => addVariantGroupMember(p));
    } else {
      chosen.forEach(p => addProductGroupMember(p));
    }
    toast.success(`${chosen.length} ürün eklendi`);
    setShowGroupProductPicker(false);
  };

  const toggleMemberVirtualVariantForm = (memberId) => {
    setOpenVirtualVariantMemberId(prev => prev === memberId ? null : memberId);
    setMemberVirtualVariantAxis('color');
    setMemberVirtualVariantValue('');
  };

  // Grup üyesine sanal (stoksuz) varyant ekler - o üyenin STOĞUNU paylaşan, kendi barkodu
  // olmayan ek bir Shopify seçeneği (bkz. shopify_virtual_variants). Üye zaten kaydedilmiş
  // (gerçek bir product_id'si) olmalı - önce grubu kaydedip sonra eklemek gerekebilir.
  const handleAddMemberVirtualVariant = async (memberId) => {
    if (!memberVirtualVariantValue.trim()) {
      toast.warning('Değer girin');
      return;
    }
    setSavingMemberVirtualVariant(true);
    try {
      const response = await fetch(`${API_URL}/products/${memberId}/virtual-variants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ axis_key: memberVirtualVariantAxis, value: memberVirtualVariantValue.trim() })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.error || 'Sanal varyant eklenemedi');
        return;
      }
      toast.success('Sanal varyant eklendi - grubu kaydedip Shopify\'a göndermeyi unutmayın');
      setVariantGroupMembers(prev => prev.map(m => m.id === memberId
        ? { ...m, virtual_variants: [...(m.virtual_variants || []), { id: data.id, axis_key: memberVirtualVariantAxis, value: memberVirtualVariantValue.trim() }] }
        : m));
      setOpenVirtualVariantMemberId(null);
      setMemberVirtualVariantValue('');
    } catch (error) {
      console.error('Sanal varyant ekleme hatası:', error);
      toast.error('Sanal varyant eklenemedi');
    } finally {
      setSavingMemberVirtualVariant(false);
    }
  };

  const handleRemoveMemberVirtualVariant = async (memberId, vvId) => {
    try {
      const response = await fetch(`${API_URL}/products/${memberId}/virtual-variants/${vvId}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.error || 'Sanal varyant silinemedi');
        return;
      }
      toast.success('Sanal varyant silindi');
      setVariantGroupMembers(prev => prev.map(m => m.id === memberId
        ? { ...m, virtual_variants: (m.virtual_variants || []).filter(vv => vv.id !== vvId) }
        : m));
    } catch (error) {
      console.error('Sanal varyant silme hatası:', error);
      toast.error('Sanal varyant silinemedi');
    }
  };

  // Grup ekranında satır üzerinde varyant alanı (Ölçü/Renk/Taş Rengi/Taş Milimi) hızlı düzenleme -
  // anlık olarak yerel listeyi günceller (eksen uyarısı canlı hesaplansın diye); blur'da ürünün kendi kaydına yazılır.
  const setVariantGroupMemberAttrLocal = (productId, field, value) => {
    setVariantGroupMembers(prev => prev.map(m => m.id === productId ? { ...m, [field]: value } : m));
  };

  const persistVariantGroupMemberAttr = async (productId, field, value) => {
    try {
      const response = await fetch(`${API_URL}/products/${productId}/variant-attrs`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value })
      });
      if (!response.ok) {
        toast.error(`${variantAxisLabel(field)} güncellenemedi`);
      }
    } catch (error) {
      console.error('Varyant alanı güncelleme hatası:', error);
      toast.error(`${variantAxisLabel(field)} güncellenemedi`);
    }
  };

  // En fazla 3 eksen seçilebilir (Shopify sınırı).
  const toggleVariantGroupAxis = (key) => {
    setVariantGroupAxes(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      if (prev.length >= 3) {
        toast.warning('Shopify en fazla 3 varyant eksenine izin verir');
        return prev;
      }
      return [...prev, key];
    });
  };

  // Kaydetmeden önce eksen tutarlılığını önizle (backend validasyonuyla aynı kural).
  // Eksen seçimi zorunlu değil - hiçbiri seçili değilse ayırt edici değer olarak barkod kullanılır.
  const getVariantGroupAxisWarning = () => {
    if (variantGroupMembers.length < 1) return null;
    if (variantGroupAxes.length === 0) return null;
    const missingAxis = variantGroupAxes.find(key => !variantGroupMembers.every(m => m[key] && String(m[key]).trim() !== ''));
    if (missingAxis) {
      return `Seçili "${variantAxisLabel(missingAxis)}" eksenindeki değer tüm ürünlerde dolu değil`;
    }
    const seen = new Set();
    for (const m of variantGroupMembers) {
      const combo = variantGroupAxes.map(key => String(m[key])).join('||');
      if (seen.has(combo)) {
        return `Aynı varyant kombinasyonuna sahip birden fazla ürün var (barkod: ${m.barcode})`;
      }
      seen.add(combo);
    }
    return null;
  };

  const saveVariantGroup = async (andSync = false) => {
    if (!variantGroupName.trim()) {
      toast.warning('Grup adı gerekli');
      return;
    }
    if (variantGroupMembers.length < 1) {
      toast.warning('En az 1 ürün ekleyin');
      return;
    }
    if (!variantGroupPrimaryId) {
      toast.warning('Bir ana ürün seçin');
      return;
    }
    const axisWarning = getVariantGroupAxisWarning();
    if (axisWarning) {
      toast.warning(axisWarning);
      return;
    }

    setSavingVariantGroup(true);
    try {
      const isEdit = !!editingVariantGroupId;
      const url = isEdit ? `${API_URL}/shopify/variant-groups/${editingVariantGroupId}` : `${API_URL}/shopify/variant-groups`;
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: variantGroupName.trim(),
          primary_product_id: variantGroupPrimaryId,
          member_product_ids: variantGroupMembers.map(m => m.id),
          variant_axes: variantGroupAxes
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.error || 'Varyant grubu kaydedilemedi');
        return;
      }

      const groupId = isEdit ? editingVariantGroupId : data.groupId;
      toast.success(isEdit ? 'Varyant grubu güncellendi' : 'Varyant grubu oluşturuldu');

      if (andSync) {
        await syncVariantGroup(groupId, false);
      }

      closeVariantGroupEditor();
      fetchVariantGroups();
    } catch (error) {
      console.error('Varyant grubu kaydetme hatası:', error);
      toast.error('Varyant grubu kaydedilemedi');
    } finally {
      setSavingVariantGroup(false);
    }
  };

  const syncVariantGroup = async (groupId, refreshList = true) => {
    setSyncingVariantGroupId(groupId);
    try {
      const response = await fetch(`${API_URL}/shopify/variant-groups/${groupId}/sync`, { method: 'POST' });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Varyant grubu Shopify\'a gönderildi');
      } else {
        toast.error(data.error || 'Shopify\'a gönderilemedi');
      }
    } catch (error) {
      console.error('Varyant grubu sync hatası:', error);
      toast.error('Shopify\'a gönderilemedi');
    } finally {
      setSyncingVariantGroupId(null);
      if (refreshList) fetchVariantGroups();
    }
  };

  const deleteVariantGroup = (group) => {
    setConfirmModalConfig({
      title: 'Varyant Grubunu Sil',
      message: group.link_mode === 'merged'
        ? `"${group.name}" grubunu silmek istediğinize emin misiniz? Shopify'daki ürün de silinecek.`
        : `"${group.name}" grubunu silmek istediğinize emin misiniz? Ürünler Shopify'da bağımsız ürün olarak kalmaya devam edecek, sadece aralarındaki bağlantı kaldırılacak.`,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/shopify/variant-groups/${group.id}`, { method: 'DELETE' });
          const data = await response.json();
          if (response.ok && data.success) {
            toast.success('Varyant grubu silindi');
            fetchVariantGroups();
          } else {
            toast.error(data.error || 'Varyant grubu silinemedi');
          }
        } catch (error) {
          console.error('Varyant grubu silme hatası:', error);
          toast.error('Varyant grubu silinemedi');
        }
      }
    });
    setShowConfirmModal(true);
  };

  // Yeni ürün modalını aç (izin verilen tür ile)
  const openNewProductModal = async () => {
    const defaultType = allowedProductTypes.includes(selectedType) ? selectedType : allowedProductTypes[0];
    const defaultCategory = getDefaultCategoryForType(defaultType);
    const updates = {
      ...newProduct,
      metal_type: defaultType,
      category: defaultCategory,
      description: '',
      notes: '',
      store_stocks: {},
      enabled_stores: {}
    };

    // Gümüş ürün: formül seçili ve 1000 ayar varsayılan
    if (defaultType === 'silver') {
      updates.pricing_type = 'formula';
      updates.silver_purity = '1000';
    }

    // Seçilen türe göre otomatik barkod oluştur
    try {
      const response = await fetch(`${API_URL}/products/generate-barcode?metal_type=${encodeURIComponent(defaultType)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.barcode) {
          updates.barcode = data.barcode;
        }
      }
    } catch (error) {
      console.error('Otomatik barkod oluşturma hatası:', error);
    }

    setNewProduct(updates);
    // Kategorileri API'den yükle
    fetchCategoriesForType(defaultType);
    setShowModal(true);
  };

  // İlgili mağazaları getir
  const getStoresForMetalType = (metalType) => {
    return stores.filter(store => {
      if (metalType === 'gold') {
        return store.id === 1; // Akik Gold Jewellery
      } else if (metalType === 'silver') {
        return store.id === 2 || store.id === 3 || store.id === 4; // Osmangazi, Yeşilsu ve Depo
      }
      // steel, zippo, brands, aksesuar için gümüş mağazaları (Osmangazi, Yeşilsu, Depo)
      return store.id === 2 || store.id === 3 || store.id === 4;
    });
  };

  // Ürün düzenle
  const openEditModal = (product) => {
    // Eğer kapanma animasyonu devam ediyorsa iptal et
    if (closeModalTimerRef.current) {
      clearTimeout(closeModalTimerRef.current);
      closeModalTimerRef.current = null;
      setClosingModal(null);
    }
    // Store stocks'u object formatına çevir
    const storeStocksObj = {};
    const enabledStoresObj = {};
    if (product.store_stocks && Array.isArray(product.store_stocks)) {
      product.store_stocks.forEach(ss => {
        storeStocksObj[ss.store_id] = ss.stock || 0;
        // Stok tanımlıysa mağaza etkin demektir
        enabledStoresObj[ss.store_id] = true;
      });
    }

    // Türü category'den çıkar
    const getTypeFromCategory = (category) => {
      if (category?.startsWith('Altın')) return 'gold';
      if (category?.startsWith('Gümüş')) return 'silver';
      if (category?.startsWith('Çelik')) return 'steel';
      if (category?.startsWith('Zippo')) return 'zippo';
      if (category?.startsWith('Marka')) return 'brands';
      if (category?.startsWith('Aksesuar')) return 'aksesuar';
      return 'gold';
    };
    const metalType = getTypeFromCategory(product.category);

    // Döviz (EUR/USD) cinsli ürünler uygulama mantığında her zaman SABİT fiyattır (grid de öyle gösterir).
    // pricing_type DB'de NULL/boş kalmış olabilir; bu durumda edit modalı yanlışlıkla "formül" sekmesini
    // aktif gösteriyordu. Burada normalize ediyoruz: döviz → fixed, TRY → kayıtlı değer (yoksa formula).
    const hasForex = product.currency && product.currency !== 'TRY';

    const productData = {
      ...product,
      metal_type: product.metal_type || metalType,
      pricing_type: hasForex ? 'fixed' : (product.pricing_type || 'formula'),
      enabled_stores: enabledStoresObj,
      sell_price: product.sell_price || '',
      min_stock: product.min_stock || 0,
      description: product.description || '',
      supplier: product.supplier || '',
      color: product.color || '',
      size: product.size || '',
      stone_color: product.stone_color || '',
      stone_mm: product.stone_mm || '',
      brand: product.brand || '',
      gender: product.gender || '',
      weight: product.weight || '',
      gold_karat: product.gold_karat || '14',
      silver_purity: product.silver_purity || '925',
      labor_cost: product.labor_cost || '',
      profit_margin: product.profit_margin || '',
      ecommerce_active: product.ecommerce_active === true || product.ecommerce_active === 1,
      shopify_active: product.shopify_active === true || product.shopify_active === 1,
      trendyol_active: product.trendyol_active === true || product.trendyol_active === 1,
      hepsiburada_active: product.hepsiburada_active === true || product.hepsiburada_active === 1,
      amazon_active: product.amazon_active === true || product.amazon_active === 1,
      n11_active: product.n11_active === true || product.n11_active === 1,
      eski_etiket: product.eski_etiket === true || product.eski_etiket === 1,
      no_discount: product.no_discount === true || product.no_discount === 1,
      active: product.active === true || product.active === 1,
      store_stocks: storeStocksObj,
      is_special_defined: product.is_special_defined === true || product.is_special_defined === 1,
      creation_rates: product.creation_rates ? (typeof product.creation_rates === 'string' ? JSON.parse(product.creation_rates) : product.creation_rates) : null
    };
    setEditProduct(productData);
    setOriginalProduct({ ...productData });
    setShowEditModal(true);
    // Kategori listesi sadece aktif sekmenin türü için yükleniyordu; barkodla aranan farklı türdeki
    // ürünün (ör. Gümüş sekmesindeyken Çelik ürün) kategori seçimi boş kalıyordu.
    fetchCategoriesForType(productData.metal_type);

    // Set bileşenlerini yükle
    if (product.is_set) {
      loadSetComponents(product.id);
      setVirtualVariants([]);
    } else {
      setSetComponents([]);
      loadVirtualVariants(product.id);
    }

    // Ürün bir varyant grubunun üyesiyse Shopify toggle'ı yerine grup rozeti gösterilecek
    setEditProductVariantGroupInfo(null);
    if (canAccessShopify && product.id) {
      fetch(`${API_URL}/shopify/variant-groups/lookup/${product.id}`)
        .then(r => r.json())
        .then(data => setEditProductVariantGroupInfo(data.inGroup ? { groupId: data.groupId, groupName: data.groupName, linkMode: data.linkMode } : null))
        .catch(() => {});
    }
  };

  // Set bileşenlerini yükle
  const loadSetComponents = async (productId) => {
    try {
      setLoadingSetComponents(true);
      const response = await fetch(`${API_URL}/products/${productId}/set-components`);
      const data = await response.json();
      setSetComponents(data || []);
    } catch (error) {
      console.error('Set bileşenleri yüklenemedi:', error);
      setSetComponents([]);
    } finally {
      setLoadingSetComponents(false);
    }
  };

  // Sanal (stoksuz) varyant seçeneklerini yükle - ürünün STOĞUNU paylaşan ek Shopify varyantları
  const loadVirtualVariants = async (productId) => {
    try {
      setLoadingVirtualVariants(true);
      const response = await fetch(`${API_URL}/products/${productId}/virtual-variants`);
      const data = await response.json();
      setVirtualVariants(data.variants || []);
    } catch (error) {
      console.error('Sanal varyantlar yüklenemedi:', error);
      setVirtualVariants([]);
    } finally {
      setLoadingVirtualVariants(false);
    }
  };

  const handleAddVirtualVariant = async () => {
    if (!newVirtualVariantValue.trim()) {
      toast.warning('Değer girin');
      return;
    }
    setSavingVirtualVariant(true);
    try {
      const response = await fetch(`${API_URL}/products/${editProduct.id}/virtual-variants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ axis_key: newVirtualVariantAxis, value: newVirtualVariantValue.trim() })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.error || 'Sanal varyant eklenemedi');
        return;
      }
      toast.success('Sanal varyant eklendi - Shopify\'a göndermek için "Güncelle" ile kaydedin');
      setNewVirtualVariantValue('');
      loadVirtualVariants(editProduct.id);
    } catch (error) {
      console.error('Sanal varyant ekleme hatası:', error);
      toast.error('Sanal varyant eklenemedi');
    } finally {
      setSavingVirtualVariant(false);
    }
  };

  const handleRemoveVirtualVariant = async (vvId) => {
    try {
      const response = await fetch(`${API_URL}/products/${editProduct.id}/virtual-variants/${vvId}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.error || 'Sanal varyant silinemedi');
        return;
      }
      toast.success('Sanal varyant silindi');
      loadVirtualVariants(editProduct.id);
    } catch (error) {
      console.error('Sanal varyant silme hatası:', error);
      toast.error('Sanal varyant silinemedi');
    }
  };

  // Set bileşeni arama
  const searchSetComponents = async (query) => {
    if (!query || query.length < 2) {
      setSetComponentResults([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/products/search-for-set/${encodeURIComponent(query)}?exclude_set_id=${editProduct?.id || ''}`);
      const data = await response.json();
      // Zaten eklenmiş bileşenleri filtrele
      const existingIds = setComponents.map(c => c.component_product_id);
      setSetComponentResults((data || []).filter(p => !existingIds.includes(p.id)));
    } catch (error) {
      console.error('Bileşen arama hatası:', error);
    }
  };

  // Set bileşeni ekle
  const addSetComponent = (product) => {
    setSetComponents(prev => [...prev, {
      component_product_id: product.id,
      name: product.name,
      barcode: product.barcode,
      image: product.image,
      total_stock: product.total_stock || 0,
      quantity: 1
    }]);
    setSetComponentSearch('');
    setSetComponentResults([]);
  };

  // Set bileşeni sil
  const removeSetComponent = (componentId) => {
    setSetComponents(prev => prev.filter(c => c.component_product_id !== componentId));
  };

  // Set bileşeni adedi güncelle
  const updateSetComponentQuantity = (componentId, quantity) => {
    const qty = Math.max(1, parseInt(quantity) || 1);
    setSetComponents(prev => prev.map(c =>
      c.component_product_id === componentId ? { ...c, quantity: qty } : c
    ));
  };

  // Set bileşenlerini kaydet
  const saveSetComponents = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/products/${productId}/set-components`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          components: setComponents.map(c => ({
            component_product_id: c.component_product_id,
            quantity: c.quantity
          }))
        })
      });
      const data = await response.json();
      if (!data.success) {
        toast.error('Set bileşenleri kaydedilemedi');
      }
    } catch (error) {
      toast.error('Set bileşenleri kaydedilemedi');
    }
  };

  // Edit form tür değişince
  const handleEditMetalTypeChange = (metalType) => {
    fetchCategoriesForType(metalType);
    const defaultCategory = getDefaultCategoryForType(metalType);
    setEditProduct(prev => ({
      ...prev,
      metal_type: metalType,
      category: defaultCategory,
      store_stocks: {},
      enabled_stores: {}
    }));
  };

  const handleEditProduct = async () => {
    if (user?.role !== 'admin' && !(user?.role === 'depo' && user?.permissions?.canEditProduct === true) && user?.permissions?.canEditProduct !== true) {
      toast.error('Ürün düzenleme yetkiniz yok!');
      return;
    }

    if (!editProduct.image) {
      toast.warning('Ürün fotoğrafı gerekli!');
      return;
    }

    if (!editProduct.metal_type) {
      toast.warning('Ürün türü seçilmeli!');
      return;
    }

    if (!editProduct.category) {
      toast.warning('Kategori seçilmeli!');
      return;
    }

    if (!editProduct.name) {
      toast.warning('Ürün adı gerekli!');
      return;
    }

    if (!editProduct.barcode || !editProduct.barcode.trim()) {
      toast.warning('Barkod numarası gerekli!');
      return;
    }

    if (!editProduct.supplier || !editProduct.supplier.trim()) {
      toast.warning('Tedarikçi bilgisi gerekli!');
      return;
    }

    const pricingError = validatePricingFields(editProduct);
    if (pricingError) {
      toast.warning(pricingError);
      return;
    }

    const isGoldOrSilverEdit = editProduct.metal_type === 'gold' || editProduct.metal_type === 'silver'
      || (editProduct.metal_type === 'aksesuar' && editProduct.category === 'Gümüş Aksesuar');
    if (!isGoldOrSilverEdit && !(parseFloat(editProduct.sell_price) > 0)) {
      toast.warning('Fiyat girilmeli!');
      return;
    }

    if (!editProduct.currency) {
      toast.warning('Para birimi seçilmeli!');
      return;
    }

    // Store stocks array oluştur - sadece enabled olan mağazalar (admin için)
    const storeStocksArray = user?.role === 'admin'
      ? Object.entries(editProduct.enabled_stores || {})
          .filter(([storeId, enabled]) => enabled)
          .map(([storeId]) => ({
            store_id: parseInt(storeId),
            stock: parseInt(editProduct.store_stocks?.[storeId]) || 0
          }))
      : undefined; // depo kullanıcıları stoku ayrı PATCH ile günceller

    try {
      const putBody = {
          name: editProduct.name,
          barcode: editProduct.barcode || null,
          metal_type: editProduct.metal_type,
          category: editProduct.category,
          description: editProduct.description || '',
          notes: editProduct.notes || '',
          supplier: editProduct.supplier || '',
          sell_price: parseFloat(editProduct.sell_price) || 0,
          currency: editProduct.currency || 'TRY',
          color: editProduct.color || '',
          size: editProduct.size || '',
          stone_color: editProduct.stone_color || '',
          stone_mm: editProduct.stone_mm || '',
          brand: editProduct.brand || '',
          gender: editProduct.gender || null,
          weight: parseFloat(editProduct.weight) || null,
          gold_karat: editProduct.metal_type === 'gold' ? editProduct.gold_karat : null,
          silver_purity: (editProduct.metal_type === 'silver' || (editProduct.metal_type === 'aksesuar' && editProduct.category === 'Gümüş Aksesuar')) ? editProduct.silver_purity : null,
          labor_cost: (editProduct.metal_type === 'gold' || editProduct.metal_type === 'silver' || (editProduct.metal_type === 'aksesuar' && editProduct.category === 'Gümüş Aksesuar')) ? parseFloat(editProduct.labor_cost) || 0 : null,
          profit_margin: (editProduct.metal_type === 'silver' || (editProduct.metal_type === 'aksesuar' && editProduct.category === 'Gümüş Aksesuar')) ? (editProduct.profit_margin !== '' && editProduct.profit_margin != null ? parseFloat(editProduct.profit_margin) : null) : null,
          pricing_type: (editProduct.metal_type === 'gold' || editProduct.metal_type === 'silver' || (editProduct.metal_type === 'aksesuar' && editProduct.category === 'Gümüş Aksesuar')) ? (editProduct.pricing_type || 'formula') : null,
          ecommerce_active: editProduct.ecommerce_active === true || editProduct.ecommerce_active === 1,
          shopify_active: editProduct.shopify_active === true || editProduct.shopify_active === 1,
          trendyol_active: editProduct.trendyol_active === true || editProduct.trendyol_active === 1,
          hepsiburada_active: editProduct.hepsiburada_active === true || editProduct.hepsiburada_active === 1,
          amazon_active: editProduct.amazon_active === true || editProduct.amazon_active === 1,
          n11_active: editProduct.n11_active === true || editProduct.n11_active === 1,
          shopify_status: editProduct.shopify_status || 'active',
          eski_etiket: editProduct.eski_etiket === true || editProduct.eski_etiket === 1,
          no_discount: editProduct.no_discount === true || editProduct.no_discount === 1,
          min_stock: parseInt(editProduct.min_stock) || 0,
          active: editProduct.active === true || editProduct.active === 1,
          is_special_defined: editProduct.metal_type === 'gold' ? editProduct.is_special_defined : false
      };
      // Admin: store_stocks PUT body'sine dahil et, depo: ayrı PATCH ile güncellenir
      if (storeStocksArray) {
        putBody.store_stocks = storeStocksArray;
      }

      const response = await fetch(`${API_URL}/products/${editProduct.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(putBody)
      });

      const data = await response.json();
      if (data.success) {
        // Depo kullanıcıları: stoku ayrı PATCH ile güncelle (diğer mağazaları etkilememek için)
        if (user?.role === 'depo' && user?.store_id) {
          const newStock = parseInt(editProduct.store_stocks?.[user.store_id]) || 0;
          const oldStock = parseInt(originalProduct?.store_stocks?.[user.store_id]) || 0;
          if (newStock !== oldStock) {
            const diff = Math.abs(newStock - oldStock);
            try {
              await fetch(`${API_URL}/products/${editProduct.id}/stock`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  quantity: diff,
                  type: newStock > oldStock ? 'add' : 'subtract',
                  store_id: user.store_id,
                  staff_id: user.id,
                  staff_name: user.name,
                  notes: `Depo sorumlusu stok düzenleme: ${oldStock} → ${newStock}`
                })
              });
            } catch (stockErr) {
              console.error('Stok güncelleme hatası:', stockErr);
              toast.error('Stok güncellenirken hata oluştu!');
            }
          }
        }

        // Set bileşenlerini kaydet
        if (setComponents.length > 0 || editProduct.is_set) {
          await saveSetComponents(editProduct.id);
        }

        toast.success('Ürün güncellendi!');

        // Auto-sync to platforms based on platform-specific flags
        const shopifyEnabled = editProduct.shopify_active === true || editProduct.shopify_active === 1;
        const wasShopifyEnabled = originalProduct?.shopify_active === true || originalProduct?.shopify_active === 1;
        const trendyolEnabled = editProduct.trendyol_active === true || editProduct.trendyol_active === 1;
        const wasTrendyolEnabled = originalProduct?.trendyol_active === true || originalProduct?.trendyol_active === 1;

        // Shopify sync (varyant grubu üyesi ürünler grup ekranından senkronize edilir, burada atlanır)
        if (shopifyEnabled && !editProductVariantGroupInfo) {
          toast.info(`Shopify'a gönderiliyor: ${editProduct.name}...`);
          try {
            const syncResponse = await fetch(`${API_URL}/shopify/products/${editProduct.id}/sync`, { method: 'POST' });
            const syncData = await syncResponse.json();
            if (syncData.success) {
              toast.success(`Shopify'a sync edildi: ${editProduct.name}`);
            } else {
              toast.warning(`Shopify sync başarısız: ${syncData.error || 'Bilinmeyen hata'}`);
            }
          } catch (syncError) {
            console.error('Shopify sync hatası:', syncError);
          }
        } else if (wasShopifyEnabled && !shopifyEnabled) {
          try {
            await fetch(`${API_URL}/shopify/products/${editProduct.id}/sync`, { method: 'DELETE' });
            toast.info(`Shopify'dan kaldırıldı: ${editProduct.name}`);
          } catch (syncError) {
            console.error('Shopify remove hatası:', syncError);
          }
        }

        // Trendyol sync
        if (trendyolEnabled) {
          toast.info(`Trendyol'a gönderiliyor: ${editProduct.name}...`);
          try {
            const syncResponse = await fetch(`${API_URL}/trendyol/products/${editProduct.id}/sync`, { method: 'POST' });
            const syncData = await syncResponse.json();
            if (syncData.success) {
              toast.success(`Trendyol'a sync edildi: ${editProduct.name}`);
            } else {
              toast.warning(`Trendyol sync başarısız: ${syncData.error || 'Bilinmeyen hata'}`);
            }
          } catch (syncError) {
            console.error('Trendyol sync hatası:', syncError);
          }
        } else if (wasTrendyolEnabled && !trendyolEnabled) {
          try {
            await fetch(`${API_URL}/trendyol/products/${editProduct.id}/sync`, { method: 'DELETE' });
            toast.info(`Trendyol'dan kaldırıldı: ${editProduct.name}`);
          } catch (syncError) {
            console.error('Trendyol remove hatası:', syncError);
          }
        }

        // Değişiklikleri tespit et
        const changes = [];
        const fieldNames = {
          name: 'Ad',
          barcode: 'Barkod',
          category: 'Kategori',
          sell_price: 'Fiyat',
          weight: 'Ağırlık',
          supplier: 'Tedarikçi',
          description: 'Açıklama',
          color: 'Renk',
          size: 'Beden',
          brand: 'Marka',
          min_stock: 'Min. Stok'
        };

        Object.keys(fieldNames).forEach(field => {
          let oldVal = originalProduct?.[field];
          let newVal = editProduct[field];

          // Sayısal değerleri karşılaştır
          if (field === 'sell_price' || field === 'weight' || field === 'min_stock') {
            oldVal = parseFloat(oldVal) || 0;
            newVal = parseFloat(newVal) || 0;
          }

          if (oldVal !== newVal && (oldVal || newVal)) {
            if (field === 'sell_price') {
              changes.push(`${fieldNames[field]}: ₺${Number(oldVal).toLocaleString()} → ₺${Number(newVal).toLocaleString()}`);
            } else if (field === 'weight') {
              changes.push(`${fieldNames[field]}: ${oldVal || 0} gr → ${newVal || 0} gr`);
            } else if (oldVal && newVal) {
              changes.push(`${fieldNames[field]}: "${oldVal}" → "${newVal}"`);
            } else if (newVal) {
              changes.push(`${fieldNames[field]} eklendi: "${newVal}"`);
            } else {
              changes.push(`${fieldNames[field]} silindi`);
            }
          }
        });

        // Stok değişikliklerini kontrol et
        const stockChanges = [];
        const oldStocks = originalProduct?.store_stocks || {};
        const newStocks = editProduct.store_stocks || {};
        const allStoreIds = new Set([...Object.keys(oldStocks), ...Object.keys(newStocks)]);

        allStoreIds.forEach(storeId => {
          const oldStock = parseInt(oldStocks[storeId]) || 0;
          const newStock = parseInt(newStocks[storeId]) || 0;
          if (oldStock !== newStock) {
            stockChanges.push({ storeId, oldStock, newStock, diff: newStock - oldStock });
            changes.push(`Stok: ${oldStock} → ${newStock} adet`);
          }
        });

        // Mağaza görünürlük değişikliklerini kontrol et
        const storeVisibilityChanges = [];
        const oldEnabledStores = originalProduct?.enabled_stores || {};
        const newEnabledStores = editProduct.enabled_stores || {};
        const allStoreIdsForVisibility = new Set([
          ...Object.keys(oldEnabledStores),
          ...Object.keys(newEnabledStores)
        ]);

        allStoreIdsForVisibility.forEach(storeId => {
          const wasEnabled = oldEnabledStores[storeId] || false;
          const isEnabled = newEnabledStores[storeId] || false;
          if (wasEnabled !== isEnabled) {
            const storeName = stores.find(s => s.id === parseInt(storeId))?.name || `Mağaza ${storeId}`;
            if (isEnabled) {
              storeVisibilityChanges.push({ storeName, action: 'eklendi' });
              changes.push(`${storeName} mağazasına eklendi`);
            } else {
              storeVisibilityChanges.push({ storeName, action: 'çıkarıldı' });
              changes.push(`${storeName} mağazasından çıkarıldı`);
            }
          }
        });

        // Mevcut enabled mağaza isimlerini al
        const currentEnabledStoreNames = Object.entries(editProduct.enabled_stores || {})
          .filter(([id, enabled]) => enabled)
          .map(([id]) => stores.find(s => s.id === parseInt(id))?.name)
          .filter(Boolean)
          .join(', ') || '-';

        const changeDesc = changes.length > 0
          ? changes.slice(0, 3).join(', ') + (changes.length > 3 ? ` (+${changes.length - 3} değişiklik)` : '')
          : 'Değişiklik yapılmadı';

        logService.log('products', 'update', `${editProduct.name} - ${changeDesc}`, {
          entity_type: 'product',
          entity_id: editProduct.id,
          details: {
            urun_adi: editProduct.name,
            degisiklikler: changes,
            stok_degisiklikleri: stockChanges.length > 0 ? stockChanges.map(sc => ({
              eski_stok: sc.oldStock,
              yeni_stok: sc.newStock,
              fark: sc.diff > 0 ? `+${sc.diff}` : sc.diff
            })) : null,
            magaza_gorunurluk_degisiklikleri: storeVisibilityChanges.length > 0 ? storeVisibilityChanges : null,
            satis_magazalari: currentEnabledStoreNames,
            onceki: {
              ad: originalProduct?.name,
              fiyat: originalProduct?.sell_price,
              kategori: originalProduct?.category
            },
            sonraki: {
              ad: editProduct.name,
              fiyat: editProduct.sell_price,
              kategori: editProduct.category
            }
          },
          status: 'success'
        });
        setShowEditModal(false);
        setEditProduct(null);
        setOriginalProduct(null);

        // Tüm listeyi sayfa 1'den yeniden çekmek yerine (scroll pozisyonu ve sıralama
        // kayıyor, düzenlerken hangi üründe olduğumuzu şaşırtıyordu), sadece güncellenen
        // ürünü tek satır olarak çekip listede aynı yere yerleştiriyoruz.
        try {
          const freshRes = await fetch(`${API_URL}/products/${editProduct.id}`);
          const freshProduct = await freshRes.json();
          if (freshRes.ok && freshProduct && !freshProduct.error) {
            setProducts(prev => prev.map(p => p.id === editProduct.id ? { ...freshProduct, is_favorite: p.is_favorite } : p));
          }
        } catch (refreshError) {
          console.error('Ürün satırı yenileme hatası:', refreshError);
        }
      } else {
        toast.error(data.error || 'Güncelleme başarısız!');
        logService.log('products', 'update', `Ürün güncellenemedi: ${editProduct.name}`, { status: 'error', details: { error: data.error } });
      }
    } catch (error) {
      console.error('Ürün güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
      logService.log('products', 'update', `Ürün güncelleme hatası: ${editProduct.name}`, { status: 'error', details: { error: error.message } });
    }
  };

  // Ürün sil
  const handleDeleteProduct = async (product) => {
    showConfirmation({
      title: 'Ürün Silme',
      message: `"${product.name}" ürününü silmek istediğinize emin misiniz?`,
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/products/${product.id}`, {
            method: 'DELETE'
          });

          const data = await response.json();
          if (data.success) {
            toast.success('Ürün silindi!');
            logService.log('products', 'delete',
              `Ürün silindi: ${product.name} - ${product.category || ''} - Barkod: ${product.barcode || '-'}`, {
              entity_type: 'product',
              entity_id: product.id,
              details: {
                urun_adi: product.name,
                barkod: product.barcode || '-',
                kategori: product.category || '-',
                fiyat: product.sell_price ? `₺${Number(product.sell_price).toLocaleString()}` : '-',
                agirlik: product.weight ? `${product.weight} gr` : '-'
              },
              status: 'success'
            });
            fetchProducts(1, false, true);
          } else {
            toast.error('Silme başarısız!');
            logService.log('products', 'delete', `Ürün silinemedi: ${product.name}`, { status: 'error' });
          }
        } catch (error) {
          console.error('Ürün silme hatası:', error);
          toast.error('Silme başarısız!');
          logService.log('products', 'delete', `Ürün silme hatası: ${product.name}`, { status: 'error', details: { error: error.message } });
        }
      }
    });
  };

  // Toplu silme - ürün seçimi toggle
  const toggleProductSelection = (productId) => {
    // "Tümünü seç" modundayken tek ürün çıkarılırsa
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      // Yüklü tüm ürünleri seç, sadece tıklananı çıkar
      setSelectedProducts(products.map(p => p.id).filter(id => id !== productId));
      return;
    }

    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Tümünü seç/kaldır
  const toggleSelectAll = () => {
    if (selectAllFiltered) {
      // Tümü seçiliydi, kaldır
      setSelectAllFiltered(false);
      setSelectedProducts([]);
    } else {
      // Filtredeki tüm ürünleri seç (sadece yüklenenler değil, hepsi)
      setSelectAllFiltered(true);
      setSelectedProducts(products.map(p => p.id));
    }
  };

  // Toplu silme işlemi
  const handleBulkDelete = async () => {
    // Tümü seçiliyse filtre bazlı silme yap
    if (selectAllFiltered) {
      await handleBulkDeleteByFilter();
      return;
    }

    if (selectedProducts.length === 0) {
      toast.warning('Lütfen silmek istediğiniz ürünleri seçin!');
      return;
    }

    showConfirmation({
      title: 'Toplu Ürün Silme',
      message: `${selectedProducts.length} ürünü silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`,
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        let successCount = 0;
        let errorCount = 0;

        for (const productId of selectedProducts) {
          try {
            const response = await fetch(`${API_URL}/products/${productId}`, {
              method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} ürün silindi!`);
          logService.log('products', 'bulk_delete', `Toplu silme: ${successCount} ürün silindi`, {
            status: 'success',
            details: { deleted_count: successCount, error_count: errorCount }
          });
        }
        if (errorCount > 0) {
          toast.error(`${errorCount} ürün silinemedi!`);
        }

        // Temizle ve yenile
        setSelectedProducts([]);
        setBulkDeleteMode(false);
        setProducts([]);
        setPage(1);
        pageRef.current = 1;
        hasMoreRef.current = true;
        loadingMoreRef.current = false;
        fetchProducts(1, false);
      }
    });
  };

  // Filtrelenen tüm ürünleri sil
  const handleBulkDeleteByFilter = async () => {
    try {
      // Önce kaç ürün silineceğini öğren
      const countResponse = await fetch(`${API_URL}/products/count-by-filter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metal_type: selectedType,
          category: selectedCategory !== 'all' ? selectedCategory : null,
          store_id: (user?.role === 'cashier' && !user?.permissions?.canEditAllStores && parseInt(user?.store_id) !== 2 && parseInt(user?.store_id) !== 3) ? user?.store_id : null
        })
      });
      const countData = await countResponse.json();
      const totalCount = countData.count;

      if (totalCount === 0) {
        toast.warning('Silinecek ürün bulunamadı!');
        return;
      }

      const categoryName = selectedCategory !== 'all' ? selectedCategory : 'tüm kategoriler';
      const typeNameMap = { gold: 'Altın', silver: 'Gümüş', steel: 'Çelik', zippo: 'Zippo', brands: 'Markalar', aksesuar: 'Aksesuar', sarf: 'Sarf', online: 'Online' };
      const typeName = typeNameMap[selectedType] || selectedType;

      // Silme işlemini gerçekleştiren fonksiyon
      const performDelete = async () => {
        const response = await fetch(`${API_URL}/products/bulk-delete-by-filter`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            metal_type: selectedType,
            category: selectedCategory !== 'all' ? selectedCategory : null,
            store_id: (user?.role === 'cashier' && !user?.permissions?.canEditAllStores && parseInt(user?.store_id) !== 2 && parseInt(user?.store_id) !== 3) ? user?.store_id : null
          })
        });

        const data = await response.json();

        if (data.success) {
          toast.success(`${data.deletedCount} ürün silindi!`);
          logService.log('products', 'bulk_delete_filter', `Toplu silme (filtre): ${data.deletedCount} ürün silindi`, {
            status: 'success',
            details: { metal_type: selectedType, category: selectedCategory, deleted_count: data.deletedCount }
          });

          // Temizle ve yenile
          setSelectedProducts([]);
          setSelectAllFiltered(false);
          setBulkDeleteMode(false);
          setProducts([]);
          setPage(1);
          pageRef.current = 1;
          hasMoreRef.current = true;
          loadingMoreRef.current = false;
          fetchProducts(1, false);
        } else {
          toast.error('Silme işlemi başarısız!');
        }
      };

      // İlk onay
      showConfirmation({
        title: 'Kategori Bazlı Toplu Silme',
        message: `${typeName} - ${categoryName} kategorisindeki ${totalCount} ürünü silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`,
        confirmText: 'Evet, Sil',
        cancelText: 'İptal',
        type: 'danger',
        onConfirm: () => {
          // İkinci onay (çok fazla ürün için)
          if (totalCount > 100) {
            showConfirmation({
              title: 'Son Onay',
              message: `DİKKAT: ${totalCount} ürün silinecek!\n\nDevam etmek istediğinize emin misiniz?`,
              confirmText: 'Evet, Devam Et',
              cancelText: 'Vazgeç',
              type: 'danger',
              onConfirm: performDelete
            });
          } else {
            performDelete();
          }
        }
      });
    } catch (error) {
      console.error('Toplu silme hatası:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  // Toplu silme modunu kapat
  const cancelBulkDelete = () => {
    setSelectedProducts([]);
    setSelectAllFiltered(false);
    setBulkDeleteMode(false);
  };

  // E-commerce toggle - quick toggle without opening modal
  const toggleEcommerce = async (product) => {
    const newValue = !(product.ecommerce_active === 1 || product.ecommerce_active === true);
    try {
      const response = await fetch(`${API_URL}/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: product.name,
          barcode: product.barcode,
          metal_type: product.metal_type,
          category: product.category,
          description: product.description || '',
          supplier: product.supplier || '',
          sell_price: product.sell_price || 0,
          color: product.color || '',
          size: product.size || '',
          brand: product.brand || '',
          weight: product.weight || null,
          gold_karat: product.gold_karat || null,
          labor_cost: product.labor_cost || null,
          ecommerce_active: newValue,
          shopify_status: product.shopify_status || 'active',
          min_stock: product.min_stock || 0,
          active: product.active === 1 || product.active === true
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`E-Ticaret ${newValue ? 'açıldı' : 'kapatıldı'}: ${product.name}`);
        fetchProducts(1, false, true);

        // Auto-sync to Shopify when enabling e-commerce
        if (newValue) {
          toast.info(`Shopify'a gönderiliyor: ${product.name}...`);
          try {
            const syncResponse = await fetch(`${API_URL}/shopify/products/${product.id}/sync`, {
              method: 'POST'
            });
            const syncData = await syncResponse.json();
            if (syncData.success) {
              toast.success(`Shopify'a eklendi: ${product.name}`);
            } else {
              toast.warning(`Shopify sync başarısız: ${syncData.error || 'Bilinmeyen hata'}`);
            }
          } catch (syncError) {
            console.error('Shopify sync hatası:', syncError);
            toast.warning('Shopify sync başarısız, daha sonra manuel deneyin');
          }
        } else {
          // When disabling, remove from Shopify
          try {
            await fetch(`${API_URL}/shopify/products/${product.id}/sync`, {
              method: 'DELETE'
            });
            toast.info(`Shopify'dan kaldırıldı: ${product.name}`);
          } catch (syncError) {
            console.error('Shopify remove hatası:', syncError);
          }
        }
      } else {
        toast.error('E-Ticaret güncellenemedi!');
      }
    } catch (error) {
      console.error('E-Ticaret toggle hatası:', error);
      toast.error('E-Ticaret güncellenemedi!');
    }
  };

  // Favori toggle - Mağaza bazlı
  const toggleFavorite = async (product) => {
    const newValue = !(product.is_favorite === 1 || product.is_favorite === true);
    const storeId = selectedStore?.id || user?.store_id || 1;
    try {
      const response = await fetch(`${API_URL}/products/${product.id}/favorite`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_favorite: newValue, store_id: storeId })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(newValue ? `⭐ Favorilere eklendi: ${product.name}` : `Favorilerden çıkarıldı: ${product.name}`);
        fetchProducts(1, false, true);
      } else {
        toast.error('Favori güncellenemedi!');
      }
    } catch (error) {
      console.error('Favori toggle hatası:', error);
      toast.error('Favori güncellenemedi!');
    }
  };

  // Resim yükleme
  const handleImageUpload = async (productId, file) => {
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_URL}/products/${productId}/image`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        // Ürün listesini güncelle
        fetchProducts(1, false, true);
        // Edit modal açıksa editProduct'ı güncelle
        setEditProduct(prev => {
          if (prev && prev.id === productId) {
            return { ...prev, image: data.image };
          }
          return prev;
        });
        // Log ekle
        const productName = editProduct?.name || 'Ürün';
        logService.log('products', 'update', `Görsel eklendi: ${productName}`, {
          entity_type: 'product',
          entity_id: productId,
          details: {
            urun_adi: productName,
            islem: 'Görsel eklendi',
            dosya_adi: file.name
          },
          status: 'success'
        });
        return data.image;
      } else {
        toast.error('Resim yüklenemedi: ' + (data.error || 'Bilinmeyen hata'));
        logService.log('products', 'update', `Görsel yüklenemedi: ${editProduct?.name || 'Ürün'}`, {
          entity_type: 'product',
          entity_id: productId,
          details: { error: data.error },
          status: 'error'
        });
      }
    } catch (error) {
      console.error('Resim yükleme hatası:', error);
      toast.error('Resim yüklenemedi!');
      logService.log('products', 'update', `Görsel yükleme hatası: ${editProduct?.name || 'Ürün'}`, {
        entity_type: 'product',
        entity_id: productId,
        details: { error: error.message },
        status: 'error'
      });
    } finally {
      setUploadingImage(false);
    }
    return null;
  };

  // Resim silme
  const handleImageDelete = async (productId) => {
    const productName = editProduct?.name || 'Ürün';

    showConfirmation({
      title: 'Resim Silme',
      message: 'Resmi silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'warning',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/products/${productId}/image`, {
            method: 'DELETE'
          });
          const data = await response.json();
          if (data.success) {
            fetchProducts(1, false, true);
            setEditProduct(prev => {
              if (prev && prev.id === productId) {
                return { ...prev, image: null };
              }
              return prev;
            });
            // Log ekle
            logService.log('products', 'update', `Görsel silindi: ${productName}`, {
              entity_type: 'product',
              entity_id: productId,
              details: {
                urun_adi: productName,
                islem: 'Görsel silindi'
              },
              status: 'success'
            });
          } else {
            logService.log('products', 'update', `Görsel silinemedi: ${productName}`, {
              entity_type: 'product',
              entity_id: productId,
              details: { error: data.error },
              status: 'error'
            });
          }
        } catch (error) {
          console.error('Resim silme hatası:', error);
          logService.log('products', 'update', `Görsel silme hatası: ${productName}`, {
            entity_type: 'product',
            entity_id: productId,
            details: { error: error.message },
            status: 'error'
          });
        }
      }
    });
  };

  // Ürün stok hareket geçmişi
  const fetchStockHistory = async (productId, storeId) => {
    setLoadingStockHistory(true);
    try {
      const storeParam = storeId ? `&store_id=${storeId}` : '';
      const response = await fetch(`${API_URL}/products/${productId}/stock-history?limit=200${storeParam}`);
      const data = await response.json();
      setStockHistory(data.movements || []);
    } catch (error) {
      console.error('Stok geçmişi hatası:', error);
      setStockHistory([]);
    } finally {
      setLoadingStockHistory(false);
    }
  };

  const openStockHistory = async (product) => {
    setSelectedProduct(product);
    const defaultStoreId = user?.role === 'admin' ? (selectedStore?.id || 2) : (selectedStore?.id || user?.store_id);
    setStockHistoryStoreId(defaultStoreId);
    setShowStockHistoryModal(true);
    fetchStockHistory(product.id, defaultStoreId);
  };

  // Tip değiştiğinde kategoriyi sıfırla
  const handleTypeChange = (type) => {
    // Ürün Grupları sekmesinden çıkılınca açık bırakılan grup satırları başka sekmede
    // (Altın/Gümüş vb.) de açık kalıyordu - sekme değişince kapat.
    if (selectedType === 'productGroups' && type !== 'productGroups') {
      setExpandedProductGroupIds(new Set());
    }
    setSelectedType(type);
    setSelectedCategory('all');
  };

  // "Markalar" sekmesindeki dropdown'dan bir marka seçilince: Markalar sekmesine geç ve
  // detaylı filtredeki "Marka" alanını o markaya sabitle
  const handleBrandMenuSelect = (brand) => {
    setBrandMenuOpen(false);
    if (selectedType === 'productGroups') {
      setExpandedProductGroupIds(new Set());
    }
    setSelectedType('brands');
    setSelectedCategory('all');
    setAdvancedFilters(prev => ({ ...prev, brand: brand || '' }));
    // Zaten 'brands' sekmesindeysek selectedType/selectedCategory değişmediği için ana
    // fetch effect'i tetiklenmiyordu → ikinci marka seçimi hiçbir şey yapmıyordu. Nonce
    // ile her seçimde yeniden çekim garanti ediliyor.
    setBrandFilterNonce(n => n + 1);
  };

  const openBrandMenu = () => {
    if (brandMenuCloseTimerRef.current) { clearTimeout(brandMenuCloseTimerRef.current); brandMenuCloseTimerRef.current = null; }
    setBrandMenuOpen(true);
  };
  const scheduleCloseBrandMenu = () => {
    if (brandMenuCloseTimerRef.current) clearTimeout(brandMenuCloseTimerRef.current);
    brandMenuCloseTimerRef.current = setTimeout(() => setBrandMenuOpen(false), 180);
  };

  // Kullanıcının görebileceği ürün türlerini belirle
  const getAllowedProductTypes = () => {
    let types;
    // Admin veya tüm mağazalarda düzenleme yetkisi olan kasiyerler tüm türleri görebilir
    if (user?.role === 'admin' || user?.permissions?.canEditAllStores) {
      types = ['gold', 'silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf', 'ecommerce', 'online'];
    } else {
      // Admin olmayan kullanıcılar (cashier, user, depo) - mağazaya göre filtrele
      const storeId = parseInt(user?.store_id);
      if (storeId) {
        // Altın mağazası (ID=1) - sadece altın ve e-ticaret görebilir
        if (storeId === 1) {
          types = ['gold', 'ecommerce'];
        }
        // Gümüş mağazaları (ID=2, ID=3) - altın hariç tüm türleri görebilir
        else if (storeId === 2 || storeId === 3) {
          types = ['silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf', 'ecommerce'];
        }
        // Depo (ID=4) - altın hariç tüm türleri görebilir
        else if (storeId === 4) {
          types = ['silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf', 'ecommerce'];
        }
        // Diğer mağazalar için varsayılan: altın ve sarf hariç
        else {
          types = ['silver', 'steel', 'zippo', 'brands', 'aksesuar', 'ecommerce'];
        }
      } else {
        // Varsayılan olarak tüm türler
        types = ['gold', 'silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf', 'ecommerce'];
      }
    }

    // Shopify erişimi olmayan kullanıcılardan e-ticaret türünü kaldır
    if (!canAccessShopify) {
      types = types.filter(t => t !== 'ecommerce');
    }

    // Online türü sadece admin için (canEditAllStores'a yetki vermiyor)
    if (user?.role !== 'admin') {
      types = types.filter(t => t !== 'online');
    }

    // Ürün Grupları sekmesi "Grup Oluştur" butonunu görebilen herkese açık - fiziksel
    // türe göre değil, gruplandırılmış ürünlere göre filtrelenir
    types = [...types, 'productGroups'];

    return types;
  };

  const allowedProductTypes = getAllowedProductTypes();

  // Sayfa yüklendiğinde izin verilen ilk türü seç
  useEffect(() => {
    if (!allowedProductTypes.includes(selectedType)) {
      setSelectedType(allowedProductTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.store_id, user?.permissions?.canEditAllStores, allowedProductTypes.length]);

  // Para birimi simgesi
  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'TRY':
      default: return '₺';
    }
  };

  // Örnek Excel şablonu indir - Dışa aktar formatıyla birebir aynı
  const handleDownloadTemplate = () => {
    // Dışa aktar ile aynı format
    const templateData = [
      {
        'ID': '',
        'Barkod': '30180001',
        'SKU': '',
        'Ürün Adı': 'Örnek Gümüş Yüzük',
        'Metal Türü': 'Gümüş',
        'Kategori': 'Gümüş Yüzük',
        'Saflık/Ayar': '925',
        'Gramaj': 5.25,
        'İşçilikli Gram': '',
        'Osmangazi Stok': 10,
        'Yeşilsu Stok': 5,
        'Depo Stok': 3,
        'Toplam Stok': '',
        'Min. Stok': 2,
        'Maliyet Fiyatı': '',
        'Satış Fiyatı': 500,
        'Para Birimi': 'TRY',
        'İşçilik': 2.5,
        'Marka': 'Akik',
        'Tedarikçi': 'TDK-001',
        'Konum': '',
        'Açıklama': 'Örnek ürün açıklaması',
        'Notlar': '',
        'Aktif': 'Evet',
        'Eski Etiket': 0
      },
      {
        'ID': '',
        'Barkod': '04010001',
        'SKU': '',
        'Ürün Adı': 'Örnek Altın Bileklik',
        'Metal Türü': 'Altın',
        'Kategori': 'Altın Bileklik',
        'Saflık/Ayar': '14',
        'Gramaj': 8.50,
        'İşçilikli Gram': '',
        'Osmangazi Stok': '',
        'Yeşilsu Stok': '',
        'Depo Stok': '',
        'Toplam Stok': 3,
        'Min. Stok': 1,
        'Maliyet Fiyatı': '',
        'Satış Fiyatı': 15000,
        'Para Birimi': 'TRY',
        'İşçilik': 50,
        'Marka': '',
        'Tedarikçi': 'TDK-002',
        'Konum': '',
        'Açıklama': '',
        'Notlar': '',
        'Aktif': 'Evet',
        'Eski Etiket': 1
      },
      {
        'ID': '',
        'Barkod': '06010001',
        'SKU': '',
        'Ürün Adı': 'Örnek Zippo Çakmak',
        'Metal Türü': 'Zippo',
        'Kategori': 'Zippo Çakmak',
        'Saflık/Ayar': '',
        'Gramaj': '',
        'İşçilikli Gram': '',
        'Osmangazi Stok': '',
        'Yeşilsu Stok': 5,
        'Depo Stok': '',
        'Toplam Stok': '',
        'Min. Stok': 2,
        'Maliyet Fiyatı': 800,
        'Satış Fiyatı': 1500,
        'Para Birimi': 'TRY',
        'İşçilik': '',
        'Marka': 'Zippo',
        'Tedarikçi': '',
        'Konum': '',
        'Açıklama': '',
        'Notlar': '',
        'Aktif': 'Evet',
        'Eski Etiket': 0
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ürünler');

    // Sütun genişlikleri - dışa aktar ile aynı sıra
    worksheet['!cols'] = [
      { wch: 8 },   // ID
      { wch: 15 },  // Barkod
      { wch: 12 },  // SKU
      { wch: 25 },  // Ürün Adı
      { wch: 12 },  // Metal Türü
      { wch: 18 },  // Kategori
      { wch: 12 },  // Saflık/Ayar
      { wch: 10 },  // Gramaj
      { wch: 14 },  // İşçilikli Gram
      { wch: 14 },  // Osmangazi Stok
      { wch: 12 },  // Yeşilsu Stok
      { wch: 10 },  // Depo Stok
      { wch: 12 },  // Toplam Stok
      { wch: 10 },  // Min. Stok
      { wch: 14 },  // Maliyet Fiyatı
      { wch: 12 },  // Satış Fiyatı
      { wch: 12 },  // Para Birimi
      { wch: 10 },  // İşçilik
      { wch: 12 },  // Marka
      { wch: 15 },  // Tedarikçi
      { wch: 12 },  // Konum
      { wch: 25 },  // Açıklama
      { wch: 25 },  // Notlar
      { wch: 8 },   // Aktif
      { wch: 12 }   // Eski Etiket
    ];

    XLSX.writeFile(workbook, 'urun_ice_aktarma_sablonu.xlsx');
    toast.success('İçe aktarma şablonu indirildi!');
  };

  // Excel dosyasını oku
  const handleExcelUpload = (e) => {
    if (user?.role !== 'admin' && user?.permissions?.canAccessExcel !== true) {
      toast.error('Excel içe aktarma yetkiniz yok!');
      e.target.value = '';
      return;
    }
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const binaryStr = evt.target.result;
        const workbook = XLSX.read(binaryStr, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // İlk satır başlık - sütun indexlerini dinamik olarak bul
        const headerRow = jsonData[0] || [];
        // NOT: .toLowerCase() Türkçe "İ" harfini "i" + görünmez birleşik nokta işaretine
        // çeviriyor (Unicode varsayılan kural), bu da "İşçilik" gibi başlıkların hiç
        // eşleşmemesine yol açıyordu. "İ"yi önce düz "i"ye çevirip öyle küçültüyoruz
        // (tr-TR locale'ine geçmek "ID" gibi harfleri "ıd"ye çevirip başka bir bug açardı).
        const headers = headerRow.map(h => h?.toString().replace(/İ/g, 'i').toLowerCase().trim() || '');

        // Sütun başlıklarına göre index eşleştirmesi
        // Önce tam eşleşme denenir, bulunamazsa alt-dize eşleşmesine düşülür.
        const getColIndex = (possibleNames) => {
          for (const name of possibleNames) {
            const idx = headers.findIndex(h => h === name);
            if (idx !== -1) return idx;
          }
          for (const name of possibleNames) {
            const idx = headers.findIndex(h => h.includes(name));
            if (idx !== -1) return idx;
          }
          return -1;
        };

        const colMap = {
          id: getColIndex(['id']),
          barcode: getColIndex(['barkod', 'barcode']),
          sku: getColIndex(['sku']),
          name: getColIndex(['ürün adı', 'urun adi', 'name', 'ad']),
          metalType: getColIndex(['metal türü', 'metal turu', 'metal type', 'tür']),
          category: getColIndex(['kategori', 'category']),
          purity: getColIndex(['saflık', 'saflik', 'ayar', 'purity']),
          weight: getColIndex(['gramaj', 'weight', 'ağırlık']),
          stock: getColIndex(['stok', 'stock', 'toplam stok']),
          osmangaziStock: getColIndex(['osmangazi stok', 'osmangazi']),
          yesilsuStock: getColIndex(['yeşilsu stok', 'yesilsu stok', 'yeşilsu', 'yesilsu']),
          depoStock: getColIndex(['depo stok', 'depo']),
          minStock: getColIndex(['min. stok', 'min stok', 'minimum stok']),
          costPrice: getColIndex(['maliyet', 'cost']),
          sellPrice: getColIndex(['satış fiyatı', 'satis fiyati', 'fiyat', 'price']),
          currency: getColIndex(['para birimi', 'currency', 'birim']),
          laborCost: getColIndex(['işçilik', 'iscilik', 'labor']),
          brand: getColIndex(['marka', 'brand']),
          supplier: getColIndex(['tedarikçi', 'tedarikci', 'supplier']),
          location: getColIndex(['konum', 'location']),
          description: getColIndex(['açıklama', 'aciklama', 'description']),
          notes: getColIndex(['notlar', 'notes']),
          active: getColIndex(['aktif', 'active']),
          eskiEtiket: getColIndex(['eski etiket', 'eski_etiket', 'old label'])
        };

        // Özet ve rapor satırlarını filtrele (dışa aktarmadan gelen)
        const skipPatterns = ['özet', 'rapor', 'toplam', 'kategori', '═', 'genel', 'tarih'];

        const dataRows = jsonData.slice(1).filter(row => {
          if (!row || row.length === 0) return false;

          // ID veya Barkod veya Ürün Adı dolu olmalı
          const hasId = colMap.id >= 0 && row[colMap.id];
          const hasBarcode = colMap.barcode >= 0 && row[colMap.barcode];
          const hasName = colMap.name >= 0 && row[colMap.name];

          if (!hasId && !hasBarcode && !hasName) return false;

          // Özet/rapor satırlarını atla
          const firstCell = row[0]?.toString().toLowerCase() || '';
          const nameCell = (colMap.name >= 0 ? row[colMap.name]?.toString().toLowerCase() : '') || '';

          for (const pattern of skipPatterns) {
            if (firstCell.includes(pattern) || nameCell.includes(pattern)) {
              return false;
            }
          }

          // ID sayısal değilse (örn: "═══") atla
          if (hasId && isNaN(parseInt(row[colMap.id]))) {
            return false;
          }

          return true;
        });

        const products = dataRows.map((row, index) => {
          // Metal türü değerini normalize et
          const turValue = colMap.metalType >= 0 ? (row[colMap.metalType]?.toString().toLowerCase().trim() || '') : '';
          let metalType = null;
          if (turValue === 'altın' || turValue === 'altin' || turValue === 'gold') {
            metalType = 'gold';
          } else if (turValue === 'gümüş' || turValue === 'gumus' || turValue === 'silver') {
            metalType = 'silver';
          } else if (turValue === 'çelik' || turValue === 'celik' || turValue === 'steel') {
            metalType = 'steel';
          } else if (turValue === 'zippo') {
            metalType = 'zippo';
          } else if (turValue === 'marka' || turValue === 'markalar' || turValue === 'brands') {
            metalType = 'brands';
          } else if (turValue === 'aksesuar' || turValue === 'accessory') {
            metalType = 'aksesuar';
          } else if (turValue === 'sarf') {
            metalType = 'sarf';
          }

          // Aktif değerini normalize et
          const activeValue = colMap.active >= 0 ? (row[colMap.active]?.toString().toLowerCase().trim() || '') : '';
          let active = null;
          if (activeValue === 'evet' || activeValue === 'yes' || activeValue === '1' || activeValue === 'true') {
            active = true;
          } else if (activeValue === 'hayır' || activeValue === 'hayir' || activeValue === 'no' || activeValue === '0' || activeValue === 'false') {
            active = false;
          }

          // Değer alma yardımcı fonksiyonları
          const getStr = (colName) => colMap[colName] >= 0 ? (row[colMap[colName]]?.toString().trim() || null) : null;
          const getNum = (colName) => {
            if (colMap[colName] < 0) return null;
            const val = row[colMap[colName]];
            if (val === undefined || val === '' || val === null) return null;
            const num = parseFloat(val);
            return isNaN(num) ? null : num;
          };
          const getInt = (colName) => {
            if (colMap[colName] < 0) return null;
            const val = row[colMap[colName]];
            if (val === undefined || val === '' || val === null) return null;
            const num = parseInt(val);
            return isNaN(num) ? null : num;
          };

          const product = {
            rowNumber: index + 2,
            id: getInt('id'),
            barcode: getStr('barcode'),
            sku: getStr('sku'),
            name: getStr('name'),
            metal_type: metalType,
            category: getStr('category'),
            purity: getStr('purity'),
            weight: getNum('weight'),
            stock: getInt('stock'),
            osmangazi_stock: getInt('osmangaziStock'),
            yesilsu_stock: getInt('yesilsuStock'),
            depo_stock: getInt('depoStock'),
            min_stock: getInt('minStock'),
            cost_price: getNum('costPrice'),
            sell_price: getNum('sellPrice'),
            currency: getStr('currency'),
            labor_cost: getNum('laborCost'),
            brand: getStr('brand'),
            supplier: getStr('supplier'),
            location: getStr('location'),
            description: getStr('description'),
            notes: getStr('notes'),
            active: active,
            eski_etiket: getInt('eskiEtiket') === 1 ? 1 : 0
          };

          return product;
        });

        if (products.length === 0) {
          toast.warning('Excel dosyasında geçerli ürün bulunamadı. Sütun başlıklarını kontrol edin.');
          return;
        }

        setImportData(products);
        setShowImportModal(true);
      } catch (error) {
        toast.error('Excel dosyası okunamadı: ' + error.message);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = ''; // Input'u sıfırla
  };

  // Excel'den import işlemi
  const handleImport = async () => {
    if (importData.length === 0) {
      toast.warning('İçe aktarılacak ürün bulunamadı');
      return;
    }

    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/products/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: importData })
      });

      const data = await response.json();
      if (data.success) {
        // Sonuç mesajını oluştur
        let resultMsg = `${data.imported || 0} ürün eklendi, ${data.updated || 0} ürün güncellendi`;
        if (data.errors && data.errors.length > 0) {
          resultMsg += `, ${data.errors.length} hata`;
          // İlk 5 hatayı göster
          const errorSample = data.errors.slice(0, 5).join('\n');
          console.warn('İçe aktarma hataları:', data.errors);
          if (data.errors.length > 0) {
            toast.warning(`${data.errors.length} satırda hata: ${data.errors[0]}${data.errors.length > 1 ? ` (+${data.errors.length - 1} hata daha)` : ''}`, { autoClose: 12000 });
          }
        }
        toast.success(resultMsg);
        // Log ekle
        logService.log('products', 'import', `Excel'den ${importData.length} ürün içe aktarıldı`, {
          entity_type: 'product',
          details: {
            toplam_urun: importData.length,
            eklenen: data.imported || 0,
            guncellenen: data.updated || 0,
            hatali: data.errors?.length || 0,
            urunler: importData.slice(0, 10).map(p => ({
              ad: p.name,
              barkod: p.barcode || '-',
              kategori: p.category || '-'
            })),
            daha_fazla: importData.length > 10 ? `+${importData.length - 10} ürün daha` : null
          },
          status: 'success'
        });
        setShowImportModal(false);
        setImportData([]);
        fetchProducts(1, false, true);
      } else {
        toast.error('İçe aktarma başarısız: ' + data.error);
        // Hata detaylarını göster
        if (data.errors && data.errors.length > 0) {
          console.error('İçe aktarma hataları:', data.errors);
          toast.warning(`İlk hata: ${data.errors[0]}`, { autoClose: 12000 });
        }
        logService.log('products', 'import', `Excel import başarısız: ${data.error}`, {
          entity_type: 'product',
          details: { error: data.error, urun_sayisi: importData.length, hatalar: data.errors?.slice(0, 10) },
          status: 'error'
        });
      }
    } catch (error) {
      toast.error('İçe aktarma hatası: ' + error.message);
      logService.log('products', 'import', `Excel import hatası: ${error.message}`, {
        entity_type: 'product',
        details: { error: error.message, urun_sayisi: importData.length },
        status: 'error'
      });
    } finally {
      setImporting(false);
    }
  };

  // Export modal'ı aç
  const openExportModal = async () => {
    setExportStep(1);
    setExportMetalType(null);
    setExportCategories([]);
    setShowExcelModal(false);

    // Backend'den ürün ve kategori sayılarını çek, sonra modalı aç
    try {
      const response = await fetch(`${API_URL}/products/counts-by-type`);
      const data = await response.json();
      setExportCounts(data.counts || {});
      setExportCategoryCounts(data.categories || {});
    } catch (error) {
      console.error('Ürün sayıları alınamadı:', error);
    }
    setShowExportModal(true);
  };

  // Metal türüne göre kategorileri getir (backend verisinden)
  const getExportCategories = (metalType) => {
    const prefix = (metalType || 'all') + '|';
    const cats = Object.keys(exportCategoryCounts)
      .filter(key => key.startsWith(prefix))
      .map(key => key.substring(prefix.length))
      .filter(Boolean);
    return [...new Set(cats)].sort();
  };

  // Filtrelenmiş ürün sayısını hesapla
  const getFilteredProductCount = () => {
    let filtered = products;
    if (exportMetalType && exportMetalType !== 'all') {
      filtered = filtered.filter(p => p.metal_type === exportMetalType);
    }
    // Çoklu kategori filtresi
    if (exportCategories.length > 0 && !exportCategories.includes('all')) {
      filtered = filtered.filter(p => exportCategories.includes(p.category));
    }
    return filtered.length;
  };

  // Kategori seçimini toggle et
  const toggleExportCategory = (cat) => {
    if (cat === 'all') {
      // "Tümü" seçilirse diğerlerini temizle
      setExportCategories(['all']);
    } else {
      setExportCategories(prev => {
        // "all" varsa kaldır
        const withoutAll = prev.filter(c => c !== 'all');
        if (withoutAll.includes(cat)) {
          // Zaten varsa kaldır
          return withoutAll.filter(c => c !== cat);
        } else {
          // Yoksa ekle
          return [...withoutAll, cat];
        }
      });
    }
  };

  // Excel'e dışa aktar (filtreleme ile)
  const handleExcelExport = async (metalType = null, categories = []) => {
    try {
      setExporting(true);
      toast.info('Ürünler hazırlanıyor...');

      // Backend'den TÜM ürünleri çek (sayfalama olmadan)
      const params = new URLSearchParams({
        page: 1,
        limit: 100000, // Tüm ürünleri çekmek için yüksek limit
        metal_type: metalType && metalType !== 'all' ? metalType : ''
      });

      const response = await fetch(`${API_URL}/products?${params}`);
      const data = await response.json();

      if (!data.products || data.products.length === 0) {
        toast.warning('Dışa aktarılacak ürün bulunamadı');
        setExporting(false);
        return;
      }

      let filteredProducts = [...data.products];

      // Metal türü filtresi (backend'de yapılmış olabilir ama emin olmak için)
      if (metalType && metalType !== 'all') {
        filteredProducts = filteredProducts.filter(p => p.metal_type === metalType);
      }
      // Çoklu kategori filtresi
      if (categories.length > 0 && !categories.includes('all')) {
        filteredProducts = filteredProducts.filter(p => categories.includes(p.category));
      }

      // Kategoriye göre sırala (alfabetik)
      filteredProducts.sort((a, b) => {
        const catA = (a.category || '').toLowerCase();
        const catB = (b.category || '').toLowerCase();
        if (catA < catB) return -1;
        if (catA > catB) return 1;
        // Aynı kategori içinde ürün adına göre sırala
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'tr');
      });

      if (filteredProducts.length === 0) {
        toast.warning('Dışa aktarılacak ürün bulunamadı');
        setExporting(false);
        return;
      }

      // Metal türü çevirisi
      const metalTypeLabels = {
        'gold': 'Altın',
        'silver': 'Gümüş',
        'other': 'Diğer'
      };

      // Tarih formatlama
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      };

      const exportData = filteredProducts.map(p => ({
        'ID': p.id || '',
        'Barkod': p.barcode || '',
        'SKU': p.sku || '',
        'Ürün Adı': p.name || '',
        'Metal Türü': metalTypeLabels[p.metal_type] || p.metal_type || '',
        'Kategori': p.category || '',
        'Saflık/Ayar': (p.metal_type === 'gold')
          ? (p.gold_karat || '')
          : ((p.metal_type === 'silver' || (p.metal_type === 'aksesuar' && p.category === 'Gümüş Aksesuar'))
              ? (p.silver_purity || '')
              : ''),
        'Gramaj': p.weight || '',
        'İşçilikli Gram': calculateLaboredGram(p) || '',
        'Osmangazi Stok': p.store_stocks?.find(ss => ss.store_id === 2)?.stock ?? 0,
        'Yeşilsu Stok': p.store_stocks?.find(ss => ss.store_id === 3)?.stock ?? 0,
        'Depo Stok': p.store_stocks?.find(ss => ss.store_id === 4)?.stock ?? 0,
        'Toplam Stok': p.stock || 0,
        'Min. Stok': p.min_stock || 0,
        'Maliyet Fiyatı': p.cost_price || '',
        'Satış Fiyatı': p.sell_price || 0,
        'Para Birimi': p.currency || p.price_currency || 'TRY',
        'İşçilik': p.labor_cost || '',
        'Kat Sayı': p.profit_margin || '',
        'Marka': p.brand || '',
        'Tedarikçi': p.supplier || '',
        'Konum': p.location || '',
        'Açıklama': p.description || '',
        'Notlar': p.notes || '',
        'Aktif': p.active ? 'Evet' : 'Hayır',
        'Eski Etiket': p.eski_etiket ? 1 : 0,
        'Giriş Tarihi': formatDate(p.created_at),
        'Güncelleme Tarihi': formatDate(p.updated_at)
      }));

      // Kategori bazlı istatistikleri hesapla
      const categoryStats = {};
      let totalStock = 0;
      let totalWeight = 0;

      filteredProducts.forEach(p => {
        const cat = p.category || 'Kategorisiz';
        if (!categoryStats[cat]) {
          categoryStats[cat] = { count: 0, stock: 0, weight: 0 };
        }
        categoryStats[cat].count += 1;
        categoryStats[cat].stock += (parseInt(p.stock) || 0);
        categoryStats[cat].weight += (parseFloat(p.weight) || 0);
        totalStock += (parseInt(p.stock) || 0);
        totalWeight += (parseFloat(p.weight) || 0);
      });

      // Boş satır ekle
      exportData.push({});
      exportData.push({});

      // Özet başlığı
      exportData.push({
        'ID': '═══════════════════════════════════════════════════════════════',
        'Barkod': '',
        'SKU': '',
        'Ürün Adı': 'ÖZET RAPOR',
        'Metal Türü': '',
        'Kategori': '',
        'Saflık/Ayar': '',
        'Gramaj': '',
        'Toplam Stok': '',
        'Min. Stok': ''
      });

      exportData.push({});

      // Kategori bazlı özet
      exportData.push({
        'ID': '',
        'Barkod': '',
        'SKU': '',
        'Ürün Adı': 'KATEGORİ',
        'Metal Türü': '',
        'Kategori': 'ÜRÜN ÇEŞİDİ',
        'Saflık/Ayar': '',
        'Gramaj': 'TOPLAM AĞIRLIK',
        'Toplam Stok': 'TOPLAM STOK',
        'Min. Stok': ''
      });

      // Her kategori için satır ekle
      Object.keys(categoryStats).sort((a, b) => a.localeCompare(b, 'tr')).forEach(cat => {
        const stats = categoryStats[cat];
        exportData.push({
          'ID': '',
          'Barkod': '',
          'SKU': '',
          'Ürün Adı': cat,
          'Metal Türü': '',
          'Kategori': stats.count,
          'Saflık/Ayar': '',
          'Gramaj': stats.weight ? stats.weight.toFixed(2) + ' gr' : '-',
          'Toplam Stok': stats.stock,
          'Min. Stok': ''
        });
      });

      // Boş satır
      exportData.push({});

      // Genel toplam
      exportData.push({
        'ID': '',
        'Barkod': '',
        'SKU': '',
        'Ürün Adı': 'GENEL TOPLAM',
        'Metal Türü': '',
        'Kategori': filteredProducts.length + ' çeşit',
        'Saflık/Ayar': '',
        'Gramaj': totalWeight ? totalWeight.toFixed(2) + ' gr' : '-',
        'Toplam Stok': totalStock + ' adet',
        'Min. Stok': ''
      });

      // Rapor tarihi
      exportData.push({});
      exportData.push({
        'ID': '',
        'Barkod': '',
        'SKU': '',
        'Ürün Adı': 'Rapor Tarihi: ' + new Date().toLocaleString('tr-TR'),
        'Metal Türü': '',
        'Kategori': '',
        'Saflık/Ayar': '',
        'Gramaj': '',
        'Toplam Stok': '',
        'Min. Stok': ''
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ürünler');

      // Sütun genişliklerini ayarla
      ws['!cols'] = [
        { wch: 8 },   // ID
        { wch: 15 },  // Barkod
        { wch: 12 },  // SKU
        { wch: 35 },  // Ürün Adı
        { wch: 10 },  // Metal Türü
        { wch: 18 },  // Kategori
        { wch: 12 },  // Saflık/Ayar
        { wch: 10 },  // Gramaj
        { wch: 14 },  // İşçilikli Gram
        { wch: 14 },  // Osmangazi Stok
        { wch: 12 },  // Yeşilsu Stok
        { wch: 10 },  // Depo Stok
        { wch: 12 },  // Toplam Stok
        { wch: 10 },  // Min. Stok
        { wch: 14 },  // Maliyet Fiyatı
        { wch: 14 },  // Satış Fiyatı
        { wch: 10 },  // Para Birimi
        { wch: 10 },  // İşçilik
        { wch: 15 },  // Marka
        { wch: 18 },  // Tedarikçi
        { wch: 10 },  // Konum
        { wch: 30 },  // Açıklama
        { wch: 30 },  // Notlar
        { wch: 8 },   // Aktif
        { wch: 18 },  // Giriş Tarihi
        { wch: 18 }   // Güncelleme Tarihi
      ];

      // Dosya adını oluştur
      let fileNameParts = ['urunler'];
      if (metalType && metalType !== 'all') fileNameParts.push(metalType);
      if (categories.length > 0 && !categories.includes('all')) {
        if (categories.length <= 2) {
          fileNameParts.push(categories.join('_').replace(/\s+/g, '_'));
        } else {
          fileNameParts.push(`${categories.length}_kategori`);
        }
      }
      fileNameParts.push(new Date().toISOString().split('T')[0]);
      const fileName = `${fileNameParts.join('_')}.xlsx`;

      XLSX.writeFile(wb, fileName);

      toast.success(`${filteredProducts.length} ürün Excel'e aktarıldı`);
      logService.log('products', 'export', `${filteredProducts.length} ürün Excel'e aktarıldı`, {
        entity_type: 'product',
        details: {
          toplam_urun: filteredProducts.length,
          dosya: fileName,
          metal_turu: metalType || 'tümü',
          kategoriler: categories.includes('all') ? 'tümü' : categories.join(', ')
        },
        status: 'success'
      });
      setExporting(false);
      setShowExportModal(false);
      setShowExcelModal(false);
    } catch (error) {
      toast.error('Dışa aktarma hatası: ' + error.message);
      setExporting(false);
    }
  };

  // Fiyat değişikliği Excel dosyasını oku
  const handlePriceExcelUpload = (e) => {
    if (user?.role !== 'admin' && user?.permissions?.canAccessExcel !== true) {
      toast.error('Fiyat güncelleme yetkiniz yok!');
      e.target.value = '';
      return;
    }
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // İlk satır başlık olduğunu varsay
        // Sütunlar: Barkod, Yeni Fiyat, Para Birimi
        const updates = data.slice(1).filter(row => row[0] && row[1]).map(row => ({
          barcode: row[0]?.toString().trim(),
          newPrice: parseFloat(row[1]) || 0,
          currency: row[2]?.toString().trim().toUpperCase() || 'TRY'
        }));

        setPriceUpdateData(updates);
        setShowPriceUpdateModal(true);
        setShowExcelModal(false);
      } catch (error) {
        toast.error('Excel dosyası okunamadı: ' + error.message);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  // Fiyat değişikliklerini uygula
  const handlePriceUpdate = async () => {
    if (priceUpdateData.length === 0) {
      toast.warning('Güncellenecek fiyat bulunamadı');
      return;
    }

    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/products/bulk-price-update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: priceUpdateData })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`${data.updated} ürün fiyatı güncellendi`);
        logService.log('products', 'price_update', `Excel ile ${data.updated} ürün fiyatı güncellendi`, {
          entity_type: 'product',
          details: { guncellenen: data.updated, toplam: priceUpdateData.length },
          status: 'success'
        });
        setShowPriceUpdateModal(false);
        setPriceUpdateData([]);
        fetchProducts(1, false, true);
      } else {
        toast.error('Fiyat güncelleme başarısız: ' + data.error);
      }
    } catch (error) {
      toast.error('Fiyat güncelleme hatası: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  // Fiyat şablonu indir
  const downloadPriceTemplate = () => {
    const templateData = products.slice(0, 5).map(p => ({
      'Barkod': p.barcode || '',
      'Yeni Fiyat': p.sell_price || 0,
      'Para Birimi': p.currency || p.price_currency || 'TRY'
    }));

    // Boş örnek satırlar ekle
    if (templateData.length === 0) {
      templateData.push({ 'Barkod': 'ORNEK123', 'Yeni Fiyat': 100, 'Para Birimi': 'TRY' });
    }

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fiyat Güncelleme');

    ws['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 12 }];

    XLSX.writeFile(wb, 'fiyat_guncelleme_sablonu.xlsx');
    toast.info('Şablon indirildi. Barkod, Yeni Fiyat ve Para Birimi sütunlarını doldurun.');
  };

  // ===================== STOK ALIM FONKSİYONLARI =====================

  // Stok alım modunu aç
  const openStockIntakeMode = () => {
    setShowStockIntakeMode(true);
    setStockIntakeData({});
    setLastScannedProduct(null);
    setStockIntakeBarcode('');
    setTimeout(() => {
      if (stockIntakeBarcodeRef.current) {
        stockIntakeBarcodeRef.current.focus();
      }
    }, 100);
  };

  // Genel onay modal'ı gösterme fonksiyonu (window.confirm yerine)
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Stok alım modunu kapat
  const closeStockIntakeMode = () => {
    if (Object.keys(stockIntakeData).length > 0) {
      showConfirmation({
        title: 'Kaydedilmemiş Stok Alımı',
        message: `${Object.keys(stockIntakeData).length} ürün henüz kaydedilmedi.\nKaydetmeden çıkmak istediğinize emin misiniz?`,
        confirmText: 'Evet, Çık',
        cancelText: 'Hayır, Kal',
        type: 'warning',
        onConfirm: () => {
          setShowStockIntakeMode(false);
          setStockIntakeData({});
          setLastScannedProduct(null);
          setStockIntakeBarcode('');
        }
      });
      return;
    }
    setShowStockIntakeMode(false);
    setStockIntakeData({});
    setLastScannedProduct(null);
    setStockIntakeBarcode('');
  };

  // Stok düşüm modunu kapat
  const closeStockDeductionMode = () => {
    if (Object.keys(stockDeductionData).length > 0) {
      showConfirmation({
        title: 'Kaydedilmemiş Stok Düşümü',
        message: `${Object.keys(stockDeductionData).length} ürün henüz kaydedilmedi.\nKaydetmeden çıkmak istediğinize emin misiniz?`,
        confirmText: 'Evet, Çık',
        cancelText: 'Hayır, Kal',
        type: 'warning',
        onConfirm: () => {
          setShowStockDeductionMode(false);
          setStockDeductionData({});
          setLastDeductedProduct(null);
          setStockDeductionBarcode('');
          setGlobalDeductionReason('');
        }
      });
      return;
    }
    setShowStockDeductionMode(false);
    setStockDeductionData({});
    setLastDeductedProduct(null);
    setStockDeductionBarcode('');
    setGlobalDeductionReason('');
  };

  // ==================
  // TRANSFER FONKSİYONLARI
  // ==================

  // Transfer seçeneğini seç - Perakende Satış'taki transfer ekranına yönlendir
  const handleTransferOptionSelect = () => {
    setShowTransferOptionsModal(false);
    openRetailSalesTransfer('transfer');
  };

  // Transfer modalını kapat
  const closeTransferModal = () => {
    if (transferItems.length > 0) {
      showConfirmation({
        title: 'Kaydedilmemiş Transfer',
        message: 'Kaydedilmemiş transfer ürünleri var. Çıkmak istediğinize emin misiniz?',
        confirmText: 'Evet, Çık',
        cancelText: 'Hayır, Kal',
        type: 'warning',
        onConfirm: () => {
          setClosingModal('transfer');
          setTimeout(() => {
            setShowTransferModal(false);
            setClosingModal(null);
            setTransferItems([]);
            setTransferBarcode('');
            setTransferNotes('');
            setTransferTargetStore('');
            setTransferFromRequest(null);
            setLastScannedTransferProduct(null);
          }, 250);
        }
      });
      return;
    }
    setClosingModal('transfer');
    setTimeout(() => {
      setShowTransferModal(false);
      setClosingModal(null);
      setTransferItems([]);
      setTransferBarcode('');
      setTransferNotes('');
      setTransferTargetStore('');
      setTransferFromRequest(null);
      setLastScannedTransferProduct(null);
    }, 250);
  };

  const closeTransferRequestModal = () => {
    if (transferRequestItems.length > 0) {
      showConfirmation({
        title: 'Kaydedilmemiş Talep',
        message: 'Kaydedilmemiş transfer talebi ürünleri var. Çıkmak istediğinize emin misiniz?',
        confirmText: 'Evet, Çık',
        cancelText: 'Hayır, Kal',
        type: 'warning',
        onConfirm: () => {
          setClosingModal('transferRequest');
          setTimeout(() => {
            setShowTransferRequestModal(false);
            setClosingModal(null);
            setTransferRequestItems([]);
            setTransferRequestBarcode('');
            setTransferRequestNotes('');
            setLastScannedTransferProduct(null);
          }, 250);
        }
      });
      return;
    }
    setClosingModal('transferRequest');
    setTimeout(() => {
      setShowTransferRequestModal(false);
      setClosingModal(null);
      setTransferRequestItems([]);
      setTransferRequestBarcode('');
      setTransferRequestNotes('');
      setLastScannedTransferProduct(null);
    }, 250);
  };

  // Transfer teslim alma modalını kapat (temizle) — sayfa yenilenmiş gibi
  const closeReceiveTransferModal = () => {
    handleCloseModal('receiveTransfer', setShowReceiveTransferModal, () => {
      setSelectedTransferForReceive(null);
      setTransferDetailItems([]);
      setTransferBarcodeInput('');
      setQuickTransferBarcode('');
      setQuickTransferItems([]);
      setShowQuickTransferSummary(false);
      setQuickTransferSummaryData(null);
      setLastScannedTransferProduct(null);
    });
  };

  // Gönderilen transferler modalını kapat (temizle)
  const closeOutgoingTransfersModal = () => {
    setSelectedOutgoingTransfer(null);
    setEditingTransfer(false);
    setEditingTransferItems([]);
    handleCloseModal('outgoingTransfers', setShowOutgoingTransfersModal);
  };

  // Transfer geçmişi modalını kapat (temizle)
  const closeTransferHistoryModal = () => {
    setSelectedHistoryTransfer(null);
    handleCloseModal('transferHistory', setShowTransferHistoryModal);
  };

  // Transfer için barkod okut
  const handleTransferBarcode = async (e) => {
    if (e.key !== 'Enter' || !transferBarcode.trim()) return;

    const barcode = transferBarcode.trim();
    setTransferBarcode('');

    if (!selectedStore) {
      toast.error('Lütfen önce bir mağaza seçin');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/transfers/product/barcode/${barcode}?store_id=${selectedStore.id}`);

      if (response.ok) {
        const product = await response.json();

        if (!product.stock_quantity || product.stock_quantity <= 0) {
          toast.error('Bu ürün seçili mağazada stokta yok');
          playStockSound('error');
          return;
        }

        setLastScannedTransferProduct({ name: product.name, barcode: product.barcode, image: product.image });

        // Ürün zaten listede mi?
        const existingIndex = transferItems.findIndex(item => item.product_id === product.id);

        if (existingIndex >= 0) {
          // Miktar artır ve en üste taşı
          const newItems = [...transferItems];
          if (newItems[existingIndex].quantity < product.stock_quantity) {
            const [item] = newItems.splice(existingIndex, 1);
            item.quantity += 1;
            newItems.unshift(item);
            setTransferItems(newItems);
            playStockSound('success');
          } else {
            toast.warning('Stokta bu kadar ürün yok');
            playStockSound('error');
          }
        } else {
          // Yeni ürün ekle
          setTransferItems([{
            product_id: product.id,
            product_name: product.name,
            barcode: product.barcode,
            image: product.image,
            quantity: 1,
            stock_quantity: product.stock_quantity
          }, ...transferItems]);
          playStockSound('success');
        }
      } else {
        toast.error('Ürün bulunamadı');
        playStockSound('error');
      }
    } catch (error) {
      console.error('Barkod arama hatası:', error);
      toast.error('Ürün aranamadı');
    }
  };

  // Transfer ürün miktarını değiştir
  const updateTransferItemQuantity = (index, newQty) => {
    const newItems = [...transferItems];
    const maxQty = newItems[index].stock_quantity;

    if (newQty > maxQty) {
      toast.warning(`Maksimum ${maxQty} adet transfer edilebilir`);
      newQty = maxQty;
    }

    if (newQty < 1) {
      newItems.splice(index, 1);
    } else {
      newItems[index].quantity = newQty;
    }
    setTransferItems(newItems);
  };

  // Transfer ürününü kaldır
  const removeTransferItem = (index) => {
    const newItems = [...transferItems];
    newItems.splice(index, 1);
    setTransferItems(newItems);
  };

  // Transfer oluştur
  const createTransfer = async () => {
    if (!selectedStore) {
      toast.error('Lütfen önce bir mağaza seçin');
      return;
    }

    if (transferItems.length === 0) {
      toast.error('En az bir ürün ekleyin');
      return;
    }

    if (transferType === 'store' && !transferTargetStore) {
      toast.error('Hedef mağaza seçin');
      return;
    }

    if (!transferStaffId) {
      toast.error('Lütfen kasiyer seçin');
      return;
    }

    setSavingTransfer(true);

    try {
      const response = await fetch(`${API_URL}/transfers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_store_id: selectedStore.id,
          to_store_id: transferType === 'store' ? transferTargetStore : null,
          transfer_type: transferType,
          items: transferItems.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          notes: transferNotes,
          created_by: user?.id,
          created_by_name: transferStaffName
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Transfer oluşturuldu: ${result.transfer_code}`);

        // Talepten gelen transfer ise, talebi tamamlandı olarak işaretle
        if (transferFromRequest) {
          try {
            await fetch(`${API_URL}/transfer-requests/${transferFromRequest.id}/complete`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: transferFromRequest.items,
                approved_by: user?.id,
                transfer_id: result.id,
                transfer_code: result.transfer_code
              })
            });
            // Gelen talep sayısını güncelle
            fetchIncomingRequestCount();
          } catch (err) {
            console.error('Talep güncelleme hatası:', err);
          }
          setTransferFromRequest(null);
        }

        setShowTransferModal(false);
        setTransferItems([]);
        setTransferBarcode('');
        setTransferNotes('');
        setTransferTargetStore('');
        // Listeyi tamamen yenile
        setProducts([]);
        setPage(1);
        pageRef.current = 1;
        hasMoreRef.current = true;
        loadingMoreRef.current = false;
        fetchProducts(1, false);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Transfer oluşturulamadı');
      }
    } catch (error) {
      console.error('Transfer hatası:', error);
      toast.error('Transfer oluşturulamadı');
    } finally {
      setSavingTransfer(false);
    }
  };

  // Transfer modallarının bu sayfadaki kopyaları yerine artık Perakende
  // Satış sayfasındaki tek/güncel tasarımı kullanıyoruz - buradan sadece
  // ilgili moda yönlendiriyoruz.
  const openRetailSalesTransfer = (mode) => {
    navigate('/retail-sales', { state: { openTransferMode: mode } });
  };

  // Stok alım seçeneğini seç
  const handleStockIntakeOptionSelect = async (type) => {
    setStockIntakeType(type);
    setShowStockIntakeOptionsModal(false);

    if (type === 'new') {
      // Normal stok alımı
      openStockIntakeMode();
    } else {
      // Transfer teslim al
      const storeId = selectedStore?.id || user?.store_id;
      if (!storeId) {
        toast.error('Lütfen önce bir mağaza seçin');
        return;
      }
      openRetailSalesTransfer('receive-transfer');
    }
  };

  // Bekleyen transferleri getir
  const fetchPendingTransfers = async () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;

    setLoadingTransfers(true);
    try {
      const response = await fetch(`${API_URL}/transfers/pending/${storeId}`);
      if (response.ok) {
        const data = await response.json();
        setPendingTransfers(data);
      }
    } catch (error) {
      console.error('Transfer getirme hatası:', error);
    } finally {
      setLoadingTransfers(false);
    }
  };

  // Transfer detayını göster
  const selectTransferForReceive = async (transfer) => {
    setSelectedTransferForReceive(transfer);
    setTransferBarcodeInput('');

    try {
      const response = await fetch(`${API_URL}/transfers/${transfer.id}`);
      if (response.ok) {
        const data = await response.json();
        // API'den gelen güncel transfer bilgisini kullan (düzenleme sonrası güncel quantity vs.)
        setSelectedTransferForReceive(data);
        // already_received: DB'den gelen daha önce teslim alınan miktar
        // received_quantity: bu seansta barkod ile okutulan yeni miktar (0'dan başlar)
        const itemsWithReceived = (data.items || []).map(item => ({
          ...item,
          already_received: item.received_quantity || 0,
          received_quantity: 0
        }));
        setTransferDetailItems(itemsWithReceived);
        // Barkod inputuna focus
        setTimeout(() => {
          transferBarcodeInputRef.current?.focus();
        }, 100);
      }
    } catch (error) {
      console.error('Transfer detay hatası:', error);
    }
  };

  // Transfer için barkod okutma
  const handleTransferBarcodeSubmit = (e) => {
    e.preventDefault();
    const barcode = transferBarcodeInput.trim();
    if (!barcode) return;

    // Barkoda göre ürün bul
    const itemIndex = transferDetailItems.findIndex(item => item.barcode === barcode);

    if (itemIndex === -1) {
      playStockSound('error');
      toast.error('Bu barkod transferde bulunamadı!');
      setTransferBarcodeInput('');
      return;
    }

    const item = transferDetailItems[itemIndex];
    const remaining = item.quantity - (item.already_received || 0);

    // Zaten tamamı teslim alındı mı kontrol et
    if (item.received_quantity >= remaining) {
      playStockSound('error');
      toast.warning(`Bu ürünün tamamı zaten teslim alındı (${(item.already_received || 0) + item.received_quantity}/${item.quantity})`);
      setTransferBarcodeInput('');
      return;
    }

    // Teslim alınan miktarı artır ve en üste taşı
    const updatedItems = [...transferDetailItems];
    const [updatedItem] = updatedItems.splice(itemIndex, 1);
    updatedItem.received_quantity += 1;
    updatedItems.unshift(updatedItem);
    setTransferDetailItems(updatedItems);

    playStockSound('success');
    const totalReceived = (item.already_received || 0) + updatedItem.received_quantity;
    toast.success(`${item.product_name} teslim alındı (${totalReceived}/${item.quantity})`);
    setTransferBarcodeInput('');
  };

  // quickTransferItems ref'ini güncel tut
  useEffect(() => {
    quickTransferItemsRef.current = quickTransferItems;
  }, [quickTransferItems]);

  // stockIntakeData ref'ini güncel tut
  useEffect(() => {
    stockIntakeDataRef.current = stockIntakeData;
  }, [stockIntakeData]);

  // Hızlı barkod ile transfer ürünü ara ve listeye ekle (henüz teslim almadan)
  const handleQuickTransferBarcode = async (e) => {
    if (e.key !== 'Enter' || !quickTransferBarcode.trim()) return;

    const barcode = quickTransferBarcode.trim();
    setQuickTransferBarcode('');

    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) {
      toast.error('Mağaza seçilmedi');
      return;
    }

    try {
      // Bekleyen transferlerde bu barkodu ara
      const response = await fetch(`${API_URL}/transfers/find-by-barcode?barcode=${barcode}&store_id=${storeId}`);
      const data = await response.json();

      if (response.ok && data.found) {
        const key = `${data.transfer.id}-${data.product.id}`;
        // Ref kullanarak güncel listeyi kontrol et (closure sorunu için)
        const existingItem = quickTransferItemsRef.current.find(item => item.key === key);

        if (existingItem) {
          // Ürün zaten listede - uyarı modalı göster
          playStockSound('warning');
          setDuplicateBarcodeData({
            key,
            transfer: data.transfer,
            product: data.product,
            expectedQty: data.expectedQty,
            currentQty: existingItem.scannedQty,
            alreadyReceived: data.alreadyReceived
          });
          setShowDuplicateBarcodeModal(true);
        } else {
          // Yeni ürün ekle
          playStockSound('success');
          toast.success(`${data.product.name} eklendi - ${data.transfer.code}`);
          setQuickTransferItems(prev => [...prev, {
            key,
            transfer: data.transfer,
            product: data.product,
            expectedQty: data.expectedQty,
            scannedQty: 1,
            alreadyReceived: data.alreadyReceived
          }]);
        }
      } else {
        playStockSound('error');
        toast.error(data.error || 'Ürün bekleyen transferlerde bulunamadı');
      }
    } catch (error) {
      console.error('Hızlı barkod arama hatası:', error);
      playStockSound('error');
      toast.error('Arama başarısız');
    }

    setTimeout(() => {
      quickTransferBarcodeRef.current?.focus();
    }, 100);
  };

  // Hızlı teslim için özet hazırla
  const prepareQuickTransferSummary = async () => {
    console.log('prepareQuickTransferSummary called, items:', quickTransferItems);

    if (quickTransferItems.length === 0) {
      toast.warning('Önce ürün okutun!');
      return;
    }

    toast.info('Özet hazırlanıyor...');

    const storeId = selectedStore?.id || user?.store_id;

    // Transferlere göre grupla
    const transferGroups = {};
    for (const item of quickTransferItems) {
      const tid = item.transfer.id;
      if (!transferGroups[tid]) {
        transferGroups[tid] = {
          transfer: item.transfer,
          scannedItems: [],
          missingItems: []
        };
      }
      transferGroups[tid].scannedItems.push(item);
    }

    console.log('Transfer groups:', transferGroups);

    // Her transfer için eksik ürünleri bul
    try {
      for (const tid of Object.keys(transferGroups)) {
        console.log('Fetching transfer:', tid);
        const response = await fetch(`${API_URL}/transfers/${tid}`);
        if (response.ok) {
          const transferData = await response.json();
          console.log('Transfer data:', transferData);
          const scannedProductIds = transferGroups[tid].scannedItems.map(i => i.product.id);

          // Henüz okutulmamış ürünleri bul
          const missing = (transferData.items || []).filter(item => {
            const remaining = item.quantity - (item.received_quantity || 0);
            return remaining > 0 && !scannedProductIds.includes(item.product_id);
          }).map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            barcode: item.barcode,
            image: item.image,
            expectedQty: item.quantity - (item.received_quantity || 0)
          }));

          transferGroups[tid].missingItems = missing;
        } else {
          console.error('Transfer fetch failed:', response.status);
        }
      }
    } catch (error) {
      console.error('Transfer detay hatası:', error);
      toast.error('Özet hazırlanamadı');
      return;
    }

    const summaryData = Object.values(transferGroups);
    console.log('Summary data:', summaryData);

    setQuickTransferSummaryData(summaryData);
    setShowQuickTransferSummary(true);
    console.log('Modal should be visible now');
  };

  // Toplu teslim al - kasiyer seçim modalını göster
  const confirmQuickTransfer = async () => {
    if (!quickTransferItems.length) return;

    setReceiveStaffId('');
    setReceiveStaffName('');
    setPendingReceiveAction('bulk');
    setShowReceiveStaffModal(true);
  };

  // Toplu teslim al - asıl işlem (kasiyer seçildikten sonra)
  const doConfirmQuickTransfer = async () => {
    setSavingQuickTransfer(true);
    const storeId = selectedStore?.id || user?.store_id;

    try {
      const response = await fetch(`${API_URL}/transfers/bulk-receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: storeId,
          received_by: user?.id,
          received_by_name: receiveStaffName,
          items: quickTransferItems.map(item => ({
            transfer_id: item.transfer.id,
            product_id: item.product.id,
            quantity: item.scannedQty
          }))
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`${data.totalReceived} ürün teslim alındı!`);

        // Temizle
        setQuickTransferItems([]);
        setShowQuickTransferSummary(false);
        setQuickTransferSummaryData(null);

        // Transfer listesini yenile
        await fetchPendingTransfers();
        fetchProducts(1, false, true);
      } else {
        toast.error(data.error || 'Teslim alma başarısız');
      }
    } catch (error) {
      console.error('Toplu teslim hatası:', error);
      toast.error('Teslim alma başarısız');
    }

    setSavingQuickTransfer(false);
  };

  // Hızlı teslim listesinden ürün çıkar
  const removeQuickTransferItem = (key) => {
    setQuickTransferItems(prev => prev.filter(item => item.key !== key));
  };

  // Hızlı teslim ürün miktarını değiştir
  const updateQuickTransferItemQty = (key, newQty) => {
    setQuickTransferItems(prev => prev.map(item => {
      if (item.key === key) {
        const maxQty = item.expectedQty;
        const qty = Math.max(1, Math.min(newQty, maxQty));
        return { ...item, scannedQty: qty };
      }
      return item;
    }));
  };

  // Transfer teslim al
  const receiveTransfer = async () => {
    if (!selectedTransferForReceive) return;

    // En az bir ürün teslim alınmış mı kontrol et
    const totalReceived = transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
    if (totalReceived === 0) {
      toast.warning('Lütfen önce barkod okutarak ürünleri teslim alın!');
      transferBarcodeInputRef.current?.focus();
      return;
    }

    // Kasiyer seçim modalını göster
    setReceiveStaffId('');
    setReceiveStaffName('');
    setPendingReceiveAction('single');
    setShowReceiveStaffModal(true);
  };

  // Teslim alma - asıl işlem (kasiyer seçildikten sonra)
  const performReceive = async () => {
    const totalReceived = transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
    try {
      const response = await fetch(`${API_URL}/transfers/${selectedTransferForReceive.id}/receive`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          received_by: user?.id,
          received_by_name: receiveStaffName,
          items: transferDetailItems.map(item => ({
            product_id: item.product_id,
            received_quantity: item.received_quantity || 0
          }))
        })
      });

      if (response.ok) {
        const data = await response.json();

        if (data.hasNegativeStock && data.negativeStockItems?.length > 0) {
          const itemsList = data.negativeStockItems.map(item =>
            `• ${item.product_name} (${item.barcode || 'Barkod yok'})\n  Önceki stok: ${item.stock_before}, Şu anki stok: ${item.stock_after}`
          ).join('\n');

          toast.warning(
            <div>
              <strong>Dikkat: Müşteriye Satılmış Ürünler!</strong>
              <br /><br />
              Aşağıdaki ürünler daha önce müşteriye satılmış ve stok eksi durumdaydı:
              <br /><br />
              <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>{itemsList}</pre>
              <br />
              Bu ürünler müşteriye teslim edilmeyi bekliyor olabilir.
            </div>,
            {
              autoClose: false,
              closeOnClick: false,
              draggable: false,
              style: { width: '400px' }
            }
          );
        }

        if (data.allFullyReceived) {
          toast.success(`Transfer teslim alındı! (${totalReceived} ürün)`);
        } else {
          toast.success(`Kısmi teslim alındı (${totalReceived} ürün). Kalan ürünler bekliyor.`);
        }
        setSelectedTransferForReceive(null);
        setTransferDetailItems([]);
        setTransferBarcodeInput('');
        await fetchPendingTransfers();

        // Sadece tamamı alındıysa ve başka transfer yoksa modalı kapat
        if (data.allFullyReceived && pendingTransfers.length <= 1) {
          setShowReceiveTransferModal(false);
        }

        setProducts([]);
        setPage(1);
        pageRef.current = 1;
        hasMoreRef.current = true;
        loadingMoreRef.current = false;
        fetchProducts(1, false);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Teslim alma başarısız');
      }
    } catch (error) {
      console.error('Teslim alma hatası:', error);
      toast.error('Teslim alma başarısız');
    }
  };

  // Kasiyer seçildikten sonra teslim al
  const handleReceiveStaffConfirm = () => {
    if (!receiveStaffId) {
      toast.error('Lütfen teslim alan kasiyer seçin');
      return;
    }
    setShowReceiveStaffModal(false);

    if (pendingReceiveAction === 'bulk') {
      doConfirmQuickTransfer();
    } else {
      const totalReceived = transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
      const totalExpected = transferDetailItems.reduce((sum, item) => sum + item.quantity, 0);
      if (totalReceived < totalExpected) {
        showConfirmation({
          title: 'Eksik Teslim Alma',
          message: `Dikkat: ${totalExpected} üründen ${totalReceived} tanesi teslim alındı.\n\nEksik teslim almak istediğinize emin misiniz?`,
          confirmText: 'Evet, Teslim Al',
          cancelText: 'İptal',
          type: 'warning',
          onConfirm: performReceive
        });
      } else {
        performReceive();
      }
    }
  };

  // ===== TRANSFER TALEBİ FONKSİYONLARI =====

  // Gelen talep sayısını getir
  const fetchIncomingRequestCount = async () => {
    if (!selectedStore?.id) return;
    try {
      const response = await fetch(`${API_URL}/transfer-requests/count/${selectedStore.id}`);
      const data = await response.json();
      setIncomingRequestCount(data.count || 0);
    } catch (error) {
      console.error('Talep sayısı hatası:', error);
    }
  };

  // Transfer talebi modalını aç - Perakende Satış'taki ekrana yönlendir
  const openTransferRequestModal = () => {
    setShowTransferOptionsModal(false);
    openRetailSalesTransfer('transfer-request');
  };

  // Transfer talebi için barkod okut
  const handleTransferRequestBarcode = async (e) => {
    if (e.key !== 'Enter' || !transferRequestBarcode.trim()) return;

    const barcode = transferRequestBarcode.trim();
    setTransferRequestBarcode('');

    try {
      // Ürünü bul (herhangi bir mağazadan)
      const response = await fetch(`${API_URL}/products/barcode/${barcode}`);
      if (!response.ok) {
        playStockSound('error');
        toast.warning('Ürün bulunamadı!');
        return;
      }

      const product = await response.json();

      setLastScannedTransferProduct({ name: product.name, barcode: product.barcode, image: product.image });

      // Zaten listede var mı?
      const existingIndex = transferRequestItems.findIndex(item => item.product_id === product.id);

      if (existingIndex >= 0) {
        const newItems = [...transferRequestItems];
        newItems[existingIndex].quantity += 1;
        setTransferRequestItems(newItems);
        playStockSound('success');
        toast.info(`${product.name} miktarı artırıldı`);
      } else {
        setTransferRequestItems([...transferRequestItems, {
          product_id: product.id,
          name: product.name,
          barcode: product.barcode,
          sku: product.sku,
          image: product.image,
          quantity: 1
        }]);
        playStockSound('success');
        toast.success(`${product.name} eklendi`);
      }
    } catch (error) {
      console.error('Barkod okuma hatası:', error);
      playStockSound('error');
      toast.error('Barkod okuma hatası');
    }
  };

  // Transfer talebi ürün miktarını değiştir
  const updateTransferRequestItemQuantity = (index, newQty) => {
    const newItems = [...transferRequestItems];
    if (newQty <= 0) {
      newItems.splice(index, 1);
    } else {
      newItems[index].quantity = newQty;
    }
    setTransferRequestItems(newItems);
  };

  // Transfer talebi ürününü kaldır
  const removeTransferRequestItem = (index) => {
    const newItems = [...transferRequestItems];
    newItems.splice(index, 1);
    setTransferRequestItems(newItems);
  };

  // Transfer talebi oluştur
  const createTransferRequest = async () => {
    if (!selectedStore?.id) {
      toast.warning('Lütfen mağaza seçin!');
      return;
    }

    if (transferRequestItems.length === 0) {
      toast.warning('Lütfen en az bir ürün ekleyin!');
      return;
    }

    if (!transferRequestFromStore) {
      toast.warning('Lütfen talep edeceğiniz mağazayı seçin!');
      return;
    }

    setSavingTransferRequest(true);

    try {
      const response = await fetch(`${API_URL}/transfer-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_store_id: selectedStore.id,
          to_store_id: transferRequestFromStore,
          items: transferRequestItems.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          notes: transferRequestNotes,
          created_by: user?.id,
          staff_id: user?.id,
          staff_name: user?.name
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Transfer talebi oluşturuldu: ${result.request_code}`);
        setShowTransferRequestModal(false);
        setTransferRequestItems([]);
        setTransferRequestBarcode('');
        setTransferRequestNotes('');
        setTransferRequestFromStore('');
        setLastScannedTransferProduct(null);
      } else {
        toast.error(result.error || 'Talep oluşturulamadı');
      }
    } catch (error) {
      console.error('Talep oluşturma hatası:', error);
      toast.error('Talep oluşturulamadı');
    } finally {
      setSavingTransferRequest(false);
    }
  };

  // Gelen talepleri getir
  const fetchIncomingRequests = async () => {
    if (!selectedStore?.id) return;

    setLoadingRequests(true);
    try {
      const response = await fetch(`${API_URL}/transfer-requests/incoming/${selectedStore.id}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setIncomingRequests(data);
      }
    } catch (error) {
      console.error('Gelen talepler hatası:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Gelen talepler modalını aç
  const openIncomingRequestsModal = () => {
    setShowTransferOptionsModal(false);
    openRetailSalesTransfer('incoming-requests');
  };

  // Gönderilen transferleri getir
  // Transfer staff listesini getir
  const fetchTransferStaff = async () => {
    try {
      const storeIdParam = user?.role === 'cashier' ? user?.store_id : selectedStore?.id;
      const response = await fetch(`${API_URL}/sales-staff${storeIdParam ? `?store_id=${storeIdParam}` : ''}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setTransferStaffList(data);
      }
    } catch (error) {
      console.error('Kasiyer listesi yükleme hatası:', error);
    }
  };

  // Stok Hareketleri fetch
  const fetchStockMovements = async (pageNum = 1) => {
    const storeId = stockMovementsStoreId || selectedStore?.id;
    if (!storeId) return;
    setStockMovementsLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum,
        limit: 50,
        include_sales: stockMovementsShowSales ? '1' : '0'
      });
      if (stockMovementsDateFrom) params.append('date_from', stockMovementsDateFrom);
      if (stockMovementsDateTo) params.append('date_to', stockMovementsDateTo);
      if (stockMovementsSearch.trim()) params.append('search', stockMovementsSearch.trim());

      const response = await fetch(`${API_URL}/stock-movements/store/${storeId}?${params}`);
      const data = await response.json();
      if (data.success) {
        setStockMovements(data.movements);
        setStockMovementsPage(data.pagination.page);
        setStockMovementsTotalPages(data.pagination.totalPages);
        setStockMovementsTotalCount(data.pagination.total);
      }
    } catch (error) {
      console.error('Stok hareketleri hatası:', error);
      toast.error('Stok hareketleri yüklenemedi');
    } finally {
      setStockMovementsLoading(false);
    }
  };

  const openStockMovementsModal = () => {
    setStockMovements([]);
    setStockMovementsPage(1);
    setStockMovementsTotalPages(1);
    setStockMovementsTotalCount(0);
    setStockMovementsShowSales(false);
    setStockMovementsSearch('');
    setStockMovementsDateFrom('');
    setStockMovementsDateTo('');
    // Admin ise varsayılan olarak Osmangazi (2), değilse mevcut mağaza
    const defaultStoreId = user?.role === 'admin' ? (selectedStore?.id || 2) : (selectedStore?.id || null);
    setStockMovementsStoreId(defaultStoreId);
    setShowStockMovementsModal(true);
  };

  // Stok hareketleri auto-fetch
  useEffect(() => {
    if (showStockMovementsModal && (stockMovementsStoreId || selectedStore?.id)) {
      fetchStockMovements(stockMovementsPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStockMovementsModal, stockMovementsPage, stockMovementsShowSales, stockMovementsDateFrom, stockMovementsDateTo, stockMovementsStoreId]);

  const handleDeleteStockMovement = (movement) => {
    showConfirmation({
      title: 'Stok Hareketi Silme',
      message: `"${movement.product_name}" ürünü için ${movement.quantity > 0 ? '+' : ''}${movement.quantity} adetlik stok hareketini silmek istediğinize emin misiniz?\n\nStok otomatik olarak geri alınacak.`,
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/stock-movements/${movement.id}`, { method: 'DELETE' });
          const data = await response.json();
          if (data.success) {
            toast.success('Stok hareketi silindi');
            fetchStockMovements(stockMovementsPage);
          } else {
            toast.error(data.error || 'Silinemedi');
          }
        } catch (error) {
          console.error('Stok hareketi silme hatası:', error);
          toast.error('Stok hareketi silinemedi');
        }
      }
    });
  };

  const handleEditStockMovement = (movement) => {
    setEditingMovement(movement);
    setEditMovementQuantity(String(movement.quantity));
  };

  const handleSaveEditMovement = async () => {
    if (!editingMovement) return;
    const newQty = parseInt(editMovementQuantity);
    if (isNaN(newQty)) {
      toast.error('Geçerli bir miktar girin');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/stock-movements/${editingMovement.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: newQty })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Stok hareketi güncellendi');
        setEditingMovement(null);
        fetchStockMovements(stockMovementsPage);
      } else {
        toast.error(data.error || 'Güncellenemedi');
      }
    } catch (error) {
      console.error('Stok hareketi düzenleme hatası:', error);
      toast.error('Stok hareketi güncellenemedi');
    }
  };

  const fetchOutgoingTransfers = async () => {
    if (!selectedStore?.id) return;

    setLoadingOutgoingTransfers(true);
    try {
      const response = await fetch(`${API_URL}/transfers/outgoing/${selectedStore.id}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setOutgoingTransfers(data);
      }
    } catch (error) {
      console.error('Gönderilen transferler hatası:', error);
    } finally {
      setLoadingOutgoingTransfers(false);
    }
  };

  // Gönderilen transferler modalını aç
  const openOutgoingTransfersModal = () => {
    openRetailSalesTransfer('sent-transfers');
  };

  // Gönderilen talepleri getir
  const fetchOutgoingRequests = async () => {
    if (!selectedStore?.id) return;

    setLoadingOutgoingRequests(true);
    try {
      const response = await fetch(`${API_URL}/transfer-requests/outgoing/${selectedStore.id}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setOutgoingRequests(data);
      }
    } catch (error) {
      console.error('Gönderilen talepler hatası:', error);
    } finally {
      setLoadingOutgoingRequests(false);
    }
  };

  // Gönderilen talepler modalını aç
  const openOutgoingRequestsModal = () => {
    openRetailSalesTransfer('sent-requests');
  };

  // Transfer iptal et
  const cancelTransfer = (transferId) => {
    showConfirmation({
      title: 'Transfer İptali',
      message: 'Bu transferi iptal etmek istediğinize emin misiniz? Stoklar geri yüklenecektir.',
      confirmText: 'İptal Et',
      cancelText: 'Vazgeç',
      type: 'warning',
      onConfirm: async () => {
        setCancellingTransfer(true);
        try {
          const response = await fetch(`${API_URL}/transfers/${transferId}/cancel`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' }
          });
          if (response.ok) {
            toast.success('Transfer iptal edildi');
            await fetchOutgoingTransfers();
          } else {
            const data = await response.json();
            toast.error(data.error || 'Transfer iptal edilemedi');
          }
        } catch (error) {
          console.error('Transfer iptal hatası:', error);
          toast.error('Transfer iptal edilemedi');
        } finally {
          setCancellingTransfer(false);
        }
      }
    });
  };

  // Transfer düzenleme başlat (stok bilgisi ile)
  const startEditingTransfer = async (transfer) => {
    try {
      const productIds = transfer.items.map(i => i.product_id);
      const response = await fetch(`${API_URL}/store-stocks/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_id: transfer.from_store_id, product_ids: productIds })
      });
      const stockMap = await response.json();
      const itemsWithStock = transfer.items.map(i => ({
        ...i,
        source_stock: stockMap[i.product_id] || 0,
        original_quantity: i.quantity
      }));
      setEditingTransferItems(itemsWithStock);
      setEditingTransfer(true);
    } catch (error) {
      console.error('Stok bilgisi alınamadı:', error);
      // Stok bilgisi alınamazsa yine de düzenlemeye izin ver ama stok göstermeden
      setEditingTransferItems(transfer.items.map(i => ({ ...i, source_stock: null, original_quantity: i.quantity })));
      setEditingTransfer(true);
    }
  };

  // Transfer düzenleme kaydet
  const updateTransfer = async (transferId) => {
    setSavingTransferEdit(true);
    try {
      const items = editingTransferItems.map(item => ({
        item_id: item.id,
        quantity: item.quantity
      }));

      const response = await fetch(`${API_URL}/transfers/${transferId}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Transfer güncellendi');
        setEditingTransfer(false);
        setEditingTransferItems([]);
        await fetchOutgoingTransfers();
        if (data.transfer) {
          setSelectedOutgoingTransfer(data.transfer);
        }
      } else {
        toast.error(data.error || 'Transfer güncellenemedi');
      }
    } catch (error) {
      console.error('Transfer güncelleme hatası:', error);
      toast.error('Transfer güncellenemedi');
    } finally {
      setSavingTransferEdit(false);
    }
  };

  // Transfer talebi iptal et
  const cancelTransferRequest = (requestId) => {
    showConfirmation({
      title: 'Transfer Talebi İptali',
      message: 'Bu transfer talebini iptal etmek istediğinize emin misiniz?',
      confirmText: 'İptal Et',
      cancelText: 'Vazgeç',
      type: 'warning',
      onConfirm: async () => {
        setCancellingRequest(true);
        try {
          const response = await fetch(`${API_URL}/transfer-requests/${requestId}/cancel`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' }
          });
          if (response.ok) {
            toast.success('Transfer talebi iptal edildi');
            await fetchOutgoingRequests();
          } else {
            const data = await response.json();
            toast.error(data.error || 'Talep iptal edilemedi');
          }
        } catch (error) {
          console.error('Transfer talebi iptal hatası:', error);
          toast.error('Talep iptal edilemedi');
        } finally {
          setCancellingRequest(false);
        }
      }
    });
  };

  // Transfer talebini güncelle
  const updateTransferRequest = async (requestId) => {
    setSavingRequest(true);
    try {
      const items = editingRequestItems
        .map(item => ({
          product_id: item.product_id,
          requested_quantity: parseInt(item.requested_quantity) || 0
        }));

      const response = await fetch(`${API_URL}/transfer-requests/${requestId}/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.cancelled ? 'Tüm ürünler kaldırıldı, talep iptal edildi' : 'Transfer talebi güncellendi');
        setEditingRequest(false);
        setEditingRequestItems([]);
        await fetchOutgoingRequests();
        if (data.cancelled) {
          setSelectedOutgoingRequest(null);
        } else {
          // Güncellenmiş detayı API'den yeniden yükle
          try {
            const detailRes = await fetch(`${API_URL}/transfer-requests/outgoing/${selectedStore?.id || user?.store_id}`);
            const allRequests = await detailRes.json();
            const updatedReq = allRequests.find(r => r.id === requestId);
            if (updatedReq) setSelectedOutgoingRequest(updatedReq);
          } catch (e) {
            console.error('Detay yenileme hatası:', e);
          }
        }
      } else {
        toast.error(data.error || 'Talep güncellenemedi');
      }
    } catch (error) {
      console.error('Transfer talebi güncelleme hatası:', error);
      toast.error('Talep güncellenemedi');
    } finally {
      setSavingRequest(false);
    }
  };

  // Transfer geçmişini getir
  const fetchTransferHistory = async () => {
    if (!selectedStore?.id) return;

    setLoadingTransferHistory(true);
    try {
      const response = await fetch(`${API_URL}/transfers/history/${selectedStore.id}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setTransferHistory(data);
      }
    } catch (error) {
      console.error('Transfer geçmişi hatası:', error);
    } finally {
      setLoadingTransferHistory(false);
    }
  };

  // Transfer geçmişi modalını aç
  const openTransferHistoryModal = () => {
    openRetailSalesTransfer('history');
  };

  // Talep detayını göster
  const selectRequestForProcess = async (request) => {
    setSelectedRequestForProcess(request);
    try {
      const response = await fetch(`${API_URL}/transfer-requests/${request.id}`);
      const data = await response.json();
      if (data.items) {
        // Her ürün için onaylanan miktarı 0 olarak ayarla (barkod okutarak artırılacak)
        setRequestDetailItems(data.items.map(item => ({
          ...item,
          approved_quantity: 0
        })));
        setTimeout(() => requestApprovalBarcodeRef.current?.focus(), 100);
      }
    } catch (error) {
      console.error('Talep detay hatası:', error);
    }
  };

  // Talep ürün miktarını güncelle
  const updateRequestItemApprovedQuantity = (index, newQty) => {
    const newItems = [...requestDetailItems];
    const item = newItems[index];
    const maxQty = Math.min(item.requested_quantity, item.available_stock || 0);
    if (newQty < 0) newQty = 0;
    if (newQty > maxQty) {
      if (maxQty < item.requested_quantity) {
        toast.warning(`Stok yetersiz! Mevcut stok: ${item.available_stock || 0}`);
      }
      newQty = maxQty;
    }
    item.approved_quantity = newQty;
    setRequestDetailItems(newItems);
  };

  // Barkod okutarak talep onayı
  const handleRequestApprovalBarcode = (e) => {
    e.preventDefault();
    const barcode = requestApprovalBarcode.trim();
    if (!barcode) return;

    const itemIndex = requestDetailItems.findIndex(item => item.barcode === barcode);

    if (itemIndex === -1) {
      playStockSound('error');
      toast.error('Bu barkod talep listesinde bulunamadı!');
      setRequestApprovalBarcode('');
      return;
    }

    const item = requestDetailItems[itemIndex];

    if (item.approved_quantity >= item.requested_quantity) {
      playStockSound('error');
      toast.warning(`Bu ürünün tamamı zaten okutuldu (${item.requested_quantity}/${item.requested_quantity})`);
      setRequestApprovalBarcode('');
      return;
    }

    if (item.approved_quantity >= (item.available_stock || 0)) {
      playStockSound('error');
      toast.warning(`Stok yetersiz! Mevcut stok: ${item.available_stock || 0}`);
      setRequestApprovalBarcode('');
      return;
    }

    const updatedItems = [...requestDetailItems];
    updatedItems[itemIndex] = {
      ...item,
      approved_quantity: item.approved_quantity + 1
    };
    setRequestDetailItems(updatedItems);

    playStockSound('success');
    toast.success(`${item.product_name} eklendi (${updatedItems[itemIndex].approved_quantity}/${item.requested_quantity})`);
    setRequestApprovalBarcode('');
  };

  // Tümünü ekle (tüm ürünleri requested_quantity veya available_stock'a eşitle)
  const fillAllRequestApproval = () => {
    const updatedItems = requestDetailItems.map(item => ({
      ...item,
      approved_quantity: Math.min(item.requested_quantity, item.available_stock || 0)
    }));
    setRequestDetailItems(updatedItems);
    const totalApproved = updatedItems.reduce((sum, item) => sum + item.approved_quantity, 0);
    toast.success(`Tüm ürünler eklendi (${totalApproved} adet)`);
  };

  // Reddet modalını aç
  const openRejectModal = () => {
    if (!selectedRequestForProcess) return;
    setRejectReason('');
    setRejectStaffId(null);
    setRejectStaffName('');
    setShowRejectModal(true);
  };

  // Reddet onayla (modaldan çağrılır)
  const confirmReject = async () => {
    if (!selectedRequestForProcess) return;
    if (!rejectReason.trim()) {
      toast.warning('Lütfen reddetme sebebini yazın');
      return;
    }
    if (!rejectStaffId) {
      toast.warning('Lütfen kasiyer seçin');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/transfer-requests/${selectedRequestForProcess.id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rejection_reason: rejectReason.trim(),
          rejected_by: user?.id,
          rejected_staff_id: rejectStaffId,
          rejected_staff_name: rejectStaffName
        })
      });

      if (response.ok) {
        toast.success('Talep reddedildi');
        setShowRejectModal(false);
        setSelectedRequestForProcess(null);
        setRequestDetailItems([]);
        setRequestApprovalBarcode('');
        await fetchIncomingRequests();
        fetchIncomingRequestCount();

        if (incomingRequests.length <= 1) {
          setShowIncomingRequestsModal(false);
        }
      } else {
        toast.error('Reddetme başarısız');
      }
    } catch (error) {
      console.error('Reddetme hatası:', error);
      toast.error('Reddetme başarısız');
    }
  };

  // Talebi onayla ve transfer oluştur
  const startApproveRequest = () => {
    if (!selectedRequestForProcess) return;
    const approvedItems = requestDetailItems.filter(item => item.approved_quantity > 0);
    if (approvedItems.length === 0) {
      toast.warning('En az bir ürün okutun veya manuel ekleyin!');
      return;
    }
    setShowApproveStaffModal(true);
  };

  const approveRequest = async (staffId, staffName) => {
    if (!selectedRequestForProcess) return;

    const totalApproved = requestDetailItems.filter(item => item.approved_quantity > 0).reduce((sum, item) => sum + item.approved_quantity, 0);

    try {
      const response = await fetch(`${API_URL}/transfer-requests/${selectedRequestForProcess.id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved_by: user?.id,
          staff_id: staffId,
          staff_name: staffName,
          items: requestDetailItems.map(item => ({
            product_id: item.product_id,
            approved_quantity: item.approved_quantity || 0
          }))
        })
      });

      if (response.ok) {
        playStockSound('success');
        toast.success(`Transfer onaylandı ve gönderildi (${totalApproved} adet)`);
        setSelectedRequestForProcess(null);
        setRequestDetailItems([]);
        setRequestApprovalBarcode('');
        await fetchIncomingRequests();
        fetchIncomingRequestCount();
        fetchProducts(1, false, true);

        if (incomingRequests.length <= 1) {
          setShowIncomingRequestsModal(false);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.error || 'Onaylama başarısız');
      }
    } catch (error) {
      console.error('Onaylama hatası:', error);
      toast.error('Onaylama başarısız');
    }
  };

  // ===== TRANSFER TALEBİ FONKSİYONLARI SONU =====

  // ===== TADİLATA GÖNDER FONKSİYONLARI =====

  // Tamirat noktalarını getir
  const fetchRepairPointsList = async () => {
    try {
      const response = await fetch(`${API_URL}/repair-points`);
      const data = await response.json();
      setRepairPointsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Tamirat noktaları yükleme hatası:', error);
    }
  };

  // Tamirata gönder modalını aç
  const openSendToRepairModal = () => {
    setShowTransferOptionsModal(false);
    setShowSendToRepairModal(true);
    setRepairItems([]);
    setRepairBarcode('');
    setRepairCustomerName('');
    setRepairCustomerPhone('');
    setRepairNotes('');
    setRepairEstimatedPrice('');
    setRepairStaffName('');
    setRepairPointId('');
    setTimeout(() => {
      if (repairBarcodeRef.current) {
        repairBarcodeRef.current.focus();
      }
    }, 100);
  };

  // Tamirata gönder modalını kapat
  const closeSendToRepairModal = () => {
    if (repairPhotoCheckIntervalRef.current) {
      clearInterval(repairPhotoCheckIntervalRef.current);
    }
    setWaitingForRepairPhoto(false);
    setRepairPhotoRequestId(null);
    setSelectedRepairTerminal(null);
    if (repairItems.length > 0) {
      showConfirmation({
        title: 'Kaydedilmemiş Tamirat',
        message: 'Kaydedilmemiş tamirat ürünleri var. Çıkmak istediğinize emin misiniz?',
        confirmText: 'Evet, Çık',
        cancelText: 'Hayır, Kal',
        type: 'warning',
        onConfirm: () => {
          setClosingModal('sendToRepair');
          setTimeout(() => {
            setShowSendToRepairModal(false);
            setClosingModal(null);
            setRepairItems([]);
            setRepairBarcode('');
            setRepairCustomerName('');
            setRepairCustomerPhone('');
            setRepairNotes('');
            setRepairEstimatedPrice('');
            setRepairStaffName('');
            setRepairPointId('');
          }, 250);
        }
      });
      return;
    }
    setClosingModal('sendToRepair');
    setTimeout(() => {
      setShowSendToRepairModal(false);
      setClosingModal(null);
      setRepairItems([]);
      setRepairBarcode('');
      setRepairCustomerName('');
      setRepairCustomerPhone('');
      setRepairNotes('');
      setRepairEstimatedPrice('');
      setRepairStaffName('');
      setRepairPointId('');
    }, 250);
  };

  // Tamirat için barkod okut
  const handleRepairBarcodeAdd = async (e) => {
    if (e.key !== 'Enter' || !repairBarcode.trim()) return;

    const barcode = repairBarcode.trim();
    setRepairBarcode('');

    try {
      const response = await fetch(`${API_URL}/products/barcode/${barcode}`);

      if (response.ok) {
        const product = await response.json();

        // Seçili mağazadaki stok miktarını al (sadece mağaza stoğu, toplam stok değil)
        const currentStore = selectedStore?.id || user?.store_id;
        const storeStock = product.store_stocks?.find(s => s.store_id === currentStore);
        const availableStock = storeStock?.stock ?? 0;

        // Bu ürün zaten listede var mı kontrol et
        const existingIndex = repairItems.findIndex(item => item.product_id === product.id);

        if (existingIndex >= 0) {
          // Ürün zaten var, miktarı artır
          const currentQty = repairItems[existingIndex].quantity || 1;

          // Stok kontrolü
          if (currentQty >= availableStock) {
            playStockSound('error');
            toast.warning(`Bu üründen stokta ${availableStock} adet var, hepsi eklenmiş!`);
            return;
          }

          // Miktarı artır
          setRepairItems(prev => {
            const newItems = [...prev];
            newItems[existingIndex] = {
              ...newItems[existingIndex],
              quantity: currentQty + 1
            };
            return newItems;
          });
          const remaining = availableStock - currentQty - 1;
          toast.success(`${product.name} miktarı artırıldı: ${currentQty + 1} adet (Kalan stok: ${remaining})`);
        } else {
          // Stok kontrolü
          if (availableStock <= 0) {
            playStockSound('error');
            toast.warning(`Bu üründen stokta yok!`);
            return;
          }

          // Yeni ürün ekle
          const newItem = {
            product_id: product.id,
            barcode: product.barcode,
            name: product.name,
            category: product.category,
            image: product.image,
            item_type: product.metal_type || 'silver',
            item_description: product.name,
            problem_description: '',
            photos: [],
            quantity: 1,
            max_stock: availableStock
          };

          setRepairItems(prev => [...prev, newItem]);
          const remaining = availableStock - 1;
          toast.success(`${product.name} eklendi (Kalan stok: ${remaining})`);
        }
        playStockSound('success');
      } else {
        toast.error('Ürün bulunamadı!');
        playStockSound('error');
      }
    } catch (error) {
      console.error('Ürün arama hatası:', error);
      playStockSound('error');
      toast.error('Ürün aranırken hata oluştu');
    }
  };

  // Ürün miktarını artır
  const increaseRepairItemQty = (index) => {
    const item = repairItems[index];
    const currentQty = item.quantity || 1;
    const maxStock = item.max_stock || 999;

    if (currentQty >= maxStock) {
      toast.warning(`Bu üründen stokta ${maxStock} adet var, hepsi eklenmiş!`);
      return;
    }

    setRepairItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], quantity: currentQty + 1 };
      return newItems;
    });
  };

  // Ürün miktarını azalt veya listeden çıkar
  const decreaseRepairItemQty = (index) => {
    const item = repairItems[index];
    const currentQty = item.quantity || 1;

    if (currentQty <= 1) {
      // Miktarı 1 ise ürünü tamamen kaldır
      setRepairItems(prev => prev.filter((_, i) => i !== index));
    } else {
      // Miktarı azalt
      setRepairItems(prev => {
        const newItems = [...prev];
        newItems[index] = { ...newItems[index], quantity: currentQty - 1 };
        return newItems;
      });
    }
  };

  // Listeden ürün çıkar (tamamen)
  const removeRepairItem = (index) => {
    setRepairItems(prev => prev.filter((_, i) => i !== index));
  };

  // Ürün açıklamasını güncelle
  const updateRepairItemDescription = (index, field, value) => {
    setRepairItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [field]: value };
      return newItems;
    });
  };

  // Tamirat ürünü için fotoğraf yükle (Repairs.js'teki handlePhotoUpload ile aynı endpoint)
  const [uploadingRepairPhoto, setUploadingRepairPhoto] = useState(false);
  const handleRepairPhotoUpload = async (e, index) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingRepairPhoto(true);
    const fd = new FormData();
    for (let i = 0; i < files.length; i++) fd.append('photos', files[i]);
    try {
      const response = await fetch(`${API_URL}/repairs/upload-photos`, { method: 'POST', body: fd });
      const data = await response.json();
      if (data.success) {
        setRepairItems(prev => {
          const n = [...prev];
          n[index] = { ...n[index], photos: [...(n[index].photos || []), ...data.photos] };
          return n;
        });
      } else {
        toast.error('Fotoğraf yüklenemedi!');
      }
    } catch (err) {
      console.error('Tamirat fotoğraf yükleme hatası:', err);
      toast.error('Fotoğraf yüklenemedi!');
    } finally {
      setUploadingRepairPhoto(false);
      e.target.value = '';
    }
  };

  const removeRepairItemPhoto = (index, photoIndex) => {
    setRepairItems(prev => {
      const n = [...prev];
      n[index] = { ...n[index], photos: (n[index].photos || []).filter((_, i) => i !== photoIndex) };
      return n;
    });
  };

  // Terminal listesini getir (mağazaya bağlı) — Repairs.js'teki fetchTerminals ile aynı akış
  const fetchRepairTerminals = async () => {
    setLoadingTerminals(true);
    try {
      const storeId = selectedStore?.id || user?.store_id || 1;
      const response = await fetch(`${API_URL}/users/terminals/${storeId}`);
      const data = await response.json();
      setTerminals(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Terminal yükleme hatası:', error);
      setTerminals([]);
    } finally {
      setLoadingTerminals(false);
    }
  };

  // Terminal seçim modalını aç
  const openRepairTerminalModal = (index) => {
    setRepairTerminalItemIndex(index);
    fetchRepairTerminals();
    setShowRepairTerminalModal(true);
    setSelectedRepairTerminal(null);
  };

  // Terminale fotoğraf çekim isteği gönder
  const sendRepairPhotoRequest = async (terminal) => {
    try {
      const response = await fetch(`${API_URL}/terminal-photo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          terminal_user_id: terminal.id,
          request_type: 'repair_photo',
          description: repairItems[repairTerminalItemIndex]?.problem_description || repairItems[repairTerminalItemIndex]?.name || 'Tamirat ürünü'
        })
      });
      const data = await response.json();
      if (data.success) {
        setRepairPhotoRequestId(data.request_id);
        setSelectedRepairTerminal(terminal);
        setShowRepairTerminalModal(false);
        setWaitingForRepairPhoto(true);
        toast.info(`${terminal.name} terminaline fotoğraf isteği gönderildi!`);
        startRepairPhotoCheck(data.request_id);
      } else {
        toast.error(data.message || 'İstek gönderilemedi!');
      }
    } catch (error) {
      console.error('Fotoğraf isteği hatası:', error);
      toast.error('İstek gönderilirken hata oluştu!');
    }
  };

  // Fotoğraf kontrolünü başlat (her 2 saniyede bir yoklama, 2 dakika timeout)
  const startRepairPhotoCheck = (requestId) => {
    if (repairPhotoCheckIntervalRef.current) {
      clearInterval(repairPhotoCheckIntervalRef.current);
    }

    repairPhotoCheckIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/terminal-photo-requests/${requestId}`);
        const data = await response.json();

        if (data.status === 'completed' && data.photo_url) {
          clearInterval(repairPhotoCheckIntervalRef.current);
          setWaitingForRepairPhoto(false);
          setRepairPhotoRequestId(null);
          setRepairItems(prev => {
            const n = [...prev];
            if (n[repairTerminalItemIndex]) {
              n[repairTerminalItemIndex] = { ...n[repairTerminalItemIndex], photos: [...(n[repairTerminalItemIndex].photos || []), data.photo_url] };
            }
            return n;
          });
          toast.success('Fotoğraf terminalden alındı!');
        } else if (data.status === 'rejected') {
          clearInterval(repairPhotoCheckIntervalRef.current);
          setWaitingForRepairPhoto(false);
          setRepairPhotoRequestId(null);
          toast.warning('Terminal fotoğraf isteğini reddetti.');
        } else if (data.status === 'expired') {
          clearInterval(repairPhotoCheckIntervalRef.current);
          setWaitingForRepairPhoto(false);
          setRepairPhotoRequestId(null);
          toast.warning('Fotoğraf isteğinin süresi doldu.');
        }
      } catch (error) {
        console.error('Fotoğraf kontrol hatası:', error);
      }
    }, 2000);

    setTimeout(() => {
      if (repairPhotoCheckIntervalRef.current) {
        clearInterval(repairPhotoCheckIntervalRef.current);
        setWaitingForRepairPhoto(false);
        setRepairPhotoRequestId(null);
        toast.warning('Fotoğraf isteği zaman aşımına uğradı.');
      }
    }, 120000);
  };

  // Fotoğraf beklemeyi iptal et
  const cancelRepairPhotoWait = async () => {
    if (repairPhotoCheckIntervalRef.current) {
      clearInterval(repairPhotoCheckIntervalRef.current);
    }
    if (repairPhotoRequestId) {
      try {
        await fetch(`${API_URL}/terminal-photo-requests/${repairPhotoRequestId}/cancel`, {
          method: 'PUT'
        });
      } catch (error) {
        console.error('İptal hatası:', error);
      }
    }
    setWaitingForRepairPhoto(false);
    setRepairPhotoRequestId(null);
    setSelectedRepairTerminal(null);
    toast.info('Fotoğraf isteği iptal edildi.');
  };

  // Component unmount olduğunda interval'ı temizle
  useEffect(() => {
    return () => {
      if (repairPhotoCheckIntervalRef.current) {
        clearInterval(repairPhotoCheckIntervalRef.current);
      }
    };
  }, []);

  // Tamirat sonrası sistem yazıcıya sırayla Müşteri ve Tamirat fişini otomatik gönder
  // (Repairs.js sayfasındaki runSystemPrintSequence ile aynı akış)
  const printRepairReceiptsAfterCreate = async (repairId, storeId) => {
    try {
      const detailRes = await fetch(`${API_URL}/repairs/${repairId}/details`);
      const detailData = await detailRes.json();
      if (!detailData.success || !detailData.repair) return;
      const repair = detailData.repair;
      const repairCode = repair.repair_code || String(repair.id).padStart(6, '0');

      let qrCodeData = null;
      try {
        const qrResponse = await fetch(`${API_URL}/public/repair-qr/${repairCode}`);
        const qrResult = await qrResponse.json();
        if (qrResult.success) qrCodeData = qrResult.qrCode;
      } catch (e) {
        console.error('QR kod alınamadı:', e);
      }

      const customerHtml = buildCustomerReceiptHtml(repair, { storeId, qrCodeData });
      const internalHtml = buildRepairReceiptHtml(repair, { storeId });

      const sendOne = async (html, label) => {
        try {
          const result = await printSystemReceipt(html, storeId, 'repair');
          if (result.mode === 'local') toast.success(`${label} yazıcıya gönderildi`);
          else if (result.pending) toast.info(`${label} kuyruğa alındı (${result.targetName})`);
          else toast.success(`${label} ${result.targetName} cihazından yazdırıldı`);
        } catch (e) {
          toast.error(`${label} yazdırma hatası: ` + (e?.message || ''));
        }
      };

      await sendOne(customerHtml, 'Müşteri Fişi');
      await sendOne(internalHtml, 'Tamirat Fişi');
    } catch (e) {
      console.error('Otomatik fiş yazdırma hatası:', e);
      toast.error('Fiş hazırlanamadı: ' + (e?.message || ''));
    }
  };

  // Tamirat kaydını oluştur
  const handleSaveRepair = async () => {
    if (repairItems.length === 0) {
      toast.error('En az bir ürün eklemelisiniz!');
      return;
    }
    if (repairItems.some(item => !item.problem_description || !item.problem_description.trim())) {
      toast.error('Her ürün için ne yapılacağı açıklamasını girmelisiniz!');
      return;
    }
    const itemWithoutPhoto = repairItems.find(item => !item.photos || item.photos.length === 0);
    if (itemWithoutPhoto) {
      toast.error(`"${itemWithoutPhoto.name}" için en az 1 fotoğraf eklemelisiniz!`);
      return;
    }
    if (!repairStaffName) {
      toast.error('Kasiyer seçmelisiniz!');
      return;
    }
    if (!repairPointId) {
      toast.error('Tamirat noktası seçmelisiniz!');
      return;
    }

    setSavingRepair(true);
    try {
      const repairData = {
        customer_name: selectedStore?.name || 'Mağaza Ürünü',
        customer_phone: '',
        store_id: selectedStore?.id,
        items: repairItems.map(item => ({
          product_id: item.product_id,
          item_type: item.item_type,
          item_description: item.item_description,
          problem_description: item.problem_description || '',
          photos: item.photos || [],
          quantity: item.quantity || 1
        })),
        estimated_price: 0,
        deposit_amount: 0,
        notes: repairNotes.trim(),
        staff_name: repairStaffName,
        repair_point_id: repairPointId
      };

      const response = await fetch(`${API_URL}/repairs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(repairData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Tamirat kaydı oluşturuldu! Fiş No: ${result.repair_code}`);
        setShowSendToRepairModal(false);
        setRepairItems([]);
        setRepairBarcode('');
        setRepairNotes('');
        setRepairStaffName('');
        setRepairPointId('');
        // Ürün listesini yenile (stok güncellendi)
        fetchProducts(true);
        // Tamirat sayfasındaki gibi otomatik fiş yazdır (Müşteri + Tamirat fişi)
        if (result.id) {
          printRepairReceiptsAfterCreate(result.id, selectedStore?.id);
        }
      } else {
        const error = await response.json();
        toast.error(error.message || 'Tamirat kaydı oluşturulamadı');
      }
    } catch (error) {
      console.error('Tamirat kayıt hatası:', error);
      toast.error('Tamirat kaydı oluşturulurken hata oluştu');
    } finally {
      setSavingRepair(false);
    }
  };

  // ===== TADİLATA GÖNDER FONKSİYONLARI SONU =====

  // ===== DEPO STOK AL VE TRANSFER YAP FONKSİYONLARI =====
  const handleDepoBarcodeScan = async (e) => {
    if (e.key !== 'Enter' || !depoBarcode.trim()) return;

    const barcode = depoBarcode.trim();
    setDepoBarcode('');

    if (depoMode === 'stock_entry') {
      // Stok giriş modu: ürünü API'den bul ve ortaya ekle
      try {
        const response = await fetch(`${API_URL}/products/barcode/${barcode}`);
        if (response.ok) {
          const product = await response.json();
          setDepoCenterItems(prev => {
            const existing = prev[product.id];
            if (existing) {
              const { [product.id]: _, ...rest } = prev;
              return { ...rest, [product.id]: { ...existing, quantity: existing.quantity + 1 } };
            }
            return { ...prev, [product.id]: { product, quantity: 1 } };
          });
          playStockSound('success');
          toast.success(`${product.name} depoya eklendi`);
        } else {
          playStockSound('error');
          toast.error('Ürün bulunamadı!');
        }
      } catch (error) {
        console.error('Depo barkod hatası:', error);
        playStockSound('error');
        toast.error('Ürün aranırken hata oluştu');
      }
    } else if (depoMode === 'transfer_osmangazi' || depoMode === 'transfer_yesilsu') {
      // Transfer modu: ortadaki ürünü barkodla bul ve ilgili tarafa taşı
      const setTargetItems = depoMode === 'transfer_osmangazi' ? setDepoOsmangaziItems : setDepoYesilsuItems;
      const targetName = depoMode === 'transfer_osmangazi' ? 'Osmangazi' : 'Yeşil Su';

      // Ortadaki ürünlerde bu barkodu bul
      const centerEntry = Object.entries(depoCenterItems).find(
        ([, val]) => val.product.barcode === barcode
      );

      if (centerEntry) {
        const [productId, centerItem] = centerEntry;

        // Ortadan 1 adet düş (en sona taşı - son dokunulan en üstte görünsün)
        if (centerItem.quantity <= 1) {
          setDepoCenterItems(prev => {
            const copy = { ...prev };
            delete copy[productId];
            return copy;
          });
        } else {
          setDepoCenterItems(prev => {
            const { [productId]: current, ...rest } = prev;
            return { ...rest, [productId]: { ...current, quantity: current.quantity - 1 } };
          });
        }

        // Hedefe 1 adet ekle (en sona taşı - son okutulan en üstte görünsün)
        setTargetItems(prev => {
          const existing = prev[productId];
          if (existing) {
            const { [productId]: _, ...rest } = prev;
            return { ...rest, [productId]: { ...existing, quantity: existing.quantity + 1 } };
          }
          return { ...prev, [productId]: { product: centerItem.product, quantity: 1 } };
        });

        playStockSound('success');
        toast.success(`${centerItem.product.name} → ${targetName}'ya aktarıldı`);
      } else {
        // Ürün center'da yok - API'den bul ve mevcut depo stoğundan direkt transfer et
        try {
          const response = await fetch(`${API_URL}/products/barcode/${barcode}`);
          if (response.ok) {
            const product = await response.json();
            setTargetItems(prev => {
              const existing = prev[product.id];
              if (existing) {
                const { [product.id]: _, ...rest } = prev;
                return { ...rest, [product.id]: { ...existing, quantity: existing.quantity + 1, fromExistingStock: true } };
              }
              return { ...prev, [product.id]: { product, quantity: 1, fromExistingStock: true } };
            });
            // Envanter listesindeki stoğu anında güncelle
            setDepoInventoryProducts(prev =>
              prev.map(p => p.id === product.id ? { ...p, stock: p.stock - 1 } : p)
            );
            playStockSound('success');
            toast.success(`${product.name} → ${targetName}'ya aktarıldı (mevcut stoktan)`);
          } else {
            playStockSound('error');
            toast.error('Ürün bulunamadı!');
          }
        } catch (error) {
          console.error('Transfer barkod hatası:', error);
          playStockSound('error');
          toast.error('Ürün aranırken hata oluştu');
        }
      }
    }
  };

  const handleDepoComplete = async () => {
    const centerCount = Object.values(depoCenterItems).reduce((s, i) => s + i.quantity, 0);
    const osmangaziCount = Object.values(depoOsmangaziItems).reduce((s, i) => s + i.quantity, 0);
    const yesilsuCount = Object.values(depoYesilsuItems).reduce((s, i) => s + i.quantity, 0);

    if (centerCount === 0 && osmangaziCount === 0 && yesilsuCount === 0) {
      toast.error('Hiç ürün eklenmemiş!');
      return;
    }

    setSavingDepoOperation(true);
    try {
      // Depoda kalacak ürünler (stock_items)
      const stock_items = Object.entries(depoCenterItems).map(([productId, item]) => ({
        product_id: parseInt(productId),
        quantity: item.quantity
      }));

      // Transfer grupları
      const transfers = [];
      if (osmangaziCount > 0) {
        transfers.push({
          to_store_id: 2,
          items: Object.entries(depoOsmangaziItems).map(([productId, item]) => ({
            product_id: parseInt(productId),
            quantity: item.quantity,
            from_existing: !!item.fromExistingStock
          }))
        });
      }
      if (yesilsuCount > 0) {
        transfers.push({
          to_store_id: 3,
          items: Object.entries(depoYesilsuItems).map(([productId, item]) => ({
            product_id: parseInt(productId),
            quantity: item.quantity,
            from_existing: !!item.fromExistingStock
          }))
        });
      }

      const response = await fetch(`${API_URL}/depo/stock-and-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          depo_store_id: 4,
          stock_items,
          transfers,
          created_by: user?.id,
          created_by_name: user?.name
        })
      });

      if (response.ok) {
        const result = await response.json();
        let msg = 'İşlem tamamlandı!';
        if (result.stock_count > 0) msg += ` ${result.stock_count} ürün depoya stok girişi yapıldı.`;
        if (result.transfers?.length > 0) {
          result.transfers.forEach(t => {
            msg += ` ${t.item_count} ürün ${t.to_store_name}'ya transfer edildi (${t.transfer_code}).`;
          });
        }
        toast.success(msg);
        localStorage.removeItem('depoModalDraft');
        setShowDepoStockTransferModal(false);
        setDepoCenterItems({});
        setDepoOsmangaziItems({});
        setDepoYesilsuItems({});
        fetchProducts(true);
      } else {
        const error = await response.json();
        toast.error(error.error || 'İşlem başarısız!');
      }
    } catch (error) {
      console.error('Depo işlem hatası:', error);
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setSavingDepoOperation(false);
    }
  };

  // Depo envanter listesini API'den çek
  const fetchDepoInventory = async () => {
    setLoadingDepoInventory(true);
    try {
      const res = await fetch(`${API_URL}/store-stocks/store/4/with-products`);
      const data = await res.json();
      setDepoInventoryProducts(data);
    } catch (err) {
      console.error('Depo envanter yüklenemedi:', err);
    } finally {
      setLoadingDepoInventory(false);
    }
  };

  // Depo ürün kartından adet artır/azalt
  const handleDepoItemQuantity = (section, productId, delta) => {
    const setItems = section === 'center' ? setDepoCenterItems
      : section === 'osmangazi' ? setDepoOsmangaziItems : setDepoYesilsuItems;

    setItems(prev => {
      const item = prev[productId];
      if (!item) return prev;
      const newQty = item.quantity + delta;
      if (newQty <= 0) {
        const copy = { ...prev };
        delete copy[productId];
        return copy;
      }
      return { ...prev, [productId]: { ...item, quantity: newQty } };
    });
  };

  // Depo ürünü bir sütundan diğerine taşı
  const handleDepoMoveItem = (fromSection, productId, toSection) => {
    const fromItems = fromSection === 'center' ? depoCenterItems
      : fromSection === 'osmangazi' ? depoOsmangaziItems : depoYesilsuItems;
    const setFromItems = fromSection === 'center' ? setDepoCenterItems
      : fromSection === 'osmangazi' ? setDepoOsmangaziItems : setDepoYesilsuItems;
    const setToItems = toSection === 'center' ? setDepoCenterItems
      : toSection === 'osmangazi' ? setDepoOsmangaziItems : setDepoYesilsuItems;

    const item = fromItems[productId];
    if (!item) return;

    if (depoSoundEnabled) playStockSound('success');

    // Kaynaktan 1 adet düş (en sona taşı - son dokunulan en üstte görünsün)
    setFromItems(prev => {
      const current = prev[productId];
      if (!current) return prev;
      if (current.quantity <= 1) {
        const copy = { ...prev };
        delete copy[productId];
        return copy;
      }
      const { [productId]: _, ...rest } = prev;
      return { ...rest, [productId]: { ...current, quantity: current.quantity - 1 } };
    });

    // Hedefe 1 adet ekle (en sona taşı - son okutulan en üstte görünsün)
    setToItems(prev => {
      const existing = prev[productId];
      if (existing) {
        const { [productId]: _, ...rest } = prev;
        return { ...rest, [productId]: { ...existing, quantity: existing.quantity + 1 } };
      }
      return { ...prev, [productId]: { product: item.product, quantity: 1 } };
    });
  };
  // Depo modalını kapatma kontrolü
  const handleDepoClose = () => {
    const hasItems = Object.keys(depoCenterItems).length > 0
      || Object.keys(depoOsmangaziItems).length > 0
      || Object.keys(depoYesilsuItems).length > 0;

    if (hasItems) {
      setShowDepoCloseConfirm(true);
    } else {
      setShowDepoStockTransferModal(false);
    }
  };

  // ===== DEPO STOK AL VE TRANSFER YAP FONKSİYONLARI SONU =====

  // Stok alım ses efekti (localStorage'dan ayar kontrolü)
  const playStockSound = (type) => {
    // Ses ayarını kontrol et - kapalıysa ses çalma
    try {
      const soundEnabled = localStorage.getItem('soundEnabled');
      if (soundEnabled === 'false') {
        return;
      }
    } catch (e) {}

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      if (type === 'success') {
        oscillator.frequency.value = 1000;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.2;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.1);
      } else if (type === 'warning') {
        // Uyarı sesi - iki kısa bip
        oscillator.frequency.value = 600;
        oscillator.type = 'triangle';
        gainNode.gain.value = 0.25;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.15);
        // İkinci bip
        setTimeout(() => {
          try {
            const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
            const osc2 = ctx2.createOscillator();
            const gain2 = ctx2.createGain();
            osc2.connect(gain2);
            gain2.connect(ctx2.destination);
            osc2.frequency.value = 600;
            osc2.type = 'triangle';
            gain2.gain.value = 0.25;
            osc2.start();
            osc2.stop(ctx2.currentTime + 0.15);
          } catch (e) {}
        }, 200);
      } else {
        oscillator.frequency.value = 300;
        oscillator.type = 'square';
        gainNode.gain.value = 0.15;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2);
      }
    } catch (error) {
      console.error('Ses çalma hatası:', error);
    }
  };

  // Barkod okutma işlemi
  const handleStockIntakeBarcode = async (e) => {
    if (e.key !== 'Enter' || !stockIntakeBarcode.trim()) return;

    const barcode = stockIntakeBarcode.trim();
    setStockIntakeBarcode('');

    try {
      const response = await fetch(`${API_URL}/products/barcode/${barcode}`);

      if (response.ok) {
        const product = await response.json();

        // Ref'ten güncel değeri al
        const currentStockIntakeData = stockIntakeDataRef.current;
        const existingItem = currentStockIntakeData[product.id];

        // Eğer bu barkod daha önce okutulmuşsa uyarı ver
        if (existingItem) {
          playStockSound('warning');
          setStockIntakeDuplicateData({
            product,
            currentQuantity: existingItem.quantity
          });
          setShowStockIntakeDuplicateModal(true);
          return;
        }

        // Ürün bulundu - ilk kez okutuldu, stoğa ekle
        playStockSound('success');

        setStockIntakeData(prev => ({
          ...prev,
          [product.id]: {
            product,
            quantity: 1
          }
        }));

        setLastScannedProduct({
          ...product,
          addedQuantity: 1,
          status: 'found'
        });

        // 3 saniye sonra temizle
        setTimeout(() => {
          setLastScannedProduct(prev =>
            prev?.id === product.id ? null : prev
          );
        }, 3000);
      } else {
        // Ürün bulunamadı - yeni ürün ekleme modalı aç
        playStockSound('error');
        setUnknownBarcode(barcode);
        setShowUnknownBarcodeModal(true);
        setLastScannedProduct({
          barcode,
          status: 'notfound',
          message: 'Sistemde kayıtlı değil!'
        });
      }
    } catch (error) {
      console.error('Barkod arama hatası:', error);
      toast.error('Barkod aranamadı!');
    }

    // Input'a tekrar odaklan
    setTimeout(() => {
      if (stockIntakeBarcodeRef.current) {
        stockIntakeBarcodeRef.current.focus();
      }
    }, 100);
  };

  // Mükerrer barkodu onaylayarak stoğa ekle (stok alımı için)
  const confirmStockIntakeDuplicate = () => {
    if (!stockIntakeDuplicateData) return;

    const { product } = stockIntakeDuplicateData;

    setStockIntakeData(prev => {
      const existing = prev[product.id];
      const { [product.id]: _, ...rest } = prev;
      return {
        ...rest,
        [product.id]: {
          ...existing,
          quantity: existing.quantity + 1
        }
      };
    });

    const newQty = (stockIntakeDataRef.current[product.id]?.quantity || 0) + 1;
    setLastScannedProduct({
      ...product,
      addedQuantity: newQty,
      status: 'found'
    });

    playStockSound('success');
    setShowStockIntakeDuplicateModal(false);
    setStockIntakeDuplicateData(null);

    // 3 saniye sonra temizle
    setTimeout(() => {
      setLastScannedProduct(prev =>
        prev?.id === product.id ? null : prev
      );
    }, 3000);

    // Input'a tekrar odaklan
    setTimeout(() => {
      if (stockIntakeBarcodeRef.current) {
        stockIntakeBarcodeRef.current.focus();
      }
    }, 100);
  };

  // Mükerrer barkodu iptal et (stok alımı için)
  const cancelStockIntakeDuplicate = () => {
    setShowStockIntakeDuplicateModal(false);
    setStockIntakeDuplicateData(null);

    // Input'a tekrar odaklan
    setTimeout(() => {
      if (stockIntakeBarcodeRef.current) {
        stockIntakeBarcodeRef.current.focus();
      }
    }, 100);
  };

  // Stok alım miktarını manuel değiştir
  const updateStockIntakeQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      // Ürünü listeden kaldır
      setStockIntakeData(prev => {
        const updated = { ...prev };
        delete updated[productId];
        return updated;
      });
    } else {
      setStockIntakeData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          quantity: newQuantity
        }
      }));
    }
  };

  // Stok geçmişini getir (artırma/düşüm modalları)
  const fetchStockMovementHistory = async (type = 'all') => {
    setStockHistoryLoading(true);
    setStockHistoryType(type);
    try {
      const storeId = selectedStore?.id || user?.store_id;
      const response = await fetch(`${API_URL}/stock-movements/store/${storeId}?limit=100&include_sales=0`);
      const data = await response.json();
      let movements = data.movements || data || [];
      if (type === 'add') {
        movements = movements.filter(m => m.quantity > 0 && (m.movement_type === 'manual' || m.reference_type === 'manual_adjustment'));
      } else if (type === 'subtract') {
        movements = movements.filter(m => m.quantity < 0 || m.movement_type === 'manual' && m.reference_type === 'manual_adjustment' && m.stock_after < m.stock_before);
      }
      setStockHistoryData(movements);
    } catch (error) {
      console.error('Stok geçmişi yükleme hatası:', error);
      toast.error('Stok geçmişi yüklenemedi!');
    } finally {
      setStockHistoryLoading(false);
    }
  };

  // Stok alımlarını kaydet
  const saveStockIntake = async () => {
    const items = Object.values(stockIntakeData);
    if (items.length === 0) {
      toast.warning('Kaydedilecek stok alımı yok!');
      return;
    }

    setSavingStockIntake(true);
    let successCount = 0;
    let errorCount = 0;

    for (const item of items) {
      try {
        // Mağaza seçili ise o mağazanın stoğunu güncelle
        const requestBody = {
          quantity: item.quantity,
          type: 'add',
          notes: 'Mağazaya stok alındı',
          staff_id: user?.id,
          staff_name: user?.name
        };

        // Admin bir mağaza seçtiyse, o mağazanın stoğunu güncelle
        if (selectedStore?.id) {
          requestBody.store_id = selectedStore.id;
        }

        console.log('Stok güncelleme isteği:', {
          url: `${API_URL}/products/${item.product.id}/stock`,
          body: requestBody
        });

        const response = await fetch(`${API_URL}/products/${item.product.id}/stock`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        const responseData = await response.json();
        console.log('Stok güncelleme yanıtı:', responseData, 'Status:', response.status);

        if (response.ok && responseData.success) {
          successCount++;
        } else {
          console.error('Stok güncelleme hatası:', responseData);
          errorCount++;
        }
      } catch (error) {
        console.error('Stok güncelleme exception:', error);
        errorCount++;
      }
    }

    setSavingStockIntake(false);

    if (errorCount === 0) {
      toast.success(`${successCount} ürünün stoğu güncellendi!`);

      // Stok alım log'u
      const stokDetaylari = items.map(item => ({
        urun: item.product.name,
        barkod: item.product.barcode || '-',
        eklenen_adet: item.quantity
      }));
      const toplamAdet = items.reduce((sum, item) => sum + item.quantity, 0);
      const magazaAdi = selectedStore?.name || 'Genel';

      logService.log('products', 'stock_in',
        `Stok alımı: ${successCount} ürün, toplam ${toplamAdet} adet - ${magazaAdi}`, {
        entity_type: 'product',
        details: {
          magaza: magazaAdi,
          toplam_urun: successCount,
          toplam_adet: toplamAdet,
          urunler: stokDetaylari
        },
        status: 'success'
      });

      setStockIntakeData({});
      setLastScannedProduct(null);
      fetchProducts(1, false, true);
    } else {
      toast.warning(`${successCount} başarılı, ${errorCount} başarısız`);

      // Kısmi başarı log'u
      logService.log('products', 'stock_in',
        `Stok alımı (kısmi): ${successCount} başarılı, ${errorCount} başarısız`, {
        entity_type: 'product',
        details: {
          basarili: successCount,
          basarisiz: errorCount
        },
        status: 'warning'
      });
    }
  };

  // ============ STOK DÜŞÜM FONKSİYONLARI ============

  // Stok düşüm barkod okuma
  const handleStockDeductionBarcode = async (e) => {
    if (e.key !== 'Enter' || !stockDeductionBarcode.trim()) return;

    const barcode = stockDeductionBarcode.trim();
    setStockDeductionBarcode('');

    try {
      const response = await fetch(`${API_URL}/products/barcode/${barcode}`);

      if (response.ok) {
        const product = await response.json();

        // Mağaza stoğunu kontrol et
        const currentStock = selectedStore?.id
          ? (product.store_stocks?.find(s => s.store_id === selectedStore.id)?.stock || 0)
          : product.stock;

        if (currentStock <= 0) {
          playStockSound('error');
          toast.error(`${product.name} için stokta ürün yok!`);
          setLastDeductedProduct({
            ...product,
            status: 'nostock',
            message: 'Stokta ürün yok!'
          });
          setTimeout(() => setLastDeductedProduct(null), 3000);
          return;
        }

        // Ürün bulundu - düşüm listesine ekle
        playStockSound('success');

        setStockDeductionData(prev => {
          const existing = prev[product.id];
          const newQty = existing ? existing.quantity + 1 : 1;

          // Stoktan fazla düşüm yapılmasın
          if (newQty > currentStock) {
            toast.warning(`Maksimum ${currentStock} adet düşülebilir!`);
            return prev;
          }

          return {
            ...prev,
            [product.id]: {
              product,
              quantity: newQty,
              reason: existing?.reason || globalDeductionReason || '',
              currentStock
            }
          };
        });

        const newQty = (stockDeductionData[product.id]?.quantity || 0) + 1;
        setLastDeductedProduct({
          ...product,
          deductedQuantity: newQty,
          currentStock,
          status: 'found'
        });

        setTimeout(() => {
          setLastDeductedProduct(prev =>
            prev?.id === product.id ? null : prev
          );
        }, 3000);
      } else {
        playStockSound('error');
        toast.error('Ürün bulunamadı!');
        setLastDeductedProduct({
          barcode,
          status: 'notfound',
          message: 'Sistemde kayıtlı değil!'
        });
        setTimeout(() => setLastDeductedProduct(null), 3000);
      }
    } catch (error) {
      console.error('Barkod arama hatası:', error);
      toast.error('Barkod aranamadı!');
    }

    setTimeout(() => {
      if (stockDeductionBarcodeRef.current) {
        stockDeductionBarcodeRef.current.focus();
      }
    }, 100);
  };

  // Stok düşüm miktarını manuel değiştir
  const updateStockDeductionQuantity = (productId, newQuantity) => {
    const item = stockDeductionData[productId];
    if (!item) return;

    if (newQuantity <= 0) {
      setStockDeductionData(prev => {
        const updated = { ...prev };
        delete updated[productId];
        return updated;
      });
    } else if (newQuantity > item.currentStock) {
      toast.warning(`Maksimum ${item.currentStock} adet düşülebilir!`);
    } else {
      setStockDeductionData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          quantity: newQuantity
        }
      }));
    }
  };

  // Stok düşüm sebebini güncelle
  const updateStockDeductionReason = (productId, reason) => {
    setStockDeductionData(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        reason
      }
    }));
  };

  // Toplu sebep uygula
  const applyGlobalReason = () => {
    if (!globalDeductionReason.trim()) return;

    setStockDeductionData(prev => {
      const updated = {};
      for (const [id, item] of Object.entries(prev)) {
        updated[id] = {
          ...item,
          reason: globalDeductionReason
        };
      }
      return updated;
    });
    toast.success('Sebep tüm ürünlere uygulandı');
  };

  // Stok düşümlerini kaydet
  const saveStockDeduction = async () => {
    if (user?.role !== 'admin' && user?.role !== 'depo' && user?.permissions?.canDeductStock !== true) {
      toast.error('Stok düşümü yapma yetkiniz yok!');
      return;
    }
    const items = Object.values(stockDeductionData);
    if (items.length === 0) {
      toast.warning('Kaydedilecek stok düşümü yok!');
      return;
    }

    // Sebep kontrolü
    const itemsWithoutReason = items.filter(item => !item.reason?.trim());
    if (itemsWithoutReason.length > 0) {
      toast.warning('Lütfen tüm ürünler için düşüm sebebi girin!');
      return;
    }

    setSavingStockDeduction(true);
    let successCount = 0;
    let errorCount = 0;

    for (const item of items) {
      try {
        const requestBody = {
          quantity: item.quantity,
          type: 'subtract',
          movement_type: 'stock_deduction',
          notes: `Stok düşümü: ${item.reason}`,
          staff_id: user?.id,
          staff_name: user?.name
        };

        if (selectedStore?.id) {
          requestBody.store_id = selectedStore.id;
        }

        const response = await fetch(`${API_URL}/products/${item.product.id}/stock`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        const responseData = await response.json();

        if (response.ok && responseData.success) {
          successCount++;
        } else {
          console.error('Stok düşüm hatası:', responseData);
          errorCount++;
        }
      } catch (error) {
        console.error('Stok düşüm exception:', error);
        errorCount++;
      }
    }

    setSavingStockDeduction(false);

    if (errorCount === 0) {
      toast.success(`${successCount} ürünün stoğu düşüldü!`);

      // Stok düşüm log'u
      const stokDetaylari = items.map(item => ({
        urun: item.product.name,
        barkod: item.product.barcode || '-',
        dusulen_adet: item.quantity,
        sebep: item.reason
      }));
      const toplamAdet = items.reduce((sum, item) => sum + item.quantity, 0);
      const magazaAdi = selectedStore?.name || 'Genel';

      logService.log('products', 'stock_out',
        `Stok düşümü: ${successCount} ürün, toplam ${toplamAdet} adet - ${magazaAdi}`, {
        entity_type: 'product',
        details: {
          magaza: magazaAdi,
          toplam_urun: successCount,
          toplam_adet: toplamAdet,
          urunler: stokDetaylari
        },
        status: 'success'
      });

      setStockDeductionData({});
      setLastDeductedProduct(null);
      setGlobalDeductionReason('');
      setShowStockDeductionMode(false);
      fetchProducts(1, false, true);
    } else {
      toast.warning(`${successCount} başarılı, ${errorCount} başarısız`);
    }
  };

  // Stok düşüm listesinden ürün sil
  const removeFromDeductionList = (productId) => {
    setStockDeductionData(prev => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  // ============ STOK DÜŞÜM FONKSİYONLARI SON ============

  // Bilinmeyen barkodla yeni ürün ekle
  const handleAddNewProductWithBarcode = () => {
    const defaultType = allowedProductTypes.includes(selectedType) ? selectedType : allowedProductTypes[0];
    const defaultCategory = getDefaultCategoryForType(defaultType);
    setNewProduct({
      ...newProduct,
      barcode: unknownBarcode,
      name: '',
      metal_type: defaultType,
      category: defaultCategory,
      store_stocks: {},
      enabled_stores: {}
    });
    setShowUnknownBarcodeModal(false);
    setShowModal(true);
  };

  // Stok alım özeti
  const stockIntakeSummary = Object.values(stockIntakeData);
  const totalIntakeItems = stockIntakeSummary.reduce((sum, item) => sum + item.quantity, 0);

  // Stok düşüm özeti
  const stockDeductionSummary = Object.values(stockDeductionData);
  const totalDeductionItems = stockDeductionSummary.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className={`products-container ${sideMenuOpen ? 'menu-open' : ''}`}>
      {/* Sol Yan Menü */}
      <div className={`side-menu ${sideMenuOpen ? 'open' : ''}`}>
        <div className="side-menu-header">
          <h3>İşlemler</h3>
        </div>
        <div className="side-menu-content">
          {/* Stok İşlemleri Kategorisi */}
          {(user?.role === 'admin' || user?.role === 'depo' || user?.permissions?.canIntakeStock === true || user?.permissions?.canInventoryCount === true || user?.permissions?.canDeductStock === true || user?.permissions?.canViewStockReport === true) && (
            <div className="menu-category">
              <button
                className={`menu-category-header ${menuAccordion.stock ? 'open' : ''}`}
                onClick={() => setMenuAccordion(prev => ({ ...prev, stock: !prev.stock }))}
              >
                <div className="menu-category-title">
                  <Download size={18} />
                  <span>Stok İşlemleri</span>
                </div>
                {menuAccordion.stock ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              <div className={`menu-category-items ${menuAccordion.stock ? 'open' : ''}`}>
                {(user?.role === 'admin' || user?.role === 'depo' || user?.permissions?.canIntakeStock === true) && (
                <button className="side-menu-subitem" onClick={() => setShowStockIntakeOptionsModal(true)} style={{ position: 'relative' }}>
                  {pendingTransferCount > 0 && (
                    <span className="menu-badge" style={{ position: 'absolute', left: '8px', marginLeft: 0 }}>{pendingTransferCount}</span>
                  )}
                  Stok Artılama
                </button>
                )}
                {(user?.role === 'admin' || user?.role === 'depo' || user?.permissions?.canDeductStock === true) && (
                <button className="side-menu-subitem" onClick={() => setShowStockDeductionMode(true)}>
                  Stok Eksileme
                </button>
                )}
                {(user?.role === 'admin' || user?.role === 'depo' || user?.permissions?.canInventoryCount === true) && (
                  <button className="side-menu-subitem" onClick={() => window.open('/inventory-count', '_blank')}>
                    Sayım Yap
                  </button>
                )}
                {(user?.role === 'admin' || user?.role === 'depo' || user?.permissions?.canViewStockReport === true) && (
                <button className="side-menu-subitem" onClick={() => window.open('/stock-report', '_blank')}>
                  Stokları İncele
                </button>
                )}
                <button className="side-menu-subitem" onClick={openStockMovementsModal}>
                  Stok Hareketleri
                </button>
              </div>
            </div>
          )}

          {/* Transfer Kategorisi */}
          {canSendToRepair && (
            <div className="menu-category">
              <button
                className={`menu-category-header ${menuAccordion.transfers ? 'open' : ''}`}
                onClick={() => setMenuAccordion(prev => ({ ...prev, transfers: !prev.transfers }))}
              >
                <div className="menu-category-title">
                  <RefreshCw size={18} />
                  <span>Transferler</span>
                </div>
                {menuAccordion.transfers ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              <div className={`menu-category-items ${menuAccordion.transfers ? 'open' : ''}`}>
                {canTransferProducts && (<>
                <button className="side-menu-subitem" onClick={() => openRetailSalesTransfer('transfer')}>
                  Mağazaya Transfer Yap
                </button>
                <button className="side-menu-subitem" onClick={() => openRetailSalesTransfer('sent-transfers')}>
                  Gönderilen Transferler
                </button>
                <button className="side-menu-subitem" onClick={() => openRetailSalesTransfer('transfer-request')}>
                  Transfer Talebi Oluştur
                </button>
                <button className="side-menu-subitem" onClick={() => openRetailSalesTransfer('sent-requests')}>
                  Gönderilen Transfer Talepleri
                </button>
                <button className="side-menu-subitem" onClick={() => openRetailSalesTransfer('incoming-requests')} style={{ position: 'relative' }}>
                  {incomingRequestCount > 0 && (
                    <span className="menu-badge" style={{ position: 'absolute', left: '8px', marginLeft: 0 }}>{incomingRequestCount}</span>
                  )}
                  Gelen Transfer Talepleri
                </button>
                <button className="side-menu-subitem" onClick={() => openRetailSalesTransfer('history')}>
                  Transfer Geçmişi
                </button>
                </>)}
                <button className="side-menu-subitem" onClick={openSendToRepairModal}>
                  Tamirata Gönder
                </button>
              </div>
            </div>
          )}

          {/* Depo İşlemleri Kategorisi */}
          {(user?.role === 'depo' || user?.role === 'admin') && (
            <div className="menu-category">
              <button
                className="menu-category-header open"
              >
                <div className="menu-category-title">
                  <Archive size={18} />
                  <span>Depo İşlemleri</span>
                </div>
              </button>
              <div className="menu-category-items open">
                <button className="side-menu-subitem" onClick={() => {
                  // localStorage'dan kayıtlı veri varsa yükle
                  const saved = localStorage.getItem('depoModalDraft');
                  if (saved) {
                    try {
                      const data = JSON.parse(saved);
                      setDepoCenterItems(data.center || {});
                      setDepoOsmangaziItems(data.osmangazi || {});
                      setDepoYesilsuItems(data.yesilsu || {});
                      setDepoMode(data.mode || 'stock_entry');
                      toast.info('Önceki kayıtlı işleminiz yüklendi', { autoClose: 4500 });
                    } catch {
                      setDepoCenterItems({});
                      setDepoOsmangaziItems({});
                      setDepoYesilsuItems({});
                      setDepoMode('stock_entry');
                    }
                  } else {
                    setDepoCenterItems({});
                    setDepoOsmangaziItems({});
                    setDepoYesilsuItems({});
                    setDepoMode('stock_entry');
                  }
                  setDepoBarcode('');
                  setShowDepoStockTransferModal(true);
                  setSideMenuOpen(false);
                }}>
                  Stok Al ve Transfer Yap
                </button>
              </div>
            </div>
          )}

          {/* Toplu İşlemler Kategorisi */}
          {(user?.role === 'admin' || user?.permissions?.canAccessExcel === true) && (
            <div className="menu-category">
              <button
                className={`menu-category-header ${menuAccordion.bulk ? 'open' : ''}`}
                onClick={() => setMenuAccordion(prev => ({ ...prev, bulk: !prev.bulk }))}
              >
                <div className="menu-category-title">
                  <Package size={18} />
                  <span>Toplu İşlemler</span>
                </div>
                {menuAccordion.bulk ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              <div className={`menu-category-items ${menuAccordion.bulk ? 'open' : ''}`}>
                <button className="side-menu-subitem" onClick={() => fileInputRef.current?.click()}>
                  Toplu Ürün Ekle
                </button>
                {!bulkDeleteMode && user?.role === 'admin' && (
                  <button className="side-menu-subitem danger" onClick={() => setBulkDeleteMode(true)}>
                    Toplu Ürün Sil
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Excel İşlemleri Kategorisi */}
          <div className="menu-category">
            {(user?.role === 'admin' || user?.permissions?.canAccessExcel === true) && (<>
            <button
              className={`menu-category-header ${menuAccordion.excel ? 'open' : ''}`}
              onClick={() => setMenuAccordion(prev => ({ ...prev, excel: !prev.excel }))}
            >
              <div className="menu-category-title">
                <FileSpreadsheet size={18} />
                <span>Excel İşlemleri</span>
              </div>
              {menuAccordion.excel ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            <div className={`menu-category-items ${menuAccordion.excel ? 'open' : ''}`}>
              {(user?.role === 'admin' || user?.permissions?.canAccessExcel === true) && (
                <button className="side-menu-subitem" onClick={() => { fileInputRef.current?.click(); setSideMenuOpen(false); }}>
                  Ürün İçe Aktar
                </button>
              )}
              <button className="side-menu-subitem" onClick={() => { openExportModal(); setSideMenuOpen(false); }}>
                Ürün Dışa Aktar
              </button>
              {(user?.role === 'admin' || user?.permissions?.canAccessExcel === true) && (
                <>
                  <button className="side-menu-subitem" onClick={() => { priceFileInputRef.current?.click(); setSideMenuOpen(false); }}>
                    Fiyat Güncelle
                  </button>
                  <button className="side-menu-subitem" onClick={() => { handleDownloadTemplate(); setSideMenuOpen(false); }}>
                    İçe Aktarma Şablonu İndir
                  </button>
                  <button className="side-menu-subitem" onClick={() => { downloadPriceTemplate(); setSideMenuOpen(false); }}>
                    Fiyat Güncelleme Şablonu İndir
                  </button>
                </>
              )}
            </div>
            </>)}
          </div>
        </div>
      </div>

      {/* Ana İçerik */}
      <div className="main-content" ref={mainContentRef}>
      <header className="page-header">
        <div className="header-left">
          <button className="hamburger-btn" onClick={toggleSideMenu}>
            <Menu size={20} />
          </button>
          <button className="back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={16} /> Ana Sayfa
          </button>
          <div className="page-title-wrapper">
            <h1>Ürün Yönetimi</h1>
          </div>
        </div>
        <div className="header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-buttons">
          {barcodePrintMode ? (
            <>
              <span className="barcode-print-info">
                {selectedProductsForPrint.length} ürün seçildi
              </span>
              <button className="action-header-btn print-btn" onClick={printBarcodes}>
                <Printer size={16} /> Yazdır
              </button>
              <button className="action-header-btn cancel-btn" onClick={exitBarcodePrintMode}>
                ✕ İptal
              </button>
            </>
          ) : (
            <>
              {/* Stok Uyarı Bildirimleri */}
              {(user?.store_id || selectedStore?.id) && (
                <button
                  className={`action-header-btn stock-alert-btn ${stockAlertCount > 0 ? 'has-alerts' : ''}`}
                  onClick={() => setShowStockAlertModal(true)}
                  title="Stok Uyarıları"
                >
                  <Bell size={16} />
                  {stockAlertCount > 0 && (
                    <span className="stock-alert-badge">{stockAlertCount}</span>
                  )}
                </button>
              )}
              <button className="action-header-btn log-btn" onClick={() => setShowLogModal(true)}>
                <ScrollText size={16} /> İşlem Geçmişi
              </button>
              <button className="action-header-btn barcode-print-btn" onClick={() => setBarcodePrintMode(true)}>
                <Tag size={16} /> Barkod Bastır
              </button>
              <button className="action-header-btn product-group-btn" onClick={() => setShowGroupTypeChooserModal(true)}>
                <Package size={16} /> Grup Oluştur
              </button>
              {/* Gizli file inputları */}
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
                hidden
              />
              <input
                type="file"
                ref={priceFileInputRef}
                accept=".xlsx,.xls"
                onChange={handlePriceExcelUpload}
                hidden
              />
              {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.canCreateProduct === true) || user?.permissions?.canCreateProduct === true) && (
              <button className="action-header-btn" onClick={openNewProductModal}>
                <Plus size={16} /> Yeni Ürün
              </button>
              )}
            </>
          )}
        </div>
      </header>

      {/* Toplu Silme Kontrol Barı */}
      {bulkDeleteMode && (
        <div className="bulk-delete-bar">
          <div className="bulk-delete-info">
            <input
              type="checkbox"
              checked={selectAllFiltered}
              onChange={toggleSelectAll}
              id="selectAll"
            />
            <label htmlFor="selectAll">Tümünü Seç ({totalProducts})</label>
            <span className="selected-count">
              {selectAllFiltered ? `${totalProducts} ürün seçildi (tümü)` : `${selectedProducts.length} ürün seçildi`}
            </span>
          </div>
          <div className="bulk-delete-actions">
            <button className="btn secondary" onClick={cancelBulkDelete}>İptal</button>
            <button
              className="btn danger"
              onClick={handleBulkDelete}
              disabled={!selectAllFiltered && selectedProducts.length === 0}
            >
              <Trash2 size={16} /> Seçilenleri Sil ({selectAllFiltered ? totalProducts : selectedProducts.length})
            </button>
          </div>
        </div>
      )}

      {/* Barkod Bastır Kontrol Barı */}
      {barcodePrintMode && (
        <div className="bulk-delete-bar" style={{ flexWrap: 'wrap', rowGap: '10px' }}>
          <div className="bulk-delete-info">
            <input
              type="checkbox"
              checked={selectAllFilteredForPrint}
              onChange={toggleSelectAllFilteredForPrint}
              id="selectAllForPrint"
            />
            <label htmlFor="selectAllForPrint">
              Tümünü Seç (Filtrelenen {labelPrintFilteredLoading ? '…' : labelPrintSummary.totalTypes} ürün)
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>Etiket Adedi:</span>
            <button
              type="button"
              className={`btn secondary ${labelQtyMode === 'manual' ? 'active' : ''}`}
              style={{ padding: '4px 10px', fontSize: '12px', fontWeight: labelQtyMode === 'manual' ? 700 : 400, background: labelQtyMode === 'manual' ? '#e0e7ff' : undefined }}
              onClick={() => setLabelQtyMode('manual')}
            >
              Sabit Sayı
            </button>
            <button
              type="button"
              className={`btn secondary ${labelQtyMode === 'stock' ? 'active' : ''}`}
              style={{ padding: '4px 10px', fontSize: '12px', fontWeight: labelQtyMode === 'stock' ? 700 : 400, background: labelQtyMode === 'stock' ? '#e0e7ff' : undefined }}
              onClick={() => setLabelQtyMode('stock')}
              title="Her ürün için o üründeki stok kadar etiket basılır"
            >
              Stok Bazlı
            </button>
            {labelQtyMode === 'stock' && (
              <select
                value={labelStockScope}
                onChange={(e) => setLabelStockScope(e.target.value === 'total' ? 'total' : parseInt(e.target.value))}
                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #ddd' }}
              >
                <option value="total">Tüm Ürünler (Toplam Stok)</option>
                {labelPrintSummary.perStoreList.map(s => (
                  <option key={s.storeId} value={s.storeId}>{s.storeName}</option>
                ))}
              </select>
            )}
          </div>

          <div className="bulk-delete-actions" style={{ flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
            {!labelPrintFilteredLoading && labelPrintSummary.totalTypes > 0 && (
              <span className="selected-count" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                {labelPrintSummary.totalTypes} çeşit, {labelPrintSummary.totalQty} adet toplam
                {labelPrintSummary.perStoreList.length > 0 && (
                  <> — {labelPrintSummary.perStoreList.map(s => `${s.storeName}: ${s.qty}`).join(', ')}</>
                )}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="products-content">
        {/* Yardım Butonu */}
        <button className="help-btn-corner" title="Yardım" onClick={() => setShowHelpModal(true)}>?</button>

        {/* Ana Tür Seçimi */}
        <div className="type-tabs">
          {allowedProductTypes.includes('gold') && (
            <button
              className={`type-tab gold ${selectedType === 'gold' ? 'active' : ''}`}
              onClick={() => handleTypeChange('gold')}
            >
              <Coins size={18} style={{ color: '#f59e0b' }} /> Altın
              {selectedType === 'gold' && <span className="tab-count">{totalProducts}</span>}
            </button>
          )}
          {allowedProductTypes.includes('silver') && (
            <button
              className={`type-tab silver ${selectedType === 'silver' ? 'active' : ''}`}
              onClick={() => handleTypeChange('silver')}
            >
              <Coins size={18} style={{ color: '#9ca3af' }} /> Gümüş
              {selectedType === 'silver' && <span className="tab-count">{totalProducts}</span>}
            </button>
          )}
          {allowedProductTypes.includes('zippo') && (
            <button
              className={`type-tab zippo ${selectedType === 'zippo' ? 'active' : ''}`}
              onClick={() => handleTypeChange('zippo')}
            >
              <Flame size={18} style={{ color: '#ef4444' }} /> Zippo
              {selectedType === 'zippo' && <span className="tab-count">{totalProducts}</span>}
            </button>
          )}
          {allowedProductTypes.includes('steel') && (
            <button
              className={`type-tab steel ${selectedType === 'steel' ? 'active' : ''}`}
              onClick={() => handleTypeChange('steel')}
            >
              <Circle size={18} style={{ color: '#374151' }} /> Çelik
              {selectedType === 'steel' && <span className="tab-count">{totalProducts}</span>}
            </button>
          )}
          {allowedProductTypes.includes('brands') && (
            <div
              className="brand-tab-wrapper"
              onMouseEnter={openBrandMenu}
              onMouseLeave={scheduleCloseBrandMenu}
            >
              <button
                className={`type-tab brands ${selectedType === 'brands' ? 'active' : ''}`}
                onClick={() => handleTypeChange('brands')}
              >
                <Tag size={18} style={{ color: '#8b5cf6' }} /> Markalar
                <ChevronDown
                  size={14}
                  className={`brand-tab-caret ${brandMenuOpen ? 'open' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setBrandMenuOpen(o => !o); }}
                />
                {selectedType === 'brands' && <span className="tab-count">{totalProducts}</span>}
              </button>
              {selectedType === 'brands' && advancedFilters.brand && (
                <span className="brand-tab-active-filter" title="Seçili marka filtresi">
                  {advancedFilters.brand}
                  <X
                    size={12}
                    onClick={(e) => { e.stopPropagation(); setAdvancedFilters(prev => ({ ...prev, brand: '' })); setBrandFilterNonce(n => n + 1); }}
                  />
                </span>
              )}
              {brandMenuOpen && (
                <div className="brand-tab-menu" onMouseEnter={openBrandMenu} onMouseLeave={scheduleCloseBrandMenu}>
                  <button
                    className={`brand-tab-menu-item ${!advancedFilters.brand ? 'active' : ''}`}
                    onClick={() => handleBrandMenuSelect('')}
                  >
                    Tüm Markalar
                  </button>
                  {brandMenuBrands.length === 0 ? (
                    <div className="brand-tab-menu-empty">Marka bulunamadı</div>
                  ) : (
                    brandMenuBrands.map(brand => (
                      <button
                        key={brand}
                        className={`brand-tab-menu-item ${advancedFilters.brand === brand ? 'active' : ''}`}
                        onClick={() => handleBrandMenuSelect(brand)}
                      >
                        {brand}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          {allowedProductTypes.includes('aksesuar') && (
            <button
              className={`type-tab aksesuar ${selectedType === 'aksesuar' ? 'active' : ''}`}
              onClick={() => handleTypeChange('aksesuar')}
            >
              <Gem size={18} style={{ color: '#06b6d4' }} /> Aksesuar
              {selectedType === 'aksesuar' && <span className="tab-count">{totalProducts}</span>}
            </button>
          )}
          {allowedProductTypes.includes('sarf') && (
            <button
              className={`type-tab sarf ${selectedType === 'sarf' ? 'active' : ''}`}
              onClick={() => handleTypeChange('sarf')}
            >
              <Package size={18} style={{ color: '#78716c' }} /> Sarf
              {selectedType === 'sarf' && <span className="tab-count">{totalProducts}</span>}
            </button>
          )}
          {allowedProductTypes.includes('ecommerce') && (
            <button
              className={`type-tab ecommerce ${selectedType === 'ecommerce' ? 'active' : ''}`}
              onClick={() => handleTypeChange('ecommerce')}
            >
              <Package size={18} style={{ color: '#10b981' }} /> E-Ticaret
              {selectedType === 'ecommerce' && <span className="tab-count">{totalProducts}</span>}
            </button>
          )}
          {allowedProductTypes.includes('online') && (
            <button
              className={`type-tab online ${selectedType === 'online' ? 'active' : ''}`}
              onClick={() => handleTypeChange('online')}
            >
              <Wifi size={18} style={{ color: '#8b5cf6' }} /> Online
              {selectedType === 'online' && <span className="tab-count">{totalProducts}</span>}
            </button>
          )}
          {allowedProductTypes.includes('productGroups') && (
            <button
              className={`type-tab product-groups ${selectedType === 'productGroups' ? 'active' : ''}`}
              onClick={() => handleTypeChange('productGroups')}
            >
              <Layers size={18} style={{ color: '#0f766e' }} /> Ürün Grupları
              {selectedType === 'productGroups' && <span className="tab-count">{productGroups.length}</span>}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-row">
            <div className="search-box">
              <span className="search-icon"><Search size={18} /></span>
              <input
                type="text"
                placeholder="Ürün adı veya barkod ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              className="advanced-filter-btn"
              onClick={() => setShowAdvancedFilter(true)}
            >
              <SlidersHorizontal size={16} /> Detaylı Filtre
            </button>
            <div className="column-settings-wrapper">
              <button
                className="column-settings-btn"
                onClick={() => setShowColumnSettings(!showColumnSettings)}
              >
                <Settings size={16} /> Sütunlar
              </button>
              {showColumnSettings && (
                <div className="column-settings-dropdown">
                  <div className="column-settings-header">
                    <span>Görünür Sütunlar</span>
                    <button className="reset-columns-btn" onClick={resetColumns}>Sıfırla</button>
                  </div>
                  <div className="column-settings-list">
                    {allColumns.filter(col => col.key !== 'profitMargin' || user?.role === 'admin').map(col => (
                      <label key={col.key} className="column-toggle">
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.key]}
                          onChange={() => toggleColumn(col.key)}
                        />
                        <span>{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="quick-filters" style={selectedType === 'productGroups' ? { display: 'none' } : undefined}>
            <div className="category-filters">
              {/* İlk 7 kategori (Tümü + 6 tane) */}
              {categories.slice(0, 7).map(cat => (
                <button
                  key={cat.id}
                  className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.name}
                </button>
              ))}
              {/* Diğer kategoriler dropdown */}
              {categories.length > 7 && (
                <div className="category-dropdown">
                  <button className={`category-btn dropdown-trigger ${categories.slice(7).some(cat => selectedCategory === cat.id) ? 'active' : ''}`}>
                    Diğer
                  </button>
                  <div className="category-dropdown-menu">
                    {categories.slice(7).map(cat => (
                      <button
                        key={cat.id}
                        className={`category-dropdown-item ${selectedCategory === cat.id ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(cat.id)}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fiyat güncelleme bilgisi */}
        {lastPriceUpdate && (
          <div className="price-update-info" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '10px',
            padding: '8px 16px',
            fontSize: '0.75rem',
            color: '#6b7280',
            background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
            borderRadius: '8px',
            marginBottom: '8px',
            border: '1px solid #bbf7d0'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: '#22c55e' }}>●</span>
              Son güncelleme: <strong>{priceUpdateText || 'az önce'}</strong>
            </span>
            <button
              onClick={handleManualPriceRefresh}
              disabled={isRefreshingPrices}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                fontSize: '0.7rem',
                fontWeight: '500',
                color: '#16a34a',
                background: '#fff',
                border: '1px solid #bbf7d0',
                borderRadius: '6px',
                cursor: isRefreshingPrices ? 'not-allowed' : 'pointer',
                opacity: isRefreshingPrices ? 0.6 : 1,
                transition: 'all 0.2s'
              }}
              title="Kurları ve fiyatları şimdi güncelle"
            >
              <RefreshCw
                size={12}
                style={{
                  animation: isRefreshingPrices ? 'spin 1s linear infinite' : 'none'
                }}
              />
              Güncelle
            </button>
          </div>
        )}

        {/* Products Table */}
        <div className="table-container" ref={tableContainerRef}>
          {loading ? (
            <div className="loading">Yükleniyor...</div>
          ) : (
            <>
            <table className="products-table">
              <thead>
                <tr>
                  {bulkDeleteMode && (
                    <th className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedProducts.length === products.length && products.length > 0}
                        onChange={toggleSelectAll}
                        title="Tümünü Seç"
                      />
                    </th>
                  )}
                  {barcodePrintMode && (
                    <th className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedProductsForPrint.length === products.length && products.length > 0}
                        onChange={toggleAllProductsForPrint}
                        title="Tümünü Seç"
                      />
                    </th>
                  )}
                  {visibleColumns.image && <th>Resim</th>}
                  {visibleColumns.name && (
                    <th className={`sortable ${sortField === 'name' ? 'sorted' : ''}`} onClick={() => handleSort('name')}>
                      Ürün Adı
                      {sortField === 'name' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.barcode && (
                    <th className={`sortable ${sortField === 'barcode' ? 'sorted' : ''}`} onClick={() => handleSort('barcode')}>
                      Barkod
                      {sortField === 'barcode' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.category && (
                    <th className={`sortable ${sortField === 'category' ? 'sorted' : ''}`} onClick={() => handleSort('category')}>
                      Kategori
                      {sortField === 'category' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.brand && (
                    <th className={`sortable ${sortField === 'brand' ? 'sorted' : ''}`} onClick={() => handleSort('brand')}>
                      Marka
                      {sortField === 'brand' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.description && <th>Açıklama</th>}
                  {visibleColumns.supplier && <th>Tedarikçi</th>}
                  {visibleColumns.color && <th>Renk</th>}
                  {visibleColumns.size && <th>Ölçü</th>}
                  {visibleColumns.weight && <th>Gramaj</th>}
                  {visibleColumns.purity && selectedType !== 'zippo' && selectedType !== 'steel' && selectedType !== 'brands' && selectedType !== 'online' && <th>Ayar</th>}
                  {visibleColumns.profitMargin && user?.role === 'admin' && <th>Kat Sayı</th>}
                  {visibleColumns.laboredGram && <th>İşçilikli Gram</th>}
                  {visibleColumns.price && (
                    <th className={`sortable ${sortField === 'price' ? 'sorted' : ''}`} onClick={() => handleSort('price')}>
                      Fiyat
                      {sortField === 'price' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.currency && <th>Para Birimi</th>}
                  {visibleColumns.storeStock && (
                    <th className={`sortable ${sortField === 'storeStock' ? 'sorted' : ''}`} onClick={() => handleSort('storeStock')}>
                      Mağaza Stoğu
                      {sortField === 'storeStock' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.osmangaziStock && (
                    <th className={`sortable ${sortField === 'osmangaziStock' ? 'sorted' : ''}`} onClick={() => handleSort('osmangaziStock')}>
                      Osmangazi Stok
                      {sortField === 'osmangaziStock' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.yesilsuStock && (
                    <th className={`sortable ${sortField === 'yesilsuStock' ? 'sorted' : ''}`} onClick={() => handleSort('yesilsuStock')}>
                      Yeşilsu Stok
                      {sortField === 'yesilsuStock' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.depoStock && (
                    <th className={`sortable ${sortField === 'depoStock' ? 'sorted' : ''}`} onClick={() => handleSort('depoStock')}>
                      Depo Stok
                      {sortField === 'depoStock' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.totalStock && (
                    <th className={`sortable ${sortField === 'totalStock' ? 'sorted' : ''}`} onClick={() => handleSort('totalStock')}>
                      Toplam Stok
                      {sortField === 'totalStock' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.minStock && user?.role === 'admin' && <th>Min. Stok</th>}
                  {visibleColumns.ecommerce && canAccessShopify && (
                    <th className={`sortable ${sortField === 'ecommerce' ? 'sorted' : ''}`} onClick={() => handleSort('ecommerce')}>
                      E-Ticaret
                      {sortField === 'ecommerce' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.eskiEtiket && <th>Eski Etiket</th>}
                  {visibleColumns.noDiscount && (
                    <th className={`sortable ${sortField === 'noDiscount' ? 'sorted' : ''}`} onClick={() => handleSort('noDiscount')}>
                      İndirim Yapılamaz {sortField === 'noDiscount' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.stockEntryDate && (
                    <th className={`sortable ${sortField === 'stockEntryDate' ? 'sorted' : ''}`} onClick={() => handleSort('stockEntryDate')}>
                      Giriş Tarihi
                      {sortField === 'stockEntryDate' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.stockChangeDate && (
                    <th className={`sortable ${sortField === 'stockChangeDate' ? 'sorted' : ''}`} onClick={() => handleSort('stockChangeDate')}>
                      Değişiklik Tarihi
                      {sortField === 'stockChangeDate' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  )}
                  {visibleColumns.createdBy && <th>Oluşturan</th>}
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                const renderProductRow = (product, productRowIndex, isGroupMember = false) => (
                  <tr
                    key={product.id}
                    className={`${isGroupMember ? 'product-group-member-row' : ''} ${barcodePrintMode && selectedProductsForPrint.includes(product.id) ? 'selected-for-print' : ''} ${bulkDeleteMode && (selectAllFiltered || selectedProducts.includes(product.id)) ? 'selected-for-delete' : ''} ${bulkDeleteMode ? 'clickable-row' : ''}`}
                    onClick={() => bulkDeleteMode && toggleProductSelection(product.id)}
                    onDoubleClick={() => !bulkDeleteMode && (user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.canEditProduct === true) || user?.permissions?.canEditProduct === true) && openEditModal(product)}
                    style={bulkDeleteMode ? { cursor: 'pointer' } : { cursor: 'pointer' }}
                  >
                    {bulkDeleteMode && (
                      <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectAllFiltered || selectedProducts.includes(product.id)}
                          onChange={() => toggleProductSelection(product.id)}
                        />
                      </td>
                    )}
                    {barcodePrintMode && (
                      <td className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={selectedProductsForPrint.includes(product.id)}
                          onChange={() => toggleProductForPrint(product.id)}
                        />
                      </td>
                    )}
                    {visibleColumns.image && (
                      <td className="product-image-cell">
                        {product.image ? (
                          <img
                            src={product.image.startsWith('http') ? product.image : `${IMAGE_HOST}${product.image}`}
                            alt={product.name}
                            className="product-thumbnail"
                            loading="lazy"
                            onClick={() => isGroupMember ? openZoomedImageForProduct(product) : openZoomedProductImage(productRowIndex)}
                          />
                        ) : (
                          <div className="no-image">
                            <img src="/akikfisligi.png" alt="Akik Gümüş" style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.6 }} />
                          </div>
                        )}
                      </td>
                    )}
                    {visibleColumns.name && <td className="product-name">{product.is_set ? <span className="set-badge">SET</span> : null}{product.name}</td>}
                    {visibleColumns.barcode && <td className="barcode">{product.barcode || '-'}</td>}
                    {visibleColumns.category && (
                      <td>
                        <span className={`category-badge ${product.metal_type}`}>{product.category}</span>
                      </td>
                    )}
                    {visibleColumns.brand && <td className="brand">{product.brand || '-'}</td>}
                    {visibleColumns.description && <td className="description-cell">{product.description || '-'}</td>}
                    {visibleColumns.supplier && <td>{product.supplier || '-'}</td>}
                    {visibleColumns.color && <td>{product.color || '-'}</td>}
                    {visibleColumns.size && <td>{product.size || '-'}</td>}
                    {visibleColumns.weight && <td>{product.weight ? `${parseFloat(product.weight).toFixed(2)} gr` : '-'}</td>}
                    {visibleColumns.purity && selectedType !== 'zippo' && selectedType !== 'steel' && selectedType !== 'brands' && selectedType !== 'online' && <td>{product.metal_type === 'gold' ? (product.gold_karat ? `${product.gold_karat}K` : '-') : (product.silver_purity ? `${product.silver_purity}` : '-')}</td>}
                    {visibleColumns.profitMargin && user?.role === 'admin' && <td>{product.profit_margin || '-'}</td>}
                    {visibleColumns.laboredGram && <td>{calculateLaboredGram(product) ? `${calculateLaboredGram(product)} gr` : '-'}</td>}
                    {visibleColumns.price && (
                      <td className="price">
                        {(() => {
                          // Formüllü ürünler için hesaplanmış fiyat (altın, gümüş, gümüş aksesuar)
                          // Döviz cinsinden (USD/EUR) fiyatlandırılmış ürünler formüle girmez, sabit fiyat olarak gösterilir
                          const hasForexCurrency = product.currency && product.currency !== 'TRY';
                          const isGoldFormula = product.metal_type === 'gold' && product.pricing_type !== 'fixed' && !hasForexCurrency;
                          const isSilverFormula = product.metal_type === 'silver' && product.pricing_type !== 'fixed' && !hasForexCurrency;
                          const isSilverAccessoryFormula = product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar' && product.pricing_type !== 'fixed' && !hasForexCurrency;

                          if (isGoldFormula || isSilverFormula || isSilverAccessoryFormula) {
                            const calculatedPrice = getProductPrice(product);
                            if (calculatedPrice === null || calculatedPrice === 0) {
                              // Fiyat hesaplanamadıysa sell_price'ı göster
                              if (product.sell_price && Number(product.sell_price) > 0) {
                                const sym = getCurrencySymbol(product.currency);
                                return <span className="price-fallback" title="Kur yüklenemedi, kayıtlı fiyat gösteriliyor">{sym}{Number(product.sell_price).toLocaleString('tr-TR')}</span>;
                              }
                              return <span className="price-loading">—</span>;
                            }
                            return `₺${Math.round(calculatedPrice).toLocaleString('tr-TR')}`;
                          }
                          // Sabit fiyatlı ürünler ve diğer aksesuarlar
                          if (hasForexCurrency && Number(product.sell_price) > 0) {
                            const rate = getSatisRate(product.currency);
                            const tlPrice = rate > 0 ? Math.round(Number(product.sell_price) * rate) : null;
                            return (
                              <span>
                                {getCurrencySymbol(product.currency)}{Number(product.sell_price).toLocaleString()}
                                {tlPrice && <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, marginTop: '1px' }}>₺{tlPrice.toLocaleString('tr-TR')}</div>}
                              </span>
                            );
                          }
                          return `${getCurrencySymbol(product.currency)}${Number(product.sell_price || 0).toLocaleString()}`;
                        })()}
                      </td>
                    )}
                    {visibleColumns.currency && <td>{product.currency || 'TRY'}</td>}
                    {visibleColumns.storeStock && (
                      <td className="stock">
                        {(() => {
                          const storeStock = product.store_stocks?.find(ss => ss.store_id === selectedStore?.id);
                          const inRepair = storeStock?.in_repair_count || 0;
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <span>{storeStock?.stock ?? '-'}</span>
                              {inRepair > 0 && (
                                <span className="repair-badge" title={`${inRepair} adet tamiratta`}>
                                  🔧 {inRepair}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {visibleColumns.osmangaziStock && (
                      <td className="stock">
                        {product.store_stocks?.find(ss => ss.store_id === 2)?.stock ?? 0}
                      </td>
                    )}
                    {visibleColumns.yesilsuStock && (
                      <td className="stock">
                        {product.store_stocks?.find(ss => ss.store_id === 3)?.stock ?? 0}
                      </td>
                    )}
                    {visibleColumns.depoStock && (
                      <td className="stock">
                        {product.store_stocks?.find(ss => ss.store_id === 4)?.stock ?? 0}
                      </td>
                    )}
                    {visibleColumns.totalStock && (
                      <td className={`stock ${user?.role === 'admin' && product.stock <= product.min_stock ? 'low' : ''}`}>
                        {product.stock}
                        {user?.role === 'admin' && product.stock <= product.min_stock && <span className="stock-warning"> <AlertTriangle size={14} /></span>}
                      </td>
                    )}
                    {visibleColumns.minStock && user?.role === 'admin' && <td className="min-stock">{product.min_stock || 0}</td>}
                    {visibleColumns.ecommerce && canAccessShopify && (
                      <td>
                        <span
                          className={`ecommerce-toggle ${product.ecommerce_active === 1 || product.ecommerce_active === true ? 'active' : ''}`}
                        >
                          {product.ecommerce_active === 1 || product.ecommerce_active === true ? 'Açık' : 'Kapalı'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.eskiEtiket && (
                      <td>
                        <span style={{
                          color: product.eski_etiket === 1 || product.eski_etiket === true ? '#f59e0b' : '#6b7280',
                          fontWeight: 500
                        }}>
                          {product.eski_etiket === 1 || product.eski_etiket === true ? 'Evet' : 'Hayır'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.noDiscount && (
                      <td>
                        <span style={{
                          color: product.no_discount === 1 || product.no_discount === true ? '#ef4444' : '#6b7280',
                          fontWeight: 500
                        }}>
                          {product.no_discount === 1 || product.no_discount === true ? 'Evet' : 'Hayır'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.stockEntryDate && (
                      <td className="stock-date-cell">
                        {product.stock_entry_date
                          ? new Date(product.stock_entry_date).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '-'}
                      </td>
                    )}
                    {visibleColumns.stockChangeDate && (
                      <td className="stock-date-cell">
                        {product.last_stock_change
                          ? new Date(product.last_stock_change).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '-'}
                      </td>
                    )}
                    {visibleColumns.createdBy && (
                      <td>{product.created_by || '-'}</td>
                    )}
                    <td className="actions">
                      <button
                        className={`table-btn favorite ${product.is_favorite == 1 ? 'active' : ''}`}
                        title={product.is_favorite == 1 ? 'Favorilerden çıkar' : 'Favorilere ekle'}
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(product); }}
                      >
                        <Star size={16} fill={product.is_favorite == 1 ? '#f59e0b' : 'none'} color={product.is_favorite == 1 ? '#f59e0b' : '#94a3b8'} />
                      </button>
                      {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.canEditProduct === true) || user?.permissions?.canEditProduct === true) && (
                        <button className="table-btn edit" title="Düzenle" onClick={() => openEditModal(product)}><Pencil size={16} /></button>
                      )}
                      <button className="table-btn print" title="Barkod Yazdır" onClick={(e) => { e.stopPropagation(); printSingleBarcode(product); }}><Printer size={16} /></button>
                      <button className="table-btn history" title="Stok Geçmişi" onClick={() => openStockHistory(product)}><ScrollText size={16} /></button>
                      {user?.role === 'admin' && (
                        <button className="table-btn delete" title="Sil" onClick={() => handleDeleteProduct(product)}><Trash2 size={16} /></button>
                      )}
                    </td>
                  </tr>
                );

                const rows = [];
                displayProductGroups.forEach(group => {
                  const isOpen = expandedProductGroupIds.has(group.id);
                  const loadingMembers = loadingProductGroupMemberIds.has(group.id);
                  const previewImg = getProductImageUrl({ image: group.preview_image });
                  const canEdit = user?.role === 'admin' || user?.permissions?.canEditProduct === true || (user?.role === 'depo' && user?.permissions?.canEditProduct === true);
                  rows.push(
                    <tr key={`pg-${group.id}`} className={`product-group-row ${isOpen ? 'open' : ''}`} onClick={() => toggleProductGroupExpand(group)} style={{ cursor: 'pointer' }}>
                      {bulkDeleteMode && <td className="checkbox-col" />}
                      {barcodePrintMode && <td className="checkbox-col" />}
                      {visibleColumns.image && (
                        <td className="product-image-cell">
                          {previewImg ? (
                            <img src={previewImg} alt="" className="product-thumbnail" loading="lazy" />
                          ) : (
                            <div className="no-image"><Package size={20} color="#94a3b8" /></div>
                          )}
                        </td>
                      )}
                      <td colSpan={99}>
                        <div className="product-group-row-inner">
                          <span className="product-group-caret">
                            {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          </span>
                          <span className="product-group-name">{group.name}</span>
                          <span className="product-group-badge">GRUP · {group.member_count} ürün</span>
                          {group.metal_type && <span className="product-group-type">{group.metal_type}</span>}
                          <span className="product-group-spacer" />
                          {canEdit && (
                            <button
                              className="table-btn edit"
                              title="Grubu Düzenle"
                              onClick={(e) => { e.stopPropagation(); openEditProductGroupEditor(group); }}
                            ><Pencil size={16} /></button>
                          )}
                          {user?.role === 'admin' && (
                            <button
                              className="table-btn delete"
                              title="Grubu Sil"
                              onClick={(e) => { e.stopPropagation(); deleteProductGroup(group); }}
                            ><Trash2 size={16} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  if (isOpen) {
                    if (loadingMembers) {
                      rows.push(
                        <tr key={`pg-${group.id}-loading`} className="product-group-member-info">
                          <td colSpan={99}>Üyeler yükleniyor...</td>
                        </tr>
                      );
                    } else {
                      const mem = productGroupMemberData[group.id] || [];
                      if (mem.length === 0) {
                        rows.push(
                          <tr key={`pg-${group.id}-empty`} className="product-group-member-info">
                            <td colSpan={99}>Bu grupta ürün yok</td>
                          </tr>
                        );
                      } else {
                        mem.forEach(m => rows.push(renderProductRow(m, -1, true)));
                      }
                    }
                  }
                });

                sortedProducts.forEach((product, productRowIndex) => {
                  rows.push(renderProductRow(product, productRowIndex));
                });

                return rows;
                })()}
              </tbody>
            </table>
            <div ref={loadMoreRef} style={{ height: 1 }} />
            {loadingMore && (
              <div className="loading-more">Daha fazla yükleniyor...</div>
            )}
            {!hasMore && products.length > 0 && (
              <div className="end-of-list">Tüm ürünler yüklendi ({totalProducts} ürün)</div>
            )}
            </>
          )}
        </div>
      </div>

      {/* Add Product Modal */}
      {showModal && (
        <div className={`modal-overlay ${closingModal === 'addProduct' ? 'closing' : ''}`}>
          <div className="modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yeni Ürün Ekle</h2>
              <button className="close-btn" onClick={() => handleCloseModal('addProduct', setShowModal)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Resim (sol) + Tür/Kategori/Temel Bilgiler (sağ) */}
              <div className="edit-top-row">
                {/* Sol: Ürün Resmi */}
                <div className="edit-product-image-area">
                  <label className="section-label" style={{ display: 'block', marginBottom: '6px' }}>Fotoğraf <span className="required-star">*</span></label>
                  {newProductImagePreview ? (
                    <>
                      <div className="edit-product-image-card">
                        <img src={newProductImagePreview} alt="Ürün" />
                      </div>
                      <div className="edit-product-image-buttons">
                        <label className="edit-img-btn change">
                          Değiştir
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) { setNewProductImage(file); setNewProductImagePreview(URL.createObjectURL(file)); }
                            }}
                            hidden
                          />
                        </label>
                        <button
                          type="button"
                          className="edit-img-btn delete"
                          onClick={() => { setNewProductImage(null); setNewProductImagePreview(null); }}
                        >
                          Sil
                        </button>
                      </div>
                    </>
                  ) : (
                    <label className="edit-product-image-empty">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#b0bec5" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <span>Resim Yükle</span>
                      <span className="hint">JPG, PNG, WEBP (max 20MB)</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) { setNewProductImage(file); setNewProductImagePreview(URL.createObjectURL(file)); }
                        }}
                        hidden
                      />
                    </label>
                  )}

                  {/* Canlı kur önizlemesi - kaydedilince creation_rates olarak saklanacak bilgiler */}
                  {newProduct.metal_type === 'gold' && newProduct.weight && newProduct.gold_karat && (() => {
                    const goldRate = getGoldGramPrice();
                    if (!(goldRate > 0)) return null;
                    const gram = parseFloat(newProduct.weight) || 0;
                    const labor = parseFloat(newProduct.labor_cost) || 0;
                    const multiplier = karatMultipliers[newProduct.gold_karat] || 0;
                    const pureGram = (multiplier * gram) / 1000;
                    const laboredGram = ((multiplier + labor) * gram) / 1000;
                    const hasMaliyeti = pureGram * goldRate;
                    const laborMaliyeti = (laboredGram - pureGram) * goldRate;
                    const tarih = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    return (
                      <div className="creation-preview-box gold">
                        <div className="creation-preview-title">Oluşturulacak Kur Bilgisi</div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Altın Kuru:</span>
                          <span className="calc-value">₺{goldRate.toFixed(2)}/gr</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Has Maliyeti:</span>
                          <span className="calc-value">₺{hasMaliyeti.toFixed(2)}</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">İşçilik Maliyeti:</span>
                          <span className="calc-value">₺{laborMaliyeti.toFixed(2)}</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Oluşturma Tarihi:</span>
                          <span className="calc-value">{tarih}</span>
                        </div>
                      </div>
                    );
                  })()}
                  {(newProduct.metal_type === 'silver' || (newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar')) && newProduct.weight && (() => {
                    const gumusSatis = getSatisRate('SILVER');
                    const usdSatis = getSatisRate('USD');
                    if (!(gumusSatis > 0)) return null;
                    const gram = parseFloat(newProduct.weight) || 0;
                    const purity = parseInt(newProduct.silver_purity) || 925;
                    const laborPerGram = parseFloat(newProduct.labor_cost) || 0;
                    const pureGram = (gram * purity) / 1000;
                    const silverCost = pureGram * gumusSatis;
                    const laborCostTL = gram * laborPerGram * usdSatis;
                    const silverCustom = customRates['SILVER'];
                    const usdCustom = customRates['USD'];
                    const silverSource = (silverCustom?.satis_percentage != null && silverCustom.satis_percentage !== '' && !isNaN(silverCustom.satis_percentage))
                      || silverCustom?.rate_satis != null || silverCustom?.rate != null ? 'manuel' : 'canlı';
                    const usdSource = (usdCustom?.satis_percentage != null && usdCustom.satis_percentage !== '' && !isNaN(usdCustom.satis_percentage))
                      || usdCustom?.rate_satis != null || usdCustom?.rate != null ? 'manuel' : 'canlı';
                    const tarih = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    return (
                      <div className="creation-preview-box silver">
                        <div className="creation-preview-title">Oluşturulacak Kur Bilgisi</div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Gümüş:</span>
                          <span className="calc-value">₺{gumusSatis.toFixed(2)}/gr <small style={{ color: silverSource === 'manuel' ? '#f59e0b' : '#10b981', marginLeft: 4 }}>({silverSource})</small></span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Dolar:</span>
                          <span className="calc-value">₺{usdSatis.toFixed(2)} <small style={{ color: usdSource === 'manuel' ? '#f59e0b' : '#10b981', marginLeft: 4 }}>({usdSource})</small></span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Has Gümüş Maliyeti:</span>
                          <span className="calc-value">₺{silverCost.toFixed(2)}</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">İşçilik Maliyeti:</span>
                          <span className="calc-value">₺{laborCostTL.toFixed(2)}</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Oluşturma Tarihi:</span>
                          <span className="calc-value">{tarih}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Sağ: Tür + Kategori + İsim + Barkod */}
                <div className="edit-top-right">
                  <div className="edit-top-right-fields">
                    {/* Tür + Kategori - yan yana, aynı hizada aynı boyutta */}
                    <div className="form-row type-category-row">
                      <div className="form-section" style={{ marginBottom: 0 }}>
                        <label className="section-label">Tür <span className="required-star">*</span></label>
                        <div className="metal-type-selector compact single-line">
                          {allowedProductTypes.includes('gold') && (
                            <button type="button" className={`metal-btn gold ${newProduct.metal_type === 'gold' ? 'active' : ''}`} onClick={() => handleMetalTypeChange('gold')}>Altın</button>
                          )}
                          {allowedProductTypes.includes('silver') && (
                            <button type="button" className={`metal-btn silver ${newProduct.metal_type === 'silver' ? 'active' : ''}`} onClick={() => handleMetalTypeChange('silver')}>Gümüş</button>
                          )}
                          {allowedProductTypes.includes('zippo') && (
                            <button type="button" className={`metal-btn zippo ${newProduct.metal_type === 'zippo' ? 'active' : ''}`} onClick={() => handleMetalTypeChange('zippo')}>Zippo</button>
                          )}
                          {allowedProductTypes.includes('steel') && (
                            <button type="button" className={`metal-btn steel ${newProduct.metal_type === 'steel' ? 'active' : ''}`} onClick={() => handleMetalTypeChange('steel')}>Çelik</button>
                          )}
                          {allowedProductTypes.includes('brands') && (
                            <button type="button" className={`metal-btn brands ${newProduct.metal_type === 'brands' ? 'active' : ''}`} onClick={() => handleMetalTypeChange('brands')}>Markalar</button>
                          )}
                          {allowedProductTypes.includes('aksesuar') && (
                            <button type="button" className={`metal-btn aksesuar ${newProduct.metal_type === 'aksesuar' ? 'active' : ''}`} onClick={() => handleMetalTypeChange('aksesuar')}>Aksesuar</button>
                          )}
                          {allowedProductTypes.includes('sarf') && (
                            <button type="button" className={`metal-btn sarf ${newProduct.metal_type === 'sarf' ? 'active' : ''}`} onClick={() => handleMetalTypeChange('sarf')}>Sarf Malzemesi</button>
                          )}
                        </div>
                      </div>
                      <div className="form-group">
                        <div className="label-with-action">
                          <label>Kategori <span className="required-star">*</span></label>
                          <div className="fill-last-wrapper">
                            <button type="button" className="btn-fill-last" onClick={fillFromLastProduct} title="Son eklenen ürünün bilgilerini doldur">Son Ürün</button>
                            <button type="button" className="btn-fill-settings" onClick={() => setShowFillSettings(!showFillSettings)} title="Hangi alanların doldurulacağını seç"><Settings size={16} /></button>
                            <button type="button" className="btn-fill-search" onClick={() => setShowProductSearchModal(true)} title="Mevcut üründen bilgi al"><Search size={16} /></button>
                            {showFillSettings && (
                              <div className="fill-settings-dropdown">
                                <div className="fill-settings-header">Doldurulacak Alanlar</div>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.metal_type} onChange={() => toggleFillOption('metal_type')} /><span>Ürün Türü</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.category} onChange={() => toggleFillOption('category')} /><span>Kategori</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.description} onChange={() => toggleFillOption('description')} /><span>Açıklama</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.sell_price} onChange={() => toggleFillOption('sell_price')} /><span>Satış Fiyatı</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.currency} onChange={() => toggleFillOption('currency')} /><span>Para Birimi</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.color} onChange={() => toggleFillOption('color')} /><span>Renk</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.size} onChange={() => toggleFillOption('size')} /><span>Beden</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.weight} onChange={() => toggleFillOption('weight')} /><span>Ağırlık</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.gold_karat} onChange={() => toggleFillOption('gold_karat')} /><span>Altın Ayar</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.silver_purity} onChange={() => toggleFillOption('silver_purity')} /><span>Gümüş Ayar</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.labor_cost} onChange={() => toggleFillOption('labor_cost')} /><span>İşçilik</span></label>
                                {user?.role === 'admin' && (
                                  <label className="fill-option"><input type="checkbox" checked={fillOptions.profit_margin} onChange={() => toggleFillOption('profit_margin')} /><span>Kat Sayı</span></label>
                                )}
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.min_stock} onChange={() => toggleFillOption('min_stock')} /><span>Min. Stok</span></label>
                                <label className="fill-option"><input type="checkbox" checked={fillOptions.gender} onChange={() => toggleFillOption('gender')} /><span>Cinsiyet</span></label>
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <select
                            value={newProduct.category}
                            onChange={(e) => {
                              if (e.target.value === '__new__') {
                                setShowNewCategoryModal(true);
                              } else {
                                const newCategory = e.target.value;
                                const updatedProduct = {...newProduct, category: newCategory};
                                if (newProduct.metal_type === 'aksesuar' && newCategory === 'Gümüş Aksesuar') {
                                  updatedProduct.silver_purity = '1000';
                                } else if (newProduct.metal_type === 'aksesuar') {
                                  updatedProduct.silver_purity = '925';
                                }
                                setNewProduct(updatedProduct);
                              }
                            }}
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            <option value="">Kategori seçin...</option>
                            {getCategoriesForType(newProduct.metal_type).filter(c => c.id !== 'all').map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                            <option value="__new__" style={{ fontWeight: 'bold', color: '#3b82f6' }}>+ Yeni Kategori Ekle</option>
                          </select>
                          {newProduct.category && (
                            <label title="Bu kategorinin tüm ürünleri Online sekmesinde görünür" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                              <input type="checkbox" checked={!!categoryOnlineMap[newProduct.category]} onChange={(e) => setCategoryOnlineFlag(newProduct.category, e.target.checked)} />
                              <span>Online</span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Ürün Adı | Barkod | Marka */}
                    <div className="form-row three-col">
                      <div className="form-group">
                        <label>Ürün Adı <span className="required-star">*</span></label>
                        <input
                          type="text"
                          placeholder=""
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Barkod <span className="required-star">*</span></label>
                        <div className="barcode-input-wrapper">
                          <input
                            type="text"
                            placeholder=""
                            value={newProduct.barcode}
                            onChange={(e) => setNewProduct({...newProduct, barcode: e.target.value})}
                          />
                          <button
                            type="button"
                            className="btn-generate-barcode"
                            onClick={async () => {
                              const barcode = await generateBarcode(newProduct.metal_type);
                              if (barcode) setNewProduct({...newProduct, barcode});
                            }}
                            title="Benzersiz barkod oluştur"
                          >
                            ↻
                          </button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Marka</label>
                        <input
                          type="text"
                          placeholder=""
                          value={newProduct.brand}
                          onChange={(e) => setNewProduct({...newProduct, brand: e.target.value})}
                        />
                      </div>
                    </div>
                    {/* Açıklama | Notlar */}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Açıklama</label>
                        <textarea
                          placeholder=""
                          value={newProduct.description}
                          onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Notlar {newProduct.metal_type === 'silver' && <span style={{ fontSize: '10px', color: '#6b7280' }}>(Gümüş kur)</span>}</label>
                        <textarea
                          placeholder=""
                          value={newProduct.notes}
                          onChange={(e) => setNewProduct({...newProduct, notes: e.target.value})}
                        />
                      </div>
                    </div>
                    {/* Renk | Ölçü | Gramaj | Cinsiyet | Tedarikçi | Özel Tanımlı Ürün | Minimum Stok - tek satırda kompakt */}
                    <div className="form-row compact-many">
                      <div className="form-group">
                        <label>Renk</label>
                        <input
                          type="text"
                          placeholder=""
                          value={newProduct.color}
                          onChange={(e) => setNewProduct({...newProduct, color: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Ölçü / Boyut</label>
                        <input
                          type="text"
                          placeholder=""
                          value={newProduct.size}
                          onChange={(e) => setNewProduct({...newProduct, size: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Taş Rengi</label>
                        <input
                          type="text"
                          placeholder=""
                          value={newProduct.stone_color}
                          onChange={(e) => setNewProduct({...newProduct, stone_color: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Taş Milimi</label>
                        <input
                          type="text"
                          placeholder=""
                          value={newProduct.stone_mm}
                          onChange={(e) => setNewProduct({...newProduct, stone_mm: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>
                          {newProduct.metal_type === 'gold' && newProduct.is_special_defined ? 'Toplam Gramaj (gr)' : 'Gramaj (gr)'}
                          {newProduct.pricing_type === 'formula' && (newProduct.metal_type === 'gold' || newProduct.metal_type === 'silver' || (newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar')) && (
                            <span className="required-star">*</span>
                          )}
                        </label>
                        <input
                          type="number"
                          placeholder=""
                          step="0.001"
                          value={newProduct.weight}
                          onChange={(e) => setNewProduct({...newProduct, weight: e.target.value})}
                        />
                      </div>
                      <div className="form-group gender-field">
                        <label>Cinsiyet</label>
                        <select
                          value={newProduct.gender}
                          onChange={(e) => setNewProduct({...newProduct, gender: e.target.value})}
                        >
                          <option value="">Seçiniz...</option>
                          <option value="erkek">Erkek</option>
                          <option value="kadın">Kadın</option>
                          <option value="unisex">Unisex</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Tedarikçi <span className="required-star">*</span></label>
                        <input
                          type="text"
                          placeholder=""
                          value={newProduct.supplier}
                          onChange={(e) => setNewProduct({...newProduct, supplier: e.target.value})}
                        />
                      </div>
                      {newProduct.metal_type === 'gold' && (
                        <div className="form-group">
                          <label>Özel Tanımlı Ürün</label>
                          <select
                            value={newProduct.is_special_defined ? 'yes' : 'no'}
                            onChange={(e) => setNewProduct({...newProduct, is_special_defined: e.target.value === 'yes'})}
                          >
                            <option value="no">Hayır</option>
                            <option value="yes">Evet</option>
                          </select>
                        </div>
                      )}
                      {user?.role === 'admin' && (
                        <div className="form-group">
                          <label>Minimum Stok</label>
                          <input
                            type="number"
                            placeholder=""
                            value={newProduct.min_stock}
                            onChange={(e) => setNewProduct({...newProduct, min_stock: e.target.value})}
                          />
                        </div>
                      )}
                    </div>

                    {/* Altın Ürünleri için Ayar ve İşçilik */}
                    {newProduct.metal_type === 'gold' && (
                      <div className="form-section gold-pricing-section">
                        <label className="section-label"><Coins size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Altın Fiyat Bilgileri</label>

                        {/* Fiyatlama Tipi Seçici */}
                        <div className="pricing-type-selector">
                          <button
                            type="button"
                            className={`pricing-type-btn formula ${newProduct.pricing_type === 'formula' ? 'active' : ''}`}
                            onClick={() => setNewProduct({...newProduct, pricing_type: 'formula', sell_price: '', currency: 'TRY'})}
                            disabled={!canEditPrice()}
                          >
                            <span className="btn-text">Formül ile Hesapla</span>
                          </button>
                          <button
                            type="button"
                            className={`pricing-type-btn fixed ${newProduct.pricing_type === 'fixed' ? 'active' : ''}`}
                            onClick={() => setNewProduct({...newProduct, pricing_type: 'fixed'})}
                            disabled={!canEditPrice()}
                          >
                            <span className="btn-text">Sabit Fiyat</span>
                          </button>
                        </div>

                        {/* Formül ile hesaplama seçiliyse */}
                        {newProduct.pricing_type === 'formula' && (
                          <>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Ayar <span className="required-star">*</span></label>
                                <select
                                  value={newProduct.gold_karat}
                                  onChange={(e) => setNewProduct({...newProduct, gold_karat: e.target.value})}
                                >
                                  <option value="8">8 Ayar (333)</option>
                                  <option value="9">9 Ayar (375)</option>
                                  <option value="10">10 Ayar (417)</option>
                                  <option value="14">14 Ayar (585)</option>
                                  <option value="18">18 Ayar (750)</option>
                                  <option value="21">21 Ayar (875)</option>
                                  <option value="22">22 Ayar (916)</option>
                                  <option value="24-995">24 Ayar (995)</option>
                                  <option value="24-999">24 Ayar (999)</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label>İşçilik ({karatMultipliers[newProduct.gold_karat] || 585}+) <span className="required-star">*</span> {!canEditLabor() && <span className="no-permission">(Yetki yok)</span>}</label>
                                <PriceInput
                                  value={parseFloat(newProduct.labor_cost) || 0}
                                  onChange={(val) => setNewProduct({...newProduct, labor_cost: val})}
                                  disabled={!canEditLabor()}
                                />
                              </div>
                            </div>
                            {newProduct.weight && (
                              <div className="gold-calculation-preview">
                                <span className="calc-label">Has Gram:</span>
                                <span className="calc-value">
                                  {((karatMultipliers[newProduct.gold_karat] || 0) * parseFloat(newProduct.weight || 0) / 1000).toFixed(3)} gr
                                </span>
                                <span className="calc-label">İşçilikli Gram:</span>
                                <span className="calc-value">
                                  {(((karatMultipliers[newProduct.gold_karat] || 0) + parseFloat(newProduct.labor_cost || 0)) * parseFloat(newProduct.weight || 0) / 1000).toFixed(3)} gr
                                </span>
                                {(() => { const p = calculateGoldPrice(newProduct); return p != null && p > 0 ? (<><span className="calc-label" style={{color:'#92400e', fontWeight:600}}>Tahmini Fiyat:</span><span className="calc-value" style={{color:'#b45309', fontWeight:700}}>{Math.round(p).toLocaleString('tr-TR')} ₺</span></>) : null; })()}
                              </div>
                            )}
                          </>
                        )}

                        {/* Sabit fiyat seçiliyse */}
                        {newProduct.pricing_type === 'fixed' && (
                          <div className="form-row">
                            <div className="form-group">
                              <label>Sabit Fiyat <span className="required-star">*</span></label>
                              <PriceInput
                                value={newProduct.sell_price}
                                onChange={(val) => setNewProduct({...newProduct, sell_price: val})}
                                placeholder=""
                                disabled={!canEditPrice()}
                              />
                            </div>
                            <div className="form-group">
                              <label>Para Birimi <span className="required-star">*</span></label>
                              <select
                                value={newProduct.currency}
                                onChange={(e) => setNewProduct({...newProduct, currency: e.target.value})}
                                disabled={!canEditPrice()}
                              >
                                <option value="TRY">TRY (₺)</option>
                                <option value="USD">USD ($)</option>
                                <option value="EUR">EUR (€)</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Gümüş Ürünleri için Ağırlık ve İşçilik */}
              {(newProduct.metal_type === 'silver' || (newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar')) && (
                <div className="form-section silver-pricing-section">
                  <label className="section-label">🥈 Gümüş Fiyat Bilgileri</label>

                  {/* Fiyatlama Tipi Seçici */}
                  <div className="pricing-type-selector">
                    <button
                      type="button"
                      className={`pricing-type-btn formula ${newProduct.pricing_type === 'formula' ? 'active' : ''}`}
                      onClick={() => setNewProduct({...newProduct, pricing_type: 'formula', sell_price: '', currency: 'TRY'})}
                      disabled={!canEditPrice()}
                    >
                      <span className="btn-text">Formül ile Hesapla</span>
                    </button>
                    <button
                      type="button"
                      className={`pricing-type-btn fixed ${newProduct.pricing_type === 'fixed' ? 'active' : ''}`}
                      onClick={() => setNewProduct({...newProduct, pricing_type: 'fixed'})}
                      disabled={!canEditPrice()}
                    >
                      <span className="btn-text">Sabit Fiyat</span>
                    </button>
                  </div>

                  {/* Formül ile hesaplama seçiliyse */}
                  {newProduct.pricing_type === 'formula' && (
                    <>
                      <div className="form-row compact-many">
                        <div className="form-group">
                          <label>Ayar (Milyem) <span className="required-star">*</span></label>
                          <select
                            value={newProduct.silver_purity}
                            onChange={(e) => setNewProduct({...newProduct, silver_purity: e.target.value})}
                          >
                            <option value="1000">1000 Ayar (Saf Gümüş)</option>
                            <option value="935">935 Ayar</option>
                            <option value="925">925 Ayar (Sterling)</option>
                            <option value="900">900 Ayar</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Ağırlık (gram) <span className="required-star">*</span></label>
                          <input
                            type="number"
                            placeholder=""
                            step="0.01"
                            value={newProduct.weight}
                            onChange={(e) => setNewProduct({...newProduct, weight: e.target.value})}
                          />
                        </div>
                        <div className="form-group">
                          <label>İşçilik Çarpanı <span className="required-star">*</span> {!canEditLabor() && <span className="no-permission">(Yetki yok)</span>}</label>
                          <input
                            type="number"
                            placeholder=""
                            step="0.1"
                            value={newProduct.labor_cost}
                            onChange={(e) => setNewProduct({...newProduct, labor_cost: e.target.value})}
                            disabled={!canEditLabor()}
                          />
                        </div>
                          <div className="form-group">
                            <label>Kat Sayı <span className="required-star">*</span></label>
                            <input
                              type="number"
                              placeholder=""
                              step="0.1"
                              min="0"
                              max="10"
                              value={newProduct.profit_margin}
                              onChange={(e) => setNewProduct({...newProduct, profit_margin: e.target.value})}
                            />
                          </div>
                        <div className="form-group">
                          <label>İşçilik</label>
                          <input
                            type="text"
                            readOnly
                            value={
                              (parseFloat(newProduct.labor_cost) && parseFloat(newProduct.profit_margin))
                                ? (parseFloat(newProduct.labor_cost) * parseFloat(newProduct.profit_margin)).toFixed(2)
                                : '-'
                            }
                            style={{ backgroundColor: '#f1f5f9', cursor: 'default' }}
                          />
                        </div>
                      </div>
                      {newProduct.weight && newProduct.labor_cost && (
                        <div className="silver-calculation-preview">
                          <span className="calc-label">Saf Gümüş:</span>
                          <span className="calc-value">
                            {((parseFloat(newProduct.weight || 0) * parseInt(newProduct.silver_purity || 925)) / 1000).toFixed(3)} gr
                          </span>
                          <span className="calc-label">İşçilik:</span>
                          <span className="calc-value">
                            {(parseFloat(newProduct.weight || 0) * parseFloat(newProduct.labor_cost || 0)).toFixed(2)} $
                          </span>
                          {(() => { const p = calculateSilverPrice(newProduct); return p != null && p > 0 ? (<><span className="calc-label" style={{color:'#0369a1', fontWeight:600}}>Tahmini Fiyat:</span><span className="calc-value" style={{color:'#0369a1', fontWeight:700}}>{Math.round(p).toLocaleString('tr-TR')} ₺</span></>) : null; })()}
                        </div>
                      )}
                    </>
                  )}

                  {/* Sabit fiyat seçiliyse */}
                  {newProduct.pricing_type === 'fixed' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Sabit Fiyat <span className="required-star">*</span></label>
                        <PriceInput
                          value={newProduct.sell_price}
                          onChange={(val) => setNewProduct({...newProduct, sell_price: val})}
                          placeholder=""
                          disabled={!canEditPrice()}
                        />
                      </div>
                      <div className="form-group">
                        <label>Para Birimi <span className="required-star">*</span></label>
                        <select
                          value={newProduct.currency}
                          onChange={(e) => setNewProduct({...newProduct, currency: e.target.value})}
                          disabled={!canEditPrice()}
                        >
                          <option value="TRY">TRY (₺)</option>
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Fiyat ve Para Birimi - Altın, Gümüş ve Gümüş Aksesuar için gizle */}
              {newProduct.metal_type !== 'gold' && newProduct.metal_type !== 'silver' && !(newProduct.metal_type === 'aksesuar' && newProduct.category === 'Gümüş Aksesuar') && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Fiyat <span className="required-star">*</span> {!canEditPrice() && <span className="no-permission">(Yetki yok)</span>}</label>
                    <PriceInput
                      value={newProduct.sell_price}
                      onChange={(val) => setNewProduct({...newProduct, sell_price: val})}
                      placeholder=""
                      disabled={!canEditPrice()}
                      className={!canEditPrice() ? 'disabled-input' : ''}
                    />
                  </div>
                  <div className="form-group">
                    <label>Para Birimi <span className="required-star">*</span></label>
                    <select
                      value={newProduct.currency}
                      onChange={(e) => setNewProduct({...newProduct, currency: e.target.value})}
                    >
                      <option value="TRY">TRY (₺)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Mağaza Seçimi ve Stokları - Admin tüm mağazaları, Depo sadece kendi mağazasını görür */}
              {(user?.role === 'admin' || user?.role === 'depo') && (
                <div className="form-section">
                  <label className="section-label">{user?.role === 'depo' ? 'Mağaza Stoku' : 'Satış Mağazaları'}</label>
                  <div className="store-stocks-grid">
                    {getStoresForMetalType(newProduct.metal_type)
                      .filter(store => user?.role === 'admin' || store.id === user?.store_id)
                      .map(store => (
                      <div key={store.id} className={`store-stock-item ${user?.role === 'depo' ? 'enabled' : (newProduct.enabled_stores[store.id] ? 'enabled' : 'disabled')}`}>
                        {user?.role === 'admin' && (
                          <div className="store-checkbox-row">
                            <label className="store-checkbox-label">
                              <input
                                type="checkbox"
                                checked={newProduct.enabled_stores[store.id] || false}
                                onChange={(e) => setNewProduct({
                                  ...newProduct,
                                  enabled_stores: {
                                    ...newProduct.enabled_stores,
                                    [store.id]: e.target.checked
                                  },
                                  store_stocks: e.target.checked
                                    ? newProduct.store_stocks
                                    : { ...newProduct.store_stocks, [store.id]: '' }
                                })}
                              />
                              <span>{store.name}</span>
                            </label>
                          </div>
                        )}
                        {user?.role === 'depo' && (
                          <div className="store-checkbox-row">
                            <label className="store-checkbox-label">
                              <span>{store.name}</span>
                            </label>
                          </div>
                        )}
                        {(user?.role === 'admin' ? newProduct.enabled_stores[store.id] : true) && (
                          <div className="store-stock-input">
                            <label>Stok:</label>
                            <input
                              type="number"
                              placeholder=""
                              min="0"
                              value={newProduct.store_stocks[store.id] || ''}
                              onChange={(e) => setNewProduct({
                                ...newProduct,
                                store_stocks: {
                                  ...newProduct.store_stocks,
                                  [store.id]: e.target.value
                                }
                              })}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
                  </div>
                </div>
              </div>

              {/* Durum Seçenekleri */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                {/* Aktif */}
                <div onClick={() => setNewProduct({...newProduct, active: !newProduct.active})}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px',
                    borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: newProduct.active ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)' : '#f8fafc',
                    border: newProduct.active ? '2px solid #22c55e' : '2px solid #e2e8f0',
                    boxShadow: newProduct.active ? '0 2px 8px rgba(34,197,94,0.15)' : 'none'
                  }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: newProduct.active ? '#16a34a' : '#cbd5e1', color: 'white', fontSize: '11px'
                  }}>{newProduct.active ? '✓' : '✕'}</div>
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: newProduct.active ? '#166534' : '#94a3b8' }}>Aktif</div>
                    <div style={{ fontSize: '0.65rem', color: newProduct.active ? '#166534' : '#94a3b8' }}>{newProduct.active ? 'Satışta' : 'Pasif'}</div>
                  </div>
                </div>
                {/* Eski Etiket */}
                <div onClick={() => setNewProduct({...newProduct, eski_etiket: !newProduct.eski_etiket})}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px',
                    borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: newProduct.eski_etiket ? 'linear-gradient(135deg, #fef9c3, #fef08a)' : '#f8fafc',
                    border: newProduct.eski_etiket ? '2px solid #eab308' : '2px solid #e2e8f0',
                    boxShadow: newProduct.eski_etiket ? '0 2px 8px rgba(234,179,8,0.15)' : 'none'
                  }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: newProduct.eski_etiket ? '#a16207' : '#cbd5e1', color: 'white', fontSize: '11px', fontWeight: 700
                  }}>E</div>
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: newProduct.eski_etiket ? '#a16207' : '#94a3b8' }}>Eski Etiket</div>
                    <div style={{ fontSize: '0.65rem', color: newProduct.eski_etiket ? '#a16207' : '#94a3b8' }}>{newProduct.eski_etiket ? 'Evet' : 'Hayır'}</div>
                  </div>
                </div>
                {/* İndirim Yapılamaz */}
                <div onClick={() => setNewProduct({...newProduct, no_discount: !newProduct.no_discount})}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px',
                    borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: newProduct.no_discount ? 'linear-gradient(135deg, #fecaca, #fca5a5)' : '#f8fafc',
                    border: newProduct.no_discount ? '2px solid #ef4444' : '2px solid #e2e8f0',
                    boxShadow: newProduct.no_discount ? '0 2px 8px rgba(239,68,68,0.15)' : 'none'
                  }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: newProduct.no_discount ? '#dc2626' : '#cbd5e1', color: 'white', fontSize: '11px', fontWeight: 700
                  }}>%</div>
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: newProduct.no_discount ? '#991b1b' : '#94a3b8' }}>İndirim</div>
                    <div style={{ fontSize: '0.65rem', color: newProduct.no_discount ? '#991b1b' : '#94a3b8' }}>{newProduct.no_discount ? 'Yapılamaz' : 'Yapılabilir'}</div>
                  </div>
                </div>
              </div>

              {/* Pazaryeri Platformları */}
              {canAccessShopify && (
              <>
              <div style={{ fontSize: '0.73rem', fontWeight: 700, color: '#475569', marginTop: '6px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"/><path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9"/><path d="M12 3v6"/></svg>
                Pazaryeri
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {/* Shopify */}
                <div onClick={() => setNewProduct({...newProduct, shopify_active: !newProduct.shopify_active, ecommerce_active: !newProduct.shopify_active || newProduct.trendyol_active})}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: newProduct.shopify_active ? 'linear-gradient(135deg, #e0f7f3, #ccfbf1)' : '#f8fafc',
                    border: newProduct.shopify_active ? '2px solid #14b8a6' : '2px solid #e2e8f0',
                    boxShadow: newProduct.shopify_active ? '0 2px 8px rgba(20,184,166,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/shopify.png" alt="Shopify" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: newProduct.shopify_active ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: newProduct.shopify_active ? '#0f766e' : '#94a3b8' }}>Shopify</div>
                    {newProduct.shopify_active ? (
                      <select value={newProduct.shopify_status || 'active'} onClick={e => e.stopPropagation()}
                        onChange={(e) => setNewProduct({...newProduct, shopify_status: e.target.value})}
                        style={{ fontSize: '0.65rem', border: 'none', background: 'transparent', color: '#0f766e', fontWeight: 500, cursor: 'pointer', padding: 0, outline: 'none' }}>
                        <option value="active">Aktif</option><option value="draft">Taslak</option><option value="archived">Listelenmemiş</option>
                      </select>
                    ) : (<div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Kapalı</div>)}
                  </div>
                </div>
                {/* Trendyol */}
                <div onClick={() => setNewProduct({...newProduct, trendyol_active: !newProduct.trendyol_active, ecommerce_active: newProduct.shopify_active || !newProduct.trendyol_active})}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: newProduct.trendyol_active ? 'linear-gradient(135deg, #fff7ed, #ffedd5)' : '#f8fafc',
                    border: newProduct.trendyol_active ? '2px solid #f97316' : '2px solid #e2e8f0',
                    boxShadow: newProduct.trendyol_active ? '0 2px 8px rgba(249,115,22,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/trendyol.png" alt="Trendyol" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: newProduct.trendyol_active ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: newProduct.trendyol_active ? '#c2410c' : '#94a3b8' }}>Trendyol</div>
                    <div style={{ fontSize: '0.65rem', color: newProduct.trendyol_active ? '#c2410c' : '#94a3b8' }}>{newProduct.trendyol_active ? 'Aktif' : 'Kapalı'}</div>
                  </div>
                </div>
                {/* Hepsiburada */}
                <div onClick={() => setNewProduct({...newProduct, hepsiburada_active: !newProduct.hepsiburada_active})}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: newProduct.hepsiburada_active ? 'linear-gradient(135deg, #fff3e0, #ffe0b2)' : '#f8fafc',
                    border: newProduct.hepsiburada_active ? '2px solid #ff6000' : '2px solid #e2e8f0',
                    boxShadow: newProduct.hepsiburada_active ? '0 2px 8px rgba(255,96,0,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/hepsiburada.png" alt="Hepsiburada" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: newProduct.hepsiburada_active ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: newProduct.hepsiburada_active ? '#e65100' : '#94a3b8' }}>Hepsiburada</div>
                    <div style={{ fontSize: '0.65rem', color: newProduct.hepsiburada_active ? '#e65100' : '#94a3b8' }}>{newProduct.hepsiburada_active ? 'Aktif' : 'Kapalı'}</div>
                  </div>
                </div>
                {/* Amazon */}
                <div onClick={() => setNewProduct({...newProduct, amazon_active: !newProduct.amazon_active})}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: newProduct.amazon_active ? 'linear-gradient(135deg, #fff8e1, #ffecb3)' : '#f8fafc',
                    border: newProduct.amazon_active ? '2px solid #ff9900' : '2px solid #e2e8f0',
                    boxShadow: newProduct.amazon_active ? '0 2px 8px rgba(255,153,0,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/amazon.png" alt="Amazon" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: newProduct.amazon_active ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: newProduct.amazon_active ? '#e65100' : '#94a3b8' }}>Amazon</div>
                    <div style={{ fontSize: '0.65rem', color: newProduct.amazon_active ? '#e65100' : '#94a3b8' }}>{newProduct.amazon_active ? 'Aktif' : 'Kapalı'}</div>
                  </div>
                </div>
                {/* N11 */}
                <div onClick={() => setNewProduct({...newProduct, n11_active: !newProduct.n11_active})}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: newProduct.n11_active ? 'linear-gradient(135deg, #f3e5f5, #e1bee7)' : '#f8fafc',
                    border: newProduct.n11_active ? '2px solid #7b2d8e' : '2px solid #e2e8f0',
                    boxShadow: newProduct.n11_active ? '0 2px 8px rgba(123,45,142,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/n11.png" alt="N11" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: newProduct.n11_active ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: newProduct.n11_active ? '#6a1b9a' : '#94a3b8' }}>N11</div>
                    <div style={{ fontSize: '0.65rem', color: newProduct.n11_active ? '#6a1b9a' : '#94a3b8' }}>{newProduct.n11_active ? 'Aktif' : 'Kapalı'}</div>
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>İptal</button>
              <button className="btn-save" onClick={handleAddProduct}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Product Search Modal */}
      {showProductSearchModal && (
        <div className="modal-overlay">
          <div className="modal medium" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ürün Ara</h2>
              <button className="close-btn" onClick={() => { setShowProductSearchModal(false); setProductSearchQuery(''); }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="product-search-input-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  placeholder="Ürün adı, barkod veya kategori ara..."
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="product-search-results">
                {products
                  .filter(p => {
                    if (!productSearchQuery.trim()) return true;
                    const query = productSearchQuery.toLowerCase();
                    return (
                      p.name?.toLowerCase().includes(query) ||
                      p.barcode?.toLowerCase().includes(query) ||
                      p.category?.toLowerCase().includes(query) ||
                      p.brand?.toLowerCase().includes(query)
                    );
                  })
                  .slice(0, 50)
                  .map(product => (
                    <div
                      key={product.id}
                      className="product-search-item"
                      onClick={() => fillFromSelectedProduct(product)}
                    >
                      <div className="product-search-item-info">
                        <span className="product-search-name">{product.name}</span>
                        <span className="product-search-details">
                          {product.category} {product.barcode && `• ${product.barcode}`}
                        </span>
                      </div>
                      <span className="product-search-price">
                        {product.currency === 'USD' ? '$' : '₺'}{parseFloat(product.sell_price || 0).toLocaleString()}
                      </span>
                    </div>
                  ))
                }
                {products.filter(p => {
                  if (!productSearchQuery.trim()) return true;
                  const query = productSearchQuery.toLowerCase();
                  return (
                    p.name?.toLowerCase().includes(query) ||
                    p.barcode?.toLowerCase().includes(query) ||
                    p.category?.toLowerCase().includes(query) ||
                    p.brand?.toLowerCase().includes(query)
                  );
                }).length === 0 && (
                  <div className="product-search-empty">Ürün bulunamadı</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && editProduct && (
        <div className={`modal-overlay ${closingModal === 'editProduct' ? 'closing' : ''}`}>
          <div className="modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ürün Düzenle</h2>
              <button className="close-btn" onClick={() => handleCloseModal('editProduct', setShowEditModal, () => { setEditProduct(null); setOriginalProduct(null); })}>✕</button>
            </div>
            <div className="modal-body">
              {/* Resim (sol) + Bilgiler (sağ) */}
              <div className="edit-top-row">
                {/* Sol: Ürün Resmi + Butonlar */}
                <div className="edit-product-image-area">
                  <label className="section-label" style={{ display: 'block', marginBottom: '6px' }}>Fotoğraf <span className="required-star">*</span></label>
                  {editProduct.image ? (
                    <>
                      <div className="edit-product-image-card" onClick={() => setShowImageLightbox(true)} title="Büyütmek için tıklayın">
                        <img src={`${IMAGE_HOST}${editProduct.image}`} alt={editProduct.name} />
                      </div>
                      <div className="edit-product-image-buttons">
                        <label className="edit-img-btn change">
                          Değiştir
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(editProduct.id, e.target.files[0])}
                            hidden
                          />
                        </label>
                        <button
                          className="edit-img-btn delete"
                          onClick={() => handleImageDelete(editProduct.id)}
                        >
                          Sil
                        </button>
                      </div>
                    </>
                  ) : (
                    <label className="edit-product-image-empty">
                      {uploadingImage ? (
                        <span className="uploading">Yükleniyor...</span>
                      ) : (
                        <>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#b0bec5" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                          <span>Resim Yükle</span>
                          <span className="hint">JPG, PNG, WEBP (max 20MB)</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(editProduct.id, e.target.files[0])}
                        hidden
                        disabled={uploadingImage}
                      />
                    </label>
                  )}

                  {/* Canlı kur önizlemesi - altın için (kayıtlı creation_rates yok); gümüş için kayıtlı creation_rates varsa onu, yoksa canlı kur önizler */}
                  {editProduct.metal_type === 'gold' && editProduct.weight && editProduct.gold_karat && (() => {
                    const goldRate = getGoldGramPrice();
                    if (!(goldRate > 0)) return null;
                    const gram = parseFloat(editProduct.weight) || 0;
                    const labor = parseFloat(editProduct.labor_cost) || 0;
                    const multiplier = karatMultipliers[editProduct.gold_karat] || 0;
                    const pureGram = (multiplier * gram) / 1000;
                    const laboredGram = ((multiplier + labor) * gram) / 1000;
                    const hasMaliyeti = pureGram * goldRate;
                    const laborMaliyeti = (laboredGram - pureGram) * goldRate;
                    const tarih = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    return (
                      <div className="creation-preview-box gold">
                        <div className="creation-preview-title">Güncel Kur Bilgisi</div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Altın Kuru:</span>
                          <span className="calc-value">₺{goldRate.toFixed(2)}/gr</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Has Maliyeti:</span>
                          <span className="calc-value">₺{hasMaliyeti.toFixed(2)}</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">İşçilik Maliyeti:</span>
                          <span className="calc-value">₺{laborMaliyeti.toFixed(2)}</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Tarih:</span>
                          <span className="calc-value">{tarih}</span>
                        </div>
                      </div>
                    );
                  })()}
                  {(editProduct.metal_type === 'silver' || (editProduct.metal_type === 'aksesuar' && editProduct.category === 'Gümüş Aksesuar')) && (() => {
                    if (editProduct.creation_rates) {
                      const cr = editProduct.creation_rates;
                      return (
                        <div className="creation-preview-box silver">
                          <div className="creation-preview-title">Oluşturulduğundaki Kur Bilgisi</div>
                          <div className="creation-rates-row">
                            <span className="calc-label">Gümüş:</span>
                            <span className="calc-value">₺{parseFloat(cr.silver || 0).toFixed(2)}/gr {cr.rateSource && <small style={{ color: cr.rateSource.silver === 'manuel' ? '#f59e0b' : '#10b981', marginLeft: 4 }}>({cr.rateSource.silver})</small>}</span>
                          </div>
                          <div className="creation-rates-row">
                            <span className="calc-label">Dolar:</span>
                            <span className="calc-value">₺{parseFloat(cr.usd || 0).toFixed(2)} {cr.rateSource && <small style={{ color: cr.rateSource.usd === 'manuel' ? '#f59e0b' : '#10b981', marginLeft: 4 }}>({cr.rateSource.usd})</small>}</span>
                          </div>
                          <div className="creation-rates-row">
                            <span className="calc-label">Has Gümüş Maliyeti:</span>
                            <span className="calc-value">₺{parseFloat(cr.silverCost || 0).toFixed(2)}</span>
                          </div>
                          <div className="creation-rates-row">
                            <span className="calc-label">İşçilik Maliyeti:</span>
                            <span className="calc-value">₺{parseFloat(cr.laborCostTL || 0).toFixed(2)}</span>
                          </div>
                          <div className="creation-rates-row">
                            <span className="calc-label">Oluşturma Tarihi:</span>
                            <span className="calc-value">{cr.date || '-'}</span>
                          </div>
                        </div>
                      );
                    }
                    const gumusSatis = getSatisRate('SILVER');
                    const usdSatis = getSatisRate('USD');
                    if (!(gumusSatis > 0) || !editProduct.weight) return null;
                    const gram = parseFloat(editProduct.weight) || 0;
                    const purity = parseInt(editProduct.silver_purity) || 925;
                    const laborPerGram = parseFloat(editProduct.labor_cost) || 0;
                    const pureGram = (gram * purity) / 1000;
                    const silverCost = pureGram * gumusSatis;
                    const laborCostTL = gram * laborPerGram * usdSatis;
                    const tarih = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    return (
                      <div className="creation-preview-box silver">
                        <div className="creation-preview-title">Güncel Kur Bilgisi</div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Gümüş:</span>
                          <span className="calc-value">₺{gumusSatis.toFixed(2)}/gr</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Dolar:</span>
                          <span className="calc-value">₺{usdSatis.toFixed(2)}</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Has Gümüş Maliyeti:</span>
                          <span className="calc-value">₺{silverCost.toFixed(2)}</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">İşçilik Maliyeti:</span>
                          <span className="calc-value">₺{laborCostTL.toFixed(2)}</span>
                        </div>
                        <div className="creation-rates-row">
                          <span className="calc-label">Tarih:</span>
                          <span className="calc-value">{tarih}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Sağ: tüm alanlar */}
                <div className="edit-top-right">
                  <div className="edit-top-right-fields">
                    {/* Tür + Kategori - yan yana, aynı hizada aynı boyutta */}
                    <div className="form-row type-category-row">
                      <div className="form-section" style={{ marginBottom: 0 }}>
                        <label className="section-label">Tür <span className="required-star">*</span></label>
                        <div className="metal-type-selector compact single-line">
                          <button type="button" className={`metal-btn gold ${editProduct.metal_type === 'gold' ? 'active' : ''}`} onClick={() => handleEditMetalTypeChange('gold')}>Altın</button>
                          <button type="button" className={`metal-btn silver ${editProduct.metal_type === 'silver' ? 'active' : ''}`} onClick={() => handleEditMetalTypeChange('silver')}>Gümüş</button>
                          <button type="button" className={`metal-btn zippo ${editProduct.metal_type === 'zippo' ? 'active' : ''}`} onClick={() => handleEditMetalTypeChange('zippo')}>Zippo</button>
                          <button type="button" className={`metal-btn steel ${editProduct.metal_type === 'steel' ? 'active' : ''}`} onClick={() => handleEditMetalTypeChange('steel')}>Çelik</button>
                          <button type="button" className={`metal-btn brands ${editProduct.metal_type === 'brands' ? 'active' : ''}`} onClick={() => handleEditMetalTypeChange('brands')}>Markalar</button>
                          <button type="button" className={`metal-btn aksesuar ${editProduct.metal_type === 'aksesuar' ? 'active' : ''}`} onClick={() => handleEditMetalTypeChange('aksesuar')}>Aksesuar</button>
                          <button type="button" className={`metal-btn sarf ${editProduct.metal_type === 'sarf' ? 'active' : ''}`} onClick={() => handleEditMetalTypeChange('sarf')}>Sarf Malzemesi</button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Kategori <span className="required-star">*</span></label>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <select
                            value={editProduct.category}
                            onChange={(e) => setEditProduct({...editProduct, category: e.target.value})}
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            {(() => {
                              const opts = getCategoriesForType(editProduct.metal_type).filter(c => c.id !== 'all');
                              // Ürünün mevcut kategorisi listede yoksa (liste henüz yüklenmedi vb.) yine de göster
                              if (editProduct.category && !opts.some(c => c.id === editProduct.category)) {
                                opts.unshift({ id: editProduct.category, name: editProduct.category });
                              }
                              return opts.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                              ));
                            })()}
                          </select>
                          {editProduct.category && (
                            <label title="Bu kategorinin tüm ürünleri Online sekmesinde görünür" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                              <input type="checkbox" checked={!!categoryOnlineMap[editProduct.category]} onChange={(e) => setCategoryOnlineFlag(editProduct.category, e.target.checked)} />
                              <span>Online</span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Ürün Adı + Barkod + Marka */}
                    <div className="form-row three-col">
                      <div className="form-group">
                        <label>Ürün Adı <span className="required-star">*</span></label>
                        <input type="text" value={editProduct.name} onChange={(e) => setEditProduct({...editProduct, name: e.target.value})} />
                      </div>
                      <div className="form-group">
                        <label>Barkod <span className="required-star">*</span></label>
                        <div className="barcode-input-wrapper">
                          <input type="text" value={editProduct.barcode || ''} onChange={(e) => setEditProduct({...editProduct, barcode: e.target.value})} />
                          <button type="button" className="btn-generate-barcode" onClick={async () => { const barcode = await generateBarcode(editProduct.metal_type); if (barcode) setEditProduct({...editProduct, barcode}); }} title="Benzersiz barkod oluştur">↻</button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Marka</label>
                        <input type="text" placeholder="" value={editProduct.brand || ''} onChange={(e) => setEditProduct({...editProduct, brand: e.target.value})} />
                      </div>
                    </div>
                    {/* Açıklama + Notlar */}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Açıklama</label>
                        <textarea placeholder="" value={editProduct.description || ''} onChange={(e) => setEditProduct({...editProduct, description: e.target.value})} />
                      </div>
                      <div className="form-group">
                        <label>Notlar</label>
                        <textarea placeholder="" value={editProduct.notes || ''} onChange={(e) => setEditProduct({...editProduct, notes: e.target.value})} />
                      </div>
                    </div>
                    {/* Renk | Ölçü | Gramaj | Cinsiyet | Tedarikçi | Özel Tanımlı Ürün | Minimum Stok - tek satırda kompakt */}
                    <div className="form-row compact-many">
                      <div className="form-group">
                        <label>Renk</label>
                        <input type="text" placeholder="" value={editProduct.color || ''} onChange={(e) => setEditProduct({...editProduct, color: e.target.value})} />
                      </div>
                      <div className="form-group">
                        <label>Ölçü / Boyut</label>
                        <input type="text" placeholder="" value={editProduct.size || ''} onChange={(e) => setEditProduct({...editProduct, size: e.target.value})} />
                      </div>
                      <div className="form-group">
                        <label>Taş Rengi</label>
                        <input type="text" placeholder="" value={editProduct.stone_color || ''} onChange={(e) => setEditProduct({...editProduct, stone_color: e.target.value})} />
                      </div>
                      <div className="form-group">
                        <label>Taş Milimi</label>
                        <input type="text" placeholder="" value={editProduct.stone_mm || ''} onChange={(e) => setEditProduct({...editProduct, stone_mm: e.target.value})} />
                      </div>
                      <div className="form-group">
                        <label>{editProduct.metal_type === 'gold' && editProduct.is_special_defined ? 'Toplam Gramaj (gr)' : 'Gramaj (gr)'}</label>
                        <input type="number" placeholder="" step="0.001" value={editProduct.weight || ''} onChange={(e) => setEditProduct({...editProduct, weight: e.target.value})} />
                      </div>
                      <div className="form-group gender-field">
                        <label>Cinsiyet</label>
                        <select value={editProduct.gender || ''} onChange={(e) => setEditProduct({...editProduct, gender: e.target.value})}>
                          <option value="">Seçiniz...</option>
                          <option value="erkek">Erkek</option>
                          <option value="kadın">Kadın</option>
                          <option value="unisex">Unisex</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Tedarikçi <span className="required-star">*</span></label>
                        <input type="text" placeholder="" value={editProduct.supplier || ''} onChange={(e) => setEditProduct({...editProduct, supplier: e.target.value})} />
                      </div>
                      {editProduct.metal_type === 'gold' && (
                        <div className="form-group">
                          <label>Özel Tanımlı Ürün</label>
                          <select value={editProduct.is_special_defined ? 'yes' : 'no'} onChange={(e) => setEditProduct({...editProduct, is_special_defined: e.target.value === 'yes'})}>
                            <option value="no">Hayır</option>
                            <option value="yes">Evet</option>
                          </select>
                        </div>
                      )}
                      {user?.role === 'admin' && (
                        <div className="form-group">
                          <label>Minimum Stok</label>
                          <input type="number" placeholder="" value={editProduct.min_stock} onChange={(e) => setEditProduct({...editProduct, min_stock: e.target.value})} />
                        </div>
                      )}
                    </div>

                    {/* Altın Ürünleri için Ayar ve İşçilik */}
              {editProduct.metal_type === 'gold' && (
                <div className="form-section gold-pricing-section">
                  <label className="section-label"><Coins size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Altın Fiyat Bilgileri</label>

                  {/* Fiyatlama Tipi Seçici */}
                  <div className="pricing-type-selector">
                    <button
                      type="button"
                      className={`pricing-type-btn formula ${(editProduct.pricing_type || 'formula') === 'formula' ? 'active' : ''}`}
                      onClick={() => setEditProduct({...editProduct, pricing_type: 'formula', sell_price: '', currency: 'TRY'})}
                      disabled={!canEditPrice()}
                    >
                      <span className="btn-text">Formül ile Hesapla</span>
                    </button>
                    <button
                      type="button"
                      className={`pricing-type-btn fixed ${editProduct.pricing_type === 'fixed' ? 'active' : ''}`}
                      onClick={() => setEditProduct({...editProduct, pricing_type: 'fixed'})}
                      disabled={!canEditPrice()}
                    >
                      <span className="btn-text">Sabit Fiyat</span>
                    </button>
                  </div>

                  {/* Formül ile hesaplama seçiliyse */}
                  {(editProduct.pricing_type || 'formula') === 'formula' && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Ayar <span className="required-star">*</span></label>
                          <select
                            value={editProduct.gold_karat || '14'}
                            onChange={(e) => setEditProduct({...editProduct, gold_karat: e.target.value})}
                          >
                            <option value="8">8 Ayar (333)</option>
                            <option value="9">9 Ayar (375)</option>
                            <option value="10">10 Ayar (417)</option>
                            <option value="14">14 Ayar (585)</option>
                            <option value="18">18 Ayar (750)</option>
                            <option value="21">21 Ayar (875)</option>
                            <option value="22">22 Ayar (916)</option>
                            <option value="24-995">24 Ayar (995)</option>
                            <option value="24-999">24 Ayar (999)</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>İşçilik ({karatMultipliers[editProduct.gold_karat] || 585}+) <span className="required-star">*</span> {!canEditLabor() && <span className="no-permission">(Yetki yok)</span>}</label>
                          <PriceInput
                            value={parseFloat(editProduct.labor_cost) || ''}
                            onChange={(val) => setEditProduct({...editProduct, labor_cost: val})}
                            disabled={!canEditLabor()}
                          />
                        </div>
                      </div>
                      {editProduct.weight && (
                        <div className="gold-calculation-preview">
                          <span className="calc-label">Has Gram:</span>
                          <span className="calc-value">
                            {((karatMultipliers[editProduct.gold_karat] || 0) * parseFloat(editProduct.weight || 0) / 1000).toFixed(3)} gr
                          </span>
                          <span className="calc-label">İşçilikli Gram:</span>
                          <span className="calc-value">
                            {(((karatMultipliers[editProduct.gold_karat] || 0) + parseFloat(editProduct.labor_cost || 0)) * parseFloat(editProduct.weight || 0) / 1000).toFixed(3)} gr
                          </span>
                          {(() => { const p = calculateGoldPrice(editProduct); return p != null && p > 0 ? (<><span className="calc-label" style={{color:'#92400e', fontWeight:600}}>Tahmini Fiyat:</span><span className="calc-value" style={{color:'#b45309', fontWeight:700}}>{Math.round(p).toLocaleString('tr-TR')} ₺</span></>) : null; })()}
                        </div>
                      )}
                    </>
                  )}

                  {/* Sabit fiyat seçiliyse */}
                  {editProduct.pricing_type === 'fixed' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Sabit Fiyat <span className="required-star">*</span></label>
                        <PriceInput
                          value={editProduct.sell_price || ''}
                          onChange={(val) => setEditProduct({...editProduct, sell_price: val})}
                          placeholder=""
                          disabled={!canEditPrice()}
                        />
                      </div>
                      <div className="form-group">
                        <label>Para Birimi <span className="required-star">*</span></label>
                        <select
                          value={editProduct.currency || 'TRY'}
                          onChange={(e) => setEditProduct({...editProduct, currency: e.target.value})}
                          disabled={!canEditPrice()}
                        >
                          <option value="TRY">TRY (₺)</option>
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Gümüş Ürünleri için Ağırlık ve İşçilik */}
              {(editProduct.metal_type === 'silver' || (editProduct.metal_type === 'aksesuar' && editProduct.category === 'Gümüş Aksesuar')) && (
                <div className="form-section silver-pricing-section">
                  <label className="section-label">🥈 Gümüş Fiyat Bilgileri</label>

                  {/* Fiyatlama Tipi Seçici */}
                  <div className="pricing-type-selector">
                    <button
                      type="button"
                      className={`pricing-type-btn formula ${(editProduct.pricing_type || 'formula') === 'formula' ? 'active' : ''}`}
                      onClick={() => setEditProduct({...editProduct, pricing_type: 'formula', sell_price: '', currency: 'TRY'})}
                      disabled={!canEditPrice()}
                    >
                      <span className="btn-text">Formül ile Hesapla</span>
                    </button>
                    <button
                      type="button"
                      className={`pricing-type-btn fixed ${editProduct.pricing_type === 'fixed' ? 'active' : ''}`}
                      onClick={() => setEditProduct({...editProduct, pricing_type: 'fixed'})}
                      disabled={!canEditPrice()}
                    >
                      <span className="btn-text">Sabit Fiyat</span>
                    </button>
                  </div>

                  {/* Formül ile hesaplama seçiliyse */}
                  {(editProduct.pricing_type || 'formula') === 'formula' && (
                    <>
                      <div className="form-row compact-many">
                        <div className="form-group">
                          <label>Ayar (Milyem) <span className="required-star">*</span></label>
                          <select
                            value={editProduct.silver_purity || '925'}
                            onChange={(e) => setEditProduct({...editProduct, silver_purity: e.target.value})}
                          >
                            <option value="1000">1000 Ayar (Saf Gümüş)</option>
                            <option value="935">935 Ayar</option>
                            <option value="925">925 Ayar (Sterling)</option>
                            <option value="900">900 Ayar</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Ağırlık (gram) <span className="required-star">*</span></label>
                          <input
                            type="number"
                            placeholder=""
                            step="0.01"
                            value={editProduct.weight || ''}
                            onChange={(e) => setEditProduct({...editProduct, weight: e.target.value})}
                          />
                        </div>
                        <div className="form-group">
                          <label>İşçilik Çarpanı <span className="required-star">*</span> {!canEditLabor() && <span className="no-permission">(Yetki yok)</span>}</label>
                          <input
                            type="number"
                            placeholder=""
                            step="0.1"
                            value={editProduct.labor_cost && parseFloat(editProduct.labor_cost) !== 0 ? editProduct.labor_cost : ''}
                            onChange={(e) => setEditProduct({...editProduct, labor_cost: e.target.value})}
                            disabled={!canEditLabor()}
                          />
                        </div>
                          <div className="form-group">
                            <label>Kat Sayı <span className="required-star">*</span></label>
                            <input
                              type="number"
                              placeholder=""
                              step="0.1"
                              min="0"
                              max="10"
                              value={editProduct.profit_margin || ''}
                              onChange={(e) => setEditProduct({...editProduct, profit_margin: e.target.value})}
                            />
                          </div>
                        <div className="form-group">
                          <label>İşçilik</label>
                          <input
                            type="text"
                            readOnly
                            value={
                              (parseFloat(editProduct.labor_cost) && parseFloat(editProduct.profit_margin))
                                ? (parseFloat(editProduct.labor_cost) * parseFloat(editProduct.profit_margin)).toFixed(2)
                                : '-'
                            }
                            style={{ backgroundColor: '#f1f5f9', cursor: 'default' }}
                          />
                        </div>
                      </div>
                      {editProduct.weight && editProduct.labor_cost && (
                        <div className="silver-calculation-preview">
                          <span className="calc-label">Saf Gümüş:</span>
                          <span className="calc-value">
                            {((parseFloat(editProduct.weight || 0) * parseInt(editProduct.silver_purity || 925)) / 1000).toFixed(3)} gr
                          </span>
                          <span className="calc-label">İşçilik:</span>
                          <span className="calc-value">
                            {(parseFloat(editProduct.weight || 0) * parseFloat(editProduct.labor_cost || 0)).toFixed(2)} $
                          </span>
                          {(() => { const p = calculateSilverPrice(editProduct); return p != null && p > 0 ? (<><span className="calc-label" style={{color:'#0369a1', fontWeight:600}}>Tahmini Fiyat:</span><span className="calc-value" style={{color:'#0369a1', fontWeight:700}}>{Math.round(p).toLocaleString('tr-TR')} ₺</span></>) : null; })()}
                        </div>
                      )}
                    </>
                  )}

                  {/* Sabit fiyat seçiliyse */}
                  {editProduct.pricing_type === 'fixed' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Sabit Fiyat <span className="required-star">*</span></label>
                        <PriceInput
                          value={editProduct.sell_price || ''}
                          onChange={(val) => setEditProduct({...editProduct, sell_price: val})}
                          placeholder=""
                          disabled={!canEditPrice()}
                        />
                      </div>
                      <div className="form-group">
                        <label>Para Birimi <span className="required-star">*</span></label>
                        <select
                          value={editProduct.currency || 'TRY'}
                          onChange={(e) => setEditProduct({...editProduct, currency: e.target.value})}
                          disabled={!canEditPrice()}
                        >
                          <option value="TRY">TRY (₺)</option>
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Fiyat + Para Birimi - Altın, Gümüş ve Gümüş Aksesuar için gizle */}
              {editProduct.metal_type !== 'gold' && editProduct.metal_type !== 'silver' && !(editProduct.metal_type === 'aksesuar' && editProduct.category === 'Gümüş Aksesuar') && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Fiyat <span className="required-star">*</span> {!canEditPrice() && <span className="no-permission">(Yetki yok)</span>}</label>
                    <PriceInput
                      value={editProduct.sell_price}
                      onChange={(val) => setEditProduct({...editProduct, sell_price: val})}
                      placeholder=""
                      disabled={!canEditPrice()}
                      className={!canEditPrice() ? 'disabled-input' : ''}
                    />
                  </div>
                  <div className="form-group">
                    <label>Para Birimi <span className="required-star">*</span></label>
                    <select
                      value={editProduct.currency || 'TRY'}
                      onChange={(e) => setEditProduct({...editProduct, currency: e.target.value})}
                    >
                      <option value="TRY">TRY (₺)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Mağaza Seçimi ve Stokları - Admin tüm mağazaları, Depo sadece kendi mağazasını görür */}
              {(user?.role === 'admin' || user?.role === 'depo') && (
                <div className="form-section">
                  <label className="section-label">{user?.role === 'depo' ? 'Mağaza Stoku' : 'Satış Mağazaları'}</label>
                  <div className="store-stocks-grid">
                    {getStoresForMetalType(editProduct.metal_type)
                      .filter(store => user?.role === 'admin' || store.id === user?.store_id)
                      .map(store => (
                      <div key={store.id} className={`store-stock-item ${user?.role === 'depo' ? 'enabled' : (editProduct.enabled_stores?.[store.id] ? 'enabled' : 'disabled')}`}>
                        {user?.role === 'admin' && (
                          <div className="store-checkbox-row">
                            <label className="store-checkbox-label">
                              <input
                                type="checkbox"
                                checked={editProduct.enabled_stores?.[store.id] || false}
                                onChange={(e) => setEditProduct({
                                  ...editProduct,
                                  enabled_stores: {
                                    ...editProduct.enabled_stores,
                                    [store.id]: e.target.checked
                                  },
                                  store_stocks: e.target.checked
                                    ? editProduct.store_stocks
                                    : { ...editProduct.store_stocks, [store.id]: '' }
                                })}
                              />
                              <span>{store.name}</span>
                            </label>
                          </div>
                        )}
                        {user?.role === 'depo' && (
                          <div className="store-checkbox-row">
                            <label className="store-checkbox-label">
                              <span>{store.name}</span>
                            </label>
                          </div>
                        )}
                        {(user?.role === 'admin' ? editProduct.enabled_stores?.[store.id] : true) && (
                          <div className="store-stock-input">
                            <label>Stok:</label>
                            <input
                              type="number"
                              placeholder=""
                              min="0"
                              value={editProduct.store_stocks?.[store.id] || ''}
                              onChange={(e) => setEditProduct({
                                ...editProduct,
                                store_stocks: {
                                  ...editProduct.store_stocks,
                                  [store.id]: e.target.value
                                }
                              })}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
                  </div>
                </div>
              </div>

              {/* Durum Seçenekleri */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                {/* Aktif */}
                <div onClick={() => setEditProduct(prev => ({...prev, active: !(prev.active === true || prev.active === 1)}))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px',
                    borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: (editProduct.active === true || editProduct.active === 1) ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)' : '#f8fafc',
                    border: (editProduct.active === true || editProduct.active === 1) ? '2px solid #22c55e' : '2px solid #e2e8f0',
                    boxShadow: (editProduct.active === true || editProduct.active === 1) ? '0 2px 8px rgba(34,197,94,0.15)' : 'none'
                  }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: (editProduct.active === true || editProduct.active === 1) ? '#16a34a' : '#cbd5e1', color: 'white', fontSize: '13px'
                  }}>{(editProduct.active === true || editProduct.active === 1) ? '✓' : '✕'}</div>
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: (editProduct.active === true || editProduct.active === 1) ? '#166534' : '#94a3b8' }}>Aktif</div>
                    <div style={{ fontSize: '0.65rem', color: (editProduct.active === true || editProduct.active === 1) ? '#166534' : '#94a3b8' }}>{(editProduct.active === true || editProduct.active === 1) ? 'Satışta' : 'Pasif'}</div>
                  </div>
                </div>
                {/* Eski Etiket */}
                <div onClick={() => setEditProduct(prev => ({...prev, eski_etiket: !(prev.eski_etiket === true || prev.eski_etiket === 1)}))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px',
                    borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: (editProduct.eski_etiket === true || editProduct.eski_etiket === 1) ? 'linear-gradient(135deg, #fef9c3, #fef08a)' : '#f8fafc',
                    border: (editProduct.eski_etiket === true || editProduct.eski_etiket === 1) ? '2px solid #eab308' : '2px solid #e2e8f0',
                    boxShadow: (editProduct.eski_etiket === true || editProduct.eski_etiket === 1) ? '0 2px 8px rgba(234,179,8,0.15)' : 'none'
                  }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: (editProduct.eski_etiket === true || editProduct.eski_etiket === 1) ? '#a16207' : '#cbd5e1', color: 'white', fontSize: '12px', fontWeight: 700
                  }}>E</div>
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: (editProduct.eski_etiket === true || editProduct.eski_etiket === 1) ? '#a16207' : '#94a3b8' }}>Eski Etiket</div>
                    <div style={{ fontSize: '0.65rem', color: (editProduct.eski_etiket === true || editProduct.eski_etiket === 1) ? '#a16207' : '#94a3b8' }}>{(editProduct.eski_etiket === true || editProduct.eski_etiket === 1) ? 'Evet' : 'Hayır'}</div>
                  </div>
                </div>
                {/* İndirim Yapılamaz */}
                <div onClick={() => setEditProduct(prev => ({...prev, no_discount: !(prev.no_discount === true || prev.no_discount === 1)}))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px',
                    borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: (editProduct.no_discount === true || editProduct.no_discount === 1) ? 'linear-gradient(135deg, #fecaca, #fca5a5)' : '#f8fafc',
                    border: (editProduct.no_discount === true || editProduct.no_discount === 1) ? '2px solid #ef4444' : '2px solid #e2e8f0',
                    boxShadow: (editProduct.no_discount === true || editProduct.no_discount === 1) ? '0 2px 8px rgba(239,68,68,0.15)' : 'none'
                  }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: (editProduct.no_discount === true || editProduct.no_discount === 1) ? '#dc2626' : '#cbd5e1', color: 'white', fontSize: '12px', fontWeight: 700
                  }}>%</div>
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: (editProduct.no_discount === true || editProduct.no_discount === 1) ? '#991b1b' : '#94a3b8' }}>İndirim</div>
                    <div style={{ fontSize: '0.65rem', color: (editProduct.no_discount === true || editProduct.no_discount === 1) ? '#991b1b' : '#94a3b8' }}>{(editProduct.no_discount === true || editProduct.no_discount === 1) ? 'Yapılamaz' : 'Yapılabilir'}</div>
                  </div>
                </div>
              </div>

              {/* Pazaryeri Platformları */}
              {canAccessShopify && (
              <>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginTop: '10px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"/><path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9"/><path d="M12 3v6"/></svg>
                Pazaryeri
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {/* Shopify */}
                {editProductVariantGroupInfo ? (
                  <div
                    onClick={() => toast.info(editProductVariantGroupInfo.linkMode === 'merged'
                      ? `Bu ürün "${editProductVariantGroupInfo.groupName}" varyant grubunun üyesi. Yönetmek için Varyant Grupları ekranını kullanın.`
                      : `Bu ürün Shopify'da bağımsız kalır, "${editProductVariantGroupInfo.groupName}" grubundaki diğer ürünlere bağlı gösterilir. Yönetmek için Varyant Grupları ekranını kullanın.`)}
                    title={editProductVariantGroupInfo.linkMode === 'merged' ? 'Varyant grubunun üyesi' : 'Bağlı ürün grubunun üyesi (Shopify\'da bağımsız kalır)'}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                      background: 'linear-gradient(135deg, #e0f7f3, #ccfbf1)', border: '2px solid #14b8a6' }}>
                    <img src="/marketplace-logos/shopify.png" alt="Shopify" style={{ width: '22px', height: '22px', borderRadius: '6px' }} />
                    <div>
                      <div style={{ fontSize: '0.73rem', fontWeight: 600, color: '#0f766e' }}>Shopify</div>
                      <div style={{ fontSize: '0.65rem', color: '#0f766e' }}>
                        {editProductVariantGroupInfo.linkMode === 'merged' ? 'Grupta' : 'Bağlı'}: {editProductVariantGroupInfo.groupName}
                      </div>
                    </div>
                  </div>
                ) : (
                <div onClick={() => setEditProduct(prev => ({...prev, shopify_active: !(prev.shopify_active === true || prev.shopify_active === 1), ecommerce_active: !(prev.shopify_active === true || prev.shopify_active === 1) || prev.trendyol_active}))}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: (editProduct.shopify_active === true || editProduct.shopify_active === 1) ? 'linear-gradient(135deg, #e0f7f3, #ccfbf1)' : '#f8fafc',
                    border: (editProduct.shopify_active === true || editProduct.shopify_active === 1) ? '2px solid #14b8a6' : '2px solid #e2e8f0',
                    boxShadow: (editProduct.shopify_active === true || editProduct.shopify_active === 1) ? '0 2px 8px rgba(20,184,166,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/shopify.png" alt="Shopify" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: (editProduct.shopify_active === true || editProduct.shopify_active === 1) ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: (editProduct.shopify_active === true || editProduct.shopify_active === 1) ? '#0f766e' : '#94a3b8' }}>Shopify</div>
                    {(editProduct.shopify_active === true || editProduct.shopify_active === 1) ? (
                      <select value={editProduct.shopify_status || 'active'} onClick={e => e.stopPropagation()}
                        onChange={(e) => setEditProduct(prev => ({...prev, shopify_status: e.target.value}))}
                        style={{ fontSize: '0.65rem', border: 'none', background: 'transparent', color: '#0f766e', fontWeight: 500, cursor: 'pointer', padding: 0, outline: 'none' }}>
                        <option value="active">Aktif</option><option value="draft">Taslak</option><option value="archived">Listelenmemiş</option>
                      </select>
                    ) : (<div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Kapalı</div>)}
                  </div>
                </div>
                )}
                {/* Trendyol */}
                <div onClick={() => setEditProduct(prev => ({...prev, trendyol_active: !(prev.trendyol_active === true || prev.trendyol_active === 1), ecommerce_active: prev.shopify_active || !(prev.trendyol_active === true || prev.trendyol_active === 1)}))}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: (editProduct.trendyol_active === true || editProduct.trendyol_active === 1) ? 'linear-gradient(135deg, #fff7ed, #ffedd5)' : '#f8fafc',
                    border: (editProduct.trendyol_active === true || editProduct.trendyol_active === 1) ? '2px solid #f97316' : '2px solid #e2e8f0',
                    boxShadow: (editProduct.trendyol_active === true || editProduct.trendyol_active === 1) ? '0 2px 8px rgba(249,115,22,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/trendyol.png" alt="Trendyol" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: (editProduct.trendyol_active === true || editProduct.trendyol_active === 1) ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: (editProduct.trendyol_active === true || editProduct.trendyol_active === 1) ? '#c2410c' : '#94a3b8' }}>Trendyol</div>
                    <div style={{ fontSize: '0.65rem', color: (editProduct.trendyol_active === true || editProduct.trendyol_active === 1) ? '#c2410c' : '#94a3b8' }}>{(editProduct.trendyol_active === true || editProduct.trendyol_active === 1) ? 'Aktif' : 'Kapalı'}</div>
                  </div>
                </div>
                {/* Hepsiburada */}
                <div onClick={() => setEditProduct(prev => ({...prev, hepsiburada_active: !(prev.hepsiburada_active === true || prev.hepsiburada_active === 1)}))}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: (editProduct.hepsiburada_active === true || editProduct.hepsiburada_active === 1) ? 'linear-gradient(135deg, #fff3e0, #ffe0b2)' : '#f8fafc',
                    border: (editProduct.hepsiburada_active === true || editProduct.hepsiburada_active === 1) ? '2px solid #ff6000' : '2px solid #e2e8f0',
                    boxShadow: (editProduct.hepsiburada_active === true || editProduct.hepsiburada_active === 1) ? '0 2px 8px rgba(255,96,0,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/hepsiburada.png" alt="Hepsiburada" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: (editProduct.hepsiburada_active === true || editProduct.hepsiburada_active === 1) ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: (editProduct.hepsiburada_active === true || editProduct.hepsiburada_active === 1) ? '#e65100' : '#94a3b8' }}>Hepsiburada</div>
                    <div style={{ fontSize: '0.65rem', color: (editProduct.hepsiburada_active === true || editProduct.hepsiburada_active === 1) ? '#e65100' : '#94a3b8' }}>{(editProduct.hepsiburada_active === true || editProduct.hepsiburada_active === 1) ? 'Aktif' : 'Kapalı'}</div>
                  </div>
                </div>
                {/* Amazon */}
                <div onClick={() => setEditProduct(prev => ({...prev, amazon_active: !(prev.amazon_active === true || prev.amazon_active === 1)}))}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: (editProduct.amazon_active === true || editProduct.amazon_active === 1) ? 'linear-gradient(135deg, #fff8e1, #ffecb3)' : '#f8fafc',
                    border: (editProduct.amazon_active === true || editProduct.amazon_active === 1) ? '2px solid #ff9900' : '2px solid #e2e8f0',
                    boxShadow: (editProduct.amazon_active === true || editProduct.amazon_active === 1) ? '0 2px 8px rgba(255,153,0,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/amazon.png" alt="Amazon" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: (editProduct.amazon_active === true || editProduct.amazon_active === 1) ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: (editProduct.amazon_active === true || editProduct.amazon_active === 1) ? '#e65100' : '#94a3b8' }}>Amazon</div>
                    <div style={{ fontSize: '0.65rem', color: (editProduct.amazon_active === true || editProduct.amazon_active === 1) ? '#e65100' : '#94a3b8' }}>{(editProduct.amazon_active === true || editProduct.amazon_active === 1) ? 'Aktif' : 'Kapalı'}</div>
                  </div>
                </div>
                {/* N11 */}
                <div onClick={() => setEditProduct(prev => ({...prev, n11_active: !(prev.n11_active === true || prev.n11_active === 1)}))}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s', flex: 1,
                    background: (editProduct.n11_active === true || editProduct.n11_active === 1) ? 'linear-gradient(135deg, #f3e5f5, #e1bee7)' : '#f8fafc',
                    border: (editProduct.n11_active === true || editProduct.n11_active === 1) ? '2px solid #7b2d8e' : '2px solid #e2e8f0',
                    boxShadow: (editProduct.n11_active === true || editProduct.n11_active === 1) ? '0 2px 8px rgba(123,45,142,0.15)' : 'none' }}>
                  <img src="/marketplace-logos/n11.png" alt="N11" style={{ width: '22px', height: '22px', borderRadius: '6px', opacity: (editProduct.n11_active === true || editProduct.n11_active === 1) ? 1 : 0.4 }} />
                  <div>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: (editProduct.n11_active === true || editProduct.n11_active === 1) ? '#6a1b9a' : '#94a3b8' }}>N11</div>
                    <div style={{ fontSize: '0.65rem', color: (editProduct.n11_active === true || editProduct.n11_active === 1) ? '#6a1b9a' : '#94a3b8' }}>{(editProduct.n11_active === true || editProduct.n11_active === 1) ? 'Aktif' : 'Kapalı'}</div>
                  </div>
                </div>
              </div>
              </>
              )}

              {/* Set/Paket Bileşenleri */}
              <div className="set-components-section">
                <h3 style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Package size={18} /> Set/Paket Bileşenleri
                </h3>
                <div className="set-component-search" style={{ position: 'relative', marginBottom: '10px' }}>
                  <input
                    type="text"
                    placeholder="Bileşen ara (isim veya barkod)..."
                    value={setComponentSearch}
                    onChange={(e) => {
                      setSetComponentSearch(e.target.value);
                      if (setComponentSearchTimeout.current) clearTimeout(setComponentSearchTimeout.current);
                      setComponentSearchTimeout.current = setTimeout(() => searchSetComponents(e.target.value), 300);
                    }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
                  />
                  {setComponentResults.length > 0 && (
                    <div className="set-component-results" style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                      background: '#fff', border: '1px solid #ddd', borderRadius: '6px',
                      maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                      {setComponentResults.map(p => {
                        const imgSrc = p.image ? (p.image.startsWith('http') ? p.image : `${IMAGE_HOST}${p.image}`) : null;
                        return (
                        <div key={p.id}
                          onClick={() => addSetComponent(p)}
                          style={{
                            padding: '8px 12px', cursor: 'pointer', display: 'flex',
                            alignItems: 'center', gap: '8px', borderBottom: '1px solid #f0f0f0'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#f5f5f5'}
                          onMouseOut={(e) => e.currentTarget.style.background = '#fff'}
                        >
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '6px', overflow: 'hidden',
                            background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, border: '1px solid #e2e8f0'
                          }}>
                            {imgSrc ? (
                              <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: '16px' }}>📦</span>
                            )}
                          </div>
                          <span style={{ flex: 1 }}>{p.name}</span>
                          <span style={{ color: '#999', fontSize: '12px' }}>{p.barcode || '-'}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: p.total_stock > 0 ? '#4caf50' : '#e53935', minWidth: '40px', textAlign: 'right' }}>{p.total_stock} stok</span>
                          <Plus size={16} style={{ color: '#4caf50' }} />
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {loadingSetComponents ? (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#999' }}>Yükleniyor...</div>
                ) : setComponents.length === 0 ? (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                    Bileşen eklenmemiş. Bu ürünü set yapmak için yukarıdan bileşen arayın.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                        <th style={{ width: '52px', padding: '6px 8px' }}></th>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Bileşen</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', width: '100px' }}>Barkod</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', width: '60px' }}>Stok</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', width: '70px' }}>Adet</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', width: '80px' }}>Katkı</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {setComponents.map(comp => {
                        const compImg = comp.image ? (comp.image.startsWith('http') ? comp.image : `${IMAGE_HOST}${comp.image}`) : null;
                        return (
                        <tr key={comp.component_product_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '6px 8px' }}>
                            <div
                              onClick={() => { if (compImg) setLightboxImageUrl(compImg); }}
                              style={{
                                width: '40px', height: '40px', borderRadius: '6px', overflow: 'hidden',
                                background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid #e2e8f0', cursor: compImg ? 'pointer' : 'default'
                              }}
                              title={compImg ? 'Büyütmek için tıklayın' : ''}
                            >
                              {compImg ? (
                                <img src={compImg} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <span style={{ fontSize: '18px' }}>📦</span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '6px 8px' }}>{comp.name}</td>
                          <td style={{ padding: '6px 8px', color: '#666', fontSize: '12px' }}>{comp.barcode || '-'}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, color: comp.total_stock > 0 ? '#4caf50' : '#e53935' }}>
                            {comp.total_stock ?? 0}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <input
                              type="number"
                              min="1"
                              value={comp.quantity}
                              onChange={(e) => updateSetComponentQuantity(comp.component_product_id, e.target.value)}
                              style={{ width: '50px', textAlign: 'center', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                            {Math.floor((comp.total_stock ?? 0) / (comp.quantity || 1))} set
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              onClick={() => removeSetComponent(comp.component_product_id)}
                              style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', padding: '4px' }}
                              title="Bileşeni kaldır"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                      <tr style={{ borderTop: '2px solid #e0e0e0', background: '#f0f7ff' }}>
                        <td colSpan="5" style={{ padding: '8px', fontWeight: 700, textAlign: 'right' }}>
                          Shopify Set Stoğu (MIN):
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', fontWeight: 700, fontSize: '14px', color: Math.min(...setComponents.map(c => Math.floor((c.total_stock ?? 0) / (c.quantity || 1)))) > 0 ? '#4caf50' : '#e53935' }}>
                          {Math.min(...setComponents.map(c => Math.floor((c.total_stock ?? 0) / (c.quantity || 1))))} set
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
                {setComponents.length >= 2 && (
                  <button
                    type="button"
                    className="btn secondary small"
                    style={{ marginTop: '10px' }}
                    onClick={() => openVariantGroupEditorFromSetComponents(editProduct, setComponents)}
                  >
                    <Layers size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Bu Bileşenlerden Varyant Grubu Oluştur
                  </button>
                )}
              </div>

              {!editProduct.is_set && !editProductVariantGroupInfo && (
                <div className="set-components-section" style={{ marginTop: '18px' }}>
                  <h3 style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Layers size={18} /> Sanal Varyant Seçenekleri (Stoksuz)
                  </h3>
                  <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '10px' }}>
                    Bu ürünün stoğunu paylaşan, ayrı barkodu olmayan ek Shopify seçenekleri (örn. bu gümüş
                    yüzük Shopify'da "Altın Rengi" olarak da satılabilsin, hangisi satılırsa aynı stoktan düşsün).
                    Sipariş geldiğinde ürünü elle özelleştirip gönderirsiniz.
                  </p>
                  {loadingVirtualVariants ? (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#999' }}>Yükleniyor...</div>
                  ) : virtualVariants.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                      {virtualVariants.map(vv => (
                        <div key={vv.id} style={{
                          display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px',
                          border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem'
                        }}>
                          <span style={{ flex: 1 }}>
                            <strong>{variantAxisLabel(vv.axis_key)}:</strong> {vv.value}
                            {!vv.shopify_variant_id && (
                              <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#b45309' }}>(Shopify'a gönderilmedi)</span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveVirtualVariant(vv.id)}
                            style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', padding: '4px' }}
                            title="Kaldır"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.78rem' }}>Eksen</label>
                      <select
                        value={newVirtualVariantAxis}
                        onChange={(e) => setNewVirtualVariantAxis(e.target.value)}
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                      >
                        {VARIANT_AXIS_OPTIONS.map(opt => (
                          <option key={opt.key} value={opt.key}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: 1 }}>
                      <label style={{ fontSize: '0.78rem' }}>Değer</label>
                      <input
                        type="text"
                        placeholder="Örn: Altın Rengi"
                        value={newVirtualVariantValue}
                        onChange={(e) => setNewVirtualVariantValue(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn secondary small"
                      disabled={savingVirtualVariant}
                      onClick={handleAddVirtualVariant}
                    >
                      <Plus size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      Ekle
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowEditModal(false); setEditProduct(null); setOriginalProduct(null); setShowImageLightbox(false); }}>İptal</button>
              <button className="btn-save" onClick={handleEditProduct}>Güncelle</button>
            </div>
          </div>
        </div>
      )}

      {/* Resim Lightbox */}
      {showImageLightbox && editProduct?.image && (
        <div className="image-lightbox-overlay" onClick={() => setShowImageLightbox(false)}>
          <button className="image-lightbox-close" onClick={() => setShowImageLightbox(false)}>✕</button>
          <img
            src={`${IMAGE_HOST}${editProduct.image}`}
            alt={editProduct.name}
            className="image-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Generic Image Lightbox */}
      {lightboxImageUrl && (
        <div className="image-lightbox-overlay" style={{ zIndex: 10000 }} onClick={() => setLightboxImageUrl(null)}>
          <button className="image-lightbox-close" onClick={() => setLightboxImageUrl(null)}>✕</button>
          <img
            src={lightboxImageUrl}
            alt="Ürün"
            className="image-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Stock History Modal */}
      {showStockHistoryModal && selectedProduct && (
        <div className={`modal-overlay ${closingModal === 'stockHistory' ? 'closing' : ''}`}>
          <div className="modal large" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div className="modal-header" style={{ flexShrink: 0 }}>
              <h2><ScrollText size={20} style={{ marginRight: '8px' }} /> {selectedProduct.name} - Stok Geçmişi</h2>
              <button className="close-btn" onClick={() => handleCloseModal('stockHistory', setShowStockHistoryModal)}>✕</button>
            </div>
            {/* Admin için mağaza sekmeleri */}
            {user?.role === 'admin' && (
              <div style={{
                display: 'flex',
                gap: '0',
                background: '#f8fafc',
                borderBottom: '2px solid #e2e8f0',
                padding: '0 16px',
                overflowX: 'auto',
                flexShrink: 0
              }}>
                {stores.filter(s => s.id !== 1).map(store => (
                  <button
                    key={store.id}
                    onClick={() => {
                      setStockHistoryStoreId(store.id);
                      setStockHistory([]);
                      fetchStockHistory(selectedProduct.id, store.id);
                    }}
                    style={{
                      padding: '8px 18px',
                      border: 'none',
                      borderBottom: stockHistoryStoreId === store.id ? '3px solid #6366f1' : '3px solid transparent',
                      background: stockHistoryStoreId === store.id ? '#eef2ff' : 'transparent',
                      color: stockHistoryStoreId === store.id ? '#4f46e5' : '#64748b',
                      fontWeight: stockHistoryStoreId === store.id ? '700' : '500',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s'
                    }}
                  >
                    {store.name}
                  </button>
                ))}
              </div>
            )}
            {/* Özet istatistikler */}
            {!loadingStockHistory && stockHistory.length > 0 && (() => {
              const totalIn = stockHistory.filter(m => m.quantity > 0 && ['manual', 'transfer_in', 'transfer_cancel', 'purchase', 'excel_import', 'refund', 'exchange_in', 'sale_delete', 'sale_cancel', 'stock_adjustment', 'inventory_count', 'repair_in'].includes(m.movement_type)).reduce((sum, m) => sum + m.quantity, 0);
              const totalSold = Math.abs(stockHistory.filter(m => m.movement_type === 'sale').reduce((sum, m) => sum + m.quantity, 0));
              const totalTransferOut = Math.abs(stockHistory.filter(m => m.movement_type === 'transfer_out' || m.movement_type === 'exchange_out').reduce((sum, m) => sum + m.quantity, 0));
              const totalManualSubtract = Math.abs(stockHistory.filter(m => m.quantity < 0 && ['manual', 'stock_adjustment', 'inventory_count'].includes(m.movement_type)).reduce((sum, m) => sum + m.quantity, 0));
              const currentStoreName = stores.find(s => s.id === stockHistoryStoreId)?.name || '';
              return (
                <div style={{ display: 'flex', gap: '10px', padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexShrink: 0, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '100px', padding: '8px 12px', background: '#dcfce7', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#166534', marginBottom: '2px' }}>Toplam Giriş</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#15803d' }}>+{totalIn}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '100px', padding: '8px 12px', background: '#fee2e2', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#991b1b', marginBottom: '2px' }}>Toplam Satış</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#dc2626' }}>-{totalSold}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '100px', padding: '8px 12px', background: '#fce7f3', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#9d174d', marginBottom: '2px' }}>Stok Eksileme</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#db2777' }}>-{totalManualSubtract}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '100px', padding: '8px 12px', background: '#ffedd5', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#9a3412', marginBottom: '2px' }}>Transfer/Değişim Çıkış</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#ea580c' }}>-{totalTransferOut}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '100px', padding: '8px 12px', background: '#e0e7ff', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#3730a3', marginBottom: '2px' }}>Mevcut Stok ({currentStoreName.replace('Akik Gümüş ', '')})</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#4f46e5' }}>{stockHistory.length > 0 ? stockHistory[0].stock_after : '-'}</div>
                  </div>
                </div>
              );
            })()}
            <div className="modal-body" style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              {loadingStockHistory ? (
                <div className="loading">Yükleniyor...</div>
              ) : stockHistory.length === 0 ? (
                <div className="empty-state">
                  <span><Package size={48} /></span>
                  <p>Bu ürün için henüz stok hareketi kaydı yok</p>
                </div>
              ) : (
                <div className="sales-history-list">
                  <table className="sales-history-table">
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>İşlem</th>
                        <th>Önceki Stok</th>
                        <th>Miktar</th>
                        <th>Sonraki Stok</th>
                        <th>Müşteri</th>
                        <th>Personel</th>
                        <th>Açıklama</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockHistory.map((movement, idx) => (
                        <tr key={idx}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {new Date(movement.created_at).toLocaleDateString('tr-TR')} {new Date(movement.created_at).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})}
                          </td>
                          <td>
                            <span className={`stock-movement-badge ${movement.movement_type}`} style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: movement.movement_type === 'sale' ? '#fee2e2' :
                                         movement.movement_type === 'refund' ? '#dcfce7' :
                                         movement.movement_type === 'exchange_in' ? '#d1fae5' :
                                         movement.movement_type === 'exchange_out' ? '#fef3c7' :
                                         movement.movement_type === 'manual' ? '#e0e7ff' :
                                         movement.movement_type === 'sale_delete' ? '#bbf7d0' :
                                         movement.movement_type === 'sale_cancel' ? '#fef3c7' :
                                         movement.movement_type === 'inventory_count' ? '#fef9c3' :
                                         movement.movement_type === 'excel_import' ? '#dbeafe' :
                                         movement.movement_type === 'transfer_in' ? '#ccfbf1' :
                                         movement.movement_type === 'transfer_out' ? '#ffedd5' :
                                         movement.movement_type === 'purchase' ? '#d1fae5' :
                                         movement.movement_type === 'stock_adjustment' ? '#f3e8ff' :
                                         movement.movement_type === 'repair_out' ? '#fef3c7' :
                                         movement.movement_type === 'repair_in' ? '#d1fae5' : '#f3f4f6',
                              color: movement.movement_type === 'sale' ? '#dc2626' :
                                    movement.movement_type === 'refund' ? '#16a34a' :
                                    movement.movement_type === 'exchange_in' ? '#059669' :
                                    movement.movement_type === 'exchange_out' ? '#d97706' :
                                    movement.movement_type === 'manual' ? '#4f46e5' :
                                    movement.movement_type === 'sale_delete' ? '#15803d' :
                                    movement.movement_type === 'sale_cancel' ? '#b45309' :
                                    movement.movement_type === 'inventory_count' ? '#ca8a04' :
                                    movement.movement_type === 'excel_import' ? '#2563eb' :
                                    movement.movement_type === 'transfer_in' ? '#0f766e' :
                                    movement.movement_type === 'transfer_out' ? '#ea580c' :
                                    movement.movement_type === 'purchase' ? '#059669' :
                                    movement.movement_type === 'stock_adjustment' ? '#9333ea' :
                                    movement.movement_type === 'repair_out' ? '#d97706' :
                                    movement.movement_type === 'repair_in' ? '#059669' :
                                    movement.movement_type === 'transfer_cancel' ? '#b91c1c' :
                                    movement.movement_type === 'stock_deduction' ? '#9d174d' : '#374151'
                            }}>
                              {movement.movement_type_label}
                            </span>
                          </td>
                          <td style={{ color: '#6b7280' }}>{movement.stock_before}</td>
                          <td style={{
                            fontWeight: '600',
                            color: movement.quantity > 0 ? '#16a34a' : '#dc2626'
                          }}>
                            {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                          </td>
                          <td style={{ fontWeight: '600' }}>{movement.stock_after}</td>
                          <td style={{ color: '#334155', fontSize: '13px' }}>
                            {movement.customer_name || '-'}
                          </td>
                          <td style={{ color: '#6b7280', fontSize: '13px' }}>{movement.staff_name || '-'}</td>
                          <td style={{ fontSize: '12px', color: '#6b7280', maxWidth: '200px' }}>
                            {movement.reference_type === 'sale' && movement.sale_receipt_number ? (
                              <span>Satış Fiş #{movement.sale_receipt_number}</span>
                            ) : movement.notes || '-'}
                          </td>
                          <td>
                            {['sale', 'sale_edit', 'sale_delete', 'sale_cancel', 'refund', 'refund_edit'].includes(movement.reference_type) && movement.reference_id && (
                              <button
                                title="Fişi Aç"
                                onClick={() => navigate(`/retail-sales?sale=${movement.reference_id}`)}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  width: '30px', height: '30px', borderRadius: '6px',
                                  border: '1px solid #e2e8f0', background: '#f8fafc',
                                  cursor: 'pointer', color: '#2563eb', transition: 'all 0.15s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#2563eb'; }}
                              >
                                <ExternalLink size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Dışa Aktarma Seçim Modal */}
      {showExportModal && (
        <div className={`modal-overlay ${closingModal === 'export' ? 'closing' : ''}`}>
          <div className="modal export-select-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Download size={20} style={{ marginRight: '8px' }} /> Dışa Aktar</h2>
              <button className="close-btn" onClick={() => handleCloseModal('export', setShowExportModal)}>✕</button>
            </div>
            <div className="modal-body">
              {exportStep === 1 ? (
                <>
                  <p className="export-step-title">Ürün Türü Seçin</p>
                  <div className="export-options-grid">
                    {[
                      { key: 'all', label: 'Tüm Ürünler', icon: <Package size={24} />, color: undefined },
                      { key: 'gold', label: 'Altın', icon: <Coins size={24} style={{ color: '#f59e0b' }} />, color: '#f59e0b' },
                      { key: 'silver', label: 'Gümüş', icon: <Coins size={24} style={{ color: '#9ca3af' }} />, color: '#9ca3af' },
                      { key: 'steel', label: 'Çelik', icon: <Circle size={24} style={{ color: '#6b7280' }} />, color: '#6b7280' },
                      { key: 'brands', label: 'Markalar', icon: <Tag size={24} style={{ color: '#8b5cf6' }} />, color: '#8b5cf6' },
                      { key: 'aksesuar', label: 'Aksesuar', icon: <Gem size={24} style={{ color: '#06b6d4' }} />, color: '#06b6d4' },
                      { key: 'sarf', label: 'Sarf', icon: <Package size={24} style={{ color: '#f97316' }} />, color: '#f97316' },
                      { key: 'zippo', label: 'Zippo', icon: <Flame size={24} style={{ color: '#ef4444' }} />, color: '#ef4444' },
                    ].filter(t => t.key === 'all' || exportCounts[t.key] > 0).map(t => (
                      <div
                        key={t.key}
                        className={`export-option-card ${exportMetalType === t.key ? 'selected' : ''}`}
                        onClick={() => { setExportMetalType(t.key); setExportStep(2); }}
                      >
                        <span className="export-option-icon">{t.icon}</span>
                        <span className="export-option-label">{t.label}</span>
                        <span className="export-option-count">{t.key in exportCounts ? exportCounts[t.key] : '...'}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="export-step-header">
                    <button className="back-btn" onClick={() => { setExportStep(1); setExportCategories([]); }}>
                      <ArrowLeft size={14} />
                    </button>
                    <p className="export-step-title">
                      {{ all: 'Tüm Ürünler', gold: 'Altın', silver: 'Gümüş', steel: 'Çelik', brands: 'Markalar', aksesuar: 'Aksesuar', sarf: 'Sarf', zippo: 'Zippo' }[exportMetalType] || exportMetalType} - Kategori Seçin
                    </p>
                  </div>
                  <p className="export-multi-hint">Birden fazla kategori seçebilirsiniz</p>
                  <div className="export-options-grid multi-select">
                    <div
                      className={`export-option-card all-category ${exportCategories.includes('all') ? 'selected' : ''}`}
                      onClick={() => toggleExportCategory('all')}
                    >
                      <span className={`export-checkbox ${exportCategories.includes('all') ? 'checked' : ''}`}>
                        {exportCategories.includes('all') ? '✓' : ''}
                      </span>
                      <span className="export-option-icon"><ClipboardList size={24} /></span>
                      <div className="export-option-info">
                        <span className="export-option-label">Tüm Kategoriler</span>
                        <span className="export-option-count">
                          {exportCounts[exportMetalType] || exportCounts.all || 0} ürün
                        </span>
                      </div>
                    </div>
                    {getExportCategories(exportMetalType).map(cat => (
                      <div
                        key={cat}
                        className={`export-option-card ${exportCategories.includes(cat) ? 'selected' : ''}`}
                        onClick={() => toggleExportCategory(cat)}
                      >
                        <span className={`export-checkbox ${exportCategories.includes(cat) ? 'checked' : ''}`}>
                          {exportCategories.includes(cat) ? '✓' : ''}
                        </span>
                        <span className="export-option-icon">📁</span>
                        <div className="export-option-info">
                          <span className="export-option-label">{cat}</span>
                          <span className="export-option-count">
                            {exportCategoryCounts[`${exportMetalType}|${cat}`] || 0} ürün
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {exportStep === 2 && exportCategories.length > 0 && (
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setShowExportModal(false)} disabled={exporting}>
                  İptal
                </button>
                <button
                  className="btn-save export-btn"
                  onClick={() => handleExcelExport(exportMetalType, exportCategories)}
                  disabled={exporting}
                >
                  {exporting ? (
                    <>
                      <RefreshCw size={16} className="spinning" /> Hazırlanıyor...
                    </>
                  ) : (
                    <>
                      <Download size={16} /> Tüm Ürünleri Dışa Aktar
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fiyat Güncelleme Modal */}
      {showPriceUpdateModal && (
        <div className={`modal-overlay ${closingModal === 'priceUpdate' ? 'closing' : ''}`}>
          <div className="modal large import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Coins size={20} style={{ marginRight: '8px' }} /> Fiyat Güncelleme</h2>
              <button className="close-btn" onClick={() => !importing && handleCloseModal('priceUpdate', setShowPriceUpdateModal)} disabled={importing}>✕</button>
            </div>
            <div className="modal-body">
              <div className="import-info">
                <p><strong>Excel Sütun Sırası:</strong></p>
                <ol>
                  <li>Barkod</li>
                  <li>Yeni Fiyat</li>
                  <li>Para Birimi (TRY, USD, EUR)</li>
                </ol>
              </div>

              <div className="import-summary">
                <span className="summary-badge">{priceUpdateData.length} ürün fiyatı güncellenecek</span>
              </div>

              {priceUpdateData.length > 0 && (
                <div className="import-preview">
                  <table className="import-table">
                    <thead>
                      <tr>
                        <th>Barkod</th>
                        <th>Yeni Fiyat</th>
                        <th>Para Birimi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceUpdateData.slice(0, 15).map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.barcode}</td>
                          <td>{item.newPrice.toLocaleString('tr-TR')}</td>
                          <td>{item.currency}</td>
                        </tr>
                      ))}
                      {priceUpdateData.length > 15 && (
                        <tr>
                          <td colSpan="3" style={{ textAlign: 'center', color: '#7f8c9a' }}>
                            ... ve {priceUpdateData.length - 15} ürün daha
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowPriceUpdateModal(false)} disabled={importing}>
                İptal
              </button>
              <button className="btn-save" onClick={handlePriceUpdate} disabled={importing || priceUpdateData.length === 0}>
                {importing ? 'Güncelleniyor...' : `${priceUpdateData.length} Fiyatı Güncelle`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className={`modal-overlay ${closingModal === 'import' ? 'closing' : ''}`}>
          <div className="modal large import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Excel'den Ürün Aktar</h2>
              <button className="close-btn" onClick={() => !importing && handleCloseModal('import', setShowImportModal)} disabled={importing}>✕</button>
            </div>
            <div className="modal-body">
              <div className="import-summary">
                <span className="summary-badge">{importData.length} ürün bulundu</span>
              </div>

              {importData.length > 0 && (
                <div className="import-preview">
                  <table className="import-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Barkod</th>
                        <th>Ürün Adı</th>
                        <th>Tür</th>
                        <th>Kategori</th>
                        <th>Gramaj</th>
                        <th>Ayar</th>
                        <th>Stok</th>
                        <th>Osmangazi</th>
                        <th>Yeşilsu</th>
                        <th>Depo</th>
                        <th>Satış Fiyatı</th>
                        <th>Para Birimi</th>
                        <th>İşçilik</th>
                        <th>Marka</th>
                        <th>Tedarikçi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importData.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.rowNumber}</td>
                          <td>{item.barcode || '-'}</td>
                          <td>{item.name || '-'}</td>
                          <td>{item.metal_type === 'gold' ? 'Altın' : item.metal_type === 'silver' ? 'Gümüş' : item.metal_type === 'steel' ? 'Çelik' : item.metal_type || '-'}</td>
                          <td>{item.category || '-'}</td>
                          <td>{item.weight || '-'}</td>
                          <td>{item.purity || '-'}</td>
                          <td>{item.stock ?? '-'}</td>
                          <td style={{color: item.osmangazi_stock != null ? '#2196F3' : '#999'}}>{item.osmangazi_stock ?? '-'}</td>
                          <td style={{color: item.yesilsu_stock != null ? '#4CAF50' : '#999'}}>{item.yesilsu_stock ?? '-'}</td>
                          <td style={{color: item.depo_stock != null ? '#FF9800' : '#999'}}>{item.depo_stock ?? '-'}</td>
                          <td>{item.sell_price || '-'}</td>
                          <td>{item.currency || '-'}</td>
                          <td>{item.labor_cost || '-'}</td>
                          <td>{item.brand || '-'}</td>
                          <td>{item.supplier || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowImportModal(false)} disabled={importing}>
                İptal
              </button>
              <button className="btn-save" onClick={handleImport} disabled={importing || importData.length === 0}>
                {importing ? 'İçe Aktarılıyor...' : `${importData.length} Ürünü İçe Aktar`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resim Zoom Modal */}
      {zoomedImage && (
        <div className="image-zoom-overlay" onClick={closeZoomedImage}>
          {zoomedProduct?.barcode && (
            <div className="zoom-barcode-bar" onClick={(e) => e.stopPropagation()}>
              {zoomedProduct.barcode}
            </div>
          )}
          {zoomedProductIndex !== null && (
            <button
              className="zoom-nav-btn zoom-nav-prev"
              onClick={(e) => { e.stopPropagation(); navigateZoomedProduct(-1); }}
              title="Önceki ürün"
            >
              <ChevronLeft size={32} />
            </button>
          )}
          <img src={zoomedImage} alt="Büyütülmüş ürün" className="zoomed-image" onClick={(e) => e.stopPropagation()} />
          {zoomedProductIndex !== null && (
            <button
              className="zoom-nav-btn zoom-nav-next"
              onClick={(e) => { e.stopPropagation(); navigateZoomedProduct(1); }}
              title="Sonraki ürün"
            >
              <ChevronRight size={32} />
            </button>
          )}
          {loadingMore && (
            <div className="zoom-loading-more" onClick={(e) => e.stopPropagation()}>
              Daha fazla ürün yükleniyor...
            </div>
          )}
          <span className="zoom-close-hint">Kapatmak için tıklayın</span>
        </div>
      )}

      {/* Stok Alım Modu Panel */}
      {showStockIntakeMode && (
        <div className="stock-intake-overlay">
          <div className="stock-intake-panel">
            <div className="stock-intake-header">
              <h2><Download size={20} style={{ marginRight: '8px' }} /> Stok Alımı</h2>
              {selectedStore ? (
                <span className="stock-intake-store-badge">{selectedStore.name}</span>
              ) : (
                <span className="stock-intake-store-badge all">Tüm Mağazalar</span>
              )}
              <button
                onClick={() => { setShowStockHistory(!showStockHistory); if (!showStockHistory) { setStockHistorySearch(''); fetchStockMovementHistory('add'); } }}
                style={{
                  background: showStockHistory ? '#059669' : '#f1f5f9',
                  color: showStockHistory ? '#fff' : '#475569',
                  border: '1px solid ' + (showStockHistory ? '#059669' : '#cbd5e1'),
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  marginLeft: 'auto',
                  marginRight: '8px'
                }}
              >
                <Clock size={14} /> Geçmiş
              </button>
              <button className="close-btn" onClick={() => { setShowStockHistory(false); closeStockIntakeMode(); }}>✕</button>
            </div>

            {showStockHistory ? (
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                <input
                  type="text"
                  placeholder="Ürün adı veya barkod ara..."
                  value={stockHistorySearch}
                  onChange={(e) => setStockHistorySearch(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', marginBottom: '10px', boxSizing: 'border-box' }}
                />
                {stockHistoryLoading ? (
                  <div className="loading">Yükleniyor...</div>
                ) : stockHistoryData.filter(m => {
                  if (!stockHistorySearch.trim()) return true;
                  const s = stockHistorySearch.toLowerCase();
                  return (m.product_name || '').toLowerCase().includes(s) || (m.product_barcode || m.barcode || '').toLowerCase().includes(s);
                }).length === 0 ? (
                  <div className="empty-state"><span>📋</span><p>Stok artırma geçmişi bulunamadı</p></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {stockHistoryData.filter(m => {
                      if (!stockHistorySearch.trim()) return true;
                      const s = stockHistorySearch.toLowerCase();
                      return (m.product_name || '').toLowerCase().includes(s) || (m.product_barcode || m.barcode || '').toLowerCase().includes(s);
                    }).map((m, i) => (
                      <div key={m.id || i} style={{
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        fontSize: '13px',
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center'
                      }}>
                        <div
                          style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            flexShrink: 0,
                            background: '#f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: (m.product_image || m.image) ? 'pointer' : 'default',
                            border: '1px solid #e2e8f0'
                          }}
                          onClick={() => {
                            const img = m.product_image || m.image;
                            if (img) setLightboxImageUrl(`${IMAGE_HOST}${img}`);
                          }}
                        >
                          {(m.product_image || m.image) ? (
                            <img src={`${IMAGE_HOST}${m.product_image || m.image}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ fontSize: '20px' }}>📦</span>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{m.product_name || 'Ürün'}</span>
                            <span style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span>{m.stock_before}</span>
                              <span style={{ fontWeight: 700, color: m.quantity > 0 ? '#16a34a' : '#dc2626' }}>
                                {m.quantity > 0 ? '+' : ''}{m.quantity}
                              </span>
                              <span>→ {m.stock_after}</span>
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748b', fontSize: '11px' }}>
                            <span style={{ fontFamily: 'monospace' }}>{m.product_barcode || m.barcode || ''}</span>
                            <span style={{
                              padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 500,
                              background: m.movement_type === 'repair_out' ? '#fef3c7' : m.movement_type === 'repair_in' ? '#d1fae5' : m.movement_type === 'transfer_in' ? '#ccfbf1' : m.movement_type === 'transfer_out' ? '#ffedd5' : m.movement_type === 'purchase' ? '#d1fae5' : m.movement_type === 'exchange_in' ? '#d1fae5' : m.movement_type === 'exchange_out' ? '#fef3c7' : '#e0e7ff',
                              color: m.movement_type === 'repair_out' ? '#d97706' : m.movement_type === 'repair_in' ? '#059669' : m.movement_type === 'transfer_in' ? '#0f766e' : m.movement_type === 'transfer_out' ? '#ea580c' : m.movement_type === 'purchase' ? '#059669' : m.movement_type === 'exchange_in' ? '#059669' : m.movement_type === 'exchange_out' ? '#d97706' : '#4f46e5'
                            }}>{m.movement_type_label || m.movement_type}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                            <span>{m.staff_name || ''}</span>
                            <span>{new Date(m.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          {m.notes && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>{m.notes}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
            <>
            {/* Barkod Giriş */}
            <div className="stock-intake-barcode-section">
              <input
                ref={stockIntakeBarcodeRef}
                type="text"
                placeholder="Barkod okutun veya girin..."
                value={stockIntakeBarcode}
                onChange={(e) => setStockIntakeBarcode(e.target.value)}
                onKeyDown={handleStockIntakeBarcode}
                className="stock-intake-barcode-input"
                autoComplete="off"
                autoFocus
              />
              <div className="stock-intake-stats">
                <span className="stat-badge">{stockIntakeSummary.length} çeşit</span>
                <span className="stat-badge primary">{totalIntakeItems} adet</span>
              </div>
            </div>

            {/* Son Okutulan Ürün */}
            {lastScannedProduct && (
              <div className={`stock-intake-last-product ${lastScannedProduct.status}`}>
                {lastScannedProduct.status === 'found' ? (
                  <>
                    <div className="last-product-image">
                      {lastScannedProduct.image ? (
                        <img src={`${IMAGE_HOST}${lastScannedProduct.image}`} alt="" />
                      ) : (
                        <span className="no-img">📦</span>
                      )}
                    </div>
                    <div className="last-product-info">
                      <h4>{lastScannedProduct.name}</h4>
                      <p className="last-product-barcode">{lastScannedProduct.barcode}</p>
                      <p className="last-product-category">{lastScannedProduct.category}</p>
                    </div>
                    <div className="last-product-quantity">
                      <span className="quantity-badge">+{lastScannedProduct.addedQuantity}</span>
                      <span className="current-stock">Mevcut: {lastScannedProduct.stock || 0}</span>
                    </div>
                  </>
                ) : (
                  <div className="last-product-notfound">
                    <span className="notfound-icon"><AlertTriangle size={24} /></span>
                    <span className="notfound-barcode">{lastScannedProduct.barcode}</span>
                    <span className="notfound-message">{lastScannedProduct.message}</span>
                  </div>
                )}
              </div>
            )}

            {/* Boş Durum */}
            {!lastScannedProduct && stockIntakeSummary.length === 0 && (
              <div className="stock-intake-empty">
                <span className="empty-icon">📦</span>
                <p>Barkod okutarak stok alımı yapın</p>
                <p className="empty-hint">Her okutmada ürün miktarı 1 artar</p>
              </div>
            )}

            {/* Alınan Ürünler Listesi */}
            {stockIntakeSummary.length > 0 && (
              <div className="stock-intake-list">
                <h4>Alınan Ürünler</h4>
                <div className="stock-intake-items">
                  {[...stockIntakeSummary].reverse().map(item => (
                    <div key={item.product.id} className="stock-intake-item">
                      <div className="item-image">
                        {item.product.image ? (
                          <img src={`${IMAGE_HOST}${item.product.image}`} alt="" loading="lazy" />
                        ) : (
                          <span>📦</span>
                        )}
                      </div>
                      <div className="item-info">
                        <span className="item-name">{item.product.name}</span>
                        <span className="item-barcode">{item.product.barcode}</span>
                      </div>
                      <div className="item-quantity-controls">
                        <button
                          className="qty-btn"
                          onClick={() => updateStockIntakeQuantity(item.product.id, item.quantity - 1)}
                        >
                          −
                        </button>
                        <span className="qty-value" onDoubleClick={(e) => { const span = e.target; const input = document.createElement('input'); input.type = 'number'; input.min = '1'; input.value = item.quantity; input.className = 'qty-edit-input'; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #3b82f6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.max(1, parseInt(input.value) || 1); updateStockIntakeQuantity(item.product.id, val); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>{item.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => updateStockIntakeQuantity(item.product.id, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                      <div className="item-stock-info">
                        <span className="current">Mevcut: {item.product.stock || 0}</span>
                        <span className="arrow">→</span>
                        <span className="new">Yeni: {(item.product.stock || 0) + item.quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Kaydet Butonları */}
            <div className="stock-intake-actions">
              <button className="cancel-btn" onClick={closeStockIntakeMode}>
                İptal
              </button>
              <button
                className="save-btn"
                onClick={saveStockIntake}
                disabled={savingStockIntake || stockIntakeSummary.length === 0}
              >
                {savingStockIntake ? 'Kaydediliyor...' : `✓ Stokları Güncelle (${totalIntakeItems} adet)`}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {/* Stok Düşüm Modal */}
      {showStockDeductionMode && (
        <div className="stock-intake-overlay stock-deduction-overlay">
          <div className="stock-intake-panel stock-deduction-panel">
            <div className="stock-intake-header stock-deduction-header">
              <h2><Upload size={20} style={{ marginRight: '8px' }} /> Stok Eksileme</h2>
              {selectedStore ? (
                <span className="stock-intake-store-badge">{selectedStore.name}</span>
              ) : (
                <span className="stock-intake-store-badge all">Tüm Mağazalar</span>
              )}
              <button
                onClick={() => { setShowStockHistory(!showStockHistory); if (!showStockHistory) { setStockHistorySearch(''); fetchStockMovementHistory('subtract'); } }}
                style={{
                  background: showStockHistory ? '#dc2626' : '#f1f5f9',
                  color: showStockHistory ? '#fff' : '#475569',
                  border: '1px solid ' + (showStockHistory ? '#dc2626' : '#cbd5e1'),
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  marginLeft: 'auto',
                  marginRight: '8px'
                }}
              >
                <Clock size={14} /> Geçmiş
              </button>
              <button className="close-btn" onClick={() => { setShowStockHistory(false); closeStockDeductionMode(); }}>✕</button>
            </div>

            {showStockHistory ? (
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                <input
                  type="text"
                  placeholder="Ürün adı veya barkod ara..."
                  value={stockHistorySearch}
                  onChange={(e) => setStockHistorySearch(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', marginBottom: '10px', boxSizing: 'border-box' }}
                />
                {stockHistoryLoading ? (
                  <div className="loading">Yükleniyor...</div>
                ) : stockHistoryData.filter(m => {
                  if (!stockHistorySearch.trim()) return true;
                  const s = stockHistorySearch.toLowerCase();
                  return (m.product_name || '').toLowerCase().includes(s) || (m.product_barcode || m.barcode || '').toLowerCase().includes(s);
                }).length === 0 ? (
                  <div className="empty-state"><span>📋</span><p>Stok eksileme geçmişi bulunamadı</p></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {stockHistoryData.filter(m => {
                      if (!stockHistorySearch.trim()) return true;
                      const s = stockHistorySearch.toLowerCase();
                      return (m.product_name || '').toLowerCase().includes(s) || (m.product_barcode || m.barcode || '').toLowerCase().includes(s);
                    }).map((m, i) => (
                      <div key={m.id || i} style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        fontSize: '13px',
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center'
                      }}>
                        <div
                          style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            flexShrink: 0,
                            background: '#fef2f2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: (m.product_image || m.image) ? 'pointer' : 'default',
                            border: '1px solid #fecaca'
                          }}
                          onClick={() => {
                            const img = m.product_image || m.image;
                            if (img) setLightboxImageUrl(`${IMAGE_HOST}${img}`);
                          }}
                        >
                          {(m.product_image || m.image) ? (
                            <img src={`${IMAGE_HOST}${m.product_image || m.image}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ fontSize: '20px' }}>📦</span>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{m.product_name || 'Ürün'}</span>
                            <span style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span>{m.stock_before}</span>
                              <span style={{ fontWeight: 700, color: '#dc2626' }}>
                                {m.quantity > 0 ? '+' : ''}{m.quantity}
                              </span>
                              <span>→ {m.stock_after}</span>
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748b', fontSize: '11px' }}>
                            <span style={{ fontFamily: 'monospace' }}>{m.product_barcode || m.barcode || ''}</span>
                            <span style={{
                              padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 500,
                              background: m.movement_type === 'repair_out' ? '#fef3c7' : m.movement_type === 'repair_in' ? '#d1fae5' : m.movement_type === 'transfer_in' ? '#ccfbf1' : m.movement_type === 'transfer_out' ? '#ffedd5' : m.movement_type === 'purchase' ? '#d1fae5' : m.movement_type === 'exchange_in' ? '#d1fae5' : m.movement_type === 'exchange_out' ? '#fef3c7' : '#e0e7ff',
                              color: m.movement_type === 'repair_out' ? '#d97706' : m.movement_type === 'repair_in' ? '#059669' : m.movement_type === 'transfer_in' ? '#0f766e' : m.movement_type === 'transfer_out' ? '#ea580c' : m.movement_type === 'purchase' ? '#059669' : m.movement_type === 'exchange_in' ? '#059669' : m.movement_type === 'exchange_out' ? '#d97706' : '#4f46e5'
                            }}>{m.movement_type_label || m.movement_type}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                            <span>{m.staff_name || ''}</span>
                            <span>{new Date(m.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          {m.notes && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>{m.notes}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
            <>
            {/* Barkod Giriş */}
            <div className="stock-intake-barcode-section stock-deduction-barcode-section">
              <input
                ref={stockDeductionBarcodeRef}
                type="text"
                placeholder="Barkod okutun veya girin..."
                value={stockDeductionBarcode}
                onChange={(e) => setStockDeductionBarcode(e.target.value)}
                onKeyDown={handleStockDeductionBarcode}
                className="stock-intake-barcode-input"
                autoComplete="off"
                autoFocus
              />
              <div className="stock-intake-stats">
                <span className="stat-badge">{stockDeductionSummary.length} çeşit</span>
                <span className="stat-badge danger">{totalDeductionItems} adet</span>
              </div>
            </div>

            {/* Toplu Sebep Girişi */}
            <div className="global-reason-section">
              <div className="global-reason-input-wrapper">
                <input
                  type="text"
                  placeholder="Toplu düşüm sebebi (örn: Kırıldı, Kayboldu, Fire...)"
                  value={globalDeductionReason}
                  onChange={(e) => setGlobalDeductionReason(e.target.value)}
                  className="global-reason-input"
                />
                <button
                  className="apply-reason-btn"
                  onClick={applyGlobalReason}
                  disabled={!globalDeductionReason.trim() || stockDeductionSummary.length === 0}
                >
                  Tümüne Uygula
                </button>
              </div>
            </div>

            {/* Son Okutulan Ürün */}
            {lastDeductedProduct && (
              <div className={`stock-intake-last-product ${lastDeductedProduct.status}`}>
                {lastDeductedProduct.status === 'found' ? (
                  <>
                    <div className="last-product-image">
                      {lastDeductedProduct.image ? (
                        <img src={`${IMAGE_HOST}${lastDeductedProduct.image}`} alt="" />
                      ) : (
                        <span className="no-img">📦</span>
                      )}
                    </div>
                    <div className="last-product-info">
                      <h4>{lastDeductedProduct.name}</h4>
                      <p className="last-product-barcode">{lastDeductedProduct.barcode}</p>
                      <p className="last-product-category">{lastDeductedProduct.category}</p>
                    </div>
                    <div className="last-product-quantity deduction">
                      <span className="quantity-badge">-{lastDeductedProduct.deductedQuantity}</span>
                      <span className="current-stock">Mevcut: {lastDeductedProduct.currentStock}</span>
                    </div>
                  </>
                ) : lastDeductedProduct.status === 'nostock' ? (
                  <div className="last-product-notfound">
                    <span className="notfound-icon"><AlertTriangle size={24} /></span>
                    <span className="notfound-barcode">{lastDeductedProduct.name}</span>
                    <span className="notfound-message">{lastDeductedProduct.message}</span>
                  </div>
                ) : (
                  <div className="last-product-notfound">
                    <span className="notfound-icon"><AlertTriangle size={24} /></span>
                    <span className="notfound-barcode">{lastDeductedProduct.barcode}</span>
                    <span className="notfound-message">{lastDeductedProduct.message}</span>
                  </div>
                )}
              </div>
            )}

            {/* Boş Durum */}
            {!lastDeductedProduct && stockDeductionSummary.length === 0 && (
              <div className="stock-intake-empty">
                <span className="empty-icon">📤</span>
                <p>Barkod okutarak stok düşümü yapın</p>
                <p className="empty-hint">Her okutmada ürün miktarı 1 düşer</p>
              </div>
            )}

            {/* Düşülecek Ürünler Listesi */}
            {stockDeductionSummary.length > 0 && (
              <div className="stock-intake-list stock-deduction-list">
                <h4>Düşülecek Ürünler</h4>
                <div className="stock-intake-items">
                  {stockDeductionSummary.map(item => (
                    <div key={item.product.id} className="stock-intake-item stock-deduction-item">
                      <div className="item-image">
                        {item.product.image ? (
                          <img src={`${IMAGE_HOST}${item.product.image}`} alt="" loading="lazy" />
                        ) : (
                          <span>📦</span>
                        )}
                      </div>
                      <div className="item-info">
                        <span className="item-name">{item.product.name}</span>
                        <span className="item-barcode">{item.product.barcode}</span>
                      </div>
                      <div className="item-quantity-controls">
                        <button
                          className="qty-btn"
                          onClick={() => updateStockDeductionQuantity(item.product.id, item.quantity - 1)}
                        >
                          −
                        </button>
                        <span className="qty-value" onDoubleClick={(e) => { const span = e.target; const input = document.createElement('input'); input.type = 'number'; input.min = '1'; input.value = item.quantity; input.className = 'qty-edit-input'; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #3b82f6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.max(1, parseInt(input.value) || 1); updateStockDeductionQuantity(item.product.id, val); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>{item.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => updateStockDeductionQuantity(item.product.id, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                      <div className="item-reason">
                        <input
                          type="text"
                          placeholder="Düşüm sebebi..."
                          value={item.reason || ''}
                          onChange={(e) => updateStockDeductionReason(item.product.id, e.target.value)}
                          className="reason-input"
                        />
                      </div>
                      <div className="item-stock-info deduction">
                        <span className="current">Mevcut: {item.currentStock}</span>
                        <span className="arrow">→</span>
                        <span className="new danger">Yeni: {item.currentStock - item.quantity}</span>
                      </div>
                      <button
                        className="remove-item-btn"
                        onClick={() => removeFromDeductionList(item.product.id)}
                        title="Listeden Kaldır"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Kaydet Butonları */}
            <div className="stock-intake-actions">
              <button className="cancel-btn" onClick={closeStockDeductionMode}>
                İptal
              </button>
              <button
                className="save-btn danger"
                onClick={saveStockDeduction}
                disabled={savingStockDeduction || stockDeductionSummary.length === 0}
              >
                {savingStockDeduction ? 'Kaydediliyor...' : `✓ Stokları Düş (${totalDeductionItems} adet)`}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {/* Varyant Grupları Listesi Modalı */}
      {showVariantGroupsModal && (
        <div className="modal-overlay" onClick={() => setShowVariantGroupsModal(false)} style={{ zIndex: 1100 }}>
          <div className="modal variant-group-list-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '760px', width: '92%' }}>
            <div className="modal-header">
              <h2><Layers size={20} style={{ marginRight: '8px' }} /> Varyant Grupları</h2>
              <button className="close-btn" onClick={() => setShowVariantGroupsModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
                <button className="btn primary" onClick={openNewVariantGroupEditor}>
                  <Plus size={16} /> Yeni Varyant Grubu
                </button>
              </div>
              {loadingVariantGroups ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>Yükleniyor...</p>
              ) : variantGroups.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>Henüz varyant grubu yok</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {variantGroups.map(group => {
                    const imgUrl = getProductImageUrl({ image: group.primary_product_image });
                    const statusLabel = { draft: 'Taslak', synced: 'Shopify\'da', error: 'Hata' }[group.status] || group.status;
                    const statusColor = { draft: '#94a3b8', synced: '#16a34a', error: '#dc2626' }[group.status] || '#94a3b8';
                    return (
                      <div key={group.id} style={{
                        display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
                        border: '1px solid #e2e8f0', borderRadius: '10px'
                      }}>
                        {imgUrl ? (
                          <img src={imgUrl} alt="" style={{ width: '44px', height: '44px', borderRadius: '8px', objectFit: 'cover', cursor: 'zoom-in' }} onClick={() => setZoomedImage(imgUrl)} />
                        ) : (
                          <div style={{ width: '44px', height: '44px', borderRadius: '8px', background: '#f1f5f9' }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{group.name}</div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            {group.primary_product_name} · {group.member_count} ürün ·{' '}
                            <span style={{ color: statusColor, fontWeight: 600 }}>{statusLabel}</span>
                            {group.link_mode !== 'merged' && (
                              <span title="Üyeler Shopify'da bağımsız ürün olarak kalır, sadece birbirine bağlanır" style={{ marginLeft: '6px', color: '#0f766e' }}>
                                · Bağlantılı (ayrı ürünler)
                              </span>
                            )}
                          </div>
                        </div>
                        <button className="btn secondary small" onClick={() => openEditVariantGroupEditor(group)}>Düzenle</button>
                        <button
                          className="btn primary small"
                          disabled={syncingVariantGroupId === group.id}
                          onClick={() => syncVariantGroup(group.id)}
                        >
                          {syncingVariantGroupId === group.id ? 'Gönderiliyor...' : (group.status === 'synced' ? 'Güncelle' : 'Shopify\'a Gönder')}
                        </button>
                        <button className="btn danger small" onClick={() => deleteVariantGroup(group)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Varyant Grubu Oluştur/Düzenle Modalı */}
      {showVariantGroupEditorModal && (
        <div className="modal-overlay" style={{ zIndex: 1300 }}>
          <div className="modal large variant-group-editor-modal group-editor-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1180px', width: '96%' }}>
            <div className="modal-header">
              <h2><Layers size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />{editingVariantGroupId ? 'Varyant Grubunu Düzenle' : 'Yeni Varyant Grubu'}</h2>
              <button className="close-btn" onClick={closeVariantGroupEditor}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row compact-many" style={{ marginBottom: '14px', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Grup Adı</label>
                  <input
                    type="text"
                    value={variantGroupName}
                    onChange={(e) => setVariantGroupName(e.target.value)}
                    placeholder="Örn: Altın Alyans Serisi"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Barkod Okut / Gir</label>
                  <div className="barcode-input-wrapper">
                    <input
                      ref={variantGroupBarcodeRef}
                      type="text"
                      value={variantGroupBarcodeInput}
                      onChange={(e) => setVariantGroupBarcodeInput(e.target.value)}
                      onKeyDown={handleAddVariantGroupBarcode}
                      placeholder="Barkod okutun veya girin, Enter'a basın"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="form-group" style={{ flex: 1, position: 'relative' }}>
                  <label>İsim / Barkod / SKU ile Ara</label>
                  <input
                    type="text"
                    value={variantGroupSearchTerm}
                    onChange={(e) => handleVariantGroupSearchChange(e.target.value)}
                    placeholder="En az 2 karakter yazın..."
                  />
                  {(variantGroupSearching || variantGroupSearchResults.length > 0) && variantGroupSearchTerm.trim().length >= 2 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: '4px',
                      background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.12)', maxHeight: '280px', overflowY: 'auto'
                    }}>
                      {variantGroupSearching ? (
                        <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#94a3b8' }}>Aranıyor...</div>
                      ) : variantGroupSearchResults.length === 0 ? (
                        <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#94a3b8' }}>Sonuç yok</div>
                      ) : variantGroupSearchResults.map(p => {
                        const imgUrl = getProductImageUrl(p);
                        return (
                          <div
                            key={p.id}
                            onClick={() => addVariantGroupMember(p)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                          >
                            {imgUrl ? (
                              <img src={imgUrl} alt="" style={{ width: '30px', height: '30px', borderRadius: '5px', objectFit: 'cover', cursor: 'zoom-in' }} onClick={(e) => { e.stopPropagation(); setZoomedImage(imgUrl); }} />
                            ) : (
                              <div style={{ width: '30px', height: '30px', borderRadius: '5px', background: '#f1f5f9', flexShrink: 0 }} />
                            )}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '0.84rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {p.is_set ? <span className="set-badge">SET</span> : null}{p.name}
                              </div>
                              <div style={{ fontSize: '0.74rem', color: '#64748b' }}>{p.barcode}{p.sku ? ` · SKU: ${p.sku}` : ''}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <button type="button" className="btn secondary" onClick={() => openGroupProductPicker('variant')}>
                  <ClipboardList size={15} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Tüm Ürünlerden Seç
                </button>
              </div>

              {variantGroupMembers.length >= 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '14px',
                  marginBottom: '12px', padding: '10px 12px', background: '#f8fafc', borderRadius: '8px'
                }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569' }}>Varyant Ekseni (en fazla 3):</span>
                  {VARIANT_AXIS_OPTIONS.map(opt => (
                    <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={variantGroupAxes.includes(opt.key)}
                        onChange={() => toggleVariantGroupAxis(opt.key)}
                        disabled={!variantGroupAxes.includes(opt.key) && variantGroupAxes.length >= 3}
                      />
                      {opt.label}
                    </label>
                  ))}
                  {variantGroupAxes.length === 0 && (
                    <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>
                      Eksen seçilmezse ayırt edici değer olarak barkod kullanılır
                    </span>
                  )}
                </div>
              )}

              {variantGroupMembers.length >= 1 && getVariantGroupAxisWarning() && (
                <div style={{
                  background: '#fef3c7', color: '#92400e', padding: '8px 12px', borderRadius: '8px',
                  fontSize: '0.82rem', marginBottom: '12px'
                }}>
                  <AlertTriangle size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {getVariantGroupAxisWarning()}
                </div>
              )}

              {variantGroupMembers.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0' }}>Henüz ürün eklenmedi - yukarıdan barkod okutun veya arayın</p>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ width: '34px', padding: '10px 6px' }}></th>
                        <th style={{ width: '42px', padding: '10px 6px' }}></th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', minWidth: '160px' }}>Ürün</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', width: '110px' }}>Ölçü</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', width: '100px' }}>Renk</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', width: '100px' }}>Taş Rengi</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', width: '100px' }}>Taş Milimi</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', minWidth: '160px' }}>Sanal Varyantlar (stoksuz)</th>
                        <th style={{ width: '36px' }}></th>
                      </tr>
                    </thead>
                    {variantGroupMembers.map(member => {
                      const imgUrl = getProductImageUrl(member);
                      const inputStyle = (axisKey) => ({
                        width: '100%', fontSize: '0.8rem', padding: '5px 6px', borderRadius: '6px',
                        border: variantGroupAxes.includes(axisKey) ? '1px solid #0f766e' : '1px solid #e2e8f0',
                        background: variantGroupAxes.includes(axisKey) ? '#f0fdfa' : '#fff'
                      });
                      return (
                        <tbody key={member.id}>
                          <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ textAlign: 'center', padding: '8px 6px' }}>
                              <input
                                type="radio"
                                name="variantGroupPrimary"
                                checked={variantGroupPrimaryId === member.id}
                                onChange={() => setVariantGroupPrimaryId(member.id)}
                                title="Ana ürün olarak seç (yalnızca görüntüleme/sıralama amaçlı - Shopify'da tüm üyeler bağımsız kalır, birleştirilmez)"
                              />
                            </td>
                            <td style={{ padding: '8px 6px' }}>
                              {imgUrl ? (
                                <img src={imgUrl} alt="" style={{ width: '34px', height: '34px', borderRadius: '6px', objectFit: 'cover', cursor: 'zoom-in' }} onClick={() => setZoomedImage(imgUrl)} />
                              ) : (
                                <div style={{ width: '34px', height: '34px', borderRadius: '6px', background: '#f1f5f9' }} />
                              )}
                            </td>
                            <td style={{ padding: '8px' }}>
                              <div style={{ fontWeight: 600 }}>
                                {member.is_set ? <span className="set-badge">SET</span> : null}
                                {member.name}
                                {variantGroupPrimaryId === member.id && (
                                  <span style={{ marginLeft: '6px', fontSize: '0.68rem', color: '#0f766e', fontWeight: 700 }}>ANA ÜRÜN</span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.74rem', color: '#64748b' }}>{member.barcode}</div>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="text"
                                value={member.size || ''}
                                placeholder="Yüzük/Bileklik/Kolye"
                                title="Ölçü (Yüzük/Bileklik/Kolye Ölçüsü)"
                                onChange={(e) => setVariantGroupMemberAttrLocal(member.id, 'size', e.target.value)}
                                onBlur={(e) => persistVariantGroupMemberAttr(member.id, 'size', e.target.value)}
                                style={inputStyle('size')}
                              />
                            </td>
                            {['color', 'stone_color', 'stone_mm'].map(axisKey => (
                              <td key={axisKey} style={{ padding: '8px' }}>
                                <input
                                  type="text"
                                  value={member[axisKey] || ''}
                                  onChange={(e) => setVariantGroupMemberAttrLocal(member.id, axisKey, e.target.value)}
                                  onBlur={(e) => persistVariantGroupMemberAttr(member.id, axisKey, e.target.value)}
                                  style={inputStyle(axisKey)}
                                />
                              </td>
                            ))}
                            <td style={{ padding: '8px' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
                                {(member.virtual_variants || []).map(vv => (
                                  <span key={vv.id} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem',
                                    background: '#ede9fe', color: '#6d28d9', padding: '2px 6px', borderRadius: '10px'
                                  }}>
                                    {variantAxisLabel(vv.axis_key)}: {vv.value}
                                    {!vv.shopify_variant_id && <span title="Shopify'a gönderilmedi">⏳</span>}
                                    <X size={10} style={{ cursor: 'pointer' }} onClick={() => handleRemoveMemberVirtualVariant(member.id, vv.id)} />
                                  </span>
                                ))}
                              </div>
                              {openVirtualVariantMemberId !== member.id && (
                                <button
                                  type="button"
                                  onClick={() => toggleMemberVirtualVariantForm(member.id)}
                                  style={{ background: 'none', border: 'none', color: '#6d28d9', fontSize: '0.74rem', cursor: 'pointer', padding: 0 }}
                                >
                                  + Sanal Varyant Ekle
                                </button>
                              )}
                            </td>
                            <td style={{ textAlign: 'center', padding: '8px' }}>
                              <button className="btn danger small" onClick={() => removeVariantGroupMember(member.id)} title="Gruptan çıkar">
                                <X size={14} />
                              </button>
                            </td>
                          </tr>
                          {openVirtualVariantMemberId === member.id && (
                            <tr style={{ background: '#faf5ff', borderBottom: '1px solid #f1f5f9' }}>
                              <td colSpan={9} style={{ padding: '8px 12px' }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <span style={{ fontSize: '0.78rem', color: '#6d28d9', fontWeight: 600 }}>Sanal Varyant:</span>
                                  <select
                                    value={memberVirtualVariantAxis}
                                    onChange={(e) => setMemberVirtualVariantAxis(e.target.value)}
                                    style={{ fontSize: '0.78rem', padding: '5px', borderRadius: '6px', border: '1px solid #ddd' }}
                                  >
                                    {VARIANT_AXIS_OPTIONS.map(opt => (
                                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    placeholder="Değer (örn: Altın Rengi)"
                                    value={memberVirtualVariantValue}
                                    onChange={(e) => setMemberVirtualVariantValue(e.target.value)}
                                    style={{ fontSize: '0.78rem', padding: '5px 8px', borderRadius: '6px', border: '1px solid #ddd', width: '150px' }}
                                  />
                                  <button
                                    type="button"
                                    className="btn secondary small"
                                    disabled={savingMemberVirtualVariant}
                                    onClick={() => handleAddMemberVirtualVariant(member.id)}
                                  >
                                    Ekle
                                  </button>
                                  <button type="button" className="btn secondary small" onClick={() => toggleMemberVirtualVariantForm(member.id)}>
                                    Vazgeç
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      );
                    })}
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn secondary" onClick={closeVariantGroupEditor} disabled={savingVariantGroup}>Vazgeç</button>
              <button className="btn primary" onClick={() => saveVariantGroup(false)} disabled={savingVariantGroup}>
                {savingVariantGroup ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button className="btn primary" onClick={() => saveVariantGroup(true)} disabled={savingVariantGroup}>
                {savingVariantGroup ? 'Kaydediliyor...' : 'Kaydet ve Shopify\'a Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Barkod Uyarı Modal */}
      {showDuplicateBarcodeModal && duplicateBarcodeData && (
        <div className="modal-overlay" style={{ zIndex: 1200 }}>
          <div className="modal duplicate-barcode-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header warning-header">
              <h2><AlertTriangle size={20} style={{ marginRight: '8px', color: '#f59e0b' }} /> Ürün Zaten Okutuldu</h2>
              <button className="close-btn" onClick={() => {
                setShowDuplicateBarcodeModal(false);
                setDuplicateBarcodeData(null);
                quickTransferBarcodeRef.current?.focus();
              }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="duplicate-warning-content">
                <div className="product-info-card">
                  {duplicateBarcodeData.product.image && (
                    <img
                      src={`${API_URL.replace('/api', '')}${duplicateBarcodeData.product.image}`}
                      alt=""
                      className="product-thumb-large"
                    />
                  )}
                  <div className="product-details">
                    <h3>{duplicateBarcodeData.product.name}</h3>
                    <p className="barcode-text">{duplicateBarcodeData.product.barcode}</p>
                    <p className="transfer-text">Transfer: {duplicateBarcodeData.transfer.code}</p>
                  </div>
                </div>
                <div className="warning-message">
                  <p>Bu ürün zaten <strong>{duplicateBarcodeData.currentQty} adet</strong> okutuldu.</p>
                  <p>Beklenen miktar: <strong>{duplicateBarcodeData.expectedQty} adet</strong></p>
                  {duplicateBarcodeData.currentQty >= duplicateBarcodeData.expectedQty && (
                    <p className="max-reached">⚠️ Maksimum miktar zaten okutuldu!</p>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer duplicate-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowDuplicateBarcodeModal(false);
                  setDuplicateBarcodeData(null);
                  quickTransferBarcodeRef.current?.focus();
                }}
              >
                Vazgeç
              </button>
              <button
                className="btn-save"
                disabled={duplicateBarcodeData.currentQty >= duplicateBarcodeData.expectedQty}
                onClick={() => {
                  // Miktarı artır
                  setQuickTransferItems(prev => {
                    const updated = [...prev];
                    const existingIndex = updated.findIndex(item => item.key === duplicateBarcodeData.key);
                    if (existingIndex >= 0) {
                      updated[existingIndex] = {
                        ...updated[existingIndex],
                        scannedQty: updated[existingIndex].scannedQty + 1
                      };
                      toast.success(`${duplicateBarcodeData.product.name} (${updated[existingIndex].scannedQty}/${duplicateBarcodeData.expectedQty})`);
                    }
                    return updated;
                  });
                  setShowDuplicateBarcodeModal(false);
                  setDuplicateBarcodeData(null);
                  quickTransferBarcodeRef.current?.focus();
                }}
              >
                <Plus size={16} /> Stoğa Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stok Alımı Duplicate Barkod Uyarı Modal */}
      {showStockIntakeDuplicateModal && stockIntakeDuplicateData && (
        <div className="modal-overlay" style={{ zIndex: 1200 }}>
          <div className="modal duplicate-barcode-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header warning-header">
              <h2><AlertTriangle size={20} style={{ marginRight: '8px', color: '#f59e0b' }} /> Ürün Zaten Okutuldu</h2>
              <button className="close-btn" onClick={cancelStockIntakeDuplicate}>✕</button>
            </div>
            <div className="modal-body">
              <div className="duplicate-warning-content">
                <div className="product-info-card">
                  {stockIntakeDuplicateData.product.image && (
                    <img
                      src={`${API_URL.replace('/api', '')}${stockIntakeDuplicateData.product.image}`}
                      alt=""
                      className="product-thumb-large"
                    />
                  )}
                  <div className="product-details">
                    <h3>{stockIntakeDuplicateData.product.name}</h3>
                    <p className="barcode-text">{stockIntakeDuplicateData.product.barcode}</p>
                    {stockIntakeDuplicateData.product.brand && (
                      <p className="brand-text">{stockIntakeDuplicateData.product.brand}</p>
                    )}
                  </div>
                </div>
                <div className="warning-message">
                  <p>Bu ürün zaten <strong>{stockIntakeDuplicateData.currentQuantity} adet</strong> okutuldu.</p>
                  <p className="warning-question">Tekrar eklemek istiyor musunuz?</p>
                </div>
              </div>
            </div>
            <div className="modal-footer duplicate-footer">
              <button
                className="btn-cancel"
                onClick={cancelStockIntakeDuplicate}
              >
                Vazgeç
              </button>
              <button
                className="btn-save"
                onClick={confirmStockIntakeDuplicate}
              >
                <Plus size={16} /> Stoğa Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10002 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{
                margin: 0,
                fontSize: '15px',
                color: '#374151',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f8fafc',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmModalConfig.onConfirm) {
                    confirmModalConfig.onConfirm();
                  }
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? '#ef4444' :
                             confirmModalConfig.type === 'info' ? '#3b82f6' : '#f59e0b',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hızlı Transfer Teslim Özet Modal */}
      {showQuickTransferSummary && quickTransferSummaryData && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal quick-transfer-summary-modal large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Package size={20} style={{ marginRight: '8px' }} /> Teslim Alma Özeti</h2>
              <button className="close-btn" onClick={() => setShowQuickTransferSummary(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="summary-content">
                {quickTransferSummaryData.map((group, idx) => (
                  <div key={idx} className="transfer-group">
                    <div className="transfer-group-header">
                      <div className="transfer-info">
                        <span className="transfer-code">{group.transfer.code}</span>
                        <span className="transfer-from">Gönderen: {group.transfer.from_store}</span>
                      </div>
                    </div>

                    {/* Teslim Alınacak Ürünler */}
                    <div className="scanned-items">
                      <h4 className="section-title received">✓ Teslim Alınacak Ürünler ({group.scannedItems.length})</h4>
                      <table className="summary-table">
                        <thead>
                          <tr>
                            <th style={{ width: '90px' }}>Foto</th>
                            <th>Ürün</th>
                            <th>Barkod</th>
                            <th>Miktar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.scannedItems.map((item, itemIdx) => (
                            <tr key={itemIdx}>
                              <td>
                                <div className="product-thumb-wrap" style={item.product.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.product.image && setZoomedImage(`${API_URL.replace('/api', '')}${item.product.image}`)}>
                                  {item.product.image ? (
                                    <img src={`${API_URL.replace('/api', '')}${item.product.image}`} alt="" className="product-thumb" />
                                  ) : (
                                    <div className="product-thumb-placeholder" />
                                  )}
                                </div>
                              </td>
                              <td><span>{item.product.name}</span></td>
                              <td>{item.product.barcode}</td>
                              <td className="qty-cell">{item.scannedQty} / {item.expectedQty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Eksik Ürünler */}
                    {group.missingItems && group.missingItems.length > 0 && (
                      <div className="missing-items">
                        <h4 className="section-title missing">⚠ Bu Fişten Okutulmayan Ürünler ({group.missingItems.length})</h4>
                        <table className="summary-table missing-table">
                          <thead>
                            <tr>
                              <th style={{ width: '90px' }}>Foto</th>
                              <th>Ürün</th>
                              <th>Barkod</th>
                              <th>Kalan</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.missingItems.map((item, itemIdx) => (
                              <tr key={itemIdx}>
                                <td>
                                  <div className="product-thumb-wrap" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && setZoomedImage(`${API_URL.replace('/api', '')}${item.image}`)}>
                                    {item.image ? (
                                      <img src={`${API_URL.replace('/api', '')}${item.image}`} alt="" className="product-thumb" />
                                    ) : (
                                      <div className="product-thumb-placeholder" />
                                    )}
                                  </div>
                                </td>
                                <td><span>{item.product_name}</span></td>
                                <td>{item.barcode}</td>
                                <td className="qty-cell missing">{item.expectedQty}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Toplam Özet */}
              <div className="summary-totals">
                <div className="total-item">
                  <span className="label">Toplam Teslim Alınacak:</span>
                  <span className="value">{quickTransferSummaryData.reduce((sum, g) => sum + g.scannedItems.reduce((s, i) => s + i.scannedQty, 0), 0)} adet</span>
                </div>
                <div className="total-item missing">
                  <span className="label">Toplam Okutulmayan:</span>
                  <span className="value">{quickTransferSummaryData.reduce((sum, g) => sum + (g.missingItems?.reduce((s, i) => s + i.expectedQty, 0) || 0), 0)} adet</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn secondary" onClick={() => setShowQuickTransferSummary(false)}>Geri Dön</button>
              <button
                className="btn primary"
                onClick={confirmQuickTransfer}
                disabled={savingQuickTransfer}
              >
                {savingQuickTransfer ? 'Kaydediliyor...' : 'Onayla ve Teslim Al'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bilinmeyen Barkod Modal */}
      {showUnknownBarcodeModal && (
        <div className={`modal-overlay ${closingModal === 'unknownBarcode' ? 'closing' : ''}`}>
          <div className="modal unknown-barcode-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><AlertTriangle size={20} style={{ marginRight: '8px' }} /> Ürün Bulunamadı</h2>
              <button className="close-btn" onClick={() => handleCloseModal('unknownBarcode', setShowUnknownBarcodeModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="unknown-barcode-info">
                <span className="barcode-icon"><Tag size={32} /></span>
                <p className="barcode-value">{unknownBarcode}</p>
                <p className="barcode-message">Bu barkod sistemde kayıtlı değil.</p>
              </div>
              <p className="action-question">Ne yapmak istersiniz?</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowUnknownBarcodeModal(false)}>
                Atla
              </button>
              <button className="btn-save" onClick={handleAddNewProductWithBarcode}>
                + Yeni Ürün Olarak Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Kategori Modal */}
      {showNewCategoryModal && (
        <div className={`modal-overlay ${closingModal === 'newCategory' ? 'closing' : ''}`}>
          <div className="modal" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Tag size={20} style={{ marginRight: '8px' }} /> Yeni Kategori Ekle</h2>
              <button className="close-btn" onClick={() => handleCloseModal('newCategory', setShowNewCategoryModal)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group">
                <label>Kategori Adı *</label>
                <input
                  type="text"
                  placeholder="Örn: Altın Bileklik, Gümüş Yüzük..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  autoFocus
                />
              </div>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '12px' }}>
                Not: Yeni kategori, bu ürün kaydedildiğinde otomatik olarak sisteme eklenecektir.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setNewCategoryName('');
                  handleCloseModal('newCategory', setShowNewCategoryModal);
                }}
              >
                İptal
              </button>
              <button
                className="btn-primary"
                disabled={!newCategoryName.trim()}
                onClick={() => {
                  const categoryName = newCategoryName.trim();
                  if (categoryName) {
                    // Yeni kategoriyi ürüne set et
                    const updatedProduct = {...newProduct, category: categoryName};
                    if (newProduct.metal_type === 'aksesuar' && categoryName === 'Gümüş Aksesuar') {
                      updatedProduct.silver_purity = '1000';
                    }
                    setNewProduct(updatedProduct);
                    // Dinamik kategorilere ekle (geçici olarak)
                    const metalType = newProduct.metal_type;
                    setDynamicCategories(prev => ({
                      ...prev,
                      [metalType]: [...(prev[metalType] || []), categoryName]
                    }));
                    setNewCategoryName('');
                    handleCloseModal('newCategory', setShowNewCategoryModal);
                    toast.success(`"${categoryName}" kategorisi eklendi`);
                  }
                }}
              >
                Kategoriyi Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ürün Grupları Listesi Modalı */}
      {showProductGroupsModal && (
        <div className="modal-overlay" onClick={() => setShowProductGroupsModal(false)} style={{ zIndex: 1100 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px', width: '92%' }}>
            <div className="modal-header">
              <h2><Package size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Ürün Grupları</h2>
              <button className="close-btn" onClick={() => setShowProductGroupsModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 0, marginBottom: '14px' }}>
                Birden çok ürünü tek satır olarak gösterir. Örn. "Alyans Grubu" oluşturup barkodları ekleyin;
                Ürün Yönetimi'nde tek satır çıkar, tıklayınca içindeki ürünler açılır.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
                <button className="btn primary" onClick={openNewProductGroupEditor}>
                  <Plus size={16} /> Yeni Ürün Grubu
                </button>
              </div>
              {loadingProductGroups ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>Yükleniyor...</p>
              ) : productGroups.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>Henüz ürün grubu yok</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {productGroups.map(group => {
                    const imgUrl = getProductImageUrl({ image: group.preview_image });
                    return (
                      <div key={group.id} style={{
                        display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
                        border: '1px solid #e2e8f0', borderRadius: '10px'
                      }}>
                        {imgUrl ? (
                          <img src={imgUrl} alt="" style={{ width: '44px', height: '44px', borderRadius: '8px', objectFit: 'cover', cursor: 'zoom-in' }} onClick={() => setZoomedImage(imgUrl)} />
                        ) : (
                          <div style={{ width: '44px', height: '44px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Package size={20} color="#94a3b8" />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{group.name}</div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {group.member_count} ürün{group.metal_type ? ` · ${group.metal_type}` : ''}
                            {group.member_names ? ` · ${group.member_names}` : ''}
                          </div>
                        </div>
                        <button className="btn secondary small" onClick={() => openEditProductGroupEditor(group)}>Düzenle</button>
                        <button className="btn danger small" onClick={() => deleteProductGroup(group)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ürün Grubu Oluştur/Düzenle Modalı */}
      {showProductGroupEditorModal && (
        <div className="modal-overlay" style={{ zIndex: 1300 }}>
          <div className="modal large group-editor-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '96%' }}>
            <div className="modal-header">
              <h2><Package size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />{editingProductGroupId ? 'Ürün Grubunu Düzenle' : 'Yeni Ürün Grubu'}</h2>
              <button className="close-btn" onClick={closeProductGroupEditor}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row" style={{ marginBottom: '14px', alignItems: 'flex-end', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ flex: '1 1 200px' }}>
                  <label>Grup Adı</label>
                  <input
                    type="text"
                    value={productGroupName}
                    onChange={(e) => setProductGroupName(e.target.value)}
                    placeholder="Örn: Alyans Grubu"
                  />
                </div>
                <div className="form-group" style={{ flex: '1 1 200px' }}>
                  <label>Barkod Okut / Gir</label>
                  <input
                    ref={productGroupBarcodeRef}
                    type="text"
                    value={productGroupBarcodeInput}
                    onChange={(e) => setProductGroupBarcodeInput(e.target.value)}
                    onKeyDown={handleAddProductGroupBarcode}
                    placeholder="Barkod okutun, Enter'a basın"
                  />
                </div>
                <div className="form-group" style={{ flex: '1 1 200px', position: 'relative' }}>
                  <label>İsim / Barkod ile Ara</label>
                  <input
                    type="text"
                    value={productGroupSearchTerm}
                    onChange={(e) => handleProductGroupSearchChange(e.target.value)}
                    placeholder="En az 2 karakter yazın..."
                  />
                  {(productGroupSearching || productGroupSearchResults.length > 0) && productGroupSearchTerm.trim().length >= 2 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: '4px',
                      background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.12)', maxHeight: '280px', overflowY: 'auto'
                    }}>
                      {productGroupSearching ? (
                        <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#94a3b8' }}>Aranıyor...</div>
                      ) : productGroupSearchResults.length === 0 ? (
                        <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#94a3b8' }}>Sonuç yok</div>
                      ) : productGroupSearchResults.map(p => {
                        const imgUrl = getProductImageUrl(p);
                        return (
                          <div
                            key={p.id}
                            onClick={() => addProductGroupMember(p)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                          >
                            {imgUrl ? (
                              <img src={imgUrl} alt="" style={{ width: '30px', height: '30px', borderRadius: '5px', objectFit: 'cover', cursor: 'zoom-in' }} onClick={(e) => { e.stopPropagation(); setZoomedImage(imgUrl); }} />
                            ) : (
                              <div style={{ width: '30px', height: '30px', borderRadius: '5px', background: '#f1f5f9', flexShrink: 0 }} />
                            )}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '0.84rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                              <div style={{ fontSize: '0.74rem', color: '#64748b' }}>{p.barcode}{p.category ? ` · ${p.category}` : ''}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <button type="button" className="btn secondary" onClick={() => openGroupProductPicker('product')}>
                  <ClipboardList size={15} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Tüm Ürünlerden Seç
                </button>
              </div>

              {productGroupMembers.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0' }}>Henüz ürün eklenmedi - yukarıdan barkod okutun, arayın veya "Tüm Ürünlerden Seç" ile ekleyin</p>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ width: '44px', padding: '10px 6px' }}></th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', minWidth: '180px' }}>Ürün</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', width: '140px' }}>Barkod</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', width: '130px' }}>Kategori</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {productGroupMembers.map(member => {
                        const imgUrl = getProductImageUrl(member);
                        return (
                          <tr key={member.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 6px' }}>
                              {imgUrl ? (
                                <img src={imgUrl} alt="" style={{ width: '34px', height: '34px', borderRadius: '6px', objectFit: 'cover', cursor: 'zoom-in' }} onClick={() => setZoomedImage(imgUrl)} />
                              ) : (
                                <div style={{ width: '34px', height: '34px', borderRadius: '6px', background: '#f1f5f9' }} />
                              )}
                            </td>
                            <td style={{ padding: '8px', fontWeight: 600 }}>{member.name}</td>
                            <td style={{ padding: '8px', color: '#64748b' }}>{member.barcode || '-'}</td>
                            <td style={{ padding: '8px', color: '#64748b' }}>{member.category || '-'}</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <button
                                className="table-btn delete"
                                title="Gruptan çıkar"
                                onClick={() => removeProductGroupMember(member.id)}
                              ><X size={16} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="form-group" style={{ marginTop: '14px' }}>
                <label>Not (opsiyonel)</label>
                <input
                  type="text"
                  value={productGroupNote}
                  onChange={(e) => setProductGroupNote(e.target.value)}
                  placeholder="Grup hakkında kısa not"
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px' }}>
              <button className="btn secondary" onClick={closeProductGroupEditor}>Vazgeç</button>
              <button className="btn primary" onClick={saveProductGroup} disabled={savingProductGroup}>
                {savingProductGroup ? 'Kaydediliyor...' : (editingProductGroupId ? 'Güncelle' : 'Grubu Oluştur')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grup Oluştur - tür seçim kartları */}
      {showGroupTypeChooserModal && (
        <div className="modal-overlay" onClick={() => setShowGroupTypeChooserModal(false)} style={{ zIndex: 1150 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', width: '92%' }}>
            <div className="modal-header">
              <h2><Package size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Grup Oluştur</h2>
              <button className="close-btn" onClick={() => setShowGroupTypeChooserModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 0, marginBottom: '16px' }}>
                Ne oluşturmak istersiniz?
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <button
                  type="button"
                  onClick={() => { setShowGroupTypeChooserModal(false); openVariantGroupsModal(); }}
                  disabled={!canAccessShopify}
                  style={{
                    textAlign: 'left', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px',
                    background: canAccessShopify ? '#fff' : '#f8fafc', cursor: canAccessShopify ? 'pointer' : 'not-allowed',
                    opacity: canAccessShopify ? 1 : 0.6, transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
                  }}
                  onMouseEnter={(e) => { if (canAccessShopify) { e.currentTarget.style.borderColor = '#00bfa6'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,191,166,0.12)'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '1rem', color: '#0d9488', marginBottom: '6px' }}>
                    <Layers size={20} /> Varyant Grubu
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.4 }}>
                    Beden / Renk gibi seçenekli ürünleri Shopify'da tek ürünün varyantları olarak birleştirir.
                    {!canAccessShopify && <span style={{ display: 'block', marginTop: '6px', color: '#b45309' }}>Shopify bağlantısı gerekli.</span>}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => { setShowGroupTypeChooserModal(false); openProductGroupsModal(); }}
                  style={{
                    textAlign: 'left', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px',
                    background: '#fff', cursor: 'pointer', transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00bfa6'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,191,166,0.12)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '1rem', color: '#0d9488', marginBottom: '6px' }}>
                    <Package size={20} /> Ürün Grubu
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.4 }}>
                    Birden çok ürünü (örn. tüm alyanslar) Ürün Yönetimi listesinde tek satır olarak gruplar; tıklayınca içindekiler açılır.
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* "Tüm Ürünlerden Seç" - barkodsuz toplu ürün seçici */}
      {showGroupProductPicker && (
        <div className="modal-overlay" style={{ zIndex: 1400 }}>
          <div className="modal large group-product-picker-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1100px', width: '96%' }}>
            <div className="modal-header">
              <h2><ClipboardList size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Tüm Ürünlerden Seç</h2>
              <button className="close-btn" onClick={() => setShowGroupProductPicker(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="gpp-filters">
                <div className="gpp-type-tabs">
                  {['gold', 'silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf']
                    .filter(t => allowedProductTypes.includes(t))
                    .map(t => (
                      <button
                        key={t}
                        type="button"
                        className={`gpp-type-tab ${groupPickerType === t ? 'active' : ''}`}
                        onClick={() => changeGroupPickerType(t)}
                      >
                        {{ gold: 'Altın', silver: 'Gümüş', steel: 'Çelik', zippo: 'Zippo', brands: 'Markalar', aksesuar: 'Aksesuar', sarf: 'Sarf' }[t]}
                      </button>
                    ))}
                </div>
                <select
                  className="gpp-input"
                  value={groupPickerCategory}
                  onChange={(e) => setGroupPickerCategory(e.target.value)}
                >
                  {getCategoriesForType(groupPickerType).map(c => (
                    <option key={c.id} value={c.id}>{c.name === 'Tümü' ? 'Tüm Kategoriler' : c.name}</option>
                  ))}
                </select>
                <input
                  className="gpp-input"
                  type="text"
                  placeholder="İsim / barkod ara..."
                  value={groupPickerSearch}
                  onChange={(e) => setGroupPickerSearch(e.target.value)}
                  style={{ flex: '1 1 180px', minWidth: '150px' }}
                />
                <input
                  className="gpp-input"
                  type="text"
                  placeholder="Marka..."
                  value={groupPickerBrand}
                  onChange={(e) => setGroupPickerBrand(e.target.value)}
                  style={{ width: '140px' }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" className="gpp-checkbox" checked={groupPickerInStockOnly} onChange={(e) => setGroupPickerInStockOnly(e.target.checked)} />
                  Sadece stokta
                </label>
              </div>

              <div className="gpp-table-wrap">
                <table className="gpp-table">
                  <thead>
                    <tr>
                      <th style={{ width: '36px' }}>
                        <input
                          type="checkbox"
                          className="gpp-checkbox"
                          onChange={toggleGroupPickerSelectAll}
                          checked={(() => {
                            const sel = groupPickerResults.map(p => p.id).filter(id => !groupPickerExistingIds.has(id));
                            return sel.length > 0 && sel.every(id => groupPickerSelected.has(id));
                          })()}
                        />
                      </th>
                      <th style={{ width: '44px' }}></th>
                      <th>Ürün</th>
                      <th style={{ width: '130px' }}>Barkod</th>
                      <th style={{ width: '120px' }}>Kategori</th>
                      <th style={{ width: '120px' }}>Marka</th>
                      <th style={{ width: '70px' }}>Stok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupPickerLoading ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>Yükleniyor...</td></tr>
                    ) : groupPickerResults.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>Sonuç yok</td></tr>
                    ) : groupPickerResults.map(p => {
                      const alreadyIn = groupPickerExistingIds.has(p.id);
                      const checked = alreadyIn || groupPickerSelected.has(p.id);
                      const imgUrl = getProductImageUrl(p);
                      return (
                        <tr
                          key={p.id}
                          className={`${checked && !alreadyIn ? 'selected' : ''} ${alreadyIn ? 'already-in' : ''}`}
                          onClick={() => toggleGroupPickerSelect(p)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="gpp-checkbox"
                              checked={checked}
                              disabled={alreadyIn}
                              onChange={() => toggleGroupPickerSelect(p)}
                            />
                          </td>
                          <td>
                            {imgUrl
                              ? <img src={imgUrl} alt="" className="gpp-thumb" />
                              : <div className="gpp-thumb" />}
                          </td>
                          <td style={{ fontWeight: 600 }}>
                            {p.is_set ? <span className="set-badge">SET</span> : null}{p.name}
                            {alreadyIn && <span style={{ marginLeft: '6px', fontSize: '0.72rem', color: '#0d9488', fontWeight: 700 }}>EKLİ</span>}
                          </td>
                          <td style={{ color: '#64748b' }}>{p.barcode || '-'}</td>
                          <td style={{ color: '#64748b' }}>{p.category || '-'}</td>
                          <td style={{ color: '#64748b' }}>{p.brand || '-'}</td>
                          <td>{p.stock ?? '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '14px 20px' }}>
              <span style={{ fontSize: '0.86rem', color: '#475569', fontWeight: 600 }}>{groupPickerSelected.size} ürün seçili</span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn secondary" onClick={() => setShowGroupProductPicker(false)}>Vazgeç</button>
                <button className="btn primary" onClick={confirmGroupProductPicker} disabled={groupPickerSelected.size === 0}>
                  Seçilenleri Ekle{groupPickerSelected.size > 0 ? ` (${groupPickerSelected.size})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yardım Modal */}
      {showHelpModal && (
        <div className={`modal-overlay ${closingModal === 'help' ? 'closing' : ''}`}>
          <div className="modal help-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header help-header">
              <h2><BookOpen size={20} style={{ marginRight: '8px' }} /> Ürün Yönetimi Yardım</h2>
              <button className="close-btn" onClick={() => handleCloseModal('help', setShowHelpModal)}>✕</button>
            </div>
            <div className="help-modal-content">
              {/* Genel Bilgi */}
              <div className="help-section">
                <h3><Package size={18} style={{ marginRight: '6px' }} /> Genel Bilgi</h3>
                <p>Bu sayfa üzerinden ürün stoğunuzu yönetebilir, yeni ürün ekleyebilir ve mevcut ürünleri düzenleyebilirsiniz. Altın ve Gümüş kategorileri ayrı sekmelerde listelenir.</p>
              </div>

              {/* Yeni Ürün */}
              <div className="help-section">
                <h3><Plus size={18} style={{ marginRight: '6px' }} /> Yeni Ürün Ekleme</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>+ Yeni Ürün</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Ürün adı ve barkod girin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Kategori ve fiyat bilgilerini doldurun</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span>Stok miktarını ve para birimini seçin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">5</span>
                    <span>Ürün resmini yükleyin (opsiyonel)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">6</span>
                    <span><strong>Kaydet</strong> butonuna tıklayın</span>
                  </div>
                </div>
              </div>

              {/* Stok Yönetimi */}
              <div className="help-section">
                <h3><BarChart3 size={18} style={{ marginRight: '6px' }} /> Stok Yönetimi</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">🟢</span>
                    <span><strong>Yeşil:</strong> Stok yeterli</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">🟡</span>
                    <span><strong>Sarı:</strong> Stok azalıyor (minimum stok seviyesine yakın)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">🔴</span>
                    <span><strong>Kırmızı:</strong> Stok kritik veya tükendi</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Minimum stok seviyesi belirleyerek otomatik uyarı alabilirsiniz.</div>
              </div>

              {/* Para Birimi */}
              <div className="help-section">
                <h3>💱 Para Birimi</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">₺</span>
                    <span><strong>TL:</strong> Türk Lirası cinsinden fiyat</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">$</span>
                    <span><strong>USD:</strong> Dolar cinsinden (satışta TL'ye çevrilir)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">€</span>
                    <span><strong>EUR:</strong> Euro cinsinden (satışta TL'ye çevrilir)</span>
                  </div>
                </div>
              </div>

              {/* Excel İşlemleri */}
              <div className="help-section">
                <h3><FileSpreadsheet size={18} style={{ marginRight: '6px' }} /> Excel İşlemleri</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">📥</span>
                    <span><strong>İçe Aktar:</strong> Excel dosyasından toplu ürün ekleyin. Barkod, ad, kategori, stok, fiyat, gramaj, işçilik bilgilerini içerir.</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">📤</span>
                    <span><strong>Dışa Aktar:</strong> Ürünleri Excel'e aktarın. Metal türü ve kategori seçerek filtreleyebilirsiniz.</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">📋</span>
                    <span><strong>Örnek Şablon:</strong> İçe aktarma için doğru formatta şablon indirin.</span>
                  </div>
                </div>
              </div>

              {/* Transfer İşlemleri */}
              <div className="help-section">
                <h3><ArrowLeftRight size={18} style={{ marginRight: '6px' }} /> Transfer İşlemleri</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">🏪</span>
                    <span><strong>Mağazalar Arası:</strong> Bir mağazadan diğerine ürün transferi yapın.</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">🏭</span>
                    <span><strong>Depodan Giriş:</strong> Depodan mağazaya yeni stok girişi yapın.</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">📝</span>
                    <span><strong>Talep Oluştur:</strong> Başka mağazadan ürün talebinde bulunun.</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">📦</span>
                    <span><strong>Transfer Al:</strong> Size gönderilen transferleri teslim alın.</span>
                  </div>
                </div>
              </div>

              {/* Formüllü Fiyatlandırma */}
              <div className="help-section">
                <h3><Calculator size={18} style={{ marginRight: '6px' }} /> Formüllü Fiyatlandırma</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">🥇</span>
                    <span><strong>Altın:</strong> Gramaj × (Ayar Çarpanı + İşçilik) × Altın Gram Fiyatı</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">🥈</span>
                    <span><strong>Gümüş:</strong> Gramaj × (Gümüş Gram Fiyatı + İşçilik)</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Ayarlar sayfasından manuel kur girerek fiyatları kontrol edebilirsiniz.</div>
              </div>

              {/* Butonlar */}
              <div className="help-section">
                <h3><CircleDot size={18} style={{ marginRight: '6px' }} /> Buton Açıklamaları</h3>
                <div className="help-buttons-grid">
                  <div className="help-button-item">
                    <span className="btn-icon"><Plus size={18} /></span>
                    <div>
                      <strong>Yeni Ürün</strong>
                      <p>Yeni ürün kaydı oluşturur</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ScrollText size={18} /></span>
                    <div>
                      <strong>İşlem Geçmişi</strong>
                      <p>Tüm işlem loglarını gösterir</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Pencil size={18} /></span>
                    <div>
                      <strong>Düzenle</strong>
                      <p>Ürün bilgilerini günceller</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ClipboardList size={18} /></span>
                    <div>
                      <strong>Satış Geçmişi</strong>
                      <p>Ürünün satış kayıtlarını gösterir</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Settings size={18} /></span>
                    <div>
                      <strong>Sütunlar</strong>
                      <p>Görünür sütunları ayarlar</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Trash2 size={18} /></span>
                    <div>
                      <strong>Sil</strong>
                      <p>Ürünü kalıcı olarak siler</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* İpuçları */}
              <div className="help-section tips-section">
                <h3><Lightbulb size={18} style={{ marginRight: '6px', color: '#f59e0b' }} /> İpuçları</h3>
                <ul className="help-tips-list">
                  <li>Altın ve Gümüş sekmelerini kullanarak hızlıca geçiş yapın</li>
                  <li>Arama kutusu ile barkod veya ürün adı arayın</li>
                  <li>Resme tıklayarak büyük görüntüleyin</li>
                  <li>Sütunlar menüsünden tabloyu özelleştirin</li>
                  <li>Sayfa sonuna kaydırınca otomatik yükleme yapılır</li>
                  <li>USD/EUR ürünler satışta güncel kurla TL'ye çevrilir</li>
                  <li>Son eklenen ürünün bilgilerini otomatik doldurabilirsiniz</li>
                  <li>Çoklu mağaza stok durumunu tabloda görüntüleyebilirsiniz</li>
                  <li>Transfer geçmişini sol menüden takip edebilirsiniz</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Seçenekleri Modal */}
      {showTransferOptionsModal && (
        <div className={`modal-overlay ${closingModal === 'transferOptions' ? 'closing' : ''}`}>
          <div className="modal options-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔄 Transfer İşlemleri</h2>
              <button className="close-btn" onClick={() => handleCloseModal('transferOptions', setShowTransferOptionsModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="options-grid">
                <button
                  className="option-card"
                  onClick={() => handleTransferOptionSelect('store')}
                >
                  <span className="option-icon">🏪</span>
                  <span className="option-title">Mağazalar Arası Transfer</span>
                  <span className="option-desc">Başka bir mağazaya ürün transfer et</span>
                </button>
                <button
                  className="option-card disabled"
                  disabled
                  title="Bu özellik yakında aktif olacak"
                >
                  <span className="option-icon">📦</span>
                  <span className="option-title">Depoya Transfer</span>
                  <span className="option-desc">Yakında...</span>
                </button>
                <button
                  className="option-card request"
                  onClick={openTransferRequestModal}
                >
                  <span className="option-icon">📋</span>
                  <span className="option-title">Transfer Talebi Oluştur</span>
                  <span className="option-desc">Başka mağazadan ürün talep et</span>
                </button>
                <button
                  className="option-card incoming"
                  onClick={openIncomingRequestsModal}
                >
                  <span className="option-icon">📥</span>
                  <span className="option-title">Gelen Talepler</span>
                  <span className="option-desc">Size gelen transfer taleplerini görün</span>
                  {incomingRequestCount > 0 && (
                    <span className="request-badge">{incomingRequestCount}</span>
                  )}
                </button>
                <button
                  className="option-card repair"
                  onClick={openSendToRepairModal}
                >
                  <span className="option-icon">🔧</span>
                  <span className="option-title">Tamirata Gönder</span>
                  <span className="option-desc">Ürünleri tamirata gönder</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className={`modal-overlay ${closingModal === 'transfer' ? 'closing' : ''}`}>
          <div className="modal transfer-modal large-modal" onClick={(e) => e.stopPropagation()} style={{ minHeight: '600px' }}>
            <div className="modal-header">
              <h2><RefreshCw size={20} style={{ marginRight: '8px' }} /> {transferType === 'store' ? 'Mağazalar Arası Transfer' : 'Depoya Transfer'}</h2>
              {selectedStore && <span className="store-badge">{selectedStore.name}</span>}
              <button className="close-btn" onClick={closeTransferModal}>✕</button>
            </div>
            <div className="modal-body">
              {!selectedStore ? (
                <div className="warning-message">
                  <p><AlertTriangle size={16} style={{ marginRight: '6px' }} /> Lütfen önce sol üstten bir mağaza seçin</p>
                </div>
              ) : (
                <div className="transfer-split-layout">
                <div className="transfer-left-panel">
                  <div className="transfer-form-row-inline">
                    {transferType === 'store' && (
                      <div className="transfer-form-row">
                        <label>Hedef Mağaza</label>
                        <select
                          value={transferTargetStore}
                          onChange={(e) => setTransferTargetStore(e.target.value)}
                          className="transfer-store-select"
                        >
                          <option value="">-- Mağaza Seçin --</option>
                          {stores.filter(s => s.id !== selectedStore?.id).map(store => (
                            <option key={store.id} value={store.id}>{store.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="transfer-form-row">
                      <label>Kasiyer</label>
                      <select
                        value={transferStaffId}
                        onChange={(e) => {
                          const staff = transferStaffList.find(s => s.id === parseInt(e.target.value));
                          setTransferStaffId(staff ? staff.id : '');
                          setTransferStaffName(staff ? staff.name : '');
                        }}
                        className="transfer-store-select"
                      >
                        <option value="">-- Kasiyer Seçin --</option>
                        {transferStaffList.map(staff => (
                          <option key={staff.id} value={staff.id}>{staff.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="transfer-form-row">
                      <label>Barkod Okutun</label>
                      <input
                        ref={transferBarcodeRef}
                        type="text"
                        value={transferBarcode}
                        onChange={(e) => setTransferBarcode(e.target.value)}
                        onKeyDown={handleTransferBarcode}
                        placeholder="Barkod okutun veya yazın..."
                        className="transfer-barcode-input"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="transfer-left-scroll">
                  <div className="inline-transfer-form">
                  {transferItems.length > 0 && (
                    <div className="transfer-items-section">
                      <h4>Transfer Edilecek Ürünler ({transferItems.length})</h4>
                      <div className="transfer-items-list">
                        {transferItems.map((item, index) => (
                          <div key={item.product_id} className="transfer-item-row">
                            <div className="transfer-item-image" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && setZoomedImage(`${IMAGE_HOST}${item.image}`)}>
                              {item.image ? (
                                <img src={`${IMAGE_HOST}${item.image}`} alt={item.product_name} />
                              ) : (
                                <div className="no-image-placeholder"><Package size={24} /></div>
                              )}
                            </div>
                            <div className="transfer-item-info">
                              <span className="transfer-item-name">{item.product_name}</span>
                              <span className="transfer-item-barcode">{item.barcode}</span>
                              <span className="transfer-item-stock">Stok: {item.stock_quantity}</span>
                            </div>
                            <div className="transfer-item-quantity">
                              <button onClick={() => updateTransferItemQuantity(index, item.quantity - 1)} className="qty-btn">-</button>
                              <span onDoubleClick={(e) => { const span = e.target; const input = document.createElement('input'); input.type = 'number'; input.min = '1'; input.value = item.quantity; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #00bfa6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.max(1, parseInt(input.value) || 1); updateTransferItemQuantity(index, val); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>{item.quantity}</span>
                              <button onClick={() => updateTransferItemQuantity(index, item.quantity + 1)} className="qty-btn" disabled={item.quantity >= item.stock_quantity}>+</button>
                            </div>
                            <button className="transfer-item-remove" onClick={() => removeTransferItem(index)}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="transfer-form-row">
                    <label>Not (Opsiyonel)</label>
                    <textarea
                      value={transferNotes}
                      onChange={(e) => setTransferNotes(e.target.value)}
                      placeholder="Transfer ile ilgili not ekleyin..."
                      className="transfer-notes-input"
                      rows={2}
                    />
                  </div>
                  </div>
                  </div>
                </div>
                <div className="transfer-right-panel">
                  {lastScannedTransferProduct ? (
                    <>
                      <div className="scanned-product-image" style={lastScannedTransferProduct.image ? { cursor: 'zoom-in' } : {}} onClick={() => lastScannedTransferProduct.image && setZoomedImage(`${IMAGE_HOST}${lastScannedTransferProduct.image}`)}>
                        {lastScannedTransferProduct.image ? (
                          <img src={`${IMAGE_HOST}${lastScannedTransferProduct.image}`} alt={lastScannedTransferProduct.name} />
                        ) : (
                          <div className="scanned-product-placeholder"><Package size={64} /></div>
                        )}
                      </div>
                      <div className="scanned-product-name">{lastScannedTransferProduct.name}</div>
                      <div className="scanned-product-barcode">{lastScannedTransferProduct.barcode}</div>
                    </>
                  ) : (
                    <div className="scanned-product-empty">
                      <Package size={48} />
                      <span>Barkod okutun</span>
                    </div>
                  )}
                  <div className="transfer-form-actions" style={{ marginTop: 'auto', paddingTop: '16px' }}>
                    <button className="transfer-cancel-btn" onClick={closeTransferModal}>İptal</button>
                    <button
                      className="transfer-submit-btn"
                      onClick={createTransfer}
                      disabled={!selectedStore || transferItems.length === 0 || savingTransfer || (transferType === 'store' && !transferTargetStore)}
                    >
                      {savingTransfer ? 'Kaydediliyor...' : `Transfer Oluştur (${transferItems.length} çeşit toplam ${transferItems.reduce((s, i) => s + (Number(i.quantity) || 0), 0)} ürün)`}
                    </button>
                  </div>
                </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stok Alım Seçenekleri Modal */}
      {showStockIntakeOptionsModal && (
        <div className={`modal-overlay ${closingModal === 'stockIntakeOptions' ? 'closing' : ''}`}>
          <div className="modal options-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Download size={20} style={{ marginRight: '8px' }} /> Stok Alımı</h2>
              <button className="close-btn" onClick={() => handleCloseModal('stockIntakeOptions', setShowStockIntakeOptionsModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="options-grid">
                <button
                  className="option-card"
                  onClick={() => handleStockIntakeOptionSelect('new')}
                >
                  <span className="option-icon"><Package size={32} /></span>
                  <span className="option-title">Yeni Ürün Teslim Al</span>
                  <span className="option-desc">Tedarikçiden gelen yeni ürünleri kaydet</span>
                </button>
                <button
                  className="option-card"
                  onClick={() => handleStockIntakeOptionSelect('transfer')}
                  style={{ position: 'relative' }}
                >
                  {pendingTransferCount > 0 && (
                    <span className="menu-badge" style={{ position: 'absolute', top: 8, left: 8, marginLeft: 0 }}>{pendingTransferCount}</span>
                  )}
                  <span className="option-icon"><RefreshCw size={32} /></span>
                  <span className="option-title">Mağazadan Transfer Teslim Al</span>
                  <span className="option-desc">Başka mağazadan gelen transferleri teslim al</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Teslim Alma Modal */}
      {showReceiveTransferModal && (
        <div className={`modal-overlay ${closingModal === 'receiveTransfer' ? 'closing' : ''}`}>
          <div className="modal receive-transfer-modal large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Download size={20} style={{ marginRight: '8px' }} /> Transfer Teslim Al</h2>
              <button className="close-btn" onClick={closeReceiveTransferModal}>✕</button>
            </div>
            <div className="modal-body">
              {loadingTransfers ? (
                <div className="loading-state">Yükleniyor...</div>
              ) : pendingTransfers.length === 0 ? (
                <div className="empty-state">
                  <Inbox size={48} />
                  <p>Bekleyen transfer bulunmuyor.</p>
                </div>
              ) : (
                <>
                <div className="receive-transfer-content">
                  {/* Sol: Transfer Listesi */}
                  <div className="transfers-list">
                    <h3>Bekleyen Transferler</h3>
                    <p style={{ fontSize: '13px', color: '#666', margin: '0 0 12px 0' }}>Teslim almak istediğiniz transferi seçin, ardından barkod okutun.</p>
                    {pendingTransfers.map(transfer => {
                      const receivedTotal = Number(transfer.received_total) || 0;
                      const totalQty = Number(transfer.total_quantity) || 0;
                      const remainingQty = totalQty - receivedTotal;
                      const isPartial = receivedTotal > 0;
                      return (
                      <div
                        key={transfer.id}
                        className={`transfer-card ${selectedTransferForReceive?.id === transfer.id ? 'selected' : ''}`}
                        onClick={() => selectTransferForReceive(transfer)}
                      >
                        <div className="transfer-code">{transfer.transfer_code}</div>
                        <div className="transfer-from">Gönderen: {transfer.from_store_name}</div>
                        {transfer.notes && (
                          <div style={{ fontSize: '12px', color: '#92400e', background: '#fffbeb', padding: '4px 8px', borderRadius: '4px', margin: '4px 0' }}>
                            {transfer.notes}
                          </div>
                        )}
                        <div className="transfer-info">
                          {transfer.item_count} ürün, {isPartial ? (
                            <span style={{ fontWeight: 'bold' }}>
                              <span style={{ color: '#28a745' }}>{receivedTotal} teslim alındı</span>
                              {' / '}
                              <span style={{ color: '#dc3545' }}>{remainingQty} kalan</span>
                              <span style={{ color: '#999', fontWeight: 'normal' }}> (toplam {totalQty})</span>
                            </span>
                          ) : (
                            <span>{totalQty} adet</span>
                          )}
                        </div>
                        {isPartial && (
                          <div style={{ width: '100%', height: '8px', background: '#e9ecef', borderRadius: '4px', margin: '8px 0', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.round((receivedTotal / totalQty) * 100)}%`, height: '100%', background: '#28a745', borderRadius: '4px', transition: 'width 0.3s ease' }} />
                          </div>
                        )}
                        <div className="transfer-date">
                          {new Date(transfer.created_at).toLocaleDateString('tr-TR')}
                        </div>
                        <span className={`status-badge ${transfer.status}`}>
                          {transfer.status === 'pending' ? 'Bekliyor' : transfer.status === 'partially_received' ? 'Kısmi Teslim' : 'Yolda'}
                        </span>
                      </div>
                      );
                    })}
                  </div>

                  {/* Sağ: Transfer Detayı */}
                  <div className="transfer-detail">
                    {selectedTransferForReceive ? (
                      <>
                        <h3>Transfer Detayı: {selectedTransferForReceive.transfer_code}</h3>
                        <div className="receive-detail-row">
                          <div className="receive-detail-info">
                            <p><strong>Gönderen:</strong> {selectedTransferForReceive.from_store_name}</p>
                            <p><strong>Oluşturan:</strong> {selectedTransferForReceive.created_by_staff || selectedTransferForReceive.created_by_name || '-'}</p>
                            <p><strong>Tarih:</strong> {new Date(selectedTransferForReceive.created_at).toLocaleString('tr-TR')}</p>
                            {selectedTransferForReceive.notes && (
                              <p className="receive-detail-note">
                                <strong>Not:</strong> {selectedTransferForReceive.notes}
                              </p>
                            )}
                          </div>
                          <form onSubmit={handleTransferBarcodeSubmit} className="receive-barcode-form" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>
                              Seans: {transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0)} / Kalan: {transferDetailItems.reduce((sum, item) => sum + item.quantity - (item.already_received || 0), 0)}
                              {transferDetailItems.some(item => (item.already_received || 0) > 0) && (
                                <> (Önceki: {transferDetailItems.reduce((sum, item) => sum + (item.already_received || 0), 0)})</>
                              )}
                            </span>
                            <div className="barcode-input-wrapper" style={{ flexDirection: 'row-reverse' }}>
                              <Search size={16} />
                              <input
                                type="text"
                                ref={transferBarcodeInputRef}
                                value={transferBarcodeInput}
                                onChange={(e) => setTransferBarcodeInput(e.target.value)}
                                placeholder="Barkod okutun..."
                                autoFocus
                              />
                            </div>
                          </form>
                        </div>

                        {/* Eksi stoklu ürün uyarısı */}
                        {transferDetailItems.some(item => item.current_stock_at_destination < 0) && (
                          <div className="negative-stock-warning" style={{
                            background: 'linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%)',
                            border: '1px solid #ffc107', borderRadius: '8px', padding: '12px 16px',
                            marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px'
                          }}>
                            <span style={{ fontSize: '24px' }}>⚠️</span>
                            <div>
                              <strong style={{ color: '#856404' }}>Dikkat: Eksi Stoklu Ürünler Var!</strong>
                              <p style={{ margin: '4px 0 0 0', color: '#856404', fontSize: '13px' }}>
                                Aşağıdaki ürünler müşteriye satıldı ve stoklarınız eksiye düştü. Teslim alındığında stoklar otomatik sıfırlanacak.
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="transfer-items-list">
                          <table className="transfer-table">
                            <thead>
                              <tr>
                                <th></th>
                                <th>Foto</th>
                                <th>Ürün</th>
                                <th>Barkod</th>
                                <th>Mevcut Stok</th>
                                <th>Gönderilen</th>
                                <th>Önceki Teslim</th>
                                <th>Kalan</th>
                                <th>Bu Seans</th>
                              </tr>
                            </thead>
                            <tbody>
                              {transferDetailItems.map((item, index) => {
                                const totalReceived = (item.already_received || 0) + (item.received_quantity || 0);
                                const isComplete = totalReceived >= item.quantity;
                                const isPartial = totalReceived > 0;
                                return (
                                <tr key={index} className={`${isComplete ? 'received-complete' : isPartial ? 'received-partial' : ''} ${item.current_stock_at_destination < 0 ? 'negative-stock-row' : ''}`}>
                                  <td className="status-icon-cell">
                                    {isComplete ? (
                                      <span className="check-icon">✓</span>
                                    ) : isPartial ? (
                                      <span className="partial-icon">◐</span>
                                    ) : (
                                      <span className="waiting-icon">○</span>
                                    )}
                                  </td>
                                  <td style={{ width: '56px', padding: '4px' }}>
                                    {item.image ? (
                                      <img src={`${API_HOST}${item.image}`} alt="" style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer' }} onClick={() => item.image && setZoomedImage(`${API_HOST}${item.image}`)} />
                                    ) : (
                                      <div style={{ width: '52px', height: '52px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Package size={22} color="#94a3b8" />
                                      </div>
                                    )}
                                  </td>
                                  <td className="product-cell">
                                    <span>{item.product_name}</span>
                                    {item.current_stock_at_destination < 0 && (
                                      <span className="sold-badge">SATILDI</span>
                                    )}
                                  </td>
                                  <td>{item.barcode}</td>
                                  <td style={{ color: item.current_stock_at_destination < 0 ? '#dc3545' : 'inherit', fontWeight: item.current_stock_at_destination < 0 ? 'bold' : 'normal' }}>
                                    {item.current_stock_at_destination}
                                  </td>
                                  <td>{item.quantity}</td>
                                  <td style={{ color: (item.already_received || 0) > 0 ? '#28a745' : '#999', fontWeight: (item.already_received || 0) > 0 ? 'bold' : 'normal' }}>
                                    {item.already_received || 0}
                                  </td>
                                  <td style={{ color: (item.quantity - (item.already_received || 0)) > 0 ? '#dc3545' : '#28a745', fontWeight: 'bold' }}>
                                    {item.quantity - (item.already_received || 0)}
                                  </td>
                                  <td className="received-qty">
                                    <div className="quantity-controls">
                                      <button onClick={() => {
                                        if ((item.received_quantity || 0) <= 0) return;
                                        const updated = [...transferDetailItems];
                                        updated[index] = { ...item, received_quantity: item.received_quantity - 1 };
                                        setTransferDetailItems(updated);
                                      }}>-</button>
                                      <span className={isComplete ? 'complete' : isPartial ? 'partial' : ''} style={{ cursor: 'pointer' }} onDoubleClick={(e) => { const span = e.target; const remaining = item.quantity - (item.already_received || 0); const input = document.createElement('input'); input.type = 'number'; input.min = '0'; input.max = remaining; input.value = item.received_quantity || 0; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #00bfa6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.min(Math.max(0, parseInt(input.value) || 0), remaining); const updated = [...transferDetailItems]; updated[index] = { ...item, received_quantity: val }; setTransferDetailItems(updated); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>
                                        {item.received_quantity || 0}
                                      </span>
                                      <button onClick={() => {
                                        const remaining = item.quantity - (item.already_received || 0);
                                        if ((item.received_quantity || 0) >= remaining) return;
                                        const updated = [...transferDetailItems];
                                        updated[index] = { ...item, received_quantity: (item.received_quantity || 0) + 1 };
                                        setTransferDetailItems(updated);
                                      }}>+</button>
                                    </div>
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="empty-detail">
                        <Inbox size={48} />
                        <p>Teslim almak için sol taraftan bir transfer seçin</p>
                      </div>
                    )}
                  </div>
                </div>
                {selectedTransferForReceive && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 0 0 0' }}>
                    <button
                      className="receive-btn"
                      onClick={receiveTransfer}
                      disabled={transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0) === 0}
                      style={{ padding: '6px 16px', fontSize: '0.8rem', width: 'auto' }}
                    >
                      ✓ Teslim Al ({transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0)})
                    </button>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tasarım Seçim Modalı */}
      {showDesignSelectModal && (
        <div className={`modal-overlay ${closingModal === 'designSelect' ? 'closing' : ''}`} style={{ zIndex: 10003 }}>
          <div className="modal design-select-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
            <div className="modal-header">
              <h2><Tag size={20} style={{ marginRight: '8px' }} /> Etiket Tasarımı Seçin</h2>
              <span style={{ fontSize: '13px', color: '#888', marginLeft: '8px' }}>
                {pendingPrintProducts.length} ürün yazdırılacak
              </span>
              <button className="close-btn" onClick={() => { handleCloseModal('designSelect', setShowDesignSelectModal); setPrintQuantity(1); }}>✕</button>
            </div>
            <div style={{ padding: '6px 16px', borderBottom: '1px solid #eee', fontSize: '12px', color: zebraStatus?.ok ? '#15803d' : '#888' }}>
              {zebraStatus === null ? 'Zebra yazıcı kontrol ediliyor...'
                : zebraStatus.ok ? `Zebra doğrudan baskı: ${zebraStatus.printer?.name || 'yazıcı bağlı'}`
                : zebraStatus.reason === 'no-printer' ? 'Qubite Print açık ama Zebra yazıcı bulunamadı — normal yazdırma kullanılacak'
                : 'Qubite Print bulunamadı — normal yazdırma kullanılacak'}
            </div>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: '12px' }}>
              {labelQtyMode === 'stock' ? (
                <span style={{ fontSize: '12px', color: '#555' }}>
                  Stok Bazlı: her ürün, seçili kaynaktaki stok kadar basılacak — toplam{' '}
                  <strong>{pendingPrintProducts.reduce((sum, p) => sum + getLabelQtyForProduct(p), 0)} etiket</strong>
                </span>
              ) : (
                <>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>Çıktı Adeti:</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={printQuantity}
                    onChange={(e) => setPrintQuantity(e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value) || ''))}
                    onBlur={() => setPrintQuantity(prev => (!prev && prev !== 0) ? 1 : Math.max(1, parseInt(prev) || 1))}
                    style={{
                      width: '70px',
                      padding: '6px 10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      textAlign: 'center',
                      fontWeight: 600,
                    }}
                  />
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    (Her ürün {printQuantity} kez yazdırılacak{printQuantity > 1 ? ` — toplam ${pendingPrintProducts.length * printQuantity} etiket` : ''})
                  </span>
                </>
              )}
            </div>
            <div className="modal-body" style={{ padding: '16px', maxHeight: '500px', overflowY: 'auto' }}>
              {loadingDesigns ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>
                  <RefreshCw size={24} style={{ marginBottom: '8px', animation: 'spin 1s linear infinite' }} />
                  <p>Tasarımlar yükleniyor...</p>
                </div>
              ) : availableDesigns.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>
                  <Tag size={32} style={{ marginBottom: '12px', opacity: 0.4 }} />
                  <p>Henüz tasarım oluşturulmamış.</p>
                  <p style={{ fontSize: '13px', marginTop: '8px' }}>Etiket Tasarım sayfasından yeni tasarım oluşturabilirsiniz.</p>
                </div>
              ) : (
                <>
                  {/* Altın tasarımlar */}
                  {availableDesigns.some(d => d.type === 'gold') && pendingPrintProducts.some(p => p.metal_type === 'gold') && (
                    <>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#b8860b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Coins size={14} /> Altın Tasarımları
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                        {availableDesigns.filter(d => d.type === 'gold').map(design => (
                          <button
                            key={design.id}
                            className="design-select-card"
                            onClick={() => printWithSelectedDesign(design)}
                            style={{
                              padding: '14px 16px',
                              border: design.is_active ? '2px solid #b8860b' : '1px solid #e0e0e0',
                              borderRadius: '10px',
                              background: design.is_active ? 'linear-gradient(135deg, #fffbe6 0%, #fff8e1 100%)' : '#fff',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'all 0.15s',
                              position: 'relative',
                            }}
                          >
                            {design.is_active && (
                              <span style={{
                                position: 'absolute', top: '6px', right: '8px',
                                fontSize: '10px', background: '#b8860b', color: '#fff',
                                padding: '2px 8px', borderRadius: '10px', fontWeight: 600
                              }}>Aktif</span>
                            )}
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                              {design.name || design.id}
                            </div>
                            <div style={{ fontSize: '11px', color: '#999' }}>
                              {design.dimensions?.labelWidth}x{design.dimensions?.labelHeight}mm
                              {design.elements ? ` • ${design.elements.length} alan` : ''}
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {/* Gümüş tasarımlar */}
                  {availableDesigns.some(d => d.type === 'silver') && pendingPrintProducts.some(p => p.metal_type !== 'gold') && (
                    <>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#666', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Gem size={14} /> Gümüş Tasarımları
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                        {availableDesigns.filter(d => d.type === 'silver').map(design => (
                          <button
                            key={design.id}
                            className="design-select-card"
                            onClick={() => printWithSelectedDesign(design)}
                            style={{
                              padding: '14px 16px',
                              border: design.is_active ? '2px solid #888' : '1px solid #e0e0e0',
                              borderRadius: '10px',
                              background: design.is_active ? 'linear-gradient(135deg, #f5f5f5 0%, #ececec 100%)' : '#fff',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'all 0.15s',
                              position: 'relative',
                            }}
                          >
                            {design.is_active && (
                              <span style={{
                                position: 'absolute', top: '6px', right: '8px',
                                fontSize: '10px', background: '#666', color: '#fff',
                                padding: '2px 8px', borderRadius: '10px', fontWeight: 600
                              }}>Aktif</span>
                            )}
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                              {design.name || design.id}
                            </div>
                            <div style={{ fontSize: '11px', color: '#999' }}>
                              {design.dimensions?.labelWidth}x{design.dimensions?.labelHeight}mm
                              {design.elements ? ` • ${design.elements.length} alan` : ''}
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {/* Tüm tasarımları göster (karışık ürün tipleri yoksa, sadece doğru tip gösterilir zaten) */}
                  {/* Hiçbir tasarım gösterilmediyse - ör. altın ürün var ama sadece gümüş tasarım var */}
                  {!availableDesigns.some(d =>
                    (d.type === 'gold' && pendingPrintProducts.some(p => p.metal_type === 'gold')) ||
                    (d.type === 'silver' && pendingPrintProducts.some(p => p.metal_type !== 'gold'))
                  ) && (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#888' }}>
                      <p>Bu ürün tipi için tasarım bulunamadı.</p>
                      <p style={{ fontSize: '13px', marginTop: '8px' }}>
                        Mevcut tasarımlardan birini kullanabilirsiniz:
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px', marginTop: '12px' }}>
                        {availableDesigns.map(design => (
                          <button
                            key={design.id}
                            className="design-select-card"
                            onClick={() => printWithSelectedDesign(design)}
                            style={{
                              padding: '14px 16px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '10px',
                              background: '#fff',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                              {design.name || design.id}
                            </div>
                            <div style={{ fontSize: '11px', color: '#999' }}>
                              {design.type === 'gold' ? 'Altın' : 'Gümüş'} • {design.dimensions?.labelWidth}x{design.dimensions?.labelHeight}mm
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stok Uyarı Bildirimleri Modal */}
      {showStockAlertModal && (
        <div className={`modal-overlay ${closingModal === 'stockAlert' ? 'closing' : ''}`}>
          <div className="modal stock-alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Bell size={20} style={{ marginRight: '8px' }} /> Stok Uyarıları</h2>
              <button className="close-btn" onClick={() => handleCloseModal('stockAlert', setShowStockAlertModal)}>✕</button>
            </div>
            <div className="modal-body">
              {loadingStockAlerts ? (
                <div className="loading-spinner">
                  <RefreshCw className="spin" size={24} />
                  <span>Stok uyarıları yükleniyor...</span>
                </div>
              ) : stockAlerts.length === 0 ? (
                <div className="empty-alerts">
                  <Bell size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
                  <p>Stok uyarısı bulunmuyor</p>
                  <span>Stoğunuz tükenen ürünler başka mağazalarda mevcut olduğunda burada bildirim göreceksiniz.</span>
                </div>
              ) : (
                <div className="stock-alerts-list">
                  <p className="alerts-description">
                    Aşağıdaki ürünlerin stoğunuz satış sonrası tükendi. Başka mağazalardan talep edebilirsiniz.
                  </p>
                  <table className="stock-alerts-table">
                    <thead>
                      <tr>
                        <th>Tür</th>
                        <th>Ürün</th>
                        <th>Barkod</th>
                        <th>Sebep</th>
                        <th>Kaynak Mağaza</th>
                        <th>Tarih</th>
                        <th>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockAlerts.map((alert) => {
                        const reasonText = alert.alertType === 'depleted'
                          ? `Stoğunuz tükendi${alert.availableStore ? ` — ${alert.availableStore} mağazasında ${alert.availableStock} adet mevcut` : ''}`
                          : `Kritik stok eşiğinin altına düştü (mevcut: ${alert.myStock} / eşik: ${alert.threshold})${alert.availableStore ? ` — ${alert.availableStore} mağazasında ${alert.availableStock} adet var` : ''}`;
                        const createdLabel = alert.createdAt
                          ? new Date(alert.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '-';
                        return (
                        <tr key={alert.id}>
                          <td>
                            {alert.alertType === 'depleted' ? (
                              <span style={{ padding: '3px 8px', borderRadius: '6px', background: '#fee2e2', color: '#991b1b', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                Tükenmiş
                              </span>
                            ) : (
                              <span style={{ padding: '3px 8px', borderRadius: '6px', background: '#fef3c7', color: '#92400e', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                Kritik Stok
                              </span>
                            )}
                          </td>
                          <td className="alert-product-name">{alert.product.name}</td>
                          <td>{alert.product.barcode}</td>
                          <td style={{ fontSize: '0.82rem', color: '#334155', maxWidth: '360px' }}>{reasonText}</td>
                          <td>
                            {alert.availableStore ? (
                              <span>{alert.availableStore} <span style={{ color: '#64748b', fontSize: '0.78rem' }}>({alert.availableStock} adet)</span></span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>-</span>
                            )}
                          </td>
                          <td style={{ fontSize: '0.78rem', color: '#64748b', whiteSpace: 'nowrap' }}>{createdLabel}</td>
                          <td className="alert-actions">
                            {alert.availableStoreId && (
                              <button
                                className="btn primary small"
                                onClick={() => handleStockAlertRequest(alert)}
                              >
                                Talep Et
                              </button>
                            )}
                            <button
                              className="btn secondary small"
                              onClick={() => removeStockAlert(alert.id)}
                              title="Bildirimi kaldır"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {stockAlerts.length > 0 && (
                <button
                  className="btn danger"
                  onClick={async () => {
                    const storeId = user?.role === 'cashier' ? user?.store_id : (selectedStore?.id || user?.store_id);
                    if (!storeId) return;
                    try {
                      await fetch(`${API_URL}/stock-alerts?store_id=${storeId}`, { method: 'DELETE' });
                      fetchStockAlerts();
                      toast.success('Tüm bildirimler temizlendi');
                    } catch (err) {
                      console.error(err);
                      toast.error('Silinemedi');
                    }
                  }}
                >
                  Tümünü Temizle
                </button>
              )}
              <button className="btn secondary" onClick={() => setShowStockAlertModal(false)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detaylı Filtre Modal */}
      {showAdvancedFilter && (
        <div className="modal-overlay">
          <div className="modal advanced-filter-modal pf-modal">
            <div className="modal-header">
              <h2><SlidersHorizontal size={20} style={{ marginRight: '8px' }} /> Detaylı Filtre</h2>
              <button className="close-btn" onClick={() => setShowAdvancedFilter(false)}>✕</button>
            </div>
            <div className="modal-body">
              {(() => {
                // Yardımcılar: tek satırlık alan tanımları (4 sütunlu kompakt düzen)
                const bind = (key) => ({
                  value: advancedFilters[key],
                  onChange: (e) => setAdvancedFilters(prev => ({ ...prev, [key]: e.target.value }))
                });
                const text = (key, label, placeholder) => (
                  <div className="pf-field" key={key}>
                    <label>{label}</label>
                    <input type="text" placeholder={placeholder} {...bind(key)} />
                  </div>
                );
                const date = (key, label, title) => (
                  <div className="pf-field" key={key} title={title}>
                    <label>{label}</label>
                    <input type="date" {...bind(key)} />
                  </div>
                );
                const num = (key, label, step, placeholder, title) => (
                  <div className="pf-field" key={key} title={title}>
                    <label>{label}</label>
                    <input type="number" min="0" step={step} placeholder={placeholder} {...bind(key)} />
                  </div>
                );
                // Dropdown: seçili değer listede yoksa da (eski metin filtresi) kaybolmasın
                const select = (key, label, options, allLabel = 'Tümü', title) => {
                  const current = advancedFilters[key];
                  const opts = options.map(o => (typeof o === 'object' ? o : { value: o, label: o }));
                  if (current && !opts.some(o => String(o.value) === String(current))) opts.unshift({ value: current, label: current });
                  return (
                    <div className="pf-field" key={key} title={title}>
                      <label>{label}</label>
                      <select {...bind(key)}>
                        <option value="">{allLabel}</option>
                        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  );
                };
                const yesNo = (key, label, yes, no) => select(key, label, [{ value: 'yes', label: yes }, { value: 'no', label: no }]);
                const pair = (a, b) => <div className="pf-pair">{a}{b}</div>;

                // Altın / gümüş ayarı: seçili metal türüne (yoksa aktif sekmeye) göre dinamik
                const effectiveMetal = advancedFilters.metalType || (['gold', 'silver', 'steel', 'aksesuar', 'brands', 'zippo', 'sarf'].includes(selectedType) ? selectedType : '');
                const showGold = !effectiveMetal || effectiveMetal === 'gold';
                const showSilver = !effectiveMetal || effectiveMetal === 'silver' || effectiveMetal === 'aksesuar';
                const fo = filterOptions || {};
                const karatOpts = (fo.goldKarats && fo.goldKarats.length ? fo.goldKarats : ['8', '14', '18', '22', '24'])
                  .map(v => ({ value: v, label: `${parseFloat(v)} ayar` }));
                const purityOpts = (fo.silverPurities && fo.silverPurities.length ? fo.silverPurities : ['925', '950', '999'])
                  .map(v => ({ value: v, label: String(parseFloat(v)) }));
                const categoryOpts = advancedFilters.metalType
                  ? (dynamicCategories[advancedFilters.metalType] || [])
                  : [...new Set(Object.values(dynamicCategories).flat())];

                return (
                  <div className="pf-columns">
                    {/* 1. Metin + metal türü + kategori/ayar */}
                    <div className="pf-col">
                      {text('name', 'Ürün Adı', 'Ürün adı...')}
                      {text('barcode', 'Barkod', 'Barkod...')}
                      {select('brand', 'Marka', fo.brands || [])}
                      {select('supplier', 'Tedarikçi', fo.suppliers || [])}
                      {text('description', 'Açıklama', 'Açıklama...')}
                      {text('notes', 'Notlar', 'Not...')}
                      <div className="pf-field">
                        <label>Metal Türü</label>
                        <select
                          value={advancedFilters.metalType}
                          onChange={(e) => {
                            const m = e.target.value;
                            setAdvancedFilters(prev => ({
                              ...prev,
                              metalType: m,
                              category: '',
                              goldKarat: m && m !== 'gold' ? '' : prev.goldKarat,
                              silverPurity: m && m !== 'silver' && m !== 'aksesuar' ? '' : prev.silverPurity
                            }));
                            if (m) fetchCategoriesForType(m);
                          }}
                        >
                          <option value="">Tümü (aktif sekme)</option>
                          {[
                            { value: 'gold', label: 'Altın' },
                            { value: 'silver', label: 'Gümüş' },
                            { value: 'steel', label: 'Çelik' },
                            { value: 'aksesuar', label: 'Aksesuar' },
                            { value: 'brands', label: 'Markalar' },
                            { value: 'zippo', label: 'Zippo' },
                            { value: 'sarf', label: 'Sarf' }
                          ].filter(o => getAllowedProductTypes().includes(o.value)).map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      {select('category', 'Kategori', categoryOpts)}
                      {showSilver && select('silverPurity', 'Gümüş Ayar', purityOpts)}
                      {showGold && select('goldKarat', 'Altın Ayar', karatOpts)}
                    </div>

                    {/* 2. Özellikler + tarihler */}
                    <div className="pf-col">
                      {select('color', 'Renk', fo.colors || [])}
                      {select('size', 'Beden', fo.sizes || [])}
                      {select('gender', 'Cinsiyet', [
                        { value: 'kadın', label: 'Kadın' }, { value: 'erkek', label: 'Erkek' },
                        { value: 'unisex', label: 'Unisex' }, { value: 'çocuk', label: 'Çocuk' }
                      ])}
                      {select('createdBy', 'Oluşturan', fo.createdBy || [])}
                      <div className="pf-sep" />
                      {pair(
                        date('stockEntryAfter', 'Stoğa Giriş (Sonra)', 'Bu tarihten sonra eklenenler'),
                        date('stockEntryBefore', 'Stoğa Giriş (Önce)', 'Bu tarihten önce eklenenler')
                      )}
                      {date('noStockChangeSince', 'Stok Hareketi Olmayan (tarihten beri)', 'Bu tarihten beri stok hareketi olmayanlar')}
                      {pair(date('createdAfter', 'Oluşturma (Sonra)'), date('createdBefore', 'Oluşturma (Önce)'))}
                      {pair(date('updatedAfter', 'Son Güncelleme (Sonra)'), date('updatedBefore', 'Son Güncelleme (Önce)'))}
                    </div>

                    {/* 3. Sayısal */}
                    <div className="pf-col">
                      {pair(
                        select('currency', 'Para Birimi', [
                          { value: 'TRY', label: 'TRY (₺)' }, { value: 'USD', label: 'USD ($)' }, { value: 'EUR', label: 'EUR (€)' }
                        ]),
                        select('pricingType', 'Fiyatlama Türü', [
                          { value: 'formula', label: 'Formüllü' }, { value: 'fixed', label: 'Sabit Fiyat' }
                        ])
                      )}
                      <div className="pf-pair">
                        <div className="pf-field" title="Seçili para birimine göre">
                          <label>Min. Fiyat</label>
                          <PriceInput hideText value={advancedFilters.minPrice} onChange={(val) => setAdvancedFilters(prev => ({ ...prev, minPrice: val }))} placeholder="0,00" />
                        </div>
                        <div className="pf-field">
                          <label>Max. Fiyat</label>
                          <PriceInput hideText value={advancedFilters.maxPrice} onChange={(val) => setAdvancedFilters(prev => ({ ...prev, maxPrice: val }))} placeholder="0,00" />
                        </div>
                      </div>
                      {select('stockScope', 'Stok Kapsamı', stores.filter(s => s.active !== 0).map(s => ({ value: s.id, label: `${s.name} stoğu` })), 'Toplam Stok', 'Min/Max Stok bu kapsama göre hesaplanır')}
                      {pair(num('minStock', 'Min. Stok', '1', '0'), num('maxStock', 'Max. Stok', '1', '∞'))}
                      {pair(num('minWeight', 'Min. Ağırlık (gr)', '0.001', '0'), num('maxWeight', 'Max. Ağırlık (gr)', '0.001', '∞'))}
                      {pair(num('minLaborCost', 'Min. İşçilik (₺/gr)', '0.01', '0'), num('maxLaborCost', 'Max. İşçilik (₺/gr)', '0.01', '∞'))}
                      {pair(num('minProfitMargin', 'Min. Kâr Marjı (x)', '0.01', '0'), num('maxProfitMargin', 'Max. Kâr Marjı (x)', '0.01', '∞'))}
                      {pair(
                        num('minMinStockThreshold', 'Min. Stok Eşiği', '1', '0', 'Ürünün uyarı eşiği (min_stock)'),
                        num('maxMinStockThreshold', 'Max. Stok Eşiği', '1', '∞')
                      )}
                    </div>

                    {/* 4. Durum */}
                    <div className="pf-col">
                      {yesNo('hasImage', 'Fotoğraf Durumu', 'Fotoğrafı Var', 'Fotoğrafı Yok')}
                      {canAccessShopify && yesNo('ecommerceActive', 'E-Ticaret Durumu', 'E-Ticaret Açık', 'E-Ticaret Kapalı')}
                      {canAccessShopify && select('shopifyStatus', 'Shopify Durumu', [
                        { value: 'active', label: 'Yayında' }, { value: 'draft', label: 'Taslak' }, { value: 'archived', label: 'Arşivli' }
                      ])}
                      {canAccessShopify && pair(yesNo('shopifyActive', 'Shopify', 'Açık', 'Kapalı'), yesNo('trendyolActive', 'Trendyol', 'Açık', 'Kapalı'))}
                      {canAccessShopify && pair(yesNo('hepsiburadaActive', 'Hepsiburada', 'Açık', 'Kapalı'), yesNo('amazonActive', 'Amazon', 'Açık', 'Kapalı'))}
                      {canAccessShopify && yesNo('n11Active', 'N11', 'Açık', 'Kapalı')}
                      {pair(yesNo('isSet', 'Set Ürün', 'Set Ürünler', 'Tekli Ürünler'), yesNo('isFavoriteFlag', 'Favori', 'Favori', 'Favori Değil'))}
                      {pair(yesNo('eskiEtiket', 'Eski Etiket', 'Eski Etiketli', 'Yeni Etiketli'), yesNo('noDiscount', 'İndirim Durumu', 'İndirimsiz', 'İndirim Uygulanabilir'))}
                      {yesNo('isSpecialDefined', 'Özel Tanımlı', 'Özel Tanımlı', 'Normal')}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={async () => {
                  setAdvancedFilters(emptyAdvancedFilters);
                  setShowAdvancedFilter(false);
                  setPage(1);
                  pageRef.current = 1;
                  hasMoreRef.current = true;
                  loadingMoreRef.current = false;
                  setProducts([]);
                  // State güncellemesinin tamamlanması için kısa bir gecikme
                  setTimeout(() => fetchProducts(1, false), 50);
                }}
              >
                <RotateCcw size={16} /> Temizle
              </button>
              <button
                className="btn-save"
                onClick={() => {
                  setShowAdvancedFilter(false);
                  setPage(1);
                  pageRef.current = 1;
                  hasMoreRef.current = true;
                  loadingMoreRef.current = false;
                  setProducts([]);
                  fetchProducts(1, false);
                }}
              >
                <Search size={16} /> Filtrele
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Modal */}
      <LogModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        module="products"
        title="Ürün Yönetimi"
        showEntityCode={false}
      />

      {/* Transfer Talebi Oluştur Modal */}
      {showTransferRequestModal && (
        <div className={`modal-overlay ${closingModal === 'transferRequest' ? 'closing' : ''}`}>
          <div className="modal transfer-modal large-modal" onClick={(e) => e.stopPropagation()} style={{ minHeight: '600px' }}>
            <div className="modal-header">
              <h2><ClipboardList size={20} style={{ marginRight: '8px' }} /> Transfer Talebi Oluştur</h2>
              <button className="close-btn" onClick={closeTransferRequestModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="transfer-split-layout">
              <div className="transfer-left-panel">
                <div className="transfer-form-row-inline">
                  <div className="transfer-form-row">
                    <label>Kaynak Mağaza</label>
                    <select
                      className="transfer-store-select"
                      value={transferRequestFromStore}
                      onChange={(e) => setTransferRequestFromStore(e.target.value)}
                    >
                      <option value="">-- Mağaza Seçin --</option>
                      {stores.filter(s => s.id !== selectedStore?.id).map(store => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="transfer-form-row">
                    <label>Kasiyer</label>
                    <select
                      value={transferStaffId}
                      onChange={(e) => {
                        const staff = transferStaffList.find(s => s.id === parseInt(e.target.value));
                        setTransferStaffId(staff ? staff.id : '');
                        setTransferStaffName(staff ? staff.name : '');
                      }}
                      className="transfer-store-select"
                    >
                      <option value="">-- Kasiyer Seçin --</option>
                      {transferStaffList.map(staff => (
                        <option key={staff.id} value={staff.id}>{staff.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="transfer-form-row">
                    <label>Barkod Okutun</label>
                    <input
                      ref={transferRequestBarcodeRef}
                      type="text"
                      className="transfer-barcode-input"
                      value={transferRequestBarcode}
                      onChange={(e) => setTransferRequestBarcode(e.target.value)}
                      onKeyDown={handleTransferRequestBarcode}
                      placeholder="Barkod okutun veya yazın..."
                    />
                  </div>
                </div>

                <div className="transfer-left-scroll">
                <div className="inline-transfer-form">
                  {transferRequestItems.length > 0 && (
                    <div className="transfer-items-section">
                      <h4>Talep Edilecek Ürünler ({transferRequestItems.length})</h4>
                      <div className="transfer-items-list">
                        {transferRequestItems.map((item, index) => (
                          <div key={index} className="transfer-item-row">
                            <div className="transfer-item-image" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && setZoomedImage(`${IMAGE_HOST}${item.image}`)}>
                              {item.image ? (
                                <img src={`${IMAGE_HOST}${item.image}`} alt={item.name} />
                              ) : (
                                <div className="no-image-placeholder"><Package size={24} /></div>
                              )}
                            </div>
                            <div className="transfer-item-info">
                              <span className="transfer-item-name">{item.name}</span>
                              <span className="transfer-item-barcode">{item.barcode}</span>
                            </div>
                            <div className="transfer-item-quantity">
                              <button onClick={() => updateTransferRequestItemQuantity(index, item.quantity - 1)} className="qty-btn">-</button>
                              <span onDoubleClick={(e) => { const span = e.target; const input = document.createElement('input'); input.type = 'number'; input.min = '1'; input.value = item.quantity; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #00bfa6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.max(1, parseInt(input.value) || 1); updateTransferRequestItemQuantity(index, val); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>{item.quantity}</span>
                              <button onClick={() => updateTransferRequestItemQuantity(index, item.quantity + 1)} className="qty-btn">+</button>
                            </div>
                            <button className="transfer-item-remove" onClick={() => removeTransferRequestItem(index)}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="transfer-form-row">
                    <label>Not (Opsiyonel)</label>
                    <textarea
                      value={transferRequestNotes}
                      onChange={(e) => setTransferRequestNotes(e.target.value)}
                      placeholder="Talep ile ilgili not ekleyin..."
                      className="transfer-notes-input"
                      rows={2}
                    />
                  </div>
                </div>
                </div>
              </div>
              <div className="transfer-right-panel">
                {lastScannedTransferProduct ? (
                  <>
                    <div className="scanned-product-image" style={lastScannedTransferProduct.image ? { cursor: 'zoom-in' } : {}} onClick={() => lastScannedTransferProduct.image && setZoomedImage(`${IMAGE_HOST}${lastScannedTransferProduct.image}`)}>
                      {lastScannedTransferProduct.image ? (
                        <img src={`${IMAGE_HOST}${lastScannedTransferProduct.image}`} alt={lastScannedTransferProduct.name} />
                      ) : (
                        <div className="scanned-product-placeholder"><Package size={64} /></div>
                      )}
                    </div>
                    <div className="scanned-product-name">{lastScannedTransferProduct.name}</div>
                    <div className="scanned-product-barcode">{lastScannedTransferProduct.barcode}</div>
                  </>
                ) : (
                  <div className="scanned-product-empty">
                    <Package size={48} />
                    <span>Barkod okutun</span>
                  </div>
                )}
                <div className="transfer-form-actions" style={{ marginTop: 'auto', paddingTop: '16px' }}>
                  <button className="transfer-cancel-btn" onClick={closeTransferRequestModal}>İptal</button>
                  <button
                    className="transfer-submit-btn request"
                    onClick={createTransferRequest}
                    disabled={!selectedStore || transferRequestItems.length === 0 || savingTransferRequest || !transferRequestFromStore}
                  >
                    {savingTransferRequest ? 'Gönderiliyor...' : `Talep Oluştur (${transferRequestItems.length} ürün)`}
                  </button>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gönderilen Transferler Modal */}
      {showOutgoingTransfersModal && (
        <div className={`modal-overlay ${closingModal === 'outgoingTransfers' ? 'closing' : ''}`}>
          <div className="modal outgoing-requests-modal large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              {selectedOutgoingTransfer ? (
                <>
                  <button onClick={() => { setSelectedOutgoingTransfer(null); setEditingTransfer(false); setEditingTransferItems([]); }} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', width: '32px', height: '32px', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowLeft size={18} color="#fff" />
                  </button>
                  <h2>Transfer Detayı</h2>
                </>
              ) : (
                <h2><Upload size={20} style={{ marginRight: '8px' }} /> Gönderilen Transferler</h2>
              )}
              <button className="close-btn" onClick={closeOutgoingTransfersModal}>✕</button>
            </div>
            <div className="modal-body">
              {selectedOutgoingTransfer ? (
                <div>
                  {/* Transfer Bilgileri */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>Hedef Mağaza</div>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>{selectedOutgoingTransfer.to_store_name}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>Tarih</div>
                      <div style={{ fontWeight: '500', color: '#1e293b' }}>
                        {new Date(selectedOutgoingTransfer.created_at).toLocaleDateString('tr-TR')} {new Date(selectedOutgoingTransfer.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>Gönderen Kasiyer</div>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>{
                        (() => {
                          const notesMatch = selectedOutgoingTransfer.notes?.match(/Kasiyer:\s*(.+)/);
                          return notesMatch ? notesMatch[1].trim() : (selectedOutgoingTransfer.created_by_staff || selectedOutgoingTransfer.created_by_name || 'Bilinmiyor');
                        })()
                      }</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>Durum</div>
                      <span style={{
                        padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                        background: selectedOutgoingTransfer.status === 'received' ? '#dcfce7' : selectedOutgoingTransfer.status === 'cancelled' ? '#fee2e2' : selectedOutgoingTransfer.status === 'in_transit' ? '#dbeafe' : '#fef3c7',
                        color: selectedOutgoingTransfer.status === 'received' ? '#16a34a' : selectedOutgoingTransfer.status === 'cancelled' ? '#dc2626' : selectedOutgoingTransfer.status === 'in_transit' ? '#2563eb' : '#d97706'
                      }}>
                        {selectedOutgoingTransfer.status === 'pending' && 'Beklemede'}
                        {selectedOutgoingTransfer.status === 'in_transit' && 'Yolda'}
                        {selectedOutgoingTransfer.status === 'partially_received' && 'Kısmi Teslim'}
                        {selectedOutgoingTransfer.status === 'received' && 'Teslim Alındı'}
                        {selectedOutgoingTransfer.status === 'cancelled' && 'İptal'}
                      </span>
                    </div>
                  </div>
                  {selectedOutgoingTransfer.notes && (
                    <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '16px', fontSize: '0.9rem', color: '#92400e' }}>
                      <strong>Not:</strong> {selectedOutgoingTransfer.notes}
                    </div>
                  )}
                  {/* Ürün Tablosu */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h4 style={{ margin: 0, color: '#334155' }}>Ürünler ({selectedOutgoingTransfer.items?.length || 0})</h4>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {!editingTransfer && ['pending', 'in_transit'].includes(selectedOutgoingTransfer.status) && (
                        <button
                          onClick={() => startEditingTransfer(selectedOutgoingTransfer)}
                          style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <Pencil size={14} /> Düzenle
                        </button>
                      )}
                      {editingTransfer && (
                        <>
                          <button
                            onClick={() => { setEditingTransfer(false); setEditingTransferItems([]); }}
                            disabled={savingTransferEdit}
                            style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                          >
                            Vazgeç
                          </button>
                          <button
                            onClick={() => updateTransfer(selectedOutgoingTransfer.id)}
                            disabled={savingTransferEdit}
                            style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: savingTransferEdit ? 0.7 : 1 }}
                          >
                            <Check size={14} /> {savingTransferEdit ? 'Kaydediliyor...' : 'Kaydet'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <table className="transfer-table">
                    <thead>
                      <tr>
                        <th style={{ width: '90px' }}>Foto</th>
                        <th>Ürün</th>
                        <th>Barkod</th>
                        {editingTransfer && <th>Eldeki Stok</th>}
                        <th>Gönderilen</th>
                        <th>Teslim Alınan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(editingTransfer ? editingTransferItems : selectedOutgoingTransfer.items)?.map((item, idx) => {
                        const maxQty = editingTransfer && item.source_stock !== null && item.source_stock !== undefined
                          ? item.original_quantity + item.source_stock
                          : undefined;
                        return (
                        <tr key={idx} style={editingTransfer && item.quantity === 0 ? { opacity: 0.4, textDecoration: 'line-through' } : {}}>
                          <td>
                            <div className="product-thumb-wrap" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && setZoomedImage(item.image.startsWith('http') ? item.image : `${IMAGE_HOST}${item.image}`)}>
                              {item.image ? (
                                <img src={item.image.startsWith('http') ? item.image : `${IMAGE_HOST}${item.image}`} alt="" className="product-thumb" loading="lazy" />
                              ) : (
                                <div className="product-thumb-placeholder" />
                              )}
                            </div>
                          </td>
                          <td><span>{item.product_name}</span></td>
                          <td>{item.barcode}</td>
                          {editingTransfer && (
                            <td style={{ textAlign: 'center' }}>
                              {item.source_stock !== null && item.source_stock !== undefined ? (
                                <span style={{ fontWeight: '600', color: item.source_stock > 0 ? '#1e293b' : '#dc2626' }}>
                                  {item.source_stock}
                                </span>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>-</span>
                              )}
                            </td>
                          )}
                          <td style={{ fontWeight: '600' }}>
                            {editingTransfer ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                  type="number"
                                  min={item.received_quantity || 0}
                                  max={maxQty}
                                  value={item.quantity}
                                  onChange={(e) => {
                                    let val = parseInt(e.target.value) || 0;
                                    const minQty = item.received_quantity || 0;
                                    if (val < minQty) val = minQty;
                                    if (maxQty !== undefined && val > maxQty) val = maxQty;
                                    setEditingTransferItems(prev => prev.map((ei, i) => i === idx ? { ...ei, quantity: val } : ei));
                                  }}
                                  style={{
                                    width: '70px', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '6px',
                                    fontSize: '14px', fontWeight: '600', textAlign: 'center',
                                    ...(maxQty !== undefined && item.quantity > maxQty ? { borderColor: '#dc2626', background: '#fef2f2' } : {}),
                                    ...(item.quantity === 0 ? { borderColor: '#dc2626', background: '#fef2f2', color: '#dc2626' } : {})
                                  }}
                                />
                                {maxQty !== undefined && (
                                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>max {maxQty}</span>
                                )}
                              </div>
                            ) : (
                              <>{item.quantity} adet</>
                            )}
                          </td>
                          <td>
                            {item.received_quantity !== null && item.received_quantity !== undefined ? (
                              <span style={{ fontWeight: '600', color: item.received_quantity >= item.quantity ? '#16a34a' : '#d97706' }}>
                                {item.received_quantity} adet
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>-</span>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : loadingOutgoingTransfers ? (
                <div className="loading-state">Yükleniyor...</div>
              ) : outgoingTransfers.length === 0 ? (
                <div className="empty-state">
                  <Package size={48} strokeWidth={1} />
                  <p>Gönderilen transfer bulunmuyor</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {outgoingTransfers.map(transfer => (
                    <div key={transfer.id} className="transfer-list-item" onClick={() => setSelectedOutgoingTransfer(transfer)}>
                      <div className="transfer-list-main">
                        <span className="transfer-code">{transfer.transfer_code}</span>
                        <span className="transfer-store">→ {transfer.to_store_name}</span>
                      </div>
                      <div className="transfer-list-meta">
                        <span className="transfer-date">
                          {new Date(transfer.created_at).toLocaleDateString('tr-TR')}
                        </span>
                        <span className={`status-text ${transfer.status}`}>
                          <span className="status-dot"></span>
                          {transfer.status === 'pending' && 'Beklemede'}
                          {transfer.status === 'in_transit' && 'Yolda'}
                          {transfer.status === 'partially_received' && 'Kısmi Teslim'}
                          {transfer.status === 'received' && 'Teslim Alındı'}
                          {transfer.status === 'cancelled' && 'İptal'}
                        </span>
                      </div>
                      <div className="transfer-list-count">
                        {transfer.item_count || transfer.items?.length || '?'} ürün
                      </div>
                      {(transfer.status === 'pending' || transfer.status === 'in_transit') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); cancelTransfer(transfer.id); }}
                          disabled={cancellingTransfer}
                          style={{
                            background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5',
                            padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <X size={14} /> İptal
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gönderilen Talepler Modal */}
      {showOutgoingRequestsModal && (
        <div className={`modal-overlay ${closingModal === 'outgoingRequests' ? 'closing' : ''}`}>
          <div className="modal outgoing-requests-modal large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              {selectedOutgoingRequest ? (
                <>
                  <button onClick={() => { setSelectedOutgoingRequest(null); setEditingRequest(false); setEditingRequestItems([]); }} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', width: '32px', height: '32px', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowLeft size={18} color="#fff" />
                  </button>
                  <h2>Talep Detayı</h2>
                </>
              ) : (
                <h2><Send size={20} style={{ marginRight: '8px' }} /> Gönderilen Talepler</h2>
              )}
              <button className="close-btn" onClick={() => { setSelectedOutgoingRequest(null); setEditingRequest(false); setEditingRequestItems([]); handleCloseModal('outgoingRequests', setShowOutgoingRequestsModal); }}>✕</button>
            </div>
            <div className="modal-body">
              {selectedOutgoingRequest ? (
                <div>
                  {/* Talep Bilgileri */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>Talep Edilen Mağaza</div>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>{selectedOutgoingRequest.to_store_name}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>Tarih</div>
                      <div style={{ fontWeight: '500', color: '#1e293b' }}>
                        {new Date(selectedOutgoingRequest.created_at).toLocaleDateString('tr-TR')} {new Date(selectedOutgoingRequest.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>Talep Eden</div>
                      <div style={{ fontWeight: '500', color: '#1e293b' }}>{selectedOutgoingRequest.requester_name || selectedOutgoingRequest.staff_name || selectedOutgoingRequest.created_by_name || 'Bilinmiyor'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>Durum</div>
                      <span className={`status-text ${selectedOutgoingRequest.status}`}>
                        <span className="status-dot"></span>
                        {selectedOutgoingRequest.status === 'pending' && 'Beklemede'}
                        {selectedOutgoingRequest.status === 'approved' && 'Onaylandı'}
                        {selectedOutgoingRequest.status === 'rejected' && 'Reddedildi'}
                        {selectedOutgoingRequest.status === 'completed' && 'Tamamlandı'}
                        {selectedOutgoingRequest.status === 'cancelled' && 'İptal'}
                      </span>
                    </div>
                  </div>
                  {selectedOutgoingRequest.notes && (
                    <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '16px', fontSize: '0.9rem', color: '#92400e' }}>
                      <strong>Not:</strong> {selectedOutgoingRequest.notes}
                    </div>
                  )}
                  {selectedOutgoingRequest.status === 'rejected' && (
                    <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', marginBottom: '16px', fontSize: '0.9rem', color: '#991b1b', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {selectedOutgoingRequest.rejection_reason && (
                        <div><strong>Red Sebebi:</strong> {selectedOutgoingRequest.rejection_reason}</div>
                      )}
                      {(selectedOutgoingRequest.rejected_by_name || selectedOutgoingRequest.rejected_staff_name) && (
                        <div>
                          <strong>Reddeden:</strong>{' '}
                          {selectedOutgoingRequest.rejected_by_name || '—'}
                          {selectedOutgoingRequest.rejected_staff_name && ` (Kasiyer: ${selectedOutgoingRequest.rejected_staff_name})`}
                        </div>
                      )}
                      {selectedOutgoingRequest.rejected_at && (
                        <div>
                          <strong>Tarih:</strong> {new Date(selectedOutgoingRequest.rejected_at).toLocaleString('tr-TR')}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Ürün Tablosu */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h4 style={{ margin: 0, color: '#334155' }}>Ürünler ({selectedOutgoingRequest.items?.length || 0})</h4>
                    {selectedOutgoingRequest.status === 'pending' && !editingRequest && (
                      <button
                        onClick={() => {
                          setEditingRequest(true);
                          setEditingRequestItems(selectedOutgoingRequest.items.map(item => ({
                            ...item,
                            requested_quantity: item.requested_quantity
                          })));
                        }}
                        style={{
                          background: '#dbeafe', color: '#2563eb', border: '1px solid #93c5fd',
                          padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                        }}
                      >
                        <Pencil size={14} /> Düzenle
                      </button>
                    )}
                    {editingRequest && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => { setEditingRequest(false); setEditingRequestItems([]); }}
                          style={{
                            background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1',
                            padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Vazgeç
                        </button>
                        <button
                          onClick={() => updateTransferRequest(selectedOutgoingRequest.id)}
                          disabled={savingRequest}
                          style={{
                            background: '#16a34a', color: '#fff', border: 'none',
                            padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                            opacity: savingRequest ? 0.6 : 1
                          }}
                        >
                          <Check size={14} /> {savingRequest ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                      </div>
                    )}
                  </div>
                  <table className="transfer-table">
                    <thead>
                      <tr>
                        <th style={{ width: '90px' }}>Fotoğraf</th>
                        <th>Ürün</th>
                        <th>Barkod</th>
                        <th>Talep</th>
                        <th>Onaylanan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(editingRequest ? editingRequestItems : selectedOutgoingRequest.items)?.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <div className="product-thumb-wrap" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && setZoomedImage(item.image.startsWith('http') ? item.image : `${IMAGE_HOST}${item.image}`)}>
                              {item.image ? (
                                <img src={item.image.startsWith('http') ? item.image : `${IMAGE_HOST}${item.image}`} alt="" className="product-thumb" loading="lazy" />
                              ) : (
                                <div className="product-thumb-placeholder" />
                              )}
                            </div>
                          </td>
                          <td><span>{item.product_name}</span></td>
                          <td>{item.barcode}</td>
                          <td style={{ fontWeight: '600' }}>
                            {editingRequest ? (
                              <input
                                type="number"
                                min="0"
                                value={item.requested_quantity}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  setEditingRequestItems(prev => prev.map((it, i) =>
                                    i === idx ? { ...it, requested_quantity: val } : it
                                  ));
                                }}
                                style={{
                                  width: '70px', padding: '6px 8px', borderRadius: '6px',
                                  border: '2px solid #93c5fd', fontSize: '14px', fontWeight: '600',
                                  textAlign: 'center', outline: 'none'
                                }}
                              />
                            ) : (
                              `${item.requested_quantity} adet`
                            )}
                          </td>
                          <td>
                            {item.approved_quantity !== null && item.approved_quantity !== undefined ? (
                              <span style={{ fontWeight: '600', color: '#16a34a' }}>{item.approved_quantity} adet</span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : loadingOutgoingRequests ? (
                <div className="loading-state">Yükleniyor...</div>
              ) : outgoingRequests.length === 0 ? (
                <div className="empty-state">
                  <Package size={48} strokeWidth={1} />
                  <p>Gönderilen talep bulunmuyor</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {outgoingRequests.map(request => (
                    <div key={request.id} className="transfer-list-item" onClick={() => setSelectedOutgoingRequest(request)}>
                      <div className="transfer-list-main">
                        <span className="transfer-code">{request.request_code}</span>
                        <span className="transfer-store">→ {request.to_store_name}</span>
                      </div>
                      <div className="transfer-list-meta">
                        <span className="transfer-date">
                          {new Date(request.created_at).toLocaleDateString('tr-TR')}
                        </span>
                        <span className={`status-text ${request.status}`}>
                          <span className="status-dot"></span>
                          {request.status === 'pending' && 'Beklemede'}
                          {request.status === 'approved' && 'Onaylandı'}
                          {request.status === 'rejected' && 'Reddedildi'}
                          {request.status === 'completed' && 'Tamamlandı'}
                          {request.status === 'cancelled' && 'İptal'}
                        </span>
                      </div>
                      <div className="transfer-list-count">
                        {request.items?.length || '?'} ürün
                      </div>
                      {request.status === 'pending' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); cancelTransferRequest(request.id); }}
                          disabled={cancellingRequest}
                          style={{
                            background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5',
                            padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <X size={14} /> İptal
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Teslim Alan Kasiyer Seçim Modalı */}
      {showReceiveStaffModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Teslim Alan Kasiyer</h2>
              <button className="close-btn" onClick={() => setShowReceiveStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {transferStaffList.map(staff => {
                  const isSelected = receiveStaffId === staff.id;
                  return (
                    <div key={staff.id} className={`staff-item ${isSelected ? 'selected' : ''}`} onClick={() => { setReceiveStaffId(staff.id); setReceiveStaffName(staff.name); }}>
                      <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                        {isSelected ? <Check size={24} strokeWidth={3} /> : staff.photo_url ? <img src={`${API_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                      </div>
                      <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                    </div>
                  );
                })}
              </div>
              <div className="staff-modal-actions">
                <button className="confirm-staff-btn" disabled={!receiveStaffId} onClick={handleReceiveStaffConfirm}>Teslim Al</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Reddet Modal */}
      {showRejectModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }} onClick={() => setShowRejectModal(false)}>
          <div className="modal" style={{ maxWidth: '520px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Talebi Reddet</h2>
              <button className="close-btn" onClick={() => setShowRejectModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#334155' }}>
                  Reddetme Sebebi <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ör: Stok yok, yanlış istek, ..."
                  rows={4}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' }}
                  autoFocus
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#334155' }}>
                  Kasiyer <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                  {transferStaffList.map(staff => {
                    const isSelected = rejectStaffId === staff.id;
                    return (
                      <div
                        key={staff.id}
                        onClick={() => { setRejectStaffId(staff.id); setRejectStaffName(staff.name); }}
                        style={{
                          cursor: 'pointer',
                          padding: '10px',
                          border: isSelected ? '2px solid #ef4444' : '1px solid #e2e8f0',
                          borderRadius: '10px',
                          background: isSelected ? '#fef2f2' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '50%',
                          background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 600, color: '#475569', overflow: 'hidden', flexShrink: 0
                        }}>
                          {staff.photo_url
                            ? <img src={`${API_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : staff.name.charAt(0)}
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b' }}>{staff.name}</span>
                      </div>
                    );
                  })}
                </div>
                {transferStaffList.length === 0 && (
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: '8px 0 0' }}>Kasiyer listesi bulunamadı.</p>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => setShowRejectModal(false)}>Vazgeç</button>
              <button className="btn danger" onClick={confirmReject} disabled={!rejectReason.trim() || !rejectStaffId}>
                Reddetmeyi Onayla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Onay Kasiyer Modal */}
      {showApproveStaffModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Gönderen Kasiyer Seç</h2>
              <button className="close-btn" onClick={() => setShowApproveStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {transferStaffList.map(staff => (
                  <div key={staff.id} className="staff-item" onClick={() => { setShowApproveStaffModal(false); approveRequest(staff.id, staff.name); }}>
                    <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                      {staff.photo_url ? <img src={`${API_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                    </div>
                    <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Geçmişi Modal */}
      {showTransferHistoryModal && (
        <div className={`modal-overlay ${closingModal === 'transferHistory' ? 'closing' : ''}`}>
          <div className="modal outgoing-requests-modal large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              {selectedHistoryTransfer ? (
                <>
                  <button onClick={() => setSelectedHistoryTransfer(null)} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', width: '32px', height: '32px', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowLeft size={18} color="#fff" />
                  </button>
                  <h2>Transfer Detayı</h2>
                </>
              ) : (
                <h2><Clock size={20} style={{ marginRight: '8px' }} /> Transfer Geçmişi</h2>
              )}
              <button className="close-btn" onClick={closeTransferHistoryModal}>✕</button>
            </div>
            <div className="modal-body">
              {loadingTransferHistory ? (
                <div className="loading-state">Yükleniyor...</div>
              ) : selectedHistoryTransfer ? (
                <div className="transfer-detail-view">
                  <div className="transfer-detail-header">
                    <div className="detail-code">
                      <span className="label">Transfer Kodu</span>
                      <span className="value">{selectedHistoryTransfer.transfer_code}</span>
                    </div>
                    <span className={`status-text ${selectedHistoryTransfer.status}`}>
                      <span className="status-dot"></span>
                      {selectedHistoryTransfer.status === 'received' && 'Teslim Alındı'}
                      {selectedHistoryTransfer.status === 'partially_received' && 'Kısmi Teslim'}
                      {selectedHistoryTransfer.status === 'cancelled' && 'İptal'}
                    </span>
                  </div>
                  <div className="transfer-detail-info">
                    <div className="info-row">
                      <span className="label">Gönderen</span>
                      <span className="value">{selectedHistoryTransfer.from_store_name}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Hedef</span>
                      <span className="value">{selectedHistoryTransfer.to_store_name}</span>
                    </div>
                    {(selectedHistoryTransfer.created_by_staff || selectedHistoryTransfer.created_by_name) && (
                      <div className="info-row">
                        <span className="label">Gönderen Kasiyer</span>
                        <span className="value">{selectedHistoryTransfer.created_by_staff || selectedHistoryTransfer.created_by_name}</span>
                      </div>
                    )}
                    <div className="info-row">
                      <span className="label">Gönderim Tarihi</span>
                      <span className="value">{new Date(selectedHistoryTransfer.created_at).toLocaleString('tr-TR')}</span>
                    </div>
                    {selectedHistoryTransfer.status === 'received' && (
                      <>
                        {(selectedHistoryTransfer.received_by_staff || selectedHistoryTransfer.received_by_name) && (
                          <div className="info-row">
                            <span className="label">Teslim Alan Kasiyer</span>
                            <span className="value">{selectedHistoryTransfer.received_by_staff || selectedHistoryTransfer.received_by_name}</span>
                          </div>
                        )}
                        {selectedHistoryTransfer.received_at && (
                          <div className="info-row">
                            <span className="label">Teslim Tarihi</span>
                            <span className="value">{new Date(selectedHistoryTransfer.received_at).toLocaleString('tr-TR')}</span>
                          </div>
                        )}
                      </>
                    )}
                    {selectedHistoryTransfer.notes && (
                      <div className="info-row">
                        <span className="label">Not</span>
                        <span className="value">{selectedHistoryTransfer.notes}</span>
                      </div>
                    )}
                  </div>
                  <div className="transfer-detail-items">
                    <h4>Ürünler ({selectedHistoryTransfer.items?.length || 0})</h4>
                    <table className="transfer-table">
                      <thead>
                        <tr>
                          <th style={{ width: '90px' }}>Fotoğraf</th>
                          <th>Ürün</th>
                          <th>Barkod</th>
                          <th>Adet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedHistoryTransfer.items?.map((item, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className="product-thumb-wrap" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && setZoomedImage(item.image.startsWith('http') ? item.image : `${IMAGE_HOST}${item.image}`)}>
                                {item.image ? (
                                  <img src={item.image.startsWith('http') ? item.image : `${IMAGE_HOST}${item.image}`} alt="" className="product-thumb" loading="lazy" />
                                ) : (
                                  <div className="product-thumb-placeholder" />
                                )}
                              </div>
                            </td>
                            <td><span>{item.product_name}</span></td>
                            <td>{item.barcode}</td>
                            <td style={{ fontWeight: '600' }}>{item.quantity} adet</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : transferHistory.length === 0 ? (
                <div className="empty-state">
                  <Package size={48} strokeWidth={1} />
                  <p>Transfer geçmişi bulunmuyor</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {transferHistory.map(transfer => (
                    <div
                      key={transfer.id}
                      className="transfer-list-item"
                      onClick={() => setSelectedHistoryTransfer(transfer)}
                    >
                      <div className="transfer-list-main">
                        <span className="transfer-code">{transfer.transfer_code}</span>
                        <span className="transfer-store">
                          {transfer.from_store_id === selectedStore?.id
                            ? `→ ${transfer.to_store_name}`
                            : `← ${transfer.from_store_name}`
                          }
                        </span>
                      </div>
                      <div className="transfer-list-meta">
                        <span className="transfer-date">
                          {new Date(transfer.created_at).toLocaleDateString('tr-TR')}
                        </span>
                        <span className={`status-text ${transfer.status}`}>
                          <span className="status-dot"></span>
                          {transfer.status === 'received' && 'Teslim Alındı'}
                          {transfer.status === 'cancelled' && 'İptal'}
                        </span>
                      </div>
                      <div className="transfer-list-count">
                        {transfer.item_count || transfer.items?.length || '?'} ürün
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gelen Transfer Talepleri Modal */}
      {showIncomingRequestsModal && (
        <div className={`modal-overlay ${closingModal === 'incomingRequests' ? 'closing' : ''}`}>
          <div className="modal receive-transfer-modal large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Download size={20} style={{ marginRight: '8px' }} /> Gelen Transfer Talepleri</h2>
              <button className="close-btn" onClick={() => handleCloseModal('incomingRequests', setShowIncomingRequestsModal, () => {
                setSelectedRequestForProcess(null);
                setRequestDetailItems([]);
                setRequestApprovalBarcode('');
              })}>✕</button>
            </div>
            <div className="modal-body">
              {loadingRequests ? (
                <div className="loading-state">Yükleniyor...</div>
              ) : incomingRequests.length === 0 ? (
                <div className="empty-state">
                  <p>Bekleyen transfer talebi bulunmuyor.</p>
                </div>
              ) : (
                <div className="receive-transfer-content">
                  {/* Sol: Talep Listesi */}
                  <div className="transfers-list">
                    <h3>Bekleyen Talepler</h3>
                    {incomingRequests.map(request => (
                      <div
                        key={request.id}
                        className={`transfer-card ${selectedRequestForProcess?.id === request.id ? 'selected' : ''}`}
                        onClick={() => selectRequestForProcess(request)}
                      >
                        <div className="transfer-code">{request.request_code}</div>
                        <div className="transfer-from">Talep Eden: {request.from_store_name}{(request.requester_name || request.staff_name) ? ` (${request.requester_name || request.staff_name})` : ''}</div>
                        <div className="transfer-info">
                          {request.item_count} ürün, {request.total_quantity} adet
                        </div>
                        <div className="transfer-date">
                          {new Date(request.created_at).toLocaleDateString('tr-TR')}
                        </div>
                        <span className="status-badge pending">Bekliyor</span>
                      </div>
                    ))}
                  </div>

                  {/* Sağ: Talep Detayı */}
                  <div className="transfer-detail">
                    {selectedRequestForProcess ? (
                      <>
                        <h3>Talep Detayı: {selectedRequestForProcess.request_code}</h3>
                        <div className="transfer-meta">
                          <p><strong>Talep Eden:</strong> {selectedRequestForProcess.from_store_name}{(selectedRequestForProcess.requester_name || selectedRequestForProcess.staff_name) ? ` (${selectedRequestForProcess.requester_name || selectedRequestForProcess.staff_name})` : ''}</p>
                          <p><strong>Tarih:</strong> {new Date(selectedRequestForProcess.created_at).toLocaleString('tr-TR')}</p>
                        </div>

                        {/* Barkod Okutma Alanı */}
                        <form onSubmit={handleRequestApprovalBarcode} className="barcode-scan-form">
                          <div className="barcode-input-wrapper">
                            <Search size={18} />
                            <input
                              type="text"
                              ref={requestApprovalBarcodeRef}
                              value={requestApprovalBarcode}
                              onChange={(e) => setRequestApprovalBarcode(e.target.value)}
                              placeholder="Barkod okutun veya yazın..."
                              autoFocus
                            />
                          </div>
                          <div className="scan-progress">
                            <span className="progress-text">
                              Onaylanan: {requestDetailItems.reduce((sum, item) => sum + (item.approved_quantity || 0), 0)} / {requestDetailItems.reduce((sum, item) => sum + item.requested_quantity, 0)}
                            </span>
                          </div>
                        </form>

                        <div className="transfer-items-list">
                          <table className="transfer-table">
                            <thead>
                              <tr>
                                <th></th>
                                <th style={{ width: '90px' }}>Fotoğraf</th>
                                <th>Ürün</th>
                                <th>Talep</th>
                                <th>Stok</th>
                                <th>Gönder</th>
                              </tr>
                            </thead>
                            <tbody>
                              {requestDetailItems.map((item, index) => (
                                <tr key={index} className={item.approved_quantity >= item.requested_quantity ? 'received-complete' : item.approved_quantity > 0 ? 'received-partial' : ''}>
                                  <td className="status-icon-cell">
                                    {item.approved_quantity >= item.requested_quantity ? (
                                      <span className="check-icon">✓</span>
                                    ) : item.approved_quantity > 0 ? (
                                      <span className="partial-icon">◐</span>
                                    ) : (
                                      <span className="waiting-icon">○</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="product-thumb-wrap" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && setZoomedImage(item.image.startsWith('http') ? item.image : `${IMAGE_HOST}${item.image}`)}>
                                      {item.image ? (
                                        <img src={item.image.startsWith('http') ? item.image : `${IMAGE_HOST}${item.image}`} alt="" className="product-thumb" loading="lazy" />
                                      ) : (
                                        <div className="product-thumb-placeholder" />
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="product-cell">
                                      <span className="product-name">{item.product_name}</span>
                                      <span className="product-barcode">{item.barcode}</span>
                                    </div>
                                  </td>
                                  <td>{item.requested_quantity}</td>
                                  <td>
                                    <span className={`stock-badge ${(item.available_stock || 0) >= item.requested_quantity ? 'sufficient' : 'insufficient'}`}>
                                      {item.available_stock || 0}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="quantity-controls">
                                      <button onClick={() => updateRequestItemApprovedQuantity(index, item.approved_quantity - 1)}>-</button>
                                      <input
                                        type="number"
                                        value={item.approved_quantity}
                                        onChange={(e) => updateRequestItemApprovedQuantity(index, parseInt(e.target.value) || 0)}
                                        min="0"
                                        max={Math.min(item.requested_quantity, item.available_stock || 0)}
                                        className="qty-input"
                                      />
                                      <button onClick={() => updateRequestItemApprovedQuantity(index, item.approved_quantity + 1)}>+</button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="request-actions">
                          <button className="btn secondary" onClick={fillAllRequestApproval} style={{ marginRight: 'auto' }}>
                            Tümünü Ekle
                          </button>
                          <button className="btn danger" onClick={openRejectModal}>
                            Reddet
                          </button>
                          <button
                            className="btn primary"
                            onClick={startApproveRequest}
                            disabled={requestDetailItems.reduce((sum, item) => sum + (item.approved_quantity || 0), 0) === 0}
                          >
                            Onayla ve Gönder ({requestDetailItems.reduce((sum, item) => sum + (item.approved_quantity || 0), 0)} adet)
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="empty-detail">
                        <p>İşlem yapmak için sol taraftan bir talep seçin</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tamirata Gönder Modal */}
      {showSendToRepairModal && (
        <div className={`modal-overlay ${closingModal === 'sendToRepair' ? 'closing' : ''}`}>
          <div className="modal transfer-modal large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Tamirata Gönder</h2>
              {selectedStore && <span className="store-badge">{selectedStore.name}</span>}
              <button className="close-btn" onClick={closeSendToRepairModal}>✕</button>
            </div>
            <div className="modal-body">
              {/* Barkod Giriş */}
              <div className="barcode-input-section">
                <input
                  ref={repairBarcodeRef}
                  type="text"
                  value={repairBarcode}
                  onChange={(e) => setRepairBarcode(e.target.value)}
                  onKeyDown={handleRepairBarcodeAdd}
                  placeholder="Barkod okutun veya girin..."
                  className="barcode-input large"
                />
                <span className="barcode-hint">Enter'a basın veya barkod okuyucu kullanın</span>
              </div>

              {/* Ürünler - Kompakt Liste (Tamirat sayfasındaki Yeni Tamirat Kaydı ile aynı düzen) */}
              {repairItems.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Ürünler <span style={{ color: '#ef4444' }}>*</span> ({repairItems.reduce((sum, item) => sum + (item.quantity || 1), 0)} adet, {repairItems.length} çeşit)</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {repairItems.map((item, index) => (
                      <div key={item.unique_id || index} style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.8rem', minWidth: '16px' }}>{index + 1}</span>
                          {item.image ? (
                            <img
                              src={`${IMAGE_HOST}${item.image}`}
                              alt={item.name}
                              style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '6px', cursor: 'zoom-in', flexShrink: 0 }}
                              onClick={() => setZoomedImage(`${IMAGE_HOST}${item.image}`)}
                            />
                          ) : (
                            <div style={{ width: '36px', height: '36px', background: '#f1f5f9', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', flexShrink: 0 }}>
                              <Package size={16} />
                            </div>
                          )}
                          <div style={{ minWidth: 0, width: '170px', flexShrink: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.name}>{item.name}</div>
                            <div style={{ fontSize: '0.72rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.barcode}{item.category ? ` · ${item.category}` : ''}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                            <button type="button" onClick={() => decreaseRepairItemQty(index)} title="Azalt" style={{ width: '26px', height: '26px', borderRadius: '6px', background: '#fee2e2', color: '#dc2626', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '15px' }}>−</button>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem', minWidth: '24px', textAlign: 'center', cursor: 'pointer' }} onDoubleClick={(e) => { const span = e.target; const input = document.createElement('input'); input.type = 'number'; input.min = '1'; input.max = item.max_stock || 999; input.value = item.quantity || 1; input.style.cssText = 'width:44px;text-align:center;font-size:0.9rem;font-weight:600;border:2px solid #3b82f6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.min(Math.max(1, parseInt(input.value) || 1), item.max_stock || 999); setRepairItems(prev => { const n = [...prev]; n[index] = { ...n[index], quantity: val }; return n; }); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>
                              {item.quantity || 1}
                            </span>
                            <button type="button" onClick={() => increaseRepairItemQty(index)} title="Artır" style={{ width: '26px', height: '26px', borderRadius: '6px', background: '#dcfce7', color: '#16a34a', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '15px' }}>+</button>
                          </div>
                          <input
                            type="text"
                            value={item.problem_description}
                            onChange={(e) => updateRepairItemDescription(index, 'problem_description', e.target.value)}
                            placeholder="Ne yapılacak? Örn: kaynak yapılacak, taş değişecek..."
                            style={{ flex: 1, minWidth: 0, border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 10px', fontSize: '0.9rem', outline: 'none', background: '#fff' }}
                          />
                          <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                            <input type="file" id={`repair-file-input-${index}`} accept="image/*" multiple onChange={(e) => handleRepairPhotoUpload(e, index)} style={{ display: 'none' }} />
                            <input type="file" id={`repair-camera-input-${index}`} accept="image/*" capture="environment" onChange={(e) => handleRepairPhotoUpload(e, index)} style={{ display: 'none' }} />
                            <button type="button" onClick={() => document.getElementById(`repair-camera-input-${index}`)?.click()} disabled={uploadingRepairPhoto} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }} title="Kamera">
                              <Camera size={16} />
                            </button>
                            <button type="button" onClick={() => document.getElementById(`repair-file-input-${index}`)?.click()} disabled={uploadingRepairPhoto} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }} title="Galeri">
                              <Image size={16} />
                            </button>
                            <button type="button" onClick={() => openRepairTerminalModal(index)} disabled={uploadingRepairPhoto || waitingForRepairPhoto} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }} title="Terminal">
                              <Smartphone size={16} />
                            </button>
                            <button type="button" onClick={() => removeRepairItem(index)} style={{ background: 'none', border: '1px solid #fecaca', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }} title="Ürünü kaldır">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        {waitingForRepairPhoto && repairTerminalItemIndex === index && (
                          <div className="waiting-photo-indicator" style={{ margin: '4px 0 0 24px' }}>
                            <span className="spinner"></span>
                            <span>Terminal'den fotoğraf bekleniyor... ({selectedRepairTerminal?.name})</span>
                            <button type="button" className="cancel-wait-btn" onClick={cancelRepairPhotoWait}>İptal</button>
                          </div>
                        )}
                        {item.photos && item.photos.length > 0 && (
                          <div style={{ display: 'flex', gap: '6px', marginTop: '6px', marginLeft: '24px', flexWrap: 'wrap' }}>
                            {item.photos.map((photo, pIdx) => (
                              <div key={pIdx} style={{ position: 'relative', width: '48px', height: '48px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                <img src={`${IMAGE_HOST}${photo}`} alt={`Fotoğraf ${pIdx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} onClick={() => setZoomedImage(`${IMAGE_HOST}${photo}`)} />
                                <button type="button" onClick={() => removeRepairItemPhoto(index, pIdx)} style={{ position: 'absolute', top: '-1px', right: '-1px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '0 6px 0 6px', width: '18px', height: '18px', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {(!item.photos || item.photos.length === 0) && (
                          <p style={{ margin: '4px 0 0 24px', fontSize: '0.75rem', color: '#ef4444' }}>⚠ En az 1 fotoğraf eklemelisiniz</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {uploadingRepairPhoto && <p className="uploading-text" style={{ margin: '4px 0 0 24px', fontSize: '0.75rem' }}>Yükleniyor...</p>}
                </div>
              )}

              {/* Kasiyer, Tamirat Noktası ve Notlar */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Kasiyer <span style={{ color: '#ef4444' }}>*</span></label>
                  <select
                    value={repairStaffName}
                    onChange={(e) => setRepairStaffName(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Kasiyer Seç</option>
                    {transferStaffList.map(staff => (
                      <option key={staff.id} value={staff.name}>{staff.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Tamirat Noktası <span style={{ color: '#ef4444' }}>*</span></label>
                  <select
                    value={repairPointId}
                    onChange={(e) => setRepairPointId(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Tamirat Noktası Seç</option>
                    {repairPointsList.map(point => (
                      <option key={point.id} value={point.id}>{point.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Notlar</label>
                  <input
                    type="text"
                    value={repairNotes}
                    onChange={(e) => setRepairNotes(e.target.value)}
                    placeholder="Ek notlar..."
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn secondary" onClick={closeSendToRepairModal}>
                İptal
              </button>
              <button
                className="btn primary"
                onClick={handleSaveRepair}
                disabled={savingRepair || repairItems.length === 0 || !repairStaffName || !repairPointId || repairItems.some(it => !it.photos || it.photos.length === 0) || repairItems.some(it => !it.problem_description || !it.problem_description.trim())}
              >
                {savingRepair ? 'Kaydediliyor...' : 'Tamirat Kaydı Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terminal Seçim Modal (Tamirata Gönder) */}
      {showRepairTerminalModal && (
        <div className={`modal-overlay ${closingModal === 'repairTerminal' ? 'closing' : ''}`}>
          <div className="modal terminal-select-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Smartphone size={20} style={{ marginRight: '8px' }} /> Terminal Seç</h2>
              <button className="close-btn" onClick={() => handleCloseModal('repairTerminal', setShowRepairTerminalModal)}>✕</button>
            </div>
            <div className="modal-body">
              <p className="terminal-modal-desc">
                Fotoğraf çekmek için bir terminal seçin. Seçilen terminale fotoğraf çekim isteği gönderilecek.
              </p>
              {loadingTerminals ? (
                <div className="loading-state">Terminaller yükleniyor...</div>
              ) : terminals.length === 0 ? (
                <div className="empty-state">
                  <span><Smartphone size={48} /></span>
                  <p>Bu mağazaya bağlı aktif terminal bulunamadı.</p>
                  <small>Terminal kullanıcısının sisteme giriş yapmış olması gerekir.</small>
                </div>
              ) : (
                <div className="terminal-list">
                  {terminals.map(terminal => (
                    <div
                      key={terminal.id}
                      className="terminal-item"
                      onClick={() => sendRepairPhotoRequest(terminal)}
                    >
                      <div className="terminal-icon"><Smartphone size={24} /></div>
                      <div className="terminal-info">
                        <span className="terminal-name">{terminal.name}</span>
                        <span className="terminal-email">{terminal.email}</span>
                      </div>
                      <div className="terminal-arrow">→</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Depo Stok Al ve Transfer Yap Modal */}
      {showDepoStockTransferModal && (
        <div className="depo-stock-transfer-overlay">
          <div className="depo-stock-transfer-modal">
            <div className="depo-modal-header">
              <h2>Depo - Stok Al ve Transfer Yap</h2>
              <button className="depo-close-btn" onClick={handleDepoClose}>
                <X size={24} />
              </button>
            </div>

            {/* Mod Seçici */}
            <div className="depo-mode-bar">
              <button
                className={`depo-mode-btn ${depoMode === 'stock_entry' ? 'active stock' : ''}`}
                onClick={() => { setDepoMode('stock_entry'); setTimeout(() => depoBarcodeRef.current?.focus(), 100); }}
              >
                <Download size={16} />
                Stok Giriş
              </button>
              <button
                className={`depo-mode-btn ${depoMode === 'transfer_osmangazi' ? 'active osmangazi' : ''}`}
                onClick={() => { setDepoMode('transfer_osmangazi'); setTimeout(() => depoBarcodeRef.current?.focus(), 100); }}
              >
                <Send size={16} />
                Osmangazi Transfer
              </button>
              <button
                className={`depo-mode-btn ${depoMode === 'transfer_yesilsu' ? 'active yesilsu' : ''}`}
                onClick={() => { setDepoMode('transfer_yesilsu'); setTimeout(() => depoBarcodeRef.current?.focus(), 100); }}
              >
                <Send size={16} />
                Yeşil Su Transfer
              </button>
              <button
                className={`depo-mode-btn ${depoSoundEnabled ? 'active stock' : ''}`}
                onClick={() => setDepoSoundEnabled(prev => !prev)}
                title={depoSoundEnabled ? 'Sesi kapat' : 'Sesi aç'}
                style={{ marginLeft: 'auto' }}
              >
                {depoSoundEnabled ? <Bell size={16} /> : <BellOff size={16} />}
              </button>
            </div>

            {/* Barkod Giriş */}
            <div className="depo-barcode-bar">
              <input
                ref={depoBarcodeRef}
                type="text"
                value={depoBarcode}
                onChange={(e) => setDepoBarcode(e.target.value)}
                onKeyDown={handleDepoBarcodeScan}
                placeholder={
                  depoMode === 'stock_entry' ? 'Barkod okutun → Depoya stok girişi yapılır...'
                  : depoMode === 'transfer_osmangazi' ? 'Barkod okutun → Osmangazi\'ye aktarılır...'
                  : 'Barkod okutun → Yeşil Su\'ya aktarılır...'
                }
                autoFocus
                className="depo-barcode-input"
              />
            </div>

            {/* 3 Kolon */}
            <div className="depo-columns">
              {/* Sol: Osmangazi */}
              <div className="depo-column osmangazi">
                <div className="depo-column-header osmangazi">
                  <h3>Osmangazi</h3>
                  <span className="depo-column-count">
                    {Object.values(depoOsmangaziItems).reduce((s, i) => s + i.quantity, 0)} ürün
                  </span>
                </div>
                <div className="depo-column-items">
                  {Object.entries(depoOsmangaziItems).reverse().map(([productId, item]) => (
                    <div key={productId} className="depo-product-card">
                      {item.product.image && (
                        <img
                          src={`${IMAGE_HOST}${item.product.image}`}
                          alt={item.product.name}
                          className="depo-product-img"
                          style={{ cursor: 'zoom-in' }}
                          onClick={() => setZoomedImage(`${IMAGE_HOST}${item.product.image}`)}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="depo-product-info">
                        <span className="depo-product-name">{item.product.name}</span>
                        <span className="depo-product-barcode">{item.product.barcode}</span>
                      </div>
                      <div className="depo-product-actions">
                        <button onClick={() => handleDepoItemQuantity('osmangazi', productId, -1)}>−</button>
                        <span className="depo-product-qty" onDoubleClick={(e) => { const span = e.target; const input = document.createElement('input'); input.type = 'number'; input.min = '1'; input.value = item.quantity; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #3b82f6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.max(1, parseInt(input.value) || 1); handleDepoItemQuantity('osmangazi', productId, val - item.quantity); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>{item.quantity}</span>
                        <button onClick={() => handleDepoItemQuantity('osmangazi', productId, 1)}>+</button>
                      </div>
                      <button className="depo-move-btn" title="Depoya geri gönder" onClick={() => handleDepoMoveItem('osmangazi', productId, 'center')}>
                        <ArrowLeftRight size={14} />
                      </button>
                    </div>
                  ))}
                  {Object.keys(depoOsmangaziItems).length === 0 && (
                    <div className="depo-column-empty">Transfer modu ile barkod okutarak ürün ekleyin</div>
                  )}
                </div>
              </div>

              {/* Orta: Depo */}
              <div className="depo-column center">
                <div className="depo-column-header center">
                  <h3>Depo (Stok Girişi)</h3>
                  <span className="depo-column-count">
                    {Object.values(depoCenterItems).reduce((s, i) => s + i.quantity, 0)} ürün
                  </span>
                  <label className="depo-inventory-checkbox">
                    <input
                      type="checkbox"
                      checked={showDepoInventory}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setShowDepoInventory(checked);
                        if (checked) fetchDepoInventory();
                      }}
                    />
                    Envanterdeki ürünleri göster
                  </label>
                </div>
                <div className="depo-column-items">
                  {Object.entries(depoCenterItems).reverse().map(([productId, item]) => (
                    <div key={productId} className="depo-product-card">
                      {item.product.image && (
                        <img
                          src={`${IMAGE_HOST}${item.product.image}`}
                          alt={item.product.name}
                          className="depo-product-img"
                          style={{ cursor: 'zoom-in' }}
                          onClick={() => setZoomedImage(`${IMAGE_HOST}${item.product.image}`)}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="depo-product-info">
                        <span className="depo-product-name">{item.product.name}</span>
                        <span className="depo-product-barcode">{item.product.barcode}</span>
                      </div>
                      <div className="depo-product-actions">
                        <button onClick={() => handleDepoItemQuantity('center', productId, -1)}>−</button>
                        <span className="depo-product-qty" onDoubleClick={(e) => { const span = e.target; const input = document.createElement('input'); input.type = 'number'; input.min = '1'; input.value = item.quantity; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #3b82f6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.max(1, parseInt(input.value) || 1); handleDepoItemQuantity('center', productId, val - item.quantity); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>{item.quantity}</span>
                        <button onClick={() => handleDepoItemQuantity('center', productId, 1)}>+</button>
                      </div>
                      <div className="depo-move-btns">
                        <button className="depo-move-btn osmangazi" title="Osmangazi'ye taşı" onClick={() => handleDepoMoveItem('center', productId, 'osmangazi')}>
                          ← OSM
                        </button>
                        <button className="depo-move-btn yesilsu" title="Yeşil Su'ya taşı" onClick={() => handleDepoMoveItem('center', productId, 'yesilsu')}>
                          YŞL →
                        </button>
                      </div>
                    </div>
                  ))}
                  {Object.keys(depoCenterItems).length === 0 && !showDepoInventory && (
                    <div className="depo-column-empty">Stok giriş modunda barkod okutarak ürün ekleyin</div>
                  )}
                  {showDepoInventory && (
                    <>
                      {Object.keys(depoCenterItems).length > 0 && (
                        <div className="depo-inventory-divider">Depo Envanteri</div>
                      )}
                      {loadingDepoInventory ? (
                        <div className="depo-column-empty">Envanter yükleniyor...</div>
                      ) : (
                        depoInventoryProducts
                          .filter(p => !depoCenterItems[p.id] && p.stock > 0)
                          .map(p => (
                            <div key={`inv-${p.id}`} className="depo-product-card depo-inventory-card">
                              {p.image && (
                                <img
                                  src={`${IMAGE_HOST}${p.image}`}
                                  alt={p.name}
                                  className="depo-product-img"
                                  style={{ cursor: 'zoom-in' }}
                                  onClick={() => setZoomedImage(`${IMAGE_HOST}${p.image}`)}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div className="depo-product-info">
                                <span className="depo-product-name">{p.name}</span>
                                <span className="depo-product-barcode">{p.barcode}</span>
                              </div>
                              <div className="depo-product-actions">
                                <span className="depo-inventory-stock">Stok: {p.stock}</span>
                              </div>
                            </div>
                          ))
                      )}
                      {!loadingDepoInventory && depoInventoryProducts.filter(p => !depoCenterItems[p.id] && p.stock > 0).length === 0 && (
                        <div className="depo-column-empty">Depoda stoklu ürün bulunamadı</div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Sağ: Yeşil Su */}
              <div className="depo-column yesilsu">
                <div className="depo-column-header yesilsu">
                  <h3>Yeşil Su</h3>
                  <span className="depo-column-count">
                    {Object.values(depoYesilsuItems).reduce((s, i) => s + i.quantity, 0)} ürün
                  </span>
                </div>
                <div className="depo-column-items">
                  {Object.entries(depoYesilsuItems).reverse().map(([productId, item]) => (
                    <div key={productId} className="depo-product-card">
                      {item.product.image && (
                        <img
                          src={`${IMAGE_HOST}${item.product.image}`}
                          alt={item.product.name}
                          className="depo-product-img"
                          style={{ cursor: 'zoom-in' }}
                          onClick={() => setZoomedImage(`${IMAGE_HOST}${item.product.image}`)}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="depo-product-info">
                        <span className="depo-product-name">{item.product.name}</span>
                        <span className="depo-product-barcode">{item.product.barcode}</span>
                      </div>
                      <div className="depo-product-actions">
                        <button onClick={() => handleDepoItemQuantity('yesilsu', productId, -1)}>−</button>
                        <span className="depo-product-qty" onDoubleClick={(e) => { const span = e.target; const input = document.createElement('input'); input.type = 'number'; input.min = '1'; input.value = item.quantity; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #3b82f6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.max(1, parseInt(input.value) || 1); handleDepoItemQuantity('yesilsu', productId, val - item.quantity); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>{item.quantity}</span>
                        <button onClick={() => handleDepoItemQuantity('yesilsu', productId, 1)}>+</button>
                      </div>
                      <button className="depo-move-btn" title="Depoya geri gönder" onClick={() => handleDepoMoveItem('yesilsu', productId, 'center')}>
                        <ArrowLeftRight size={14} />
                      </button>
                    </div>
                  ))}
                  {Object.keys(depoYesilsuItems).length === 0 && (
                    <div className="depo-column-empty">Transfer modu ile barkod okutarak ürün ekleyin</div>
                  )}
                </div>
              </div>
            </div>

            {/* Alt Özet Bar */}
            <div className="depo-summary-bar">
              <div className="depo-summary-stats">
                <span className="depo-stat total">
                  <strong>
                    {Object.values(depoCenterItems).reduce((s, i) => s + i.quantity, 0)
                    + Object.values(depoOsmangaziItems).reduce((s, i) => s + i.quantity, 0)
                    + Object.values(depoYesilsuItems).reduce((s, i) => s + i.quantity, 0)}
                  </strong> toplam ürün
                </span>
                <span className="depo-stat osmangazi">
                  <strong>{Object.values(depoOsmangaziItems).reduce((s, i) => s + i.quantity, 0)}</strong> Osmangazi'ye
                </span>
                <span className="depo-stat yesilsu">
                  <strong>{Object.values(depoYesilsuItems).reduce((s, i) => s + i.quantity, 0)}</strong> Yeşil Su'ya
                </span>
                <span className="depo-stat center">
                  <strong>{Object.values(depoCenterItems).reduce((s, i) => s + i.quantity, 0)}</strong> depoda kalacak
                </span>
              </div>
              <button
                className="depo-complete-btn"
                onClick={handleDepoComplete}
                disabled={savingDepoOperation || (
                  Object.keys(depoCenterItems).length === 0 &&
                  Object.keys(depoOsmangaziItems).length === 0 &&
                  Object.keys(depoYesilsuItems).length === 0
                )}
              >
                {savingDepoOperation ? 'İşlem yapılıyor...' : 'İşlemi Tamamla'}
              </button>
            </div>

            {/* Depo Kapatma Onay Modalı */}
            {showDepoCloseConfirm && (
              <div className="modal-overlay" style={{ zIndex: 10001 }}>
                <div className="modal" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2><AlertTriangle size={20} style={{ marginRight: '8px', color: '#f59e0b' }} /> Transferi Tamamlamadınız</h2>
                  </div>
                  <div className="modal-body">
                    <p style={{ color: '#64748b', marginBottom: '8px' }}>
                      Henüz tamamlanmamış ürünler var. Ne yapmak istersiniz?
                    </p>
                  </div>
                  <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowDepoCloseConfirm(false)}
                    >
                      İptal
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        localStorage.setItem('depoModalDraft', JSON.stringify({
                          center: depoCenterItems,
                          osmangazi: depoOsmangaziItems,
                          yesilsu: depoYesilsuItems,
                          mode: depoMode
                        }));
                        toast.success('İşlem kaydedildi, daha sonra devam edebilirsiniz', { autoClose: 4500 });
                        setShowDepoCloseConfirm(false);
                        setShowDepoStockTransferModal(false);
                      }}
                    >
                      Daha Sonra Tamamla
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        localStorage.removeItem('depoModalDraft');
                        setShowDepoCloseConfirm(false);
                        setShowDepoStockTransferModal(false);
                        setDepoCenterItems({});
                        setDepoOsmangaziItems({});
                        setDepoYesilsuItems({});
                      }}
                    >
                      Tamamlamadan Kapat
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stok Hareketleri Modal */}
      {showStockMovementsModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }} onClick={() => setShowStockMovementsModal(false)}>
          <div className="stock-movements-modal" onClick={e => e.stopPropagation()}>
            <div className="stock-movements-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#fff' }}>Stok Hareketleri</h2>
                {user?.role !== 'admin' && (
                  <span style={{
                    background: 'rgba(255,255,255,0.2)',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    color: '#fff',
                    fontWeight: 500
                  }}>
                    {selectedStore?.name}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowStockMovementsModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: '#fff',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Admin için mağaza sekmeleri */}
            {user?.role === 'admin' && (
              <div style={{
                display: 'flex',
                gap: '0',
                background: '#f0fdfa',
                borderBottom: '2px solid #e2e8f0',
                padding: '0 16px',
                overflowX: 'auto'
              }}>
                {stores.filter(s => s.id !== 1).map(store => (
                  <button
                    key={store.id}
                    onClick={() => {
                      setStockMovementsStoreId(store.id);
                      setStockMovementsPage(1);
                      setStockMovements([]);
                    }}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      borderBottom: stockMovementsStoreId === store.id ? '3px solid #0f766e' : '3px solid transparent',
                      background: stockMovementsStoreId === store.id ? '#ccfbf1' : 'transparent',
                      color: stockMovementsStoreId === store.id ? '#0f766e' : '#64748b',
                      fontWeight: stockMovementsStoreId === store.id ? '700' : '500',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s'
                    }}
                  >
                    {store.name}
                  </button>
                ))}
              </div>
            )}

            <div className="stock-movements-filters">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}>Tarih:</label>
                <input
                  type="date"
                  value={stockMovementsDateFrom}
                  onChange={e => { setStockMovementsDateFrom(e.target.value); setStockMovementsPage(1); }}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                />
                <span style={{ color: '#9ca3af' }}>-</span>
                <input
                  type="date"
                  value={stockMovementsDateTo}
                  onChange={e => { setStockMovementsDateTo(e.target.value); setStockMovementsPage(1); }}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '200px' }}>
                <Search size={16} style={{ color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Ürün adı veya barkod..."
                  value={stockMovementsSearch}
                  onChange={e => {
                    const val = e.target.value;
                    setStockMovementsSearch(val);
                    if (stockMovementsSearchTimeout.current) clearTimeout(stockMovementsSearchTimeout.current);
                    stockMovementsSearchTimeout.current = setTimeout(() => {
                      setStockMovementsPage(1);
                      fetchStockMovements(1);
                    }, 400);
                  }}
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={stockMovementsShowSales}
                  onChange={e => { setStockMovementsShowSales(e.target.checked); setStockMovementsPage(1); }}
                  style={{ width: '16px', height: '16px', accentColor: '#0f766e' }}
                />
                Satış ve iade hareketlerini göster
              </label>

              <span style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                Toplam: <strong>{stockMovementsTotalCount.toLocaleString('tr-TR')}</strong> hareket
              </span>
            </div>

            <div className="stock-movements-body">
              {stockMovementsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px', color: '#6b7280' }}>
                  Yükleniyor...
                </div>
              ) : stockMovements.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px', color: '#9ca3af', fontSize: '15px' }}>
                  Stok hareketi bulunamadı
                </div>
              ) : (
                <table className="stock-movements-table">
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Ürün</th>
                      <th>Hareket Tipi</th>
                      <th>Miktar</th>
                      <th className="sm-hide-mobile">Stok Önce</th>
                      <th className="sm-hide-mobile">Stok Sonra</th>
                      <th className="sm-hide-mobile">Personel</th>
                      <th className="sm-hide-mobile">Açıklama</th>
                      <th style={{ width: '80px', textAlign: 'center' }}>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockMovements.map((m, idx) => (
                      <tr key={m.id || idx}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                          {new Date(m.created_at).toLocaleDateString('tr-TR')}
                          {' '}
                          <span style={{ color: '#9ca3af' }}>
                            {new Date(m.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {m.product_image ? (
                              <img
                                src={m.product_image.startsWith('http') ? m.product_image : `${IMAGE_HOST}${m.product_image}`}
                                alt=""
                                style={{ width: '32px', height: '32px', borderRadius: '4px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div style={{ width: '32px', height: '32px', borderRadius: '4px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Package size={14} style={{ color: '#9ca3af' }} />
                              </div>
                            )}
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 500, color: '#1f2937', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {m.product_name || '-'}
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                {m.product_barcode || ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 500,
                            background: m.movement_type === 'sale' ? '#fee2e2' :
                                       m.movement_type === 'refund' ? '#dcfce7' :
                                       m.movement_type === 'exchange_in' ? '#d1fae5' :
                                       m.movement_type === 'exchange_out' ? '#fef3c7' :
                                       m.movement_type === 'manual' ? '#e0e7ff' :
                                       m.movement_type === 'sale_delete' ? '#bbf7d0' :
                                       m.movement_type === 'sale_cancel' ? '#fef3c7' :
                                       m.movement_type === 'inventory_count' ? '#fef9c3' :
                                       m.movement_type === 'excel_import' ? '#dbeafe' :
                                       m.movement_type === 'transfer_in' ? '#ccfbf1' :
                                       m.movement_type === 'transfer_out' ? '#ffedd5' :
                                       m.movement_type === 'purchase' ? '#d1fae5' :
                                       m.movement_type === 'stock_adjustment' ? '#f3e8ff' : '#f3f4f6',
                            color: m.movement_type === 'sale' ? '#dc2626' :
                                  m.movement_type === 'refund' ? '#16a34a' :
                                  m.movement_type === 'exchange_in' ? '#059669' :
                                  m.movement_type === 'exchange_out' ? '#d97706' :
                                  m.movement_type === 'manual' ? '#4f46e5' :
                                  m.movement_type === 'sale_delete' ? '#15803d' :
                                  m.movement_type === 'sale_cancel' ? '#b45309' :
                                  m.movement_type === 'inventory_count' ? '#ca8a04' :
                                  m.movement_type === 'excel_import' ? '#2563eb' :
                                  m.movement_type === 'transfer_in' ? '#0f766e' :
                                  m.movement_type === 'transfer_out' ? '#ea580c' :
                                  m.movement_type === 'purchase' ? '#059669' :
                                  m.movement_type === 'stock_adjustment' ? '#9333ea' : '#374151'
                          }}>
                            {m.movement_type_label}
                          </span>
                        </td>
                        <td style={{
                          fontWeight: 600,
                          fontSize: '14px',
                          color: m.quantity > 0 ? '#16a34a' : '#dc2626'
                        }}>
                          {m.quantity > 0 ? '+' : ''}{m.quantity}
                        </td>
                        <td className="sm-hide-mobile" style={{ color: '#6b7280', textAlign: 'center' }}>{m.stock_before}</td>
                        <td className="sm-hide-mobile" style={{ fontWeight: 600, textAlign: 'center' }}>{m.stock_after}</td>
                        <td className="sm-hide-mobile" style={{ color: '#6b7280', fontSize: '13px' }}>{m.staff_name || '-'}</td>
                        <td className="sm-hide-mobile" style={{ fontSize: '12px', color: '#6b7280', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.notes || (m.sale_receipt_number ? `Fiş: ${m.sale_receipt_number}` : '-')}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {!['sale', 'refund', 'sale_delete', 'sale_cancel'].includes(m.movement_type) && (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleEditStockMovement(m)}
                                title="Düzenle"
                                style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', padding: '4px', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteStockMovement(m)}
                                title="Sil"
                                style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '4px', padding: '4px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {stockMovementsTotalPages > 1 && (
              <div className="stock-movements-pagination">
                <button
                  disabled={stockMovementsPage <= 1}
                  onClick={() => setStockMovementsPage(p => Math.max(1, p - 1))}
                  style={{
                    padding: '6px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: stockMovementsPage <= 1 ? '#f3f4f6' : '#fff',
                    cursor: stockMovementsPage <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    color: stockMovementsPage <= 1 ? '#9ca3af' : '#374151'
                  }}
                >
                  Önceki
                </button>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  Sayfa <strong>{stockMovementsPage}</strong> / {stockMovementsTotalPages}
                </span>
                <button
                  disabled={stockMovementsPage >= stockMovementsTotalPages}
                  onClick={() => setStockMovementsPage(p => Math.min(stockMovementsTotalPages, p + 1))}
                  style={{
                    padding: '6px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: stockMovementsPage >= stockMovementsTotalPages ? '#f3f4f6' : '#fff',
                    cursor: stockMovementsPage >= stockMovementsTotalPages ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    color: stockMovementsPage >= stockMovementsTotalPages ? '#9ca3af' : '#374151'
                  }}
                >
                  Sonraki
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stok Hareketi Düzenleme Modalı */}
      {editingMovement && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001
        }} onClick={() => setEditingMovement(null)}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '24px', width: '400px', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1f2937' }}>Stok Hareketi Düzenle</h3>
            <div style={{ marginBottom: '12px', fontSize: '14px', color: '#6b7280' }}>
              <strong>{editingMovement.product_name}</strong>
            </div>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#9ca3af' }}>
              Mevcut miktar: <strong style={{ color: '#374151' }}>{editingMovement.quantity}</strong>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '6px', fontWeight: 500 }}>
                Yeni Miktar
              </label>
              <input
                type="number"
                value={editMovementQuantity}
                onChange={e => setEditMovementQuantity(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px',
                  fontSize: '14px', outline: 'none', boxSizing: 'border-box'
                }}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleSaveEditMovement(); }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingMovement(null)}
                style={{
                  padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '8px',
                  background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151'
                }}
              >
                İptal
              </button>
              <button
                onClick={handleSaveEditMovement}
                style={{
                  padding: '8px 16px', border: 'none', borderRadius: '8px',
                  background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500
                }}
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      </div> {/* main-content end */}
    </div>
  );
};

export default Products;
